import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom'; // Refreshed types
import Cropper, { Area } from 'react-easy-crop';
import io from 'socket.io-client';
import { MediaSession } from '@capgo/capacitor-media-session';
import type {
    PostType, MediaType, Comment, MediaAttachment, Post, HydratedPost,
    AchievementDifficulty, Achievement, CompletedAchievement, DailyAchievements,
    UserData, NotificationType, Notification, Attachment, Message,
    Conversation, Community, PageState, VoiceParticipant, Destination, FollowerGroup, SystemMod, Channel, ShareTarget
} from './types';
import {
    LikeIcon, BrokenHeartIcon, CommentIcon, CheckmarkIcon, BumpIcon,
    BookmarkIcon, ImageIcon, VideoIcon, AudioIcon, MicIcon, CloseIcon,
    PlayIcon, PauseIcon, TrashIcon, CameraIcon, HomeIcon, UserIcon,
    UsersIcon, MailIcon, SendIcon, SettingsIcon, EditIcon,
    ExternalLinkIcon, GalleryIcon, ChevronLeftIcon, ChevronRightIcon,
    VolumeHighIcon, VolumeOffIcon, DownloadIcon, LogoutIcon, SearchIcon,
    GoodieWhiteIcon, ReplyIcon, ForwardIcon, EmojiIcon, MoreHorizontalIcon,
    PlusIcon, EyeIcon, EyeOffIcon, TrophyIcon, HashtagIcon, GlobeIcon,
    FilterIcon, XIcon, PrivateIcon, PostcardIcon
} from './components/Icons';
import { saveImage, getAllImages, deleteImage, getPendingUploads, markImageUploaded, savePendingMessage, getAllPendingMessages, deletePendingMessage } from './indexedDb';
import { fetchAllUsers, signupUser, loginUser, guestLoginUser, claimGuestAccount, updateUserOnServer, deleteUserOnServer, fetchAllPosts, createPostOnServer, updatePostOnServer, deletePostOnServer, unbumpPostOnServer, toggleArchivePostOnServer, fetchAchievements, createAchievementOnServer, updateAchievementOnServer, deleteAchievementOnServer, reorderAchievementsOnServer, fetchConversations, createYipYapOnServer, deleteConversationOnServer, updateConversationOnServer, addMessageToConversationOnServer, deleteMessageFromConversationOnServer, markConversationAsReadOnServer, reactToMessageOnServer, startConversationOnServer, registerPushToken, deactivateAccount, deleteAccountPermanently, API_URL, fetchAllCommunities, createCommunityOnServer, fetchCommunityDetails, joinCommunityOnServer, leaveCommunityOnServer, fetchCommunityPosts, fetchJoinedCommunitiesPosts, updateCommunityOnServer, grantAdminOnServer, revokeAdminOnServer, removeMemberOnServer, sendLinkRequest, sendChannelMessage } from './api';
import { requestPasswordReset, verifyResetToken, resetPassword } from './api';
import { Geolocation } from '@capacitor/geolocation';
import { canViewPost, getFootprintCycleDate, wasPostMadeInFootprintWindow } from './utils/helpers';
import { NavBar } from './components/NavBar';
import { TodoMod } from './components/TodoMod';
import { MOD_REGISTRY, getAllMods, getDynamicModDefinition, getActiveMods } from './utils/ModRegistry';
import { PostGrid } from './components/PostGrid';
import { PostModal } from './components/PostModal';
import { GlobalPostModal } from './components/GlobalPostModal';
import { BumpActionModal, BumpModal, DeleteConfirmationModal, PostOptionsModal, SharePostModal, RemoveConnectionModal } from './components/Modals';
import { ConversationThemeModal, ForwardMessageModal, DeleteMessageModal, YipYapCreateModal, NewConversationModal } from './components/MessageModals';
import { PostCard } from './components/PostCard';
import { AudioPlayerPill } from './components/AudioPlayerPill';
import UploadProgressBar from './components/UploadProgressBar';
import { BackgroundVideoRenderer } from './components/BackgroundVideoRenderer';
import { PostCreator, FootprintPostCreatorModal } from './components/PostCreator';
import { FormattedText } from './components/FormattedText';
import { getWeightedUserList } from './utils/interactionUtils';
import { HashtagText } from './components/HashtagText';
import { LinkifyText } from './components/LinkifyText';
import { CustomAudioPlayer, AudioRecorder } from './components/AudioComponents';
import { HomePage } from './components/HomePage';
import { ProfilePage } from './components/ProfilePage';
import { ShoppingCartDrawer } from './components/ShoppingCartDrawer';
import ProxiedIframe from './components/ProxiedIframe';
import { FootprintPage } from './components/FootprintPage';
import { MessagesPage } from './components/MessagesPage';
import { FollowListModal } from './components/FollowListModal';
import { FootprintAdminModal } from './components/AdminModals';
import { Leaderboards, LeaderboardModal } from './components/Leaderboards';
import { PrivacyPage, SafetyPage } from './components/PolicyPages';
import { HashtagPage } from './components/HashtagPage';
import { CommunitiesPage } from './components/CommunitiesPage';
import { CommunityDetailPage } from './components/CommunityDetailPage';
import { PfpCropperModal } from './components/PfpCropperModal';
import { GlobalSettingsModal, AdditionalSettingsModal } from './components/SettingsModals';
import { PullToRefresh } from './components/PullToRefresh';
import { LiveNotificationPopup } from './components/LiveNotificationPopup';
import { RevertEmailPage } from './components/RevertEmailPage';
import { ShareToGoodLyfeModal } from './components/ShareToGoodLyfeModal';
import { YourCommunitiesModal } from './components/YourCommunitiesModal';
import { VoiceRoom } from './components/VoiceRoom';
import { FollowerGroupsModal } from './components/FollowerGroupsModal';
import { LinkModal } from './components/LinkModal';
import { ModLibraryModal } from './components/ModLibraryModal';
import { SystemModModal } from './components/SystemModModal';
import { MasterHand } from './components/MasterHand';
import { IdeaBankModal } from './components/IdeaBankModal';
import { AccountSwitcherModal } from './components/AccountSwitcherModal';
import TiledVideoBackground from './components/TiledVideoBackground';
import { stickerTextStyle } from './components/StickerText';

import { uploadMediaFile, compressImage, compressVideo, compressAudio, deleteMediaFile } from './utils/mediaUtils';
import { getLinkPreview } from './utils/linkPreview';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { LocalNotifications } from '@capacitor/local-notifications';
import { setupPushNotifications, requestAppPermissions, playRingtone, stopRingtone } from './utils/notificationUtils';
import { DebugLogViewer } from './components/DebugLogViewer';
import { initializeApp as initFirebase } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { AuthContext, useAuth, AudioPlayerStateContext, AudioPlayerProgressContext, useAudioPlayerState, useAudioPlayerProgress } from './contexts/AuthContext';
import { UploadProvider } from './contexts/UploadContext';
import { useScrollLock } from './hooks/useScrollLock';
import { usePersistentState } from './hooks/usePersistentState';
import { SplashScreen } from './components/SplashScreen';
import { EulaModal, EULA_ACCEPTED_KEY } from './components/EulaModal';
import { CapacitorShareTarget } from '@capgo/capacitor-share-target';
import DirectShare from './plugins/DirectShare';
import { performOAuthLogin, verifyOAuthWithBackend, finalizeOAuthSignup, OAuthProvider } from './utils/firebaseAuth';
import { AppShortcuts } from '@capawesome/capacitor-app-shortcuts';
import { getAppName } from './utils/flavor';
import { getSlideIntervalMs } from './utils/slideshowUtils';

// ========= HELPERS =========
const isVideoUrl = (url: string | undefined): boolean => {
    if (!url) return false;
    return url.includes('#video') || /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(url);
};

const preloadImage = (url: string) => {
    if (!url) return Promise.resolve();
    return new Promise((resolve) => {
        const img = new Image();
        img.src = url;
        img.onload = resolve;
        img.onerror = resolve; // Continue even if image fails to load
    });
};

console.log('[App] Script Execution Started');

const isNative = Capacitor.isNativePlatform();
const isIos = Capacitor.getPlatform() === 'ios' || /iPad|iPhone|iPod/.test(navigator.userAgent);
const isAndroid = Capacitor.getPlatform() === 'android';

window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    (window as any).deferredPrompt = e;
});

// Minimal ResetPasswordPage placeholder (restores routing until full impl is re-added)
const ResetPasswordPage = ({ token }: { token: string }) => {
    return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="text-white">Reset token: {token}</div>
        </div>
    );
};

// ========= UNIQUE MESSAGE ID COUNTER =========
// Guarantees no duplicate keys even when multiple messages are sent in the same ms
let _msgIdSeq = Date.now();
const nextMsgId = () => {
    _msgIdSeq += 1;
    return _msgIdSeq;
};

// Optimistic UI Trackers to prevent server sockets from reverting local state
const locallyDeletedMessageIds = new Set<number>();
const recentlyReactedMessageIds = new Map<number, number>();
const recentlyUpdatedConversationMetadata = new Map<number, { theme?: any, hideBubbles?: boolean, nicknames?: Record<string, string>, timestamp: number }>();
// ========= FIREBASE SETUP =========
const firebaseConfig = {
    apiKey: "AIzaSyAcLX0QqTz7_2Y0WzeG3WAfgyL-gn586gw",
    authDomain: "goodlyfe-2a70e.firebaseapp.com",
    projectId: "goodlyfe-2a70e",
    storageBucket: "goodlyfe-2a70e.firebasestorage.app",
    messagingSenderId: "190689134320",
    appId: "1:190689134320:web:e65269c4c4344c84886435",
    measurementId: "G-5Z66DGH8ZE"
};

// VAPID key for web push (from Firebase Console -> Project Settings -> Cloud Messaging -> Web Push certificates)
const VAPID_KEY = "BH0YGCl_jurDp7e7aFlOBZ2__vLaAl-RPiXzaIFz6uh63-sgVglUyuRsWcKbm4fMrJTIy1tYGqSFYcy_WqYmsnE";

let firebaseApp: any;
let messaging: any;

try {
    if (firebaseConfig.apiKey !== "YOUR_API_KEY") {
        firebaseApp = initFirebase(firebaseConfig);
        if ('serviceWorker' in navigator) {
            messaging = getMessaging(firebaseApp);
            const registerSW = () => {
                navigator.serviceWorker.register('/firebase-messaging-sw.js')
                    .then(reg => console.log('[ServiceWorker] Registered with scope:', reg.scope))
                    .catch(err => console.error('[ServiceWorker] Registration failed:', err));
            };
            if (document.readyState === 'complete') {
                registerSW();
            } else {
                window.addEventListener('load', registerSW);
            }
        }
    }
} catch (error) {
    console.error('[Firebase] Init Error:', error);
}

const VoicePortalWrapper = ({ children }: { children: React.ReactNode }) => {
    const [destination, setDestination] = useState<Element | null>(null);

    useEffect(() => {
        const updateDestination = () => {
            const el = document.getElementById('voice-portal-destination');
            if (el !== destination) setDestination(el);
        };

        updateDestination();
        const timer = setInterval(updateDestination, 500);
        return () => clearInterval(timer);
    }, [destination]);

    if (destination) {
        return createPortal(children, destination);
    }

    return <div className="hidden" aria-hidden="true">{children}</div>;
};

// Notification and permission setup moved to src/utils/notificationUtils.ts

// ========= DEBUGGING =========


// Simple Error Boundary component
class ErrorBoundary extends React.Component<{ children: React.ReactNode, onCrash: (error: string) => void }, { hasError: boolean, errorText?: string }> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError(error: any) { return { hasError: true, errorText: error.toString() }; }
    componentDidCatch(error: any, errorInfo: any) {
        const errorString = `${error.toString()}\n${errorInfo.componentStack}`;
        this.setState({ errorText: errorString });
        this.props.onCrash(errorString);
    }
    render() {
        if (this.state.hasError) return (
            <div style={{ padding: '20px', color: 'white', textAlign: 'center', marginTop: '100px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#f00' }}>Something went wrong.</h1>
                <pre style={{ textAlign: 'left', background: 'rgba(0,0,0,0.5)', padding: '10px', fontSize: '10px', overflow: 'auto', maxHeight: '400px' }}>
                    {this.state.errorText}
                </pre>

                <button
                    onClick={() => window.location.reload()}
                    style={{ marginTop: '20px', padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px' }}
                >
                    Retry Loading
                </button>
            </div>
        );
        return this.props.children;
    }
}


// Track created object URLs so we can revoke them on unmount to avoid leaks
const createdObjectUrls = new Set<string>();

// ========= TYPES =========




const getDefaultUserData = (username: string): Omit<UserData, 'password' | 'email' | 'role'> => ({
    displayName: username,
    bio: 'Just vibing.',
    websites: [],
    pfp: `https://i.pravatar.cc/150?u=${username}`,
    profileBg: { url: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80', style: 'cover' },
    homeBg: { url: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80', style: 'cover' },
    friends: [],
    following: [],
    followers: [],
    friendRequests: [],
    followerGroups: [],
    likedPosts: [],
    bookmarkedPosts: [],
    bumpedPosts: [],
    totalPoints: 0,
    streak: 0,
    completedAchievements: [],
    vouchMessage: 'I vouch for this!',
    homeBgColor: '#000000',
    homeBgMode: 'image',
    profileBgColor: '#000000',
    profileBgMode: 'image',
    loginBg: { url: 'https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2029&auto=format&fit=crop', style: 'cover' },
    loginBgColor: '#000000',
    loginBgMode: 'image',
    homeBgResolution: 100,
    profileBgResolution: 100,
    homeBgSlideshow: {
        enabled: false,
        attachments: [],
        intervalValue: 10,
        intervalUnit: 'seconds'
    },
    profileBgSlideshow: {
        enabled: false,
        attachments: [],
        intervalValue: 10,
        intervalUnit: 'seconds'
    },
    loginBgResolution: 100,
    loginBgSlideshow: {
        enabled: false,
        attachments: [],
        intervalValue: 10,
        intervalUnit: 'seconds'
    },
    allowFollowers: true, // Default to allowing followers
    showCommunityPostsOnHomeFeed: true, // Default to showing community posts
    enableNavbarAutohide: false, // Default to no autohide
    followerRequests: [],
    notificationSettings: {
        likes: true,
        comments: true,
        follows: true,
        vouches: true,
        bumps: true,
        communities: true,
        linkRequests: true
    },
    appThemeColor: '#161b22',
    appTextColor: '#ffffff',
    enforceThemeColor: false,
    enforceThemeOpacity: false,
    mutedUsers: [],
    blockedUsers: [],
    commentsTabVisibility: 'private',
    favoriteEmoji: '❤️',
    purchasedItems: [
        'classic_postcards',
        'classic_animations',
        'classic_bubbles',
        'classic_stickers',
        'classic_themes',
        'classic_icons',
        'classic_fonts',
        'classic_pfp_themes',
        'classic_navbar'
    ],
    postcardDefault: 'classic_postcards',
    activeCustomizations: {},
    status: 'active'
});

// ========= ROUTING UTILS =========
const pageToUrl = (page: PageState): string => { //when post is clicked
    switch (page.type) {
        case 'home': return '/';
        case 'profile': return `/profile/${encodeURIComponent(page.userId)}`;
        case 'storefront': return `/store/${encodeURIComponent(page.vendorName)}`;
        case 'footprint': return '/footprint';
        case 'leaderboards': return '/leaderboards';
        case 'hashtag': return `/hashtag/${encodeURIComponent(page.tag)}`;
        case 'messages': return page.username ? `/messages/${encodeURIComponent(page.username)}` : '/messages';
        case 'post': return `/post/${page.postId}`;
        case 'privacy': return '/privacy';
        case 'safety': return '/safety';
        case 'settings': return '/settings';
        case 'additional_settings': return '/additional_settings';
        case 'search': return '/search';
        case 'notifications': return '/notifs';
        case 'store': return '/store';
        case 'art_class': return '/art-class';
        case 'communities': return '/communities';
        case 'community_detail': return `/community/${encodeURIComponent(page.query)}${page.channelId ? `/${encodeURIComponent(page.channelId)}` : ''}${page.messageId ? `/${encodeURIComponent(page.messageId)}` : ''}`;
        case 'revert_email': return `/revert-email/${encodeURIComponent(page.username)}/${encodeURIComponent(page.token)}`;
        case 'reset_password': return `/reset-password/${encodeURIComponent(page.token)}`;
        case 'master_hand': return '/master-hand';
        case 'mod':
            if (page.modId === 'pimp_my_ride') return '/pimp-my-ride';
            if (page.modId === 'vibeman1_footprint') return '/footprint';
            return `/mod/${encodeURIComponent(page.modId)}${page.params ? `?${new URLSearchParams(page.params).toString()}` : ''}`;
        default: return '/';
    }
};

const getPageKey = (p: PageState): string | null => {
    switch (p.type) {
        case 'home': return 'home';
        case 'profile': return `profile:${p.userId}`;
        case 'storefront': return `storefront:${p.vendorName}`;
        case 'communities': return 'communities';
        case 'community_detail': return `community:${p.query}${p.channelId ? `:${p.channelId}` : ''}`;
        case 'footprint': return 'footprint';
        case 'leaderboards': return 'leaderboards';
        case 'messages': return p.username ? `messages:${p.username}` : 'messages';
        case 'notifications': return 'notifications';
        case 'search': return 'search';
        case 'store': return 'store';
        case 'hashtag': return `hashtag:${p.tag}`;
        case 'reset_password': return `reset_password:${p.token}`;
        case 'master_hand': return 'master_hand';
        default: return null;
    }
};

const urlToPage = (pathname: string, fromLink: boolean = false): PageState => {
    let cleanPath = pathname;
    try {
        if (pathname.includes('://')) {
            cleanPath = new URL(pathname).pathname;
        }
    } catch (e) { }

    console.log('[Routing] Mapping path to page:', cleanPath, { fromLink });
    cleanPath = cleanPath.replace(/\/+$/, '') || '/';

    if (cleanPath === '/' || cleanPath === '') return { type: 'home' };

    if (cleanPath.startsWith('/profile/')) {
        const id = decodeURIComponent(cleanPath.replace('/profile/', '')).trim();
        return { type: 'profile', userId: id };
    }
    if (cleanPath.startsWith('/store/')) {
        const vendorName = decodeURIComponent(cleanPath.replace('/store/', '')).trim();
        return { type: 'storefront', vendorName };
    }
    if (cleanPath.startsWith('/post/')) {
        const postIdStr = cleanPath.replace('/post/', '');
        const postId = parseInt(postIdStr, 10);
        if (isNaN(postId)) {
            console.warn('[Routing] Invalid Post ID:', postIdStr);
            return { type: 'home' };
        }
        return { type: 'post', postId: postId.toString(), fromLink };
    }
    if (cleanPath === '/footprint') { return { type: 'mod', modId: 'vibeman1_footprint' }; }
    if (cleanPath.startsWith('/revert-email/')) {
        const parts = cleanPath.replace('/revert-email/', '').split('/');
        const username = decodeURIComponent(parts[0]).trim();
        const token = decodeURIComponent(parts[1] || '').trim();
        return { type: 'revert_email', username, token };
    }
    if (cleanPath.startsWith('/reset-password/') || cleanPath === '/reset-password') {
        // Try path parameter first: /reset-password/TOKEN
        let token = decodeURIComponent(cleanPath.replace('/reset-password/', '')).trim();
        if (token === '/reset-password' || token === '') {
            // Try query parameter: /reset-password?token=TOKEN
            try {
                const url = new URL(window.location.href);
                token = url.searchParams.get('token') || '';
            } catch (e) {
                token = '';
            }
        }
        return { type: 'reset_password', token };
    }
    if (cleanPath.startsWith('/messages/')) {
        const username = decodeURIComponent(cleanPath.replace('/messages/', '')).trim();
        return { type: 'messages', username };
    }
    if (cleanPath.startsWith('/hashtag/')) {
        const tag = decodeURIComponent(cleanPath.replace('/hashtag/', ''));
        return { type: 'hashtag', tag };
    }
    if (cleanPath === '/leaderboards') { return { type: 'mod', modId: 'vibeman1_footprint', params: { tab: 'leaderboard' } }; }
    if (cleanPath === '/footprint') { return { type: 'mod', modId: 'vibeman1_footprint' }; }
    if (cleanPath === '/todo') { return { type: 'mod', modId: 'todo' }; }
    if (cleanPath === '/privacy') { return { type: 'privacy' }; }
    if (cleanPath === '/safety') { return { type: 'safety' }; }
    if (cleanPath === '/settings') { return { type: 'settings' }; }
    if (cleanPath === '/additional_settings') { return { type: 'additional_settings' }; }
    if (cleanPath === '/messages') return { type: 'messages' };
    if (cleanPath === '/communities') return { type: 'communities' };
    if (cleanPath === '/search') return { type: 'search' };
    if (cleanPath === '/notifs') return { type: 'notifications' };
    if (cleanPath === '/art-class') return { type: 'mod', modId: 'art_class' };
    if (cleanPath === '/pimp-my-ride') return { type: 'mod', modId: 'pimp_my_ride' };
    if (cleanPath === '/goodie_store') return { type: 'mod', modId: 'pimp_my_ride' };
    if (cleanPath === '/store') return { type: 'mod', modId: 'pimp_my_ride' };
    if (cleanPath === '/master-hand') return { type: 'master_hand' };

    if (cleanPath.startsWith('/community/')) {
        const parts = cleanPath.replace('/community/', '').split('/');
        const query = decodeURIComponent(parts[0] || '').trim();
        const channelId = parts[1] ? decodeURIComponent(parts[1]).trim() : undefined;
        const messageId = parts[2] ? decodeURIComponent(parts[2]).trim() : undefined;
        return { type: 'community_detail', query, channelId, messageId };
    }

    if (cleanPath.startsWith('/mod/')) {
        const parts = cleanPath.replace('/mod/', '').split('?');
        const modId = decodeURIComponent(parts[0]).trim();
        let params = {};
        if (parts[1]) {
            try {
                params = Object.fromEntries(new URLSearchParams(parts[1]));
            } catch (e) { }
        }
        return { type: 'mod', modId, params };
    }

    console.warn('[Routing] Unhandled path:', cleanPath);
    return { type: 'home' };
};

// ========= AUTHENTICATION =========
const AuthPage = ({
    hasAcceptedEula,
    setHasAcceptedEula,
    loginBg,
    loginBgMode,
    loginBgColor,
    loginBgResolution,
    loginBgSlideshow,
    loggedInAccounts = [],
    onCancelSwitchBack,
    onLoginSuccess
}: {
    hasAcceptedEula: boolean,
    setHasAcceptedEula: (val: boolean) => void,
    loginBg?: { url: string; style: string },
    loginBgMode?: 'image' | 'color',
    loginBgColor?: string,
    loginBgResolution?: number,
    loginBgSlideshow?: UserData['loginBgSlideshow'],
    loggedInAccounts?: string[],
    onCancelSwitchBack?: () => void,
    onLoginSuccess?: (username: string, password: string) => void
}) => {
    const [isLoginView, setIsLoginView] = useState(true);
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');

    const [loginSlideshowIdx, setLoginSlideshowIdx] = useState(0);

    const [savedCreds, setSavedCreds] = useState<any[]>([]);
    const [showSavedCredsPrompt, setShowSavedCredsPrompt] = useState(true);
    const [isAutoLoggingIn, setIsAutoLoggingIn] = useState<string | null>(null);

    useEffect(() => {
        try {
            const stored = localStorage.getItem('goodlyfe_saved_credentials');
            if (stored) {
                setSavedCreds(JSON.parse(stored));
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    const handleRemoveSavedCredential = (e: React.MouseEvent, targetUsername: string) => {
        e.stopPropagation();
        try {
            const updated = savedCreds.filter(c => c.username.toLowerCase() !== targetUsername.toLowerCase());
            localStorage.setItem('goodlyfe_saved_credentials', JSON.stringify(updated));
            setSavedCreds(updated);
        } catch (err) {
            console.error(err);
        }
    };

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const handleSavedCredLogin = async (c: any) => {
        setIsAutoLoggingIn(c.username);
        setError('');
        try {
            const decodedPassword = decodeURIComponent(escape(atob(c.password)));
            await login(c.username, decodedPassword);
            onLoginSuccess?.(c.username, decodedPassword);
        } catch (err: any) {
            console.error("Saved credentials login failed:", err);
            setError("Saved login failed. Please enter credentials manually.");
            setIsAutoLoggingIn(null);
            setShowSavedCredsPrompt(false);
        }
    };

    useEffect(() => {
        const attachments = loginBgSlideshow?.attachments || [];
        if (!attachments.length || attachments.length <= 1) return;

        const slide = attachments[loginSlideshowIdx % attachments.length];
        const intervalMs = getSlideIntervalMs(slide, loginBgSlideshow);
        const timer = window.setInterval(() => {
            setLoginSlideshowIdx(prev => (prev + 1) % attachments.length);
        }, intervalMs);

        return () => window.clearInterval(timer);
    }, [loginBgSlideshow, loginSlideshowIdx]);

    const activeLoginBg = useMemo<{ url: string; style: string; resolution?: number }>(() => {
        const defaultBg: { url: string; style: string; resolution?: number } = { url: 'https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2029&auto=format&fit=crop', style: 'cover' };
        let bg: { url: string; style: string; resolution?: number } = loginBg || defaultBg;
        const slides = loginBgSlideshow?.attachments;
        if (loginBgMode === 'image' && slides && slides.length > 0) {
            const slide = slides[loginSlideshowIdx % slides.length];
            if (slide && slide.url) {
                bg = {
                    url: slide.url,
                    style: slide.style || loginBg?.style || 'cover',
                    resolution: slide.resolution || loginBgResolution || 100
                };
            }
        }
        return bg;
    }, [loginBg, loginBgMode, loginBgSlideshow, loginSlideshowIdx]);

    const { login, signup, loginAsDemo, guestLogin, oauthLogin, usersData } = useAuth();
    const [showInviteOptions, setShowInviteOptions] = useState(() => {
        const p = window.location.pathname;
        return p.startsWith('/communities/') || p.startsWith('/yipyap/') || p.startsWith('/footprint');
    });

    // OAuth State
    const [isOAuthSignup, setIsOAuthSignup] = useState(false);
    const [oauthData, setOauthData] = useState<any>(null);
    const [oauthLoading, setOauthLoading] = useState<string | null>(null);

    const handleOAuthFlow = async (provider: OAuthProvider) => {
        try {
            setOauthLoading(provider);
            setError('');
            const { idToken } = await performOAuthLogin(provider);
            const res = await verifyOAuthWithBackend(idToken, provider);

            if (res.isNewUser) {
                setOauthData({ ...res.oauthData, idToken });
                setIsOAuthSignup(true);
                setIsLoginView(false); // Switch to join view
            } else {
                await oauthLogin(res.user);
            }
        } catch (err: any) {
            console.error('[OAuth] flow failed:', err);
            setError(`OAuth Login failed: ${err.message}`);
        } finally {
            setOauthLoading(null);
        }
    };

    // Forgot Password State
    const [isForgotModalOpen, setIsForgotModalOpen] = useState(false);
    useScrollLock(isForgotModalOpen);
    const [resetEmail, setResetEmail] = useState('');
    const [resetStatus, setResetStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

    // EULA Logic
    const [eulaChecked, setEulaChecked] = useState(hasAcceptedEula);
    const [showEula, setShowEula] = useState(false);

    useEffect(() => {
        if (hasAcceptedEula && !eulaChecked) {
            setEulaChecked(true);
        }
    }, [hasAcceptedEula]);

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setResetStatus('loading');
        try {
            await requestPasswordReset(resetEmail);
            setResetStatus('success');
        } catch (err: any) {
            setResetStatus('error');
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!username || !password) {
            setError('Please enter both username and password.');
            return;
        }
        try {
            await login(username, password);
            onLoginSuccess?.(username, password);
        } catch (err: any) {
            const status = err.response?.status;
            const detail = err.response?.data?.error || err.message;
            if (status === 404) {
                setError(`Account "${username}" not found.`);
            } else if (status === 401) {
                setError('Invalid password.');
            } else {
                setError(`Login failed: ${detail}`);
            }
            return;
        }
        // Fire-and-forget: trigger Google/Apple password manager save prompt
        // Must be outside the try/catch so it never blocks or interferes with navigation
        if ((window as any).PasswordCredential && navigator.credentials) {
            try {
                const cred = new (window as any).PasswordCredential({
                    id: username,
                    password: password,
                    name: username,
                });
                navigator.credentials.store(cred); // intentionally no await
            } catch (_) { /* silently ignore if unsupported */ }
        }
    };

    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (isOAuthSignup && oauthData) {
            if (!username) {
                setError('Please choose a username.');
                return;
            }
            try {
                // Call finalizeOAuthSignup which will create the user and return user object
                const { idToken } = await performOAuthLogin(oauthData.provider); // Need to get fresh token if expired, or better, we could have saved it. But Capacitor plugin might cache it. Let's assume we can just use finalizeOAuthSignup with the current state or get a new one. Wait, we can't save `idToken` easily in state securely for long, but we can call finalize with `idToken`.
                // Actually we should have saved `idToken` in state. Let's add it to oauthData.
                // For now, let's assume `oauthData.idToken` is set in handleOAuthFlow.
                const res = await finalizeOAuthSignup(oauthData.idToken, username, oauthData.provider);
                await oauthLogin(res.user);
            } catch (err: any) {
                setError(err.response?.data?.error || err.message);
            }
            return;
        }

        if (!username || !email || !password || !confirmPassword) {
            setError('Please fill out all fields.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        try {
            await signup(username, email, password);
        } catch (err: any) {
            setError(err.message);
        }
    };

    return (
        <>
            <div className="fixed inset-0 z-[-1] overflow-hidden bg-black">
                {loginBgMode === 'color' ? (
                    <div className="absolute inset-0 transition-all duration-700" style={{ backgroundColor: loginBgColor || '#000000' }} />
                ) : isVideoUrl(activeLoginBg?.url) ? (
                    activeLoginBg?.style === 'tile' ? (
                        <TiledVideoBackground
                            src={activeLoginBg.url}
                            resolutionVw={loginBgResolution || 100}
                            style={{ position: 'absolute', inset: 0, zIndex: -2 }}
                        />
                    ) : (
                        <video
                            key={activeLoginBg?.url}
                            src={activeLoginBg?.url}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="absolute inset-0 w-full h-full object-cover transition-all duration-700"
                            style={{ pointerEvents: 'none' }}
                        />
                    )
                ) : (
                    <div
                        className="absolute inset-0 transition-all duration-700"
                        style={{
                            backgroundImage: activeLoginBg?.url ? `url(${activeLoginBg.url})` : 'none',
                            backgroundSize: activeLoginBg?.style === 'tile' ? `${activeLoginBg?.resolution || loginBgResolution || 100}%` : 'cover',
                            backgroundRepeat: activeLoginBg?.style === 'tile' ? 'repeat' : 'no-repeat',
                            backgroundPosition: 'center'
                        }}
                    />
                )}
                {/* Overlay to ensure readability */}
                <div className="absolute inset-0 bg-black/60 " />
            </div>

            <div className="min-h-[100vh] flex items-center justify-center p-4 relative z-10">
                <div className="w-full max-w-md">
                    <div className="bg-gray-900/60 rounded-[2.5rem] shadow-2xl p-8 border border-white/10">
                        <h2 className="text-3xl font-bold text-raised text-center text-white mb-2">{isLoginView ? getAppName() : 'Join the fun!'}</h2>
                        <p className="text-center text-raised text-gray-400 mb-6">{isLoginView ? 'an actually fun social media' : 'Make life a game!'}</p>

                        {error && <p className="bg-red-500/20 text-red-400 text-sm p-3 rounded-lg mb-4 text-center">{error}</p>}

                        {showInviteOptions ? (
                            <div className="flex flex-col gap-4">
                                <button
                                    onClick={() => setShowInviteOptions(false)}
                                    className="w-full font-bold py-4 px-4 rounded-2xl transition-all text-lg shadow-lg bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/20 active:scale-[0.98]"
                                >
                                    Log In / Sign Up
                                </button>
                                <button
                                    onClick={guestLogin}
                                    className="w-full font-bold py-4 px-4 rounded-2xl transition-all text-lg shadow-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white active:scale-[0.98]"
                                >
                                    Continue as Guest
                                </button>
                            </div>
                        ) : isLoginView ? (
                            <form
                                id="login-form"
                                onSubmit={handleLogin}
                                method="POST"
                                action="#"
                                autoComplete="on"
                            >
                                <div className="mb-4">
                                    <label className="block text-gray-400 text-sm text-raised font-bold mb-2" htmlFor="login-username">Username</label>
                                    <input
                                        id="login-username"
                                        name="username"
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="goodlyfer123"
                                        autoComplete="username"
                                        className="w-full bg-gray-800/60 rounded-lg py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                                        required
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="block text-gray-400 text-raised text-sm font-bold mb-2" htmlFor="login-password">Password</label>
                                    <div className="relative">
                                        <input
                                            id="login-password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            autoComplete="current-password"
                                            className="w-full bg-gray-800/60 rounded-lg py-2 pl-4 pr-10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors p-1"
                                            tabIndex={-1}
                                        >
                                            {showPassword ? (
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    <div className="flex justify-end mt-2">
                                        <button
                                            type="button"
                                            onClick={() => setIsForgotModalOpen(true)}
                                            className="text-sm text-blue-400 hover:text-blue-300 transition-colors bg-transparent border-none cursor-pointer"
                                        >
                                            Reset Password
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 mb-6 bg-gray-800/40 p-3 rounded-xl border border-gray-700/30">
                                    <input
                                        type="checkbox"
                                        id="login-eula-auth"
                                        checked={eulaChecked}
                                        onChange={(e) => setEulaChecked(e.target.checked)}
                                        className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500/50 transition-all cursor-pointer"
                                    />
                                    <label htmlFor="login-eula-auth" className="text-sm text-gray-300 leading-tight cursor-pointer select-none">
                                        I agree to the <button type="button" onClick={() => setShowEula(true)} className="text-blue-400 hover:text-blue-300 font-semibold transition-colors underline decoration-blue-400/30 underline-offset-4">Terms and Agreement</button>
                                    </label>
                                </div>
                                <button
                                    type="submit"
                                    disabled={!eulaChecked}
                                    className={`w-full font-bold py-4 px-4 rounded-2xl transition-all text-lg shadow-lg ${eulaChecked
                                        ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/20 active:scale-[0.98]'
                                        : 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700'}`}
                                >
                                    Log In
                                </button>
                            </form>
                        ) : (
                            <form
                                id="signup-form"
                                onSubmit={handleSignup}
                                method="POST"
                                action="#"
                                autoComplete="on"
                            >
                                {!isOAuthSignup && (
                                    <div className="mb-4">
                                        <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="signup-email">Email</label>
                                        <input
                                            id="signup-email"
                                            name="email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@example.com"
                                            autoComplete="email"
                                            className="w-full bg-gray-800/60 rounded-lg py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                                            required={!isOAuthSignup}
                                        />
                                    </div>
                                )}
                                <div className="mb-4">
                                    <label className="block text-gray-400 text-sm text-raised font-bold mb-2" htmlFor="signup-username">Username</label>
                                    <input
                                        id="signup-username"
                                        name="username"
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="goodlyfer123"
                                        autoComplete="username"
                                        className="w-full bg-gray-800/60 rounded-lg py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                                        required
                                    />
                                </div>
                                {!isOAuthSignup && (
                                    <div className="mb-6">
                                        <label className="block text-gray-400 text-raised text-sm font-bold mb-2" htmlFor="signup-password">Password</label>
                                        <div className="relative">
                                            <input
                                                id="signup-password"
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="••••••••"
                                                autoComplete="new-password"
                                                className="w-full bg-gray-800/60 rounded-lg py-2 pl-4 pr-10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                                                required={!isOAuthSignup}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors p-1"
                                                tabIndex={-1}
                                            >
                                                {showPassword ? (
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {(!isLoginView && !isOAuthSignup) && (
                                    <div className="mb-6">
                                        <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="signup-confirm-password">Confirm Password</label>
                                        <div className="relative">
                                            <input
                                                id="signup-confirm-password"
                                                name="confirm-password"
                                                type={showPassword ? "text" : "password"}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                placeholder="••••••••"
                                                autoComplete="new-password"
                                                className="w-full bg-gray-800/60 rounded-lg py-2 pl-4 pr-10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                                                required={!isOAuthSignup}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors p-1"
                                                tabIndex={-1}
                                            >
                                                {showPassword ? (
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div className="flex items-center gap-3 mb-6 bg-gray-800/40 p-3 rounded-xl border border-gray-700/30">
                                    <input
                                        type="checkbox"
                                        id="signup-eula-auth"
                                        checked={eulaChecked}
                                        onChange={(e) => setEulaChecked(e.target.checked)}
                                        className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500/50 transition-all cursor-pointer"
                                    />
                                    <label htmlFor="signup-eula-auth" className="text-sm text-gray-300 leading-tight cursor-pointer select-none">
                                        I agree to the <button type="button" onClick={() => setShowEula(true)} className="text-blue-400 hover:text-blue-300 font-semibold transition-colors underline decoration-blue-400/30 underline-offset-4">Terms and Agreement</button>
                                    </label>
                                </div>
                                <button
                                    type="submit"
                                    disabled={!eulaChecked}
                                    className={`w-full font-bold py-4 px-4 rounded-2xl transition-all text-lg shadow-lg ${eulaChecked
                                        ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/20 active:scale-[0.98]'
                                        : 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700'}`}
                                >
                                    Sign Up
                                </button>
                            </form>
                        )}

                        {/* OAuth Login Buttons (Temporarily Disabled) */}
                        {/* 
                        {isLoginView && (
                            <div className="mt-6">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="h-px bg-gray-800/50 flex-1"></div>
                                    <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">or continue with</span>
                                    <div className="h-px bg-gray-800/50 flex-1"></div>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <button 
                                        type="button"
                                        onClick={() => handleOAuthFlow('google')}
                                        disabled={oauthLoading !== null}
                                        className="flex items-center justify-center py-3 bg-white hover:bg-gray-100 rounded-xl transition-all disabled:opacity-50"
                                    >
                                        {oauthLoading === 'google' ? <span className="w-5 h-5 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin"></span> : (
                                            <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                                        )}
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => handleOAuthFlow('apple')}
                                        disabled={oauthLoading !== null}
                                        className="flex items-center justify-center py-3 bg-black hover:bg-gray-900 border border-white/10 rounded-xl transition-all disabled:opacity-50"
                                    >
                                        {oauthLoading === 'apple' ? <span className="w-5 h-5 border-2 border-gray-600 border-t-white rounded-full animate-spin"></span> : (
                                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M15.42 12.01c0-2.31 1.89-3.48 1.98-3.53-1.07-1.57-2.74-1.78-3.34-1.81-1.43-.14-2.79.84-3.53.84-.73 0-1.85-.82-3.03-.8-1.53.02-2.95.89-3.74 2.27-1.59 2.76-.41 6.84 1.15 9.09.76 1.1 1.67 2.32 2.87 2.28 1.15-.04 1.59-.74 2.99-.74 1.38 0 1.78.74 2.99.71 1.25-.02 2.03-1.11 2.78-2.21 1.07-1.56 1.51-3.08 1.53-3.15-.03-.01-2.65-1.02-2.65-3.95zm-2.07-5.32c.62-.75 1.04-1.79.93-2.83-.89.04-1.97.6-2.62 1.35-.58.67-1.06 1.74-.93 2.76.99.08 1.99-.54 2.62-1.28z"/></svg>
                                        )}
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => handleOAuthFlow('facebook')}
                                        disabled={oauthLoading !== null}
                                        className="flex items-center justify-center py-3 bg-[#1877F2] hover:bg-[#166FE5] rounded-xl transition-all disabled:opacity-50"
                                    >
                                        {oauthLoading === 'facebook' ? <span className="w-5 h-5 border-2 border-blue-300 border-t-white rounded-full animate-spin"></span> : (
                                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                        */
                        }

                        {loggedInAccounts.length > 0 && onCancelSwitchBack && (
                            <div className="mt-4">
                                <button
                                    type="button"
                                    onClick={onCancelSwitchBack}
                                    className="w-full py-3 px-4 rounded-2xl bg-white/5 border border-white/10 text-white font-bold text-sm hover:bg-white/10 hover:border-white/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                                >
                                    Cancel & Switch Back
                                </button>
                            </div>
                        )}
                        {!showInviteOptions && (
                            <>
                                <p className="text-center text-gray-500 text-sm mt-6">
                                    {isLoginView ? "Don't have an account?" : "Already have an account?"}
                                    <button onClick={() => { setIsLoginView(!isLoginView); setError('') }} className="font-bold text-blue-500 hover:text-blue-400 ml-2">
                                        {isLoginView ? 'Join the fun!' : 'Log In'}
                                    </button>
                                </p>

                                {loggedInAccounts.length === 0 && (
                                    <div className="mt-8 pt-6 border-t border-gray-800/50 flex flex-col items-center gap-4">
                                        <button
                                            onClick={loginAsDemo}
                                            className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                                        >
                                            Just want to look around? <span className="text-blue-400">Play Around</span>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {!isNative && (
                            <div className="mt-8 grid grid-cols-2 gap-4">
                                <a
                                    href="https://apps.apple.com/us/app/goodlyfe-digital-footprint/id6759883239"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-col items-center justify-center p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group"
                                >
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Get on iOS</span>
                                    <span className="text-sm text-white font-bold">App Store</span>
                                </a>
                                <a
                                    href="https://play.google.com/store/apps/details?id=com.digitalfootprint.app"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-col items-center justify-center p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group"
                                >
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Get on Android</span>
                                    <span className="text-sm text-white font-bold">Google Play</span>
                                </a>
                            </div>
                        )}

                        {((window as any).Capacitor?.getPlatform() !== 'ios') && (
                            <div className="pt-6 text-center">
                                <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-4">Support please, your lyfe will be gooder</p>
                                <div className="flex flex-wrap justify-center gap-3 mb-6">
                                    <a href="https://cash.app/$ejikthxbai" target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-6 py-2 bg-[#00D632]/10 hover:bg-[#00D632]/20 text-[#00D632] rounded-full text-xs font-black transition-all border border-[#00D632]/20 shadow-lg shadow-[#00D632]/5">
                                        <span className="text-sm font-bold"></span> CashApp
                                    </a>
                                    <a href="https://venmo.com/ejiketion" target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-6 py-2 bg-[#008CFF]/10 hover:bg-[#008CFF]/20 text-[#008CFF] rounded-full text-xs font-black transition-all border border-[#008CFF]/20 shadow-lg shadow-[#008CFF]/5">
                                        Venmo
                                    </a>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-center gap-4 mt-4">
                            <button
                                onClick={() => window.location.pathname = '/privacy'}
                                className="text-xs text-gray-400 hover:text-white transition-colors"
                            >
                                Privacy Policy
                            </button>
                            <button
                                onClick={() => window.location.pathname = '/safety'}
                                className="text-xs text-gray-400 hover:text-white transition-colors"
                            >
                                Safety Standards
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* EULA Modal Overlay */}
            {showEula && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
                    <div className="bg-gray-900 border border-gray-700 w-full max-w-2xl rounded-3xl overflow-hidden flex flex-col shadow-2xl max-h-[80vh]">
                        <div className="p-6 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
                            <h3 className="text-xl font-bold text-white">Terms and Agreement</h3>
                            <button onClick={() => setShowEula(false)} className="p-2 hover:bg-gray-800 rounded-full transition-colors">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto text-gray-300 space-y-4 text-sm leading-relaxed custom-scrollbar">
                            <p className="text-blue-400 font-semibold mb-2">Welcome to GoodLyfe! By using our platform, you agree to these community standards and policies:</p>

                            <h4 className="text-white font-bold mt-4 uppercase tracking-wider text-xs">1. User Conduct</h4>
                            <p>GoodLyfe is a positive community. You agree not to post content that is illegal, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or invasive of another's privacy.</p>

                            <h4 className="text-white font-bold mt-4 uppercase tracking-wider text-xs">2. Zero Tolerance for Objectionable Content</h4>
                            <p>There is no tolerance for objectionable content or abusive users. Any reports of such content will be reviewed within 24 hours and may result in immediate content removal and/or account termination.</p>

                            <h4 className="text-white font-bold mt-4 uppercase tracking-wider text-xs">3. Respecting Other Users</h4>
                            <p>GoodLyfe provides tools to mute and block users. We encourage users to use these safety features to manage their own experience and report any violations of our terms.</p>

                            <h4 className="text-white font-bold mt-4 uppercase tracking-wider text-xs">4. Content Ownership</h4>
                            <p>You retain ownership of the content you post, but you grant GoodLyfe a worldwide, non-exclusive license to host, store, and display your content to provide our services.</p>

                            <h4 className="text-white font-bold mt-4 uppercase tracking-wider text-xs">5. Privacy</h4>
                            <p>Your privacy is important to us. Please review our Privacy Policy to understand how we collect and protect your data.</p>
                        </div>
                        <div className="p-6 border-t border-gray-800 bg-gray-900/50">
                            <button
                                onClick={() => { setHasAcceptedEula(true); setEulaChecked(true); setShowEula(false); }}
                                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-2xl transition-all"
                            >
                                I Accept These Terms
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Forgot Password Modal */}
            {isForgotModalOpen && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 animate-fade-in-fast backdrop-blur-sm">
                    <div className="bg-gray-900 p-8 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl relative">
                        <button
                            onClick={() => setIsForgotModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        <h3 className="text-xl font-bold mb-4 text-white">Reset Password</h3>
                        {resetStatus === 'success' ? (
                            <div className="text-center py-4">
                                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                </div>
                                <h4 className="text-lg font-bold text-white mb-2">Check your inbox</h4>
                                <p className="text-gray-400 mb-6">We've sent a password reset link to <span className="text-white">{resetEmail}</span>.</p>
                                <button onClick={() => setIsForgotModalOpen(false)} className="w-full bg-gray-800 hover:bg-gray-700 py-3 rounded-lg text-white font-bold transition-colors">Close</button>
                            </div>
                        ) : (
                            <form onSubmit={handleForgotPassword} method="POST" action="#">
                                <p className="text-gray-400 mb-6 text-sm">Enter the email address associated with your account and we'll send you a link to reset your password.</p>

                                <div className="mb-4">
                                    <label className="block text-gray-400 text-xs font-bold mb-2 uppercase tracking-wider">Email Address</label>
                                    <input
                                        type="email"
                                        value={resetEmail}
                                        onChange={e => setResetEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        className="w-full bg-gray-800 p-3 rounded-lg text-white border border-gray-700 focus:border-blue-500 outline-none transition-colors"
                                        required
                                        autoFocus
                                    />
                                </div>

                                {resetStatus === 'error' && (
                                    <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-lg mb-4 text-sm flex items-center gap-2">
                                        <span>Failed to send reset email. Please try again.</span>
                                    </div>
                                )}

                                <div className="flex justify-end gap-3 mt-6">
                                    <button type="button" onClick={() => setIsForgotModalOpen(false)} className="px-5 py-2.5 text-gray-400 hover:text-white font-medium transition-colors">Cancel</button>
                                    <button type="submit" disabled={resetStatus === 'loading'} className="px-5 py-2.5 bg-blue-600 rounded-lg text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-bold shadow-lg shadow-blue-900/20">
                                        {resetStatus === 'loading' ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Saved Credentials Prompt */}
            {savedCreds.length > 0 && showSavedCredsPrompt && (() => {
                const defaultUserThemeColor = usersData['goodlyfe']?.appThemeColor || '#3b82f6';
                const defaultUserTextColor = usersData['goodlyfe']?.appTextColor || '#ffffff';
                const defaultUserOpacity = (usersData['goodlyfe']?.postBlurOpacity !== undefined ? usersData['goodlyfe'].postBlurOpacity : 50) / 100;
                const modalBg = getExactModalBg(defaultUserThemeColor, defaultUserOpacity);

                return (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center z-[120] p-4 animate-fade-in">
                        <div
                            className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col border border-white/10 shadow-2xl backdrop-blur-md animate-scale-up"
                            style={{ backgroundColor: modalBg, color: defaultUserTextColor }}
                        >
                            <div className="p-8 flex flex-col">
                                <h3
                                    className="text-2xl font-bold text-center mb-2"
                                    style={{ color: defaultUserThemeColor, ...stickerTextStyle(0.15) }}
                                >
                                    Sign In as Saved User
                                </h3>
                                <p
                                    className="text-sm text-center opacity-80 mb-6"
                                    style={stickerTextStyle(0.18)}
                                >
                                    We found saved credentials on this device. Select an account to log in.
                                </p>

                                <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                                    {savedCreds.map(c => {
                                        const pfp = usersData[c.username]?.pfp || `https://api.dicebear.com/7.x/pixel-art/svg?seed=${c.username}`;
                                        const isLoggingInThis = isAutoLoggingIn === c.username;
                                        return (
                                            <div
                                                key={c.username}
                                                onClick={() => !isAutoLoggingIn && handleSavedCredLogin(c)}
                                                className={`flex items-center justify-between p-4 bg-black/20 border border-white/10 rounded-2xl hover:bg-white/5 transition-all ${isAutoLoggingIn ? 'cursor-not-allowed opacity-55' : 'cursor-pointer'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={pfp}
                                                        alt={c.username}
                                                        className="w-10 h-10 rounded-full border border-white/10 object-cover"
                                                    />
                                                    <span className="font-bold text-sm text-white">{c.username}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {isLoggingInThis ? (
                                                        <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={(e) => handleRemoveSavedCredential(e, c.username)}
                                                                className="p-1.5 rounded-full hover:bg-white/10 text-white/50 hover:text-red-400 transition-colors"
                                                                title="Remove saved credential"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                            <span
                                                                className="text-xs font-semibold px-3 py-1.5 rounded-full text-white shadow-sm transition-all"
                                                                style={{ backgroundColor: defaultUserThemeColor }}
                                                            >
                                                                Login
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => setShowSavedCredsPrompt(false)}
                                    disabled={!!isAutoLoggingIn}
                                    className="mt-6 w-full py-3 rounded-2xl font-bold transition-all text-sm bg-white/5 hover:bg-white/10 text-white border border-white/10 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Enter credentials manually
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </>
    );
};

// ========= COMPONENTS =========

const GlobalVideoOverlay = ({
    video,
    rect,
    onClose
}: {
    video: { id: number, html: string, videoId: string, isShorts?: boolean },
    rect: { top: number, left: number, width: number, height: number, isPiP: boolean },
    onClose: () => void
}) => {
    const isPiP = rect.isPiP;

    // Smooth transition between postcard and PiP corner
    const style: React.CSSProperties = {
        position: 'fixed',
        top: isPiP ? 'auto' : rect.top,
        left: isPiP ? 'auto' : rect.left,
        bottom: isPiP ? '96px' : 'auto',
        right: isPiP ? '16px' : 'auto',
        width: isPiP ? '224px' : rect.width,
        height: isPiP ? '126px' : rect.height,
        zIndex: 1000,
        pointerEvents: 'auto',
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        overflow: 'hidden',
        borderRadius: isPiP ? '12px' : '0',
        boxShadow: isPiP ? '0 20px 25px -5px rgb(0 0 0 / 0.5), 0 8px 10px -6px rgb(0 0 0 / 0.5)' : 'none',
        border: isPiP ? '1px solid rgba(255,255,255,0.1)' : 'none',
        backgroundColor: 'black'
    };

    return (
        <div style={style} className="group animate-in fade-in duration-300">
            <div className={`absolute inset-0 z-20 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 flex items-start justify-end p-2 ${!isPiP ? 'hidden' : ''}`}>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    className="p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-all hover:scale-110"
                >
                    <XIcon className="w-4 h-4" />
                </button>
            </div>
            <div
                className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full"
                dangerouslySetInnerHTML={{
                    __html: (() => {
                        let html = video.html;
                        if (html.includes('youtube.com/embed') || html.includes('youtube-proxy')) {
                            return `<iframe class="w-full h-full border-none bg-black/20" src="https://www.youtube.com/embed/${video.videoId}?autoplay=1&mute=1&playsinline=1&controls=1" allow="autoplay; fullscreen; picture-in-picture" title="YouTube Video Player"></iframe>`;
                        }
                        return html;
                    })()
                }}
            />
        </div>
    );
};

const mergeServerUsers = (serverUsers: Record<string, any>, prev: Record<string, UserData>, isDemo: boolean) => {
    try {
        // Safety check: ensure prev is an object
        const safePrev = (prev && typeof prev === 'object') ? prev : {};
        const mergedUsers: Record<string, UserData> = { ...safePrev };

        Object.keys(serverUsers).forEach(key => {
            const serverUser = serverUsers[key];
            const defaults = getDefaultUserData(serverUser.username || key);
            mergedUsers[key] = {
                ...defaults,
                ...(safePrev[key] || {}),
                ...serverUser,
                notificationSettings: {
                    ...defaults.notificationSettings,
                    ...(serverUser.notificationSettings || {})
                },
                friends: serverUser.friends || [],
                following: serverUser.following || [],
                followers: serverUser.followers || [],
                friendRequests: serverUser.friendRequests || [],
                followerRequests: serverUser.followerRequests || [],
                followerGroups: (serverUser.followerGroups || []).map((g: any) => ({
                    id: g.id,
                    name: g.name,
                    followers: g.followers || [],
                    isDefault: g.isDefault,
                    isReadOnlyName: g.isReadOnlyName
                })),
                likedPosts: serverUser.likedPosts || [],
                bookmarkedPosts: serverUser.bookmarkedPosts || [],
                bumpedPosts: serverUser.bumpedPosts || [],
                completedAchievements: (() => {
                    const serverCompletions = serverUser.completedAchievements || [];
                    const localCompletions = safePrev[key]?.completedAchievements || [];
                    // Keep any local entries whose postId doesn't exist on server yet (optimistic)
                    const serverPostIds = new Set(serverCompletions.map((c: any) => c.postId));
                    const localOnly = localCompletions.filter((c: any) => !serverPostIds.has(c.postId));
                    return [...serverCompletions, ...localOnly];
                })(),
                allowFollowers: serverUser.allowFollowers !== undefined ? serverUser.allowFollowers : (serverUser.autoApproveFollows !== undefined ? serverUser.autoApproveFollows : (safePrev[key]?.allowFollowers !== undefined ? safePrev[key].allowFollowers : defaults.allowFollowers)),
                showCommunityPostsOnHomeFeed: serverUser.showCommunityPostsOnHomeFeed !== undefined ? serverUser.showCommunityPostsOnHomeFeed : (safePrev[key]?.showCommunityPostsOnHomeFeed !== undefined ? safePrev[key].showCommunityPostsOnHomeFeed : defaults.showCommunityPostsOnHomeFeed),
                enableNavbarAutohide: serverUser.enableNavbarAutohide !== undefined ? serverUser.enableNavbarAutohide : (safePrev[key]?.enableNavbarAutohide !== undefined ? safePrev[key].enableNavbarAutohide : defaults.enableNavbarAutohide),
                vouchMessage: serverUser.vouchMessage || defaults.vouchMessage,
                websites: serverUser.websites || [],
                profileBg: {
                    url: serverUser.profileBg?.url || defaults.profileBg.url,
                    style: serverUser.profileBg?.style || defaults.profileBg.style || 'cover'
                },
                profileBgSlideshow: (() => {
                    let slideshow = serverUser.profileBgSlideshow || safePrev[key]?.profileBgSlideshow || defaults.profileBgSlideshow || { attachments: [], intervalValue: 10, intervalUnit: 'seconds' };
                    if (!slideshow.attachments) {
                        slideshow.attachments = [];
                    }

                    const customUrl = serverUser.profileBg?.url;
                    const defaultProfileUrl = defaults.profileBg?.url;
                    const defaultHomeUrl = defaults.homeBg?.url;

                    const isCustomUrl = customUrl &&
                        customUrl.trim() !== '' &&
                        !customUrl.includes('photo-1507525428034-b723a996f6ea') &&
                        customUrl !== defaultProfileUrl &&
                        customUrl !== defaultHomeUrl;

                    if (isCustomUrl) {
                        const alreadyExists = slideshow.attachments.some((att: any) => att.url === customUrl);
                        if (!alreadyExists) {
                            slideshow = {
                                ...slideshow,
                                attachments: [
                                    { url: customUrl, type: 'image' },
                                    ...slideshow.attachments
                                ]
                            };
                        }
                    }
                    return slideshow;
                })(),
                homeBg: {
                    url: serverUser.homeBg?.url || defaults.homeBg.url,
                    style: serverUser.homeBg?.style || defaults.homeBg.style || 'cover'
                },
                loginBg: {
                    url: serverUser.loginBg?.url || defaults.loginBg?.url || 'https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2029&auto=format&fit=crop',
                    style: serverUser.loginBg?.style || defaults.loginBg?.style || 'cover'
                },
                loginBgSlideshow: (() => {
                    let slideshow = serverUser.loginBgSlideshow || safePrev[key]?.loginBgSlideshow || defaults.loginBgSlideshow || { attachments: [], intervalValue: 10, intervalUnit: 'seconds' };
                    if (!slideshow.attachments) {
                        slideshow.attachments = [];
                    }

                    const customUrl = serverUser.loginBg?.url;
                    const defaultLoginUrl = defaults.loginBg?.url || 'https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2029&auto=format&fit=crop';

                    const isCustomUrl = customUrl &&
                        customUrl.trim() !== '' &&
                        customUrl !== defaultLoginUrl;

                    if (isCustomUrl) {
                        const alreadyExists = slideshow.attachments.some((att: any) => att.url === customUrl);
                        if (!alreadyExists) {
                            slideshow = {
                                ...slideshow,
                                attachments: [
                                    { url: customUrl, type: 'image' },
                                    ...slideshow.attachments
                                ]
                            };
                        }
                    }
                    return slideshow;
                })(),
                appThemeColor: serverUser.appThemeColor || defaults.appThemeColor,
                appTextColor: serverUser.appTextColor || defaults.appTextColor,
                enforceThemeColor: serverUser.enforceThemeColor !== undefined ? serverUser.enforceThemeColor : defaults.enforceThemeColor,
                enforceThemeOpacity: serverUser.enforceThemeOpacity !== undefined ? serverUser.enforceThemeOpacity : defaults.enforceThemeOpacity,
                galleryAnimation: serverUser.galleryAnimation || defaults.galleryAnimation || 'fade',
                postBlurOpacity: serverUser.postBlurOpacity !== undefined ? serverUser.postBlurOpacity : (defaults.postBlurOpacity || 50),
                mutedUsers: serverUser.mutedUsers || [],
                favoriteEmoji: serverUser.favoriteEmoji || safePrev[key]?.favoriteEmoji || defaults.favoriteEmoji,
                commentsTabVisibility: serverUser.commentsTabVisibility || safePrev[key]?.commentsTabVisibility || defaults.commentsTabVisibility,
                postcardDefault: serverUser.postcardDefault === 'postcard_classic' ? 'classic_postcards' : (serverUser.postcardDefault || defaults.postcardDefault),
                postcardAppearance: serverUser.postcardAppearance || defaults.postcardAppearance,
                purchasedItems: Array.from(new Set([
                    ...(defaults.purchasedItems || []),
                    ...(serverUser.purchasedItems || [])
                ])).map(item => item === 'postcard_classic' ? 'classic_postcards' : item),
                customMods: serverUser.customMods || [],
                activeSystemModId: serverUser.activeSystemModId || safePrev[key]?.activeSystemModId
            };
        });

        if (isDemo && safePrev['guest']) {
            mergedUsers['guest'] = safePrev['guest'];
        } else if (isDemo && !mergedUsers['guest']) {
            const demoUser = 'guest';
            mergedUsers[demoUser] = {
                ...getDefaultUserData(demoUser),
                displayName: 'Guest',
                bio: 'Just looking around Peek 👀',
                pfp: `https://i.pravatar.cc/150?u=goodlyfe_guest`,
                following: ['vibeman1'],
                followers: [],
            } as UserData;
        }

        // Inject virtual GoodLyfe 'goodlyfe' user entry
        mergedUsers['goodlyfe'] = {
            ...getDefaultUserData('goodlyfe'),
            ...(mergedUsers['goodlyfe'] || {}),
            displayName: mergedUsers['goodlyfe']?.displayName || 'GoodieBoy',
            pfp: mergedUsers['goodlyfe']?.pfp || '/assets/goodieboy.png',
            bio: (mergedUsers['goodlyfe'] && mergedUsers['goodlyfe'].bio !== undefined) ? mergedUsers['goodlyfe'].bio : 'The Intellectual Jester of GoodLyfe. Here to make your digital life actually fun. @vibeman1 is my boss, but I run the show.',
            streak: mergedUsers['goodlyfe']?.streak || 777,
            totalPoints: mergedUsers['goodlyfe']?.totalPoints || 999999,
            verified: true,
            isSystem: true,
            role: 'admin',
            activeCustomizations: mergedUsers['goodlyfe']?.activeCustomizations || {
                mods: ['vibeman1_footprint', 'pimp_my_ride']
            }
        } as any;

        // ENSURE ESSENTIAL MODS FOR ALL USERS (Migration)
        Object.keys(mergedUsers).forEach(uid => {
            const u = mergedUsers[uid];
            if (u && u.activeCustomizations) {
                if (!u.activeCustomizations.mods) u.activeCustomizations.mods = [];
                // Force Daily Footprint for everyone as it's a core pillar
                if (!u.activeCustomizations.mods.includes('vibeman1_footprint')) {
                    u.activeCustomizations.mods.push('vibeman1_footprint');
                }
            }
        });

        console.log('[App] mergeServerUsers: Successfully merged data for', Object.keys(mergedUsers).length, 'users');
        return mergedUsers;
    } catch (mergeError) {
        console.error('[App] CRITICAL Error during state merge in mergeServerUsers:', mergeError);
        // If everything fails, return the existing state to avoid crashing the whole sync loop
        return prev;
    }
};

function MainApp() {
    const {
        page, setPageState,
        currentUser, usersData, communities, setCommunities, updateUserData, logout, setCurrentUser, isDemo,
        homeFeedMode, setHomeFeedMode,
        footprintFeedMode, setFootprintFeedMode,
        activeVideo, setActiveVideo, activeVideoRect, setActiveVideoRect, setIgnoredPipIds,
        sessionHistoryCount,
        loggedInAccounts, setLoggedInAccounts, handleSwitchAccount, handleAddAccount
    } = useAuth();

    const {
        activeAudioPost, setAudioPlayerOpen, audioPlayerOpen, audioIsPlaying
    } = useAudioPlayerState();
    const setPage = setPageState;

    const [incomingCall, setIncomingCall] = useState<{ caller: string, nickname: string, conversationId: string, channelId: string } | null>(null);

    const handleAcceptCall = useCallback(() => {
        if (incomingCall) {
            stopRingtone();
            handleJoinVoice(incomingCall.conversationId, incomingCall.channelId);
            setIncomingCall(null);
        }
    }, [incomingCall]);

    const handleDeclineCall = useCallback(async () => {
        if (incomingCall) {
            stopRingtone();
            try {
                let base = API_URL.trim().replace(/\/+$/, '');
                if (base.toLowerCase().endsWith('/api')) {
                    base = base.substring(0, base.length - 4);
                }
                const serverUrl = base || 'https://goodlyfe.net';
                await fetch(`${serverUrl}/api/call/decline`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        conversationId: incomingCall.conversationId,
                        recipient: currentUser
                    })
                });
            } catch (err) {
                console.error('Failed to decline call:', err);
            }
            setIncomingCall(null);
        }
    }, [incomingCall, currentUser]);

    // Helper to find the correct cased username key in usersData
    const getUserKeyCaseInsensitive = useCallback((username: string | undefined): string | null => {
        if (!username) return null;
        let cleanUsername = username.trim();
        if (cleanUsername.startsWith('@')) {
            cleanUsername = cleanUsername.slice(1);
        }
        if (!cleanUsername) return null;

        const normalize = (s: string) => s.trim().toLowerCase().replace(/\./g, '_');
        const target = normalize(cleanUsername);

        // 1. Try exact match (fast)
        if (usersData[cleanUsername]) return cleanUsername;

        // 2. Try matching vendorName for isVendor users
        const vendorKey = Object.keys(usersData).find(key => {
            const u = usersData[key];
            return u && u.isVendor && u.vendorName && normalize(u.vendorName) === target;
        });
        if (vendorKey) return vendorKey;

        // 3. Try normalized match (handles dots, casing, and spaces)
        const found = Object.keys(usersData).find(key => normalize(key) === target);
        if (found) return found;

        // 4. Case-insensitive fallback
        const lowerName = cleanUsername.toLowerCase();
        return Object.keys(usersData).find(key => key.toLowerCase() === lowerName) || null;
    }, [usersData]);

    const activeMods = useMemo(() => {
        const mods = currentUser ? (usersData[currentUser]?.activeCustomizations?.mods || []) : [];
        if (!mods.includes('vibeman1_footprint')) {
            return ['vibeman1_footprint', ...mods];
        }
        return mods;
    }, [usersData, currentUser]);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [pipInteractMode, setPipInteractMode] = useState(false);

    const [selectedPost, setSelectedPost] = useState<HydratedPost | null>(null);
    const [selectedFeedPostId, setSelectedFeedPostId] = useState<number | null>(null);

    useEffect(() => {
        const handleSelectedEvent = (e: Event) => {
            const id = (e as CustomEvent).detail?.postId;
            setSelectedFeedPostId(id);
        };
        const handleDeselectedEvent = (e: Event) => {
            const id = (e as CustomEvent).detail?.postId;
            setSelectedFeedPostId(prev => prev === id ? null : prev);
        };
        window.addEventListener('postcard-selected', handleSelectedEvent);
        window.addEventListener('postcard-deselected', handleDeselectedEvent);
        return () => {
            window.removeEventListener('postcard-selected', handleSelectedEvent);
            window.removeEventListener('postcard-deselected', handleDeselectedEvent);
        };
    }, []);
    const [homeBgSlideshowIdx, setHomeBgSlideshowIdx] = useState(0);
    const [profileBgSlideshowIdx, setProfileBgSlideshowIdx] = useState(0);
    const [communityBgSlideshowIdx, setCommunityBgSlideshowIdx] = useState(0);

    // Track previous page for post modal back navigation
    const [previousPage, setPreviousPage] = useState<PageState | null>(null);
    const backgroundPage = (['post', 'privacy', 'safety', 'settings', 'additional_settings', 'notifications', 'search', 'store'].includes(page.type) && previousPage) ? previousPage : page;

    const [lastVisitedProfileUserId, setLastVisitedProfileUserId] = useState<string | null>(() => currentUser);

    useEffect(() => {
        if (currentUser && !lastVisitedProfileUserId) {
            setLastVisitedProfileUserId(currentUser);
        }
    }, [currentUser]);





    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);

        // Block iOS System Zoom
        const handleGesture = (e: any) => e.preventDefault();
        document.addEventListener('gesturestart', handleGesture);

        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('gesturestart', handleGesture);
        };
    }, []);

    useEffect(() => {

        console.log('[MainApp] Component Mounted');

        const processShareIntent = (event: any) => {
            console.log('[Share] Processing Intent:', event);
            try {
                const transformedData = {
                    text: event.texts?.[0] || '',
                    url: event.texts?.find((t: string) => t.startsWith('http')) || '',
                    files: event.files?.map((f: any) => ({
                        url: f.uri ? Capacitor.convertFileSrc(f.uri) : '',
                        type: f.mimeType?.startsWith('video') ? 'video' : f.mimeType?.startsWith('audio') ? 'audio' : 'image'
                    }))
                };
                setIncomingShareData(transformedData);
                setIsShareToGoodLyfeModalOpen(true);
                pushModalState('share_intent');
            } catch (err) {
                console.error('[Share] Process intent failed', err);
            }
        };

        // Check if there is already a cached intent from index.tsx cold start
        if ((window as any).cachedShareIntent) {
            processShareIntent((window as any).cachedShareIntent);
            delete (window as any).cachedShareIntent; // clear it so we don't process it twice
        }

        // App Shortcuts listener
        if (Capacitor.isNativePlatform()) {
            AppShortcuts.addListener('click', (event: any) => {
                const id = event.shortcutId;
                if (id === 'default_communities') {
                    setPage({ type: 'communities' });
                } else if (id === 'default_footprint') {
                    setPage({ type: 'mod', modId: 'vibeman1_footprint' });
                } else if (id === 'default_art_class') {
                    setPage({ type: 'mod', modId: 'art-class' });
                } else if (id.startsWith('community_')) {
                    const commId = id.replace('community_', '');
                    setPage({ type: 'community_detail', query: commId });
                } else if (id.startsWith('mod_')) {
                    const modId = id.replace('mod_', '');
                    setPage({ type: 'mod', modId });
                }
            });
        }

        // Listen for future intents while app is active
        const handleShareEvent = (e: Event) => {
            const customEvent = e as CustomEvent;
            if (customEvent.detail) {
                processShareIntent(customEvent.detail);
                delete (window as any).cachedShareIntent;
            }
        };

        window.addEventListener('shareIntentReceived', handleShareEvent);

        return () => {
            window.removeEventListener('shareIntentReceived', handleShareEvent);
        };
    }, [setPageState]);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareSlideIndex, setShareSlideIndex] = useState<number | undefined>(undefined);
    const [postToShare, setPostToShare] = useState<HydratedPost | null>(null);
    const isDesktopLayout = typeof window !== 'undefined' && window.innerWidth >= 1024;
    const [gridScale, setGridScale] = usePersistentState<number>('gridScale', isDesktopLayout ? 0.9 : 1.0);
    const [isBumpActionModalOpen, setIsBumpActionModalOpen] = useState(false);
    const [isBumpModalOpen, setIsBumpModalOpen] = useState(false);
    const [isGlobalPostModalOpen, setIsGlobalPostModalOpen] = useState(false);
    const [globalPostMedia, setGlobalPostMedia] = useState<string | undefined>(undefined);

    useEffect(() => {
        const handleOpenGlobalPost = (e: any) => {
            if (e.detail?.media) {
                setGlobalPostMedia(e.detail.media);
            }
            setIsGlobalPostModalOpen(true);
        };
        window.addEventListener('open-global-post', handleOpenGlobalPost);
        return () => window.removeEventListener('open-global-post', handleOpenGlobalPost);
    }, []);
    const [postToBump, setPostToBump] = useState<HydratedPost | null>(null);
    const [pendingReply, setPendingReply] = useState<{ conversationId: number, message: Message } | null>(null);
    const [pendingForward, setPendingForward] = useState<Message | null>(null);
    const [typingUsers, setTypingUsers] = useState<Record<number, string[]>>({});
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isAdditionalSettingsModalOpen, setIsAdditionalSettingsModalOpen] = useState(false);
    const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
    const [isVouchModalOpen, setIsVouchModalOpen] = useState(false);
    const [isShareToGoodLyfeModalOpen, setIsShareToGoodLyfeModalOpen] = useState(false);
    const [removeConnectionModalState, setRemoveConnectionModalState] = useState<{ isOpen: boolean, targetId: string, type: 'friend' | 'request' } | null>(null);
    const [isYourCommunitiesModalOpen, setIsYourCommunitiesModalOpen] = useState(false);
    const [postToVouch, setPostToVouch] = useState<HydratedPost | null>(null);
    const [vouchMessage, setVouchMessage] = useState('');
    const [activeLiveNotification, setActiveLiveNotification] = useState<Notification | null>(null);

    const [createYipYapTarget, setCreateYipYapTarget] = useState<string | null>(null);
    const [createCommunityTarget, setCreateCommunityTarget] = useState<string | null>(null);
    const [showNewMessageModal, setShowNewMessageModal] = useState(false);
    const [flashMessage, setFlashMessage] = useState<string | null>(null);
    const [isDebugOpen, setIsDebugOpen] = useState(false);
    const [isIdeaBankOpen, setIsIdeaBankOpen] = useState(false);

    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [feedScrollPosition, setFeedScrollPosition] = useState(0);
    // Tab memory states to keep views mounted and track last active sub-pages
    const [lastProfileUserId, setLastProfileUserId] = useState<string | null>(null);
    const [lastCommunityQuery, setLastCommunityQuery] = useState<string | null>(null);
    const [lastCommunityChannelId, setLastCommunityChannelId] = useState<string | undefined>(undefined);
    const [lastCommunityMessageId, setLastCommunityMessageId] = useState<string | undefined>(undefined);
    const [lastHashtagTag, setLastHashtagTag] = useState<string | null>(null);
    const [hasVisitedProfile, setHasVisitedProfile] = useState(false);
    const [hasVisitedCommunities, setHasVisitedCommunities] = useState(false);
    const [hasVisitedCommunityDetail, setHasVisitedCommunityDetail] = useState(false);
    const [hasVisitedMessages, setHasVisitedMessages] = useState(false);
    const [hasVisitedFootprint, setHasVisitedFootprint] = useState(false);
    const [lastFootprintTaskId, setLastFootprintTaskId] = useState<number | undefined>(undefined);
    const [hasVisitedHashtag, setHasVisitedHashtag] = useState(false);
    const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
    const [isSystemModModalOpen, setIsSystemModModalOpen] = useState(false);
    const lastBackPressTimeRef = useRef(0);
    const isPopStateRef = useRef(false);
    const skipNextPopStateRef = useRef(false);
    const isSwipingPostRef = useRef(false);
    const navHistoryRef = useRef<PageState[]>([{ type: 'home' }]);
    const [isFollowerGroupsModalOpen, setIsFollowerGroupsModalOpen] = useState(false);
    const [isPeeking, setIsPeeking] = useState(false);

    const [followerGroupsTargetUser, setFollowerGroupsTargetUser] = useState<string | undefined>(undefined);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const initialAppIconRouteHandled = useRef(false);

    useEffect(() => {
        if (!isInitialLoad && !initialAppIconRouteHandled.current) {
            initialAppIconRouteHandled.current = true;
            if (currentUser && usersData[currentUser] && page.type === 'home' && (window.location.pathname === '/' || window.location.pathname === '')) {
                const appIcon = usersData[currentUser].appIconOverride;
                if (appIcon === 'yipyap') {
                    setPage({ type: 'messages', tab: 'yipyap' });
                } else if (appIcon === 'communities') {
                    setPage({ type: 'communities' });
                } else if (appIcon === 'footprint') {
                    setPage({ type: 'mod', modId: 'vibeman1_footprint' });
                }
            }
        }
    }, [isInitialLoad, currentUser, usersData, page.type, setPage]);

    useEffect(() => {
        if (backgroundPage.type !== 'profile') {
            setIsPeeking(false);
        }
    }, [backgroundPage.type]);

    const [pinnedModId, setPinnedModId] = usePersistentState<string>('pinnedModId', 'vibeman1_footprint');
    useEffect(() => {
        if (pinnedModId === 'daily_footprint') {
            setPinnedModId('vibeman1_footprint');
        }
    }, [pinnedModId, setPinnedModId]);
    const [isModLibraryModalOpen, setIsModLibraryModalOpen] = useState(false);
    const [isAccountSwitcherOpen, setIsAccountSwitcherOpen] = useState(false);
    const [incomingShareData, setIncomingShareData] = useState<{
        text?: string;
        url?: string;
        files?: { url: string; type: 'image' | 'video' | 'audio' }[];
    } | null>(null);



    const [voiceRooms, setVoiceRooms] = useState<Record<string, VoiceParticipant[]>>({});
    const [voiceRemoteStreams, setVoiceRemoteStreams] = useState<Record<string, MediaStream>>({});
    const [voiceLocalStream, setVoiceLocalStream] = useState<MediaStream | null>(null);
    const [activeVoiceData, setActiveVoiceData] = useState<{ communityId: string, channelId: string } | null>(null);
    const [voiceIsMuted, setVoiceIsMuted] = useState(false);
    const [voiceIsDeafened, setVoiceIsDeafened] = useState(false);
    const activeVoiceDataRef = useRef<any>(null);
    useEffect(() => {
        activeVoiceDataRef.current = activeVoiceData;
    }, [activeVoiceData]);

    // Restore active voice room on mobile
    useEffect(() => {
        if (!currentUser) return;
        const isMobile = Capacitor.isNativePlatform() || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
            const savedVoice = localStorage.getItem('goodlyfe_active_voice_room');
            if (savedVoice) {
                try {
                    const parsed = JSON.parse(savedVoice);
                    if (parsed && parsed.communityId && parsed.channelId) {
                        console.log('[Voice] Restoring active voice room from localStorage:', parsed);
                        setActiveVoiceData(parsed);
                    }
                } catch (e) {
                    console.error('[Voice] Failed to parse saved voice room:', e);
                }
            }
        }
    }, [currentUser]);

    // Persist active voice room on mobile
    useEffect(() => {
        const isMobile = Capacitor.isNativePlatform() || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
            if (activeVoiceData) {
                localStorage.setItem('goodlyfe_active_voice_room', JSON.stringify(activeVoiceData));
            } else {
                localStorage.removeItem('goodlyfe_active_voice_room');
            }
        }
    }, [activeVoiceData]);

    // Local notifications actions registration
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        const initLocalNotifications = async () => {
            try {
                const permission = await LocalNotifications.requestPermissions();
                console.log('[LocalNotifications] Permission status:', permission);

                await LocalNotifications.registerActionTypes({
                    types: [
                        {
                            id: 'VOICE_CONTROL_UNMUTED_UNDEAFENED',
                            actions: [
                                {
                                    id: 'mute_toggle',
                                    title: 'Mute',
                                    foreground: false
                                },
                                {
                                    id: 'deafen_toggle',
                                    title: 'Deafen',
                                    foreground: false
                                },
                                {
                                    id: 'end_call',
                                    title: 'End Call',
                                    foreground: false,
                                    destructive: true
                                }
                            ]
                        },
                        {
                            id: 'VOICE_CONTROL_MUTED_UNDEAFENED',
                            actions: [
                                {
                                    id: 'mute_toggle',
                                    title: 'Unmute',
                                    foreground: false
                                },
                                {
                                    id: 'deafen_toggle',
                                    title: 'Deafen',
                                    foreground: false
                                },
                                {
                                    id: 'end_call',
                                    title: 'End Call',
                                    foreground: false,
                                    destructive: true
                                }
                            ]
                        },
                        {
                            id: 'VOICE_CONTROL_UNMUTED_DEAFENED',
                            actions: [
                                {
                                    id: 'mute_toggle',
                                    title: 'Mute',
                                    foreground: false
                                },
                                {
                                    id: 'deafen_toggle',
                                    title: 'Undeafen',
                                    foreground: false
                                },
                                {
                                    id: 'end_call',
                                    title: 'End Call',
                                    foreground: false,
                                    destructive: true
                                }
                            ]
                        },
                        {
                            id: 'VOICE_CONTROL_MUTED_DEAFENED',
                            actions: [
                                {
                                    id: 'mute_toggle',
                                    title: 'Unmute',
                                    foreground: false
                                },
                                {
                                    id: 'deafen_toggle',
                                    title: 'Undeafen',
                                    foreground: false
                                },
                                {
                                    id: 'end_call',
                                    title: 'End Call',
                                    foreground: false,
                                    destructive: true
                                }
                            ]
                        },
                        {
                            id: 'DM_CALL_INCOMING',
                            actions: [
                                {
                                    id: 'accept_call',
                                    title: 'Accept',
                                    foreground: true
                                },
                                {
                                    id: 'decline_call',
                                    title: 'Decline',
                                    foreground: false,
                                    destructive: true
                                }
                            ]
                        }
                    ]
                });
                console.log('[LocalNotifications] Registered VOICE_CONTROL & DM_CALL_INCOMING action types');
            } catch (e) {
                console.error('[LocalNotifications] Registration error:', e);
            }
        };

        initLocalNotifications();
    }, []);

    // Local notification action listener
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        const actionListener = LocalNotifications.addListener(
            'localNotificationActionPerformed',
            (notificationAction) => {
                const actionId = notificationAction.actionId;
                const notification = notificationAction.notification;
                console.log('[LocalNotifications] Action clicked:', actionId, notification);

                if (actionId === 'tap') {
                    const extra = notification.extra;
                    if (extra && extra.communityId && extra.channelId) {
                        console.log('[LocalNotifications] Navigating back to voice channel:', extra);
                        setPage({
                            type: 'community_detail',
                            query: extra.communityId,
                            channelId: extra.channelId
                        });
                    } else if (extra && extra.openAudioPlayer) {
                        console.log('[LocalNotifications] Opening audio player from notification tap');
                        setAudioPlayerOpen(true);
                    } else if (extra && extra.downloadPath) {
                        // Open standard share sheet as a fallback if FileOpener is missing
                        // This allows clicking the notification to view, send, or open the file.
                        Share.share({
                            title: 'Open Downloaded File',
                            url: extra.downloadPath
                        }).catch(err => {
                            console.error('[LocalNotifications] Failed to open downloaded file share:', err);
                        });
                    }
                } else if (actionId === 'mute_toggle') {
                    setVoiceIsMuted(prev => !prev);
                } else if (actionId === 'deafen_toggle') {
                    setVoiceIsDeafened(prev => {
                        const next = !prev;
                        if (next) {
                            setVoiceIsMuted(true);
                        }
                        return next;
                    });
                } else if (actionId === 'end_call') {
                    setActiveVoiceData(null);
                } else if (actionId === 'accept_call') {
                    stopRingtone();
                    const extra = notification.extra;
                    if (extra && extra.conversationId) {
                        handleJoinVoice(extra.conversationId.toString(), extra.channelId || 'dm');
                    }
                } else if (actionId === 'decline_call') {
                    stopRingtone();
                    const extra = notification.extra;
                    if (extra && extra.conversationId && extra.recipient) {
                        const serverUrl = extra.serverUrl || 'https://goodlyfe.net';
                        fetch(`${serverUrl}/api/call/decline`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ conversationId: extra.conversationId, recipient: extra.recipient })
                        }).catch(console.error);
                    }
                }
            }
        );

        return () => {
            actionListener.then(l => l.remove());
        };
    }, []);

    // Schedule/Update/Cancel Local Notification for voice room
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        const manageVoiceNotification = async () => {
            if (activeVoiceData) {
                let statusText = 'Connected';
                if (voiceIsDeafened) {
                    statusText = 'Deafened & Muted';
                } else if (voiceIsMuted) {
                    statusText = 'Muted';
                }

                const actionTypeId = `VOICE_CONTROL_${voiceIsMuted ? 'MUTED' : 'UNMUTED'}_${voiceIsDeafened ? 'DEAFENED' : 'UNDEAFENED'}`;

                try {
                    await LocalNotifications.schedule({
                        notifications: [
                            {
                                id: 10001,
                                title: `Active Voice Room`,
                                body: `Status: ${statusText}. Tap to open.`,
                                ongoing: true,
                                autoCancel: false,
                                actionTypeId: actionTypeId,
                                extra: {
                                    communityId: activeVoiceData.communityId,
                                    channelId: activeVoiceData.channelId
                                }
                            }
                        ]
                    });
                    console.log('[LocalNotifications] Scheduled voice notification:', statusText, actionTypeId);
                } catch (err) {
                    console.error('[LocalNotifications] Failed to schedule notification:', err);
                }
            } else {
                try {
                    await LocalNotifications.cancel({
                        notifications: [{ id: 10001 }]
                    });
                    console.log('[LocalNotifications] Canceled voice notification');
                } catch (err) {
                    console.error('[LocalNotifications] Failed to cancel notification:', err);
                }
            }
        };

        manageVoiceNotification();
    }, [activeVoiceData, voiceIsMuted, voiceIsDeafened]);

    // Schedule/Update/Cancel Local Notification for audio player
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        const manageAudioNotification = async () => {
            if (activeAudioPost) {
                const media = (activeAudioPost.type === 'bump' || activeAudioPost.type === 'repost') ? (activeAudioPost.originalPost?.content.media) : activeAudioPost.content.media;
                const firstAudio = media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
                const audioName = firstAudio?.name;
                const songTitle = audioName || activeAudioPost.content?.text || (activeAudioPost.type === 'voice' ? 'Voice note' : 'Audio');
                const author = activeAudioPost.author || 'GoodLyfe';
                const artworkUrl = activeAudioPost.content?.coverImageUrl || usersData[activeAudioPost.author]?.pfp || 'https://goodlyfe.net/logo.png';
                try {
                    await LocalNotifications.schedule({
                        notifications: [
                            {
                                id: 10002,
                                title: `Now Playing`,
                                body: `${songTitle} - by ${author}`,
                                ongoing: true,
                                autoCancel: false,
                                largeIcon: artworkUrl,
                                extra: {
                                    openAudioPlayer: true
                                }
                            }
                        ]
                    });
                    console.log('[LocalNotifications] Scheduled audio notification:', songTitle);
                } catch (err) {
                    console.error('[LocalNotifications] Failed to schedule audio notification:', err);
                }
            } else {
                try {
                    await LocalNotifications.cancel({
                        notifications: [{ id: 10002 }]
                    });
                    console.log('[LocalNotifications] Canceled audio notification');
                } catch (err) {
                    console.error('[LocalNotifications] Failed to cancel audio notification:', err);
                }
            }
        };

        manageAudioNotification();
    }, [activeAudioPost]);
    const [messageFontSize, setMessageFontSize] = usePersistentState<number>('messageFontSize', 16);
    const updateMessageFontSize = (size: number) => setMessageFontSize(size);
    const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const mainRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (mainRef.current) {
            mainRef.current.style.transform = '';
            mainRef.current.style.transition = '';
        }
        setPipInteractMode(false);
    }, [page, backgroundPage]);




    const socketRef = useRef<any>(null);
    const scrollPositions = useRef<Record<string, { scrollY: number, visibleCount: number }>>({});
    const lastPageKeyRef = useRef<string | null>(null);
    const lastVisibleCountRef = useRef<number>(24);
    const syncAppDataRef = useRef<(() => void) | null>(null);
    const [posts, setPosts] = useState<Post[]>([]);
    const [animatingLikes, setAnimatingLikes] = useState<Set<number>>(new Set());
    const [animatingBumps, setAnimatingBumps] = useState<Set<number>>(new Set());
    const [animatingComments, setAnimatingComments] = useState<Set<number>>(new Set());
    const [animatingBookmarks, setAnimatingBookmarks] = useState<Set<number>>(new Set());
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
    const [activeUsers, setActiveUsers] = useState<string[]>([]);

    // Shopping Cart State
    const [cart, setCart] = useState<{ post: Post; quantity: number }[]>([]);
    const [isCartOpen, setIsCartOpen] = useState(false);

    const handleAddToCart = (post: Post) => {
        setCart(prev => {
            const existing = prev.find(item => item.post.id === post.id);
            if (existing) {
                return prev.map(item => item.post.id === post.id ? { ...item, quantity: item.quantity + 1 } : item);
            }
            return [...prev, { post, quantity: 1 }];
        });
        setFlashMessage(`Added "${post.content.text || 'Item'}" to Cart!`);
        setTimeout(() => setFlashMessage(null), 2000);
    };

    const handleRemoveFromCart = (postId: number) => {
        setCart(prev => prev.filter(item => item.post.id !== postId));
    };

    const handleUpdateCartQuantity = (postId: number, delta: number) => {
        setCart(prev => prev.map(item => {
            if (item.post.id === postId) {
                const newQty = item.quantity + delta;
                return newQty > 0 ? { ...item, quantity: newQty } : item;
            }
            return item;
        }).filter(item => item.quantity > 0));
    };

    const handleClearCart = () => {
        setCart([]);
    };

    useEffect(() => {
        let targetAuthor: string | null = null;

        if (page.type === 'profile' && page.userId) {
            targetAuthor = page.userId;
        } else if (page.type === 'storefront' && page.vendorName) {
            targetAuthor = page.vendorName;
        } else if (selectedPost) {
            targetAuthor = selectedPost.author;
        } else if (page.type === 'post' && page.postId) {
            const rawPost = posts.find(p => p.id.toString() === page.postId);
            if (rawPost) {
                targetAuthor = rawPost.author;
            }
        } else if (selectedFeedPostId !== null) {
            const rawPost = posts.find(p => p.id === selectedFeedPostId);
            if (rawPost) {
                targetAuthor = rawPost.author;
            }
        }

        if (targetAuthor) {
            const postAuthorId = getUserKeyCaseInsensitive(targetAuthor) || targetAuthor;
            const postAuthorData = postAuthorId ? usersData[postAuthorId] : null;
            if (postAuthorData) {
                setLastVisitedProfileUserId(postAuthorId);
            }
        }
    }, [page, selectedPost, posts, usersData, selectedFeedPostId]);

    // Legacy profileBg and loginBg migration helper to ensure legacy settings are safely moved to slideshow and cleared on server
    useEffect(() => {
        if (!currentUser || isDemo || !updateUserData) return;

        const userData = usersData[currentUser];
        if (userData) {
            const defaults = getDefaultUserData(currentUser);
            const customUrl = userData.profileBg?.url;
            const defaultProfileUrl = defaults.profileBg?.url;
            const defaultHomeUrl = defaults.homeBg?.url;

            const isCustomUrl = customUrl &&
                customUrl.trim() !== '' &&
                !customUrl.includes('photo-1507525428034-b723a996f6ea') &&
                customUrl !== defaultProfileUrl &&
                customUrl !== defaultHomeUrl;

            if (isCustomUrl) {
                const slideshow = userData.profileBgSlideshow || { enabled: false, attachments: [], intervalValue: 10, intervalUnit: 'seconds' };
                const alreadyExists = slideshow.attachments?.some((att: any) => att.url === customUrl);
                const updatedSlideshow = alreadyExists ? slideshow : {
                    ...slideshow,
                    attachments: [
                        { url: customUrl, type: 'image' },
                        ...(slideshow.attachments || [])
                    ]
                };

                // Perform the update locally
                updateUserData((prev: any) => ({
                    ...prev,
                    [currentUser]: {
                        ...prev[currentUser],
                        profileBgSlideshow: updatedSlideshow,
                        profileBg: {
                            ...(prev[currentUser]?.profileBg || {}),
                            url: defaultProfileUrl
                        }
                    }
                }));

                // Persist to server
                updateUserOnServer(currentUser, {
                    profileBgSlideshow: updatedSlideshow,
                    profileBg: {
                        url: defaultProfileUrl,
                        style: userData.profileBg?.style || defaults.profileBg.style || 'cover'
                    }
                }).catch(console.error);
            }
        }

        // Also handle the legacy loginBg migration for the 'goodlyfe' virtual/system user
        const goodlyfeData = usersData['goodlyfe'];
        if (goodlyfeData) {
            const defaults = getDefaultUserData('goodlyfe');
            const customUrl = goodlyfeData.loginBg?.url;
            const defaultLoginUrl = defaults.loginBg?.url || 'https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2029&auto=format&fit=crop';

            const isCustomUrl = customUrl &&
                customUrl.trim() !== '' &&
                customUrl !== defaultLoginUrl;

            if (isCustomUrl) {
                const slideshow = goodlyfeData.loginBgSlideshow || { enabled: false, attachments: [], intervalValue: 10, intervalUnit: 'seconds' };
                const alreadyExists = slideshow.attachments?.some((att: any) => att.url === customUrl);
                const updatedSlideshow = alreadyExists ? slideshow : {
                    ...slideshow,
                    attachments: [
                        { url: customUrl, type: 'image' },
                        ...(slideshow.attachments || [])
                    ]
                };

                // Perform the update locally
                updateUserData((prev: any) => ({
                    ...prev,
                    goodlyfe: {
                        ...prev.goodlyfe,
                        loginBgSlideshow: updatedSlideshow,
                        loginBg: {
                            ...(prev.goodlyfe?.loginBg || {}),
                            url: defaultLoginUrl
                        }
                    }
                }));

                // Persist to server
                updateUserOnServer('goodlyfe', {
                    loginBgSlideshow: updatedSlideshow,
                    loginBg: {
                        url: defaultLoginUrl,
                        style: goodlyfeData.loginBg?.style || defaults.loginBg?.style || 'cover'
                    }
                }).catch(console.error);
            }
        }
    }, [currentUser, usersData, isDemo, updateUserData]);

    // Slideshow interval timer
    useEffect(() => {
        const currentUserData = (currentUser && usersData[currentUser]) ? usersData[currentUser] : usersData['goodlyfe'];
        const defaultData = getDefaultUserData(currentUser || 'goodlyfe');
        const baseUserData = currentUserData || defaultData;

        // Home background slideshow
        const homeSlideshow = baseUserData.homeBgSlideshow;
        let homeTimer: any = null;
        if (homeSlideshow && homeSlideshow.attachments && homeSlideshow.attachments.length > 1) {
            const slide = homeSlideshow.attachments[homeBgSlideshowIdx % homeSlideshow.attachments.length];
            const intervalMs = getSlideIntervalMs(slide, homeSlideshow);
            homeTimer = setInterval(() => {
                setHomeBgSlideshowIdx(prev => (prev + 1) % homeSlideshow.attachments.length);
            }, intervalMs);
        }

        // Profile background slideshow
        let profileUserId = page.type === 'profile' ? page.userId : lastVisitedProfileUserId;
        let usePostAuthorBg = false;

        let activePostAuthor: string | null = null;
        if (selectedPost) {
            activePostAuthor = selectedPost.author;
        } else if (page.type === 'post' && page.postId) {
            const rawPost = posts.find(p => p.id.toString() === page.postId);
            if (rawPost) {
                activePostAuthor = rawPost.author;
            }
        } else if (selectedFeedPostId !== null) {
            const rawPost = posts.find(p => p.id === selectedFeedPostId);
            if (rawPost) {
                activePostAuthor = rawPost.author;
            }
        }

        if (activePostAuthor) {
            const postAuthorId = getUserKeyCaseInsensitive(activePostAuthor) || activePostAuthor;
            const postAuthorData = postAuthorId ? usersData[postAuthorId] : null;
            if (postAuthorData) {
                profileUserId = postAuthorId;
                usePostAuthorBg = true;
            }
        }
        const profileUserData = profileUserId ? usersData[profileUserId] : null;
        const profileSlideshow = profileUserData?.profileBgSlideshow;
        let profileTimer: any = null;
        if (profileSlideshow && profileSlideshow.attachments && profileSlideshow.attachments.length > 1) {
            const slide = profileSlideshow.attachments[profileBgSlideshowIdx % profileSlideshow.attachments.length];
            const intervalMs = getSlideIntervalMs(slide, profileSlideshow);
            profileTimer = setInterval(() => {
                setProfileBgSlideshowIdx(prev => (prev + 1) % profileSlideshow.attachments.length);
            }, intervalMs);
        }

        // Community background slideshow
        let communityTimer: any = null;
        if (!usePostAuthorBg && backgroundPage.type === 'community_detail') {
            const communityId = backgroundPage.query;
            const community = communities.find((c: any) => c._id === communityId);
            const communitySlideshow = community?.bgSlideshow;
            if (communitySlideshow && communitySlideshow.attachments && communitySlideshow.attachments.length > 1) {
                const slide = communitySlideshow.attachments[communityBgSlideshowIdx % communitySlideshow.attachments.length];
                const intervalMs = getSlideIntervalMs(slide, communitySlideshow);
                communityTimer = setInterval(() => {
                    setCommunityBgSlideshowIdx(prev => (prev + 1) % communitySlideshow.attachments.length);
                }, intervalMs);
            }
        }

        return () => {
            if (homeTimer) clearInterval(homeTimer);
            if (profileTimer) clearInterval(profileTimer);
            if (communityTimer) clearInterval(communityTimer);
        };
    }, [currentUser, usersData, page.type, page.userId, lastVisitedProfileUserId, backgroundPage, communities, selectedPost, posts, selectedFeedPostId]);

    useEffect(() => {
        if (!currentUser || conversations.length === 0 || !Capacitor.isNativePlatform()) return;

        try {
            const recentDms = conversations
                .filter(c => c.participants.length > 1) // Any active chat
                .sort((a, b) => {
                    const aLast = a.messages.length > 0 ? new Date(a.messages[a.messages.length - 1].timestamp).getTime() : 0;
                    const bLast = b.messages.length > 0 ? new Date(b.messages[b.messages.length - 1].timestamp).getTime() : 0;
                    return bLast - aLast;
                })
                .slice(0, 3)
                .map(convo => {
                    const partnerId = convo.participants.find(id => id !== currentUser);
                    const partnerName = partnerId ? (usersData[partnerId]?.displayName || partnerId) : 'User';
                    return { id: partnerId || convo.id.toString(), name: partnerName, type: 'dm' };
                });

            if (recentDms.length > 0) {
                DirectShare.pushContacts({ contacts: recentDms }).catch(console.warn);
            }
        } catch (e) { }
    }, [conversations, currentUser, usersData]);

    const [lastCommunityPage, setLastCommunityPage] = useState<PageState>({ type: 'communities' });
    const [lastMessagesPage, setLastMessagesPage] = useState<PageState>({ type: 'messages' });
    const [lastModPage, setLastModPage] = useState<PageState | null>(null);

    // --- GUEST ACTIONS STATE ---
    const [guestActions, setGuestActions] = useState<{
        likedPosts: number[],
        bookmarkedPosts: number[],
        bumpedPosts: number[],
        localComments: Record<number, Comment[]>
    }>(() => {
        try {
            const stored = localStorage.getItem('goodlyfe_guest_actions');
            return stored ? JSON.parse(stored) : { likedPosts: [], bookmarkedPosts: [], bumpedPosts: [], localComments: {} };
        } catch (e) {
            return { likedPosts: [], bookmarkedPosts: [], bumpedPosts: [], localComments: {} };
        }
    });

    useEffect(() => {
        localStorage.setItem('goodlyfe_guest_actions', JSON.stringify(guestActions));
    }, [guestActions]);

    useEffect(() => {
        if (page.type === 'communities' || page.type === 'community_detail') {
            setLastCommunityPage(page);
        }
        if (page.type === 'messages') {
            setLastMessagesPage(page);
        }
        if (page.type === 'mod' && page.modId !== 'pimp_my_ride') {
            setLastModPage(page);
        }
    }, [page]);
    const [postToEdit, setPostToEdit] = useState<HydratedPost | null>(null);
    const [postToDeleteId, setPostToDeleteId] = useState<number | null>(null);
    const [postToDelete, setPostToDelete] = useState<Post | HydratedPost | null>(null);


    const postsById = useMemo(() => new Map(posts.map(p => [p.id, p])), [posts]);

    const isPostVisible = useCallback((post: Post) => {
        if (post.linkRequestDetails?.isLinkRequest) return false;
        const isDirectLink = page.type === 'post' && page.postId === post.id.toString();
        if (!canViewPost(post, currentUser, usersData, communities, isDirectLink)) return false;

        // Hide simple bumps/reposts (no added text) if the original post is unavailable
        if ((post.type === 'bump' || post.type === 'repost') && (!post.content.text || post.content.text.trim() === '')) {
            const origId = post.originalPostId;
            const originalPost = origId ? postsById.get(Number(origId)) : undefined;
            const isOrigDirectLink = isDirectLink || (page.type === 'post' && page.postId === origId?.toString());
            if (!originalPost || !canViewPost(originalPost, currentUser, usersData, communities, isOrigDirectLink)) {
                return false;
            }
        }

        return true;
    }, [currentUser, usersData, communities, postsById, page]);

    const getHydratedPosts = useCallback((postsToHydrate: Post[]): HydratedPost[] => {
        const currentUserData = currentUser && currentUser !== 'guest' ? usersData[currentUser] : null;

        return postsToHydrate.map(post => {
            const origId = post.originalPostId;
            const originalPost = origId ? postsById.get(Number(origId)) : undefined;
            const originalPostId = origId ?? post.id;

            let isLiked = false;
            let isBookmarked = false;
            let isBumped = false;
            let comments = post.comments || [];

            if (currentUser === 'guest') {
                isLiked = (guestActions?.likedPosts || []).includes(post.id);
                isBookmarked = (guestActions?.bookmarkedPosts || []).includes(post.id);
                isBumped = (guestActions?.bumpedPosts || []).includes(originalPostId);
                comments = [...comments, ...(guestActions?.localComments?.[post.id] || [])];
            } else if (currentUserData) {
                isLiked = (currentUserData.likedPosts || []).includes(post.id);
                isBookmarked = (currentUserData.bookmarkedPosts || []).includes(post.id);
                isBumped = (currentUserData.bumpedPosts || []).includes(originalPostId);
            }

            const type = post.type === 'repost' ? 'bump' : post.type;

            return {
                ...post,
                type,
                comments,
                originalPost: originalPost ? {
                    ...originalPost,
                    type: originalPost.type === 'repost' ? 'bump' : originalPost.type,
                    isLiked: currentUser === 'guest' ? (guestActions?.likedPosts || []).includes(originalPost.id) : (currentUserData?.likedPosts || []).includes(originalPost.id),
                    isBookmarked: currentUser === 'guest' ? (guestActions?.bookmarkedPosts || []).includes(originalPost.id) : (currentUserData?.bookmarkedPosts || []).includes(originalPost.id),
                    isBumped: currentUser === 'guest' ? (guestActions?.bumpedPosts || []).includes(originalPost.id) : (currentUserData?.bumpedPosts || []).includes(originalPost.id),
                } as HydratedPost : undefined,
                isLiked,
                isBookmarked,
                isBumped,
            };
        });
    }, [currentUser, usersData, postsById, guestActions]);

    const [followListModalState, setFollowListModalState] = useState<{ isOpen: boolean, listType: 'followers' | 'following' | 'friends' | 'connections' | null, userId: string | null }>({ isOpen: false, listType: null, userId: null });
    const [isInternalModalOpen, setIsInternalModalOpen] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const pushModalState = useCallback((modalName: string) => {
        if (window.history.state?.modal === modalName) {
            return;
        }
        window.history.pushState({ modal: modalName }, '', window.location.pathname);
        if (sessionHistoryCount) {
            sessionHistoryCount.current++;
        }
    }, [sessionHistoryCount]);

    useEffect(() => {
        if (isInternalModalOpen) {
            pushModalState('internal');
        } else if (window.history.state?.modal === 'internal') {
            // The modal was closed manually (not via history back), so we need to pop the state
            window.history.back();
        }
    }, [isInternalModalOpen, pushModalState]);

    useEffect(() => {
        if (isIdeaBankOpen) {
            pushModalState('idea_bank');
        } else if (window.history.state?.modal === 'idea_bank') {
            window.history.back();
        }
    }, [isIdeaBankOpen, pushModalState]);

    const handleCloseModal = useCallback(() => {
        // Tell URL sync effect not to push new history entry during this manual transition
        isPopStateRef.current = true;

        // Only go back if this modal has a URL and we have history to consume
        const hasUrl = ['post', 'settings', 'search', 'notifications', 'store'].includes(page.type);

        // Always close all modal states explicitly for instant UI feedback
        setSelectedPost(null);
        setIsSettingsModalOpen(false);
        setIsAdditionalSettingsModalOpen(false);
        setIsAdminModalOpen(false);
        setIsBumpActionModalOpen(false);
        setIsBumpModalOpen(false);
        setPostToEdit(null);
        setIsDeleteModalOpen(false);
        setIsInternalModalOpen(false);
        setIsVouchModalOpen(false);
        setIsEditModalVisible(false);
        setShareModalOpen(false);
        setIsShareToGoodLyfeModalOpen(false);
        setFollowListModalState({ isOpen: false, listType: null, userId: null });

        if (hasUrl) {
            const targetPage = previousPage || { type: 'home' };
            setPage(targetPage);
            if (sessionHistoryCount.current > 0) {
                window.history.back();
            } else {
                const targetUrl = pageToUrl(targetPage);
                window.history.replaceState({ type: targetPage.type }, '', targetUrl);
            }
        }
        setPreviousPage(null);

        // Keep scroll restoration in a small timeout to ensure DOM update
        setTimeout(() => {
            window.scrollTo({ top: feedScrollPosition, behavior: 'auto' });
        }, 0);
    }, [page.type, previousPage, feedScrollPosition, sessionHistoryCount]);

    useEffect(() => {
        setIsModalVisible(!!(
            selectedPost ||
            isSettingsModalOpen ||
            isAdditionalSettingsModalOpen ||
            isAdminModalOpen ||
            isBumpActionModalOpen ||
            isBumpModalOpen ||
            postToEdit ||
            isDeleteModalOpen ||
            isInternalModalOpen ||
            shareModalOpen ||
            isEditModalVisible ||
            isShareToGoodLyfeModalOpen ||
            isVouchModalOpen ||
            followListModalState.isOpen ||
            createYipYapTarget !== null
        ));
    }, [
        selectedPost, isSettingsModalOpen, isAdditionalSettingsModalOpen, isAdminModalOpen,
        isBumpActionModalOpen, isBumpModalOpen, isGlobalPostModalOpen, postToEdit, isDeleteModalOpen, isInternalModalOpen,
        shareModalOpen, isEditModalVisible, isShareToGoodLyfeModalOpen, isVouchModalOpen,
        followListModalState.isOpen, createYipYapTarget
    ]);

    useEffect(() => {
        if (isModalVisible || audioPlayerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isModalVisible, audioPlayerOpen]);

    const NAV_SEQUENCE: PageState['type'][] = ['home', 'profile', 'communities', 'messages', 'footprint'];

    const handleNavigateWithAnimation = useCallback((targetPage: PageState) => {
        if (isModalVisible || !currentUser) {
            setPage(targetPage);
            return;
        }

        const getBaseType = (type: string) => {
            if (type === 'community_detail') return 'communities';
            if (type === 'hashtag' || type === 'post') return 'home';
            if (['additional_settings', 'search'].includes(type)) return 'settings';
            return type;
        };

        const currentCategory = getBaseType(page.type);
        const targetCategory = getBaseType(targetPage.type);

        const currentIndex = NAV_SEQUENCE.indexOf(currentCategory as any);
        const targetIndex = NAV_SEQUENCE.indexOf(targetCategory as any);

        if (currentIndex !== -1 && targetIndex !== -1 && currentIndex !== targetIndex) {
            // Forward (index increases) = scroll right (content follows finger left)
            // Backward (index decreases) = scroll left (content follows finger right)
            const direction = targetIndex > currentIndex ? 'left' : 'right';
            setSwipeDirection(direction);
        }

        // Apply navigation logic (preserving existing sub-page behavior)
        if (targetPage.type === 'communities') {
            if (page.type === 'communities' || page.type === 'community_detail') {
                setPage({ type: 'communities' });
            } else {
                setPage(lastCommunityPage);
            }
        } else if (targetPage.type === 'messages') {
            if (page.type === 'messages') {
                setActiveConversationId(null);
                setPage({ type: 'messages' });
            } else {
                setPage(lastMessagesPage);
            }
        } else if (targetPage.type === 'mod' && !targetPage.modId) {
            const lastModId = lastModPage && (lastModPage as any).modId;
            const isActive = lastModId && (lastModId === 'pimp_my_ride' || activeMods.includes(lastModId));
            if (isActive && lastModId !== 'pimp_my_ride') {
                setPage(lastModPage!);
            } else {
                const isPinnedModActive = pinnedModId && activeMods.includes(pinnedModId) && pinnedModId !== 'pimp_my_ride';
                const fallbackModId = isPinnedModActive ? pinnedModId : activeMods.find((id: string) => id !== 'pimp_my_ride');
                if (fallbackModId) {
                    setPage({ type: 'mod', modId: fallbackModId });
                } else {
                    setPage({ type: 'home' });
                }
            }
        } else {
            setPage(targetPage);
        }
    }, [page, lastCommunityPage, lastMessagesPage, lastModPage, setPage, currentUser, isModalVisible, activeMods, pinnedModId]);

    // Global Navigation Listener for Custom Events
    useEffect(() => {
        const handleAppNavigate = (e: Event) => {
            const customEvent = e as CustomEvent;
            if (customEvent.detail) {
                handleNavigateWithAnimation(customEvent.detail);
            }
        };
        window.addEventListener('app-navigate', handleAppNavigate);
        return () => window.removeEventListener('app-navigate', handleAppNavigate);
    }, [handleNavigateWithAnimation]);

    const handleSwipeTransition = useCallback((direction: 'left' | 'right') => {
        const hasPostCreator = !!document.querySelector('.post-creator-container');
        if (isModalVisible || isSettingsModalOpen || (isInternalModalOpen && !hasPostCreator) || isAdditionalSettingsModalOpen || !currentUser || (page.type === 'mod' && page.modId === 'pimp_my_ride')) return;

        const hasMod = page.type === 'mod' || activeMods.some((id: string) => id !== 'pimp_my_ride');
        const SWIPE_SEQUENCE: PageState['type'][] = hasMod
            ? ['home', 'profile', 'communities', 'messages', 'mod', 'settings']
            : ['home', 'profile', 'communities', 'messages', 'settings'];

        let resolvedType = page.type as string;
        if (resolvedType === 'community_detail') resolvedType = 'communities';
        if (resolvedType === 'hashtag' || resolvedType === 'post') resolvedType = 'home';
        if (['settings', 'search', 'notifications', 'store'].includes(resolvedType)) resolvedType = 'settings';

        const currentIndex = SWIPE_SEQUENCE.indexOf(resolvedType as any);
        if (currentIndex === -1) return;

        // Map left/right swipe to next/prev in sequence
        // Swiping LEFT (finger moves left, deltaX < 0) = go to NEXT (higher index) page
        // Swiping RIGHT (finger moves right, deltaX > 0) = go to PREV (lower index) page
        const seqDirection = direction === 'left' ? 'next' : 'prev';

        let nextIndex = currentIndex;
        if (seqDirection === 'next') {
            if (currentIndex < SWIPE_SEQUENCE.length - 1) {
                nextIndex = currentIndex + 1;
            }
        } else {
            if (currentIndex > 0) {
                nextIndex = currentIndex - 1;
            }
        }

        if (nextIndex !== currentIndex) {
            const nextType = SWIPE_SEQUENCE[nextIndex];
            setSwipeDirection(direction);

            if (nextType === 'profile') {
                setPage({ type: 'profile', userId: currentUser });
            } else if (nextType === 'communities') {
                setPage(lastCommunityPage);
            } else if (nextType === 'messages') {
                setPage(lastMessagesPage);
            } else if (nextType === 'settings') {
                setPage({ type: 'settings' });
            } else if (nextType === 'mod') {
                const lastModId = lastModPage && (lastModPage as any).modId;
                const isActive = lastModId && (lastModId === 'pimp_my_ride' || activeMods.includes(lastModId));
                if (isActive && lastModId !== 'pimp_my_ride') {
                    setPage(lastModPage!);
                } else {
                    const isPinnedModActive = pinnedModId && activeMods.includes(pinnedModId) && pinnedModId !== 'pimp_my_ride';
                    const fallbackModId = isPinnedModActive ? pinnedModId : activeMods.find((id: string) => id !== 'pimp_my_ride');
                    if (fallbackModId) {
                        setPage({ type: 'mod', modId: fallbackModId });
                    } else {
                        setPage({ type: 'home' });
                    }
                }
            } else {
                setPage({ type: nextType } as any);
            }
        }
    }, [page.type, page.modId, currentUser, isModalVisible, isInternalModalOpen, isAdditionalSettingsModalOpen, lastCommunityPage, lastMessagesPage, lastModPage, usersData, activeMods, pinnedModId]);


    const touchContainerRef = useRef<HTMLDivElement>(null);
    const swipeStartX = useRef<number | null>(null);
    const swipeStartY = useRef<number | null>(null);
    const swipeStartTime = useRef<number | null>(null);
    const isSwipeGesture = useRef<boolean>(false);
    const isVerticalScroll = useRef<boolean>(false);

    const lastMultiTouchTime = useRef<number>(0);
    const isPinchingRef = useRef<boolean>(false);

    // Helper to check if a touch starts inside any scrollable container or excluded element (like inputs, buttons, range sliders)
    const isTargetScrollableOrExcluded = (target: any, isNavbarZone: boolean): boolean => {
        if (!target) return false;

        // Resolve text nodes to parent element
        let el = target.nodeType === 3 ? target.parentNode : target;

        while (el && el !== document.documentElement && el !== touchContainerRef.current) {
            if (el.nodeType === 1) { // Element node
                const tagName = el.tagName;

                // Form controls and interactive tags are always excluded, unless they are inside the PostCreator
                if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || tagName === 'BUTTON') {
                    let checkCreatorEl = el;
                    let insideCreator = false;
                    while (checkCreatorEl && checkCreatorEl !== document.documentElement) {
                        if (checkCreatorEl.classList && checkCreatorEl.classList.contains('post-creator-container')) {
                            insideCreator = true;
                            break;
                        }
                        checkCreatorEl = checkCreatorEl.parentNode;
                    }
                    if (!insideCreator) {
                        return true;
                    }
                }

                // Explicit classes for horizontal scroll or exclusions
                if (el.classList && (
                    el.classList.contains('postcard-selected') ||
                    el.classList.contains('no-swipe') ||
                    el.classList.contains('horizontal-scroll') ||
                    el.classList.contains('overflow-x-auto') ||
                    el.classList.contains('overflow-x-scroll')
                )) {
                    return true;
                }

                // Navbar zone specific elements (messages, etc.)
                if (!isNavbarZone && el.classList && (
                    el.classList.contains('message-item') ||
                    el.classList.contains('message-bubble') ||
                    el.classList.contains('messages-page-no-swipe')
                )) {
                    return true;
                }

                // Computed style check for horizontal scrollability
                try {
                    const style = window.getComputedStyle(el);
                    if ((style.overflowX === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'overlay') && el.scrollWidth > el.clientWidth) {
                        return true;
                    }
                } catch (err) {
                    // Ignore errors if window/style isn't available
                }
            }
            el = el.parentNode;
        }
        return false;
    };

    const handleTouchStart = (e: any) => {
        if (e.touches.length > 1) {
            lastMultiTouchTime.current = Date.now();
            isPinchingRef.current = true;
            swipeStartX.current = null;
            swipeStartY.current = null;
            swipeStartTime.current = null;
            isSwipeGesture.current = false;
            isVerticalScroll.current = false;
            return;
        }

        // Reset pinching flag if starting a new single-touch gesture after cooldown
        if (Date.now() - lastMultiTouchTime.current > 2000) {
            isPinchingRef.current = false;
        }

        // Prevent navbar swipe when starting on a postcard, scrollable tab, or message, UNLESS it is in the navbar zone (bottom 35% of screen)
        const startY = e.touches[0].clientY;
        const isNavbarZone = startY > window.innerHeight * 0.65;

        if (isPinchingRef.current || isTargetScrollableOrExcluded(e.target, isNavbarZone)) {
            swipeStartX.current = null;
            swipeStartY.current = null;
            swipeStartTime.current = null;
            isSwipeGesture.current = false;
            isVerticalScroll.current = false;
            return;
        }

        swipeStartX.current = e.touches[0].pageX;
        swipeStartY.current = e.touches[0].pageY;
        swipeStartTime.current = Date.now();
        isSwipeGesture.current = false;
        isVerticalScroll.current = false;
    };

    const handleTouchMove = (e: any) => {
        if (e.touches.length > 1) {
            lastMultiTouchTime.current = Date.now();
            isPinchingRef.current = true;
            setSwipeOffset(0);
            swipeStartX.current = null;
            swipeStartY.current = null;
            swipeStartTime.current = null;
            isSwipeGesture.current = false;
            isVerticalScroll.current = false;
            return;
        }

        if (isPinchingRef.current || Date.now() - lastMultiTouchTime.current < 2000) return;

        // Double check target exclusion inside touchmove to be absolutely sure
        const startY = e.touches[0].clientY;
        const isNavbarZone = startY > window.innerHeight * 0.65;
        if (isTargetScrollableOrExcluded(e.target, isNavbarZone)) {
            swipeStartX.current = null;
            swipeStartY.current = null;
            swipeStartTime.current = null;
            isSwipeGesture.current = false;
            isVerticalScroll.current = false;
            return;
        }

        // Horizontal swipe check
        const hasPostCreator = !!document.querySelector('.post-creator-container');
        if (swipeStartX.current !== null && swipeStartY.current !== null && !isAnyGlobalModalOpen && (!isInternalModalOpen || hasPostCreator) && !activeConversationId && page.type !== 'master_hand' && !(page.type === 'mod' && page.modId === 'pimp_my_ride')) {
            const currentX = e.touches[0].pageX;
            const currentY = e.touches[0].pageY;
            const deltaX = currentX - swipeStartX.current;
            const deltaY = currentY - swipeStartY.current;
            const absDeltaX = Math.abs(deltaX);
            const absDeltaY = Math.abs(deltaY);

            // Require a 6px deadzone before any live tracking begins (prevents micro-jitter and locks gesture early)
            const DEADZONE = 6;
            // Require gesture angle to be within 30° of horizontal (tan(30°) ≈ 0.577)
            const ANGLE_RATIO = 0.577;

            if (!isSwipeGesture.current && !isVerticalScroll.current) {
                if (absDeltaX > DEADZONE && absDeltaX > absDeltaY && (absDeltaY / absDeltaX) < ANGLE_RATIO) {
                    isSwipeGesture.current = true;
                } else if (absDeltaY > DEADZONE) {
                    isVerticalScroll.current = true;
                }
            }

            if (isSwipeGesture.current) {
                // Prevent browser default vertical scroll when swiping horizontally
                if (e.cancelable) {
                    e.preventDefault();
                }
                const hasMod = page.type === 'mod' || activeMods.some((id: string) => id !== 'pimp_my_ride');
                const SWIPE_SEQUENCE: PageState['type'][] = hasMod
                    ? ['home', 'profile', 'communities', 'messages', 'mod', 'settings']
                    : ['home', 'profile', 'communities', 'messages', 'settings'];

                let resolvedType = page.type as string;
                if (resolvedType === 'community_detail') resolvedType = 'communities';
                if (resolvedType === 'hashtag' || resolvedType === 'post') resolvedType = 'home';
                if (['settings', 'search', 'notifications', 'store'].includes(resolvedType)) resolvedType = 'settings';

                const currentIndex = SWIPE_SEQUENCE.indexOf(resolvedType as any);
                const isSwipingRight = deltaX > 0;
                const isSwipingLeft = deltaX < 0;

                if (currentIndex === -1) {
                    // Not in SWIPE_SEQUENCE
                } else if (currentIndex === 0 && isSwipingRight) {
                    // At first page (home), can't go to prev page
                } else if (currentIndex === SWIPE_SEQUENCE.length - 1 && isSwipingLeft) {
                    // At last page (settings), can't go to next page
                } else {
                    // Write directly to DOM — no React re-render, zero lag
                    if (mainRef.current) {
                        mainRef.current.style.transition = 'none';
                        mainRef.current.style.transform = `translateX(${deltaX * 0.85}px)`;
                    }
                }
            }
        }
    };

    const handleTouchEnd = (e: any) => {
        if (isPinchingRef.current || Date.now() - lastMultiTouchTime.current < 2000) {
            if (e.touches.length === 0) {
                // If all fingers are up, we can consider resetting the pinch lock after a delay
                setTimeout(() => { isPinchingRef.current = false; }, 500);
            }
            setSwipeOffset(0);
            swipeStartX.current = null;
            swipeStartY.current = null;
            swipeStartTime.current = null;
            isSwipeGesture.current = false;
            isVerticalScroll.current = false;
            if (mainRef.current) {
                mainRef.current.style.transition = 'transform 0.3s ease';
                mainRef.current.style.transform = '';
            }
            return;
        }

        const hasPostCreator = !!document.querySelector('.post-creator-container');
        if (swipeStartX.current !== null && swipeStartY.current !== null && !isAnyGlobalModalOpen && (!isInternalModalOpen || hasPostCreator) && !activeConversationId && page.type !== 'master_hand' && !(page.type === 'mod' && page.modId === 'pimp_my_ride')) {
            const currentX = e.changedTouches[0].pageX;
            const currentY = e.changedTouches[0].pageY;
            const deltaX = currentX - swipeStartX.current;
            const deltaY = currentY - swipeStartY.current;

            if (isSwipeGesture.current) {
                const duration = Date.now() - (swipeStartTime.current || 0);
                const velocity = Math.abs(deltaX) / (duration || 1); // px/ms
                const isFlick = velocity > 0.3 && Math.abs(deltaX) > 40;
                const isDrag = Math.abs(deltaX) > 80;

                if (isDrag || isFlick) {
                    console.log(`[App] Triggering swipe transition: ${deltaX > 0 ? 'right' : 'left'} (deltaX: ${deltaX}, velocity: ${velocity.toFixed(2)})`);
                    handleSwipeTransition(deltaX > 0 ? 'right' : 'left');
                    // Trigger global swipe-nav event to unhide NavBar
                    window.dispatchEvent(new CustomEvent('swipe-nav'));
                }
            }
        }
        // Snap back: apply transition then clear the direct-DOM transform
        if (mainRef.current) {
            mainRef.current.style.transition = 'transform 0.3s ease';
            mainRef.current.style.transform = '';
        }
        swipeStartX.current = null;
        swipeStartY.current = null;
        swipeStartTime.current = null;
        isSwipeGesture.current = false;
        isVerticalScroll.current = false;
    };

    const handleTouchCancel = (e: any) => {
        if (mainRef.current) {
            mainRef.current.style.transition = 'transform 0.3s ease';
            mainRef.current.style.transform = '';
        }
        setSwipeOffset(0);
        swipeStartX.current = null;
        swipeStartY.current = null;
        swipeStartTime.current = null;
        isSwipeGesture.current = false;
        isVerticalScroll.current = false;
    };

    // Keep handlers completely fresh using refs to prevent stale closure bugs in imperative listeners
    const handleTouchStartRef = useRef<any>(null);
    const handleTouchMoveRef = useRef<any>(null);
    const handleTouchEndRef = useRef<any>(null);
    const handleTouchCancelRef = useRef<any>(null);

    useEffect(() => {
        handleTouchStartRef.current = handleTouchStart;
        handleTouchMoveRef.current = handleTouchMove;
        handleTouchEndRef.current = handleTouchEnd;
        handleTouchCancelRef.current = handleTouchCancel;
    });

    // Bind non-passive touchmove listener to touchContainerRef to allow preventDefault for scrolling lockout
    useEffect(() => {
        const container = touchContainerRef.current;
        if (!container) return;

        const startHandler = (e: TouchEvent) => handleTouchStartRef.current?.(e);
        const moveHandler = (e: TouchEvent) => handleTouchMoveRef.current?.(e);
        const endHandler = (e: TouchEvent) => handleTouchEndRef.current?.(e);
        const cancelHandler = (e: TouchEvent) => handleTouchCancelRef.current?.(e);

        container.addEventListener('touchstart', startHandler, { passive: true });
        container.addEventListener('touchmove', moveHandler, { passive: false });
        container.addEventListener('touchend', endHandler, { passive: true });
        container.addEventListener('touchcancel', cancelHandler, { passive: true });

        return () => {
            container.removeEventListener('touchstart', startHandler);
            container.removeEventListener('touchmove', moveHandler);
            container.removeEventListener('touchend', endHandler);
            container.removeEventListener('touchcancel', cancelHandler);
        };
    }, []);

    // Sync page state with global modal states (to support direct URL navigation)
    useEffect(() => {

        if (['settings', 'search', 'notifications', 'store'].includes(page.type) || (page.type === 'mod' && page.modId === 'pimp_my_ride')) {
            setIsSettingsModalOpen(true);
            setIsAdditionalSettingsModalOpen(false);
        } else if (page.type === 'additional_settings') {
            setIsSettingsModalOpen(false);
            setIsAdditionalSettingsModalOpen(true);
        } else {
            setIsSettingsModalOpen(false);
            setIsAdditionalSettingsModalOpen(false);
        }

        // Clear active conversation when navigating away from messages page
        // Clear active conversation when navigating away from a specific thread
        // (Either leaving messages entirely, or moving back to the conversation list)
        const isYipYapActive = activeConversationId && conversations.find(c => c.id === activeConversationId)?.isYipYap;
        if ((page.type !== 'messages' && page.type !== 'post') || (page.type === 'messages' && !page.username && !isYipYapActive)) {
            setActiveConversationId(null);
        }
    }, [page, activeConversationId, conversations]);



    // Keep track of the last "main" page to support modal return navigation
    useEffect(() => {
        // Reset transform on page change to prevent stuck UI states from aborted or completed gestures
        if (mainRef.current) {
            mainRef.current.style.transition = 'none';
            mainRef.current.style.transform = '';
        }

        const modalPages = ['post', 'privacy', 'safety', 'settings', 'additional_settings', 'notifications', 'search', 'store'];
        if (!modalPages.includes(page.type)) {
            setPreviousPage(page);
        }

        // Update persistent tab memory values
        if (page.type === 'profile' && page.userId) {
            setLastProfileUserId(page.userId);
            setHasVisitedProfile(true);
        }
        if (page.type === 'storefront' && page.vendorName) {
            setLastProfileUserId(page.vendorName);
            setHasVisitedProfile(true);
        }
        if (page.type === 'community_detail' && page.query) {
            setLastCommunityQuery(page.query);
            setLastCommunityChannelId(page.channelId);
            setLastCommunityMessageId(page.messageId);
            setHasVisitedCommunityDetail(true);
        }
        if (page.type === 'hashtag' && page.tag) {
            setLastHashtagTag(page.tag);
            setHasVisitedHashtag(true);
        }
        if (page.type === 'communities') {
            setHasVisitedCommunities(true);
        }
        if (page.type === 'messages') {
            setHasVisitedMessages(true);
        }
        if (page.type === 'mod' && (page as any).modId === 'vibeman1_footprint') {
            setLastFootprintTaskId((page as any).params?.taskId ?? undefined);
            setHasVisitedFootprint(true);
        }
        setIsShakingMode(false);
    }, [page]);
    const [gridColumns, setGridColumns] = usePersistentState<1 | 2 | 3 | 4 | 5 | 6>('gridColumns', 2);
    const [isShakingMode, setIsShakingMode] = useState(false);
    const [postcardGap, setPostcardGap] = usePersistentState<number>('postcardGap', 8);



    const isAnyGlobalModalOpenExceptSettings = useMemo(() => {
        return isModalVisible || isAdditionalSettingsModalOpen || isAdminModalOpen || isVouchModalOpen || followListModalState.isOpen || isShareToGoodLyfeModalOpen || isBroadcastModalOpen || isSystemModModalOpen || isModLibraryModalOpen || isFollowerGroupsModalOpen || isLinkModalOpen || isDebugOpen || isAccountSwitcherOpen || isIdeaBankOpen || isGlobalPostModalOpen;
    }, [isModalVisible, isAdditionalSettingsModalOpen, isAdminModalOpen, isVouchModalOpen, followListModalState.isOpen, isShareToGoodLyfeModalOpen, isBroadcastModalOpen, isSystemModModalOpen, isModLibraryModalOpen, isFollowerGroupsModalOpen, isLinkModalOpen, isDebugOpen, isAccountSwitcherOpen, isIdeaBankOpen, isGlobalPostModalOpen]);

    const isAnyGlobalModalOpen = useMemo(() => {
        return isModalVisible || isSettingsModalOpen || isAdditionalSettingsModalOpen || isAdminModalOpen || isVouchModalOpen || followListModalState.isOpen || isShareToGoodLyfeModalOpen || isBroadcastModalOpen || isSystemModModalOpen || isModLibraryModalOpen || isFollowerGroupsModalOpen || isLinkModalOpen || isDebugOpen || isAccountSwitcherOpen || isIdeaBankOpen || isGlobalPostModalOpen;
    }, [isModalVisible, isSettingsModalOpen, isAdditionalSettingsModalOpen, isAdminModalOpen, isVouchModalOpen, followListModalState.isOpen, isShareToGoodLyfeModalOpen, isBroadcastModalOpen, isSystemModModalOpen, isModLibraryModalOpen, isFollowerGroupsModalOpen, isLinkModalOpen, isDebugOpen, isAccountSwitcherOpen, isIdeaBankOpen, isGlobalPostModalOpen]);

    const [closeInternalModalsTrigger, setCloseInternalModalsTrigger] = useState(0);

    useScrollLock(isAnyGlobalModalOpen);

    // Pause all media in inactive page containers when backgroundPage changes
    useEffect(() => {
        // If settings modal is open or we are on settings/search/notifs/store pages, do not auto-pause background media
        if (isSettingsModalOpen || ['settings', 'search', 'notifications', 'store', 'additional_settings'].includes(page.type)) {
            return;
        }

        const pages = ['home', 'profile', 'footprint', 'messages', 'hashtag', 'community_detail', 'communities'];
        pages.forEach(p => {
            if (backgroundPage.type !== p) {
                const container = document.querySelector(`[data-page-container="${p}"]`);
                if (container) {
                    container.querySelectorAll('audio, video').forEach(el => {
                        try {
                            if (el.id !== 'global-audio-player') {
                                (el as HTMLAudioElement | HTMLVideoElement).pause();
                            }
                        } catch (e) { }
                    });
                }
            }
        });
    }, [backgroundPage.type, isSettingsModalOpen, page.type]);

    // Persistent Scroll History
    useEffect(() => {
        const currentKey = getPageKey(page);

        // 1. Save scroll position of the page we are LEAVING
        if (lastPageKeyRef.current && currentKey !== lastPageKeyRef.current && !isAnyGlobalModalOpen) {
            scrollPositions.current[lastPageKeyRef.current] = {
                scrollY: window.scrollY,
                visibleCount: lastVisibleCountRef.current
            };
        }

        // 2. Restore scroll and count for the NEW page
        if (currentKey && !isAnyGlobalModalOpen) {
            const saved = scrollPositions.current[currentKey];
            if (saved) {
                // Restoration of count happens via props passed to components
                // We just need to trigger the scroll once the DOM is ready
                const timer = setTimeout(() => {
                    window.scrollTo({ top: saved.scrollY, behavior: 'auto' });
                }, 0);
                return () => clearTimeout(timer);
            }
        }

        if (currentKey) {
            lastPageKeyRef.current = currentKey;
            // Reset count ref for the new page until updated by component
            lastVisibleCountRef.current = scrollPositions.current[currentKey]?.visibleCount || 24;
        }
    }, [page.type, (page as any).userId, (page as any).query, (page as any).tag, isAnyGlobalModalOpen]);
    const [achievements, setAchievements] = useState<Achievement[]>([]);
    const [dailyAchievements, setDailyAchievements] = usePersistentState<DailyAchievements>('dailyAchievements', { date: '', daily: null });
    const [failedPosts, setFailedPosts] = useState<Post[]>([]);
    const hasLoadedFailedPostsRef = React.useRef(false);

    // Sync failed posts to localStorage
    useEffect(() => {
        if (currentUser && hasLoadedFailedPostsRef.current) {
            localStorage.setItem(`goodlyfe_failed_posts_${currentUser}`, JSON.stringify(failedPosts));
        }
    }, [failedPosts, currentUser]);

    // Initial Data Sync
    useEffect(() => {
        const syncAppData = async () => {
            if (!currentUser) return;
            try {
                // Fetch Posts
                const serverPosts = await fetchAllPosts(currentUser);
                if (serverPosts && Array.isArray(serverPosts)) {
                    // Read stored failed posts from localStorage
                    let localFailedPosts: Post[] = [];
                    try {
                        const storedFailed = localStorage.getItem(`goodlyfe_failed_posts_${currentUser}`);
                        if (storedFailed) {
                            localFailedPosts = JSON.parse(storedFailed);
                        }
                    } catch (e) {
                        console.error('Failed to parse local failed posts', e);
                    }

                    // RESTORE MEDIA BLOBS FROM INDEXEDDB SO THEY DON'T GET LOST OR BROKEN ON RESTART
                    const { getPendingPostAttachments } = require('./indexedDb');
                    const restoredFailedPosts = await Promise.all(localFailedPosts.map(async (p) => {
                        try {
                            const localId = (p as any).localId || p.id;
                            const stored = await getPendingPostAttachments(Number(localId));
                            if (stored && stored.attachments) {
                                const restoredMedia = stored.attachments.map((a: any) => {
                                    const url = URL.createObjectURL(a.file);
                                    return {
                                        url,
                                        type: a.type,
                                        isLive: a.isLive,
                                        name: a.name
                                    };
                                });
                                let restoredCover = p.content.coverImageUrl;
                                if (stored.coverImage && stored.coverImage.file) {
                                    restoredCover = URL.createObjectURL(stored.coverImage.file);
                                }
                                return {
                                    ...p,
                                    content: {
                                        ...p.content,
                                        media: restoredMedia,
                                        coverImageUrl: restoredCover
                                    }
                                };
                            }
                        } catch (err) {
                            console.error("Error restoring local post attachments", err);
                        }
                        return p;
                    }));

                    setFailedPosts(restoredFailedPosts);
                    hasLoadedFailedPostsRef.current = true;

                    // Prepend local failed posts to server posts so they load on top
                    const combinedPosts = [...restoredFailedPosts, ...serverPosts.filter(p => !restoredFailedPosts.some(lp => lp.id === p.id))];
                    setPosts(combinedPosts);
                }

                // Fetch Achievements
                const serverAchievements = await fetchAchievements();
                if (serverAchievements && Array.isArray(serverAchievements)) {
                    if (serverAchievements.length > 0) {
                        setAchievements(serverAchievements);
                    } else {
                        setAchievements([]);
                    }
                }

                // Fetch Conversations
                const serverConvos = await fetchConversations(currentUser);
                if (serverConvos && Array.isArray(serverConvos)) {
                    // Deduplicate conversations based on participants
                    const uniqueConvos = new Map<string, Conversation>();

                    serverConvos.forEach((c: Conversation) => {
                        // For YipYaps, use ID as key to prevent deduplicating different channels by the same owner
                        // For DMs, use sorted participant list to ensure only one thread per pair/group
                        const key = c.isYipYap ? `yip-${c.id}` : [...c.participants].sort().join(':');

                        if (uniqueConvos.has(key)) {
                            const existing = uniqueConvos.get(key)!;
                            // Keep the one with MORE messages 
                            if ((c.messages || []).length > (existing.messages || []).length) {
                                uniqueConvos.set(key, c);
                            } else if ((c.messages || []).length === (existing.messages || []).length) {
                                if (c.id > existing.id) {
                                    uniqueConvos.set(key, c);
                                }
                            }
                        } else {
                            uniqueConvos.set(key, c);
                        }
                    });

                    const dedupedConvos = Array.from(uniqueConvos.values());

                    // Since conversation messages are high frequency, we might want to merge carefully
                    // For MVP migration: replace local with server if server has data
                    if (dedupedConvos.length > 0) {
                        setConversations(dedupedConvos);
                    }
                }
                // Fetch Communities
                const serverCommunities = await fetchAllCommunities(currentUser);
                if (serverCommunities && Array.isArray(serverCommunities)) {
                    setCommunities(serverCommunities);
                }

            } catch (error) {
                console.error("Failed to sync app data:", error);
            } finally {
                setIsInitialLoad(false);
            }
        };
        syncAppDataRef.current = syncAppData;
        syncAppData();
    }, [currentUser, setPosts, setAchievements, setConversations]);

    // Recover unsent messages from IndexedDB on startup
    useEffect(() => {
        if (!currentUser) return;
        const recoverMessages = async () => {
            try {
                const pending = await getAllPendingMessages();
                if (pending && pending.length > 0) {
                    // Filter out truly ancient ones or ones from other users if possible, 
                    // though IndexedDB is local to this device/user session mostly.
                    for (const msg of pending) {
                        // Resend them. Pass isRecovering=true to avoid re-saving to IDB
                        handleSendMessage(msg.conversationId, msg.text, msg.replyTo, msg.attachments, msg.isNote, true, msg.tempId);
                    }
                }
            } catch (err) {
                console.error('Failed to recover pending messages:', err);
            }
        };
        recoverMessages();
    }, [currentUser]);

    // Socket.IO Real-time Updates
    useEffect(() => {
        if (!currentUser) return;

        // Determine Socket.IO server URL based on environment
        const getSocketUrl = () => {
            let base = API_URL.trim().replace(/\/+$/, '');
            if (base.toLowerCase().endsWith('/api')) {
                base = base.substring(0, base.length - 4);
            }
            return base || 'https://goodlyfe.net';
        };

        const socketUrl = getSocketUrl();
        console.log('[Socket] Connecting to server:', socketUrl);

        const socket = io(socketUrl, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000
        });
        socketRef.current = socket;

        socket.on('connect', () => {
            console.log('[Socket] ✅ Connected successfully! Socket ID:', socket.id);
            // Join user's personal room for notifications
            socket.emit('join', currentUser);
            console.log('[Socket] Emitted join event for user:', currentUser);
        });

        socket.on('connect_error', (error) => {
            console.error('[Socket] ❌ Connection error:', error);
        });

        socket.on('disconnect', (reason) => {
            console.log('[Socket] Disconnected. Reason:', reason);
        });

        socket.on('reconnect', (attemptNumber) => {
            console.log('[Socket] Reconnected after', attemptNumber, 'attempts');
        });

        // Listen for new posts
        socket.on('new-post', (newPost: Post) => {
            console.log('[Socket] 📍 New post received:', newPost);
            setPosts(prev => {
                // Check for exact ID match — already in state, skip
                if (prev.some(p => p.id === newPost.id)) return prev;

                // Check for optimistic match: a local placeholder for THIS specific post.
                // Criteria:
                //  - The local post has a temporary Date.now() ID (> 1e12)
                //  - Same author, same type
                //  - Created within 15s of the server post (server ID ≈ Date.now() at creation)
                //  - Same text content (for text-only posts we require text equality, not just media length)
                const optimisticMatch = prev.find(p =>
                    p.id > 1000000000000 &&
                    p.author === newPost.author &&
                    p.type === newPost.type &&
                    Math.abs(p.id - newPost.id) < 15000 &&
                    (
                        p.content.text === newPost.content.text ||
                        (
                            (p.content.media?.length ?? 0) > 0 &&
                            (p.content.media?.length ?? 0) === (newPost.content.media?.length ?? 0)
                        )
                    )
                );

                if (optimisticMatch) {
                    console.log('[Socket] Replacing optimistic post with server post:', newPost.id);
                    return prev.map(p => p.id === optimisticMatch.id ? newPost : p);
                }

                return [newPost, ...prev];
            });
        });

        // Listen for post updates (likes, comments, etc.)
        socket.on('post-updated', (updatedPost: Post) => {
            console.log('[Socket] 🔄 Post updated:', updatedPost);
            setPosts(prev => prev.map(p => {
                if (p.id === updatedPost.id) {
                    // Deep-merge content to prevent server from stripping locally-set fields
                    // (e.g. media[].fit, coverImageFit) that may not yet be in the server schema
                    const mergedContent = {
                        ...p.content,
                        ...updatedPost.content,
                        // Preserve local media fit values if server stripped them
                        media: updatedPost.content?.media?.map((serverItem: any, idx: number) => ({
                            ...serverItem,
                            fit: serverItem.fit || p.content?.media?.[idx]?.fit || 'auto'
                        })) || p.content?.media,
                        coverImageFit: updatedPost.content?.coverImageFit || p.content?.coverImageFit || 'auto'
                    };
                    return { ...p, ...updatedPost, content: mergedContent };
                }
                return p;
            }));
        });

        // Listen for post deletions
        socket.on('post-deleted', (deletedPostId: number) => {
            console.log('[Socket] 🗑️ Post deleted:', deletedPostId);
            setPosts(prev => prev.filter(p => p.id !== deletedPostId));
        });

        // Listen for conversation updates (new messages)
        socket.on('conversation-updated', (updatedConversation: Conversation) => {
            console.log('[Socket] 💬 Conversation updated:', updatedConversation.id);
            setConversations(prev => {
                const exists = prev.find(c => c.id === updatedConversation.id);
                if (exists) {
                    // We need to merge messages gracefully because we might have optimistic messages
                    // with temporary local IDs (large numbers from nextMsgId sequence > 1000000000000).
                    // The server's messages will have real DB IDs.
                    const newMessages = [...(exists.messages || [])];

                    for (const serverMsg of (updatedConversation.messages || [])) {
                        // Skip messages we've optimistically deleted locally
                        if (locallyDeletedMessageIds.has(serverMsg.id)) continue;

                        // See if we already have this exact server message by ID
                        const hasServerMsg = newMessages.some(m => m.id === serverMsg.id);
                        if (!hasServerMsg) {
                            // It's a new message from the server.
                            // Is there an optimistic message locally that matches it? 
                            // (Same text, same sender, sent recently, and has a large temporary ID)
                            const optimisticMatchIndex = newMessages.findIndex(m =>
                                m.id > 1000000000000 &&
                                m.senderId === serverMsg.senderId &&
                                m.text === serverMsg.text &&
                                Math.abs(new Date(m.timestamp).getTime() - new Date(serverMsg.timestamp).getTime()) < 5000
                            );

                            if (optimisticMatchIndex !== -1) {
                                // Swap out our temporary optimistic message with the real server message
                                newMessages[optimisticMatchIndex] = serverMsg;
                            } else {
                                // Genuinely new message from someone else, or an old one we lacked
                                newMessages.push(serverMsg);
                            }
                        } else {
                            // We have it by ID. Update it (e.g. read receipts or reactions)
                            const idx = newMessages.findIndex(m => m.id === serverMsg.id);
                            if (idx !== -1) {
                                const localReactionTime = recentlyReactedMessageIds.get(serverMsg.id) || 0;
                                if (Date.now() - localReactionTime < 5000) {
                                    // Keep our local optimistic reactions, overwrite the rest
                                    newMessages[idx] = { ...serverMsg, reactions: newMessages[idx].reactions };
                                } else {
                                    newMessages[idx] = serverMsg;
                                }
                            }
                        }
                    }

                    // Keep them sorted by timestamp
                    newMessages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

                    return prev.map(c => {
                        if (c.id === updatedConversation.id) {
                            const meta = recentlyUpdatedConversationMetadata.get(c.id);
                            if (meta && Date.now() - meta.timestamp < 5000) {
                                // Keep our local metadata (theme, hideBubbles, nicknames) but deep merge to prevent data loss
                                return {
                                    ...updatedConversation,
                                    messages: newMessages,
                                    theme: { ...updatedConversation.theme, ...(meta.theme || {}) } as Conversation['theme'],
                                    nicknames: { ...(updatedConversation.nicknames || {}), ...(meta.nicknames || {}) },
                                    hideBubbles: meta.hideBubbles !== undefined ? meta.hideBubbles : updatedConversation.hideBubbles,
                                };
                            } else {
                                return { ...updatedConversation, messages: newMessages };
                            }
                        }
                        return c;
                    });
                } else {
                    return [...prev, { ...updatedConversation, messages: updatedConversation.messages || [] }];
                }
            });
        });

        socket.on('conversation-deleted', (convoId: number) => {
            console.log(`[Socket] 🗑️ Conversation deleted: ${convoId}`);
            setConversations(prev => prev.filter(c => c.id !== convoId));
            if (activeConversationId === convoId) {
                setActiveConversationId(null);
            }
        });

        // Listen for voice room updates
        socket.on('voice-rooms-update', (updatedVoiceRooms: Record<string, VoiceParticipant[]>) => {
            console.log('[Socket] 🎙️  Voice rooms update:', updatedVoiceRooms);
            setVoiceRooms(updatedVoiceRooms);
        });

        // Listen for real-time notifications (e.g., mentions)
        socket.on('notification', (notification: Notification) => {
            console.log('[Socket] 🔔 Notification received:', notification);
            setActiveLiveNotification(notification);
            updateUserData((prevData: Record<string, UserData>) => {
                const currentData = prevData[currentUser] || {};
                return {
                    ...prevData,
                    [currentUser]: {
                        ...currentData,
                        notifications: [notification, ...(currentData.notifications || [])]
                    }
                };
            });
        });

        socket.on('notification_removed', (criteria: any) => {
            console.log('[Socket] 🔕 Notification removal received:', criteria);

            // Clear flashing notification if it matches
            setActiveLiveNotification(prev => {
                if (!prev) return null;
                const matches = (
                    prev.type === criteria.type &&
                    prev.fromUser === criteria.fromUser &&
                    (criteria.postId === undefined || prev.postId === criteria.postId) &&
                    (criteria.id === undefined || prev.id === criteria.id) &&
                    (criteria.commentId === undefined || (prev.commentId === criteria.commentId))
                );
                return matches ? null : prev;
            });

            updateUserData((prevData: Record<string, UserData>) => {
                const currentData = prevData[currentUser] || {};
                const notifications = currentData.notifications || [];

                const filteredNotifs = notifications.filter(n => {
                    const matches = (
                        n.type === criteria.type &&
                        n.fromUser === criteria.fromUser &&
                        (criteria.postId === undefined || n.postId === criteria.postId) &&
                        (criteria.id === undefined || n.id === criteria.id) &&
                        (criteria.commentId === undefined || n.commentId === criteria.commentId)
                    );
                    return !matches;
                });

                return {
                    ...prevData,
                    [currentUser]: {
                        ...currentData,
                        notifications: filteredNotifs
                    }
                };
            });
        });

        socket.on('post-deleted', ({ postId }: { postId: number }) => {
            console.log('[Socket] 🗑️ Post deleted signal received:', postId);

            // Clear flashing notification if it belongs to this post
            setActiveLiveNotification(prev => {
                if (!prev || prev.postId !== postId) return prev;
                return null;
            });

            updateUserData((prevData: Record<string, UserData>) => {
                const currentData = prevData[currentUser] || {};
                const notifications = currentData.notifications || [];
                const filteredNotifs = notifications.filter(n => n.postId !== postId);

                return {
                    ...prevData,
                    [currentUser]: {
                        ...currentData,
                        notifications: filteredNotifs
                    }
                };
            });
        });

        socket.on('user-updated', ({ username, updates }: { username: string, updates: Partial<UserData> }) => {
            console.log('[Socket] 👤 User updated:', username, updates);
            updateUserData((prevData: Record<string, UserData>) => {
                const existingUser = prevData[username];
                if (!existingUser) return prevData;
                return {
                    ...prevData,
                    [username]: {
                        ...existingUser,
                        ...updates
                    }
                };
            });
        });

        socket.on('active-users-update', (users: string[]) => {
            console.log('[Socket] 👥 Active users update:', users);
            setActiveUsers(users);
        });

        socket.on('incoming-call', ({ caller, nickname, conversationId, channelId }) => {
            console.log(`[Call] Incoming call from ${nickname} (${caller})`);
            setIncomingCall({ caller, nickname, conversationId, channelId });
            playRingtone();
        });

        socket.on('call-declined', () => {
            console.log('[Call] Call declined by recipient');
            stopRingtone();
            setIncomingCall(null);
            if (activeVoiceDataRef.current) {
                handleLeaveVoice();
                alert('Call declined by recipient');
            }
        });

        socket.on('call-timeout', () => {
            console.log('[Call] Call timed out (no answer)');
            stopRingtone();
            setIncomingCall(null);
            if (activeVoiceDataRef.current) {
                handleLeaveVoice();
                alert('No answer');
            }
        });

        // Cleanup on unmount
        return () => {
            console.log('[Socket] Cleaning up and disconnecting...');
            socket.disconnect();
            socketRef.current = null;
        };
    }, [currentUser]);

    // Handle joining/leaving conversation rooms on the SAME socket
    useEffect(() => {
        const socket = socketRef.current;
        if (!socket || !currentUser || !activeConversationId) return;

        console.log(`[Socket] 🚪 Joining room: conversation:${activeConversationId}`);
        socket.emit('join-conversation', activeConversationId);

        return () => {
            if (socketRef.current && activeConversationId) {
                console.log(`[Socket] 🚪 Leaving room: conversation:${activeConversationId}`);
                socketRef.current.emit('leave-conversation', activeConversationId);
            }
        };
    }, [activeConversationId, currentUser]);



    // Refs to hold the latest states to prevent event listener churn
    const popStateDataRef = useRef({
        posts,
        selectedPost
    });
    useEffect(() => {
        popStateDataRef.current = { posts, selectedPost };
    });

    const handleBackNavigationRef = useRef<(() => void) | null>(null);
    useEffect(() => {
        handleBackNavigationRef.current = handleBackNavigation;
    });

    const onPopState = useCallback(() => {
        console.log('[Routing] onPopState event triggered. Pathname:', window.location.pathname);
        if (skipNextPopStateRef.current) {
            skipNextPopStateRef.current = false;
            return;
        }
        isPopStateRef.current = true;
        const newPage = urlToPage(window.location.pathname);

        // Reset transform on popstate to prevent sticky page transitions on browser history back/forward swipes
        if (mainRef.current) {
            mainRef.current.style.transition = 'none';
            mainRef.current.style.transform = '';
        }

        const { posts: latestPosts, selectedPost: latestSelectedPost } = popStateDataRef.current;

        // If navigating between posts, try to keep the post data if we have it
        if (newPage.type === 'post') {
            const found = latestPosts.find(p => p.id.toString() === newPage.postId);
            if (found) {
                setSelectedPost(getHydratedPosts([found])[0]);
            } else if (latestSelectedPost && latestSelectedPost.id.toString() === newPage.postId) {
                // Keep existing selectedPost if IDs match
            } else {
                setSelectedPost(null); // Will be fetched or hydrated by page effect
            }
        } else {
            setSelectedPost(null);
        }

        // Sync settings modal states explicitly
        if (['settings', 'search', 'notifications', 'store'].includes(newPage.type)) {
            setIsSettingsModalOpen(true);
            setIsAdditionalSettingsModalOpen(false);
        } else if (newPage.type === 'additional_settings') {
            setIsSettingsModalOpen(false);
            setIsAdditionalSettingsModalOpen(true);
        } else {
            setIsSettingsModalOpen(false);
            setIsAdditionalSettingsModalOpen(false);
        }

        if (sessionHistoryCount.current > 0) {
            sessionHistoryCount.current--;
        }
        if (navHistoryRef.current.length > 1) {
            navHistoryRef.current.pop();
        }
        setPage(newPage);

        // Close other modal states when navigating via history
        setIsIdeaBankOpen(false);
        setFollowListModalState({ isOpen: false, listType: null, userId: null });
        setIsDeleteModalOpen(false);
        setIsEditModalVisible(false);
        setIsBumpModalOpen(false);
        setIsBumpActionModalOpen(false);
        setIsVouchModalOpen(false);
        setShareModalOpen(false);
        setIsShareToGoodLyfeModalOpen(false);
        setIsAdminModalOpen(false);
        setIsInternalModalOpen(false);
        setCloseInternalModalsTrigger(prev => prev + 1);
    }, [getHydratedPosts]);

    const onPopStateRef = useRef(onPopState);
    useEffect(() => {
        onPopStateRef.current = onPopState;
    });

    useEffect(() => {
        const handleEscKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleBackNavigationRef.current?.();
            }
        };

        const handleDigitalFootprintBack = () => {
            handleBackNavigationRef.current?.();
        };

        const onPopStateWrapper = () => {
            onPopStateRef.current();
        };

        const handlePageShow = () => {
            if (mainRef.current) {
                mainRef.current.style.transition = 'none';
                mainRef.current.style.transform = '';
            }
        };

        const handleGlobalTouchCancel = () => {
            if (mainRef.current) {
                mainRef.current.style.transition = 'transform 0.3s ease';
                mainRef.current.style.transform = '';
            }
            swipeStartX.current = null;
            swipeStartY.current = null;
            swipeStartTime.current = null;
            isSwipeGesture.current = false;
            isVerticalScroll.current = false;
        };

        window.addEventListener('keydown', handleEscKey);
        window.addEventListener('digitalFootprintBack', handleDigitalFootprintBack);
        window.addEventListener('popstate', onPopStateWrapper);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('touchcancel', handleGlobalTouchCancel);

        return () => {
            window.removeEventListener('keydown', handleEscKey);
            window.removeEventListener('digitalFootprintBack', handleDigitalFootprintBack);
            window.removeEventListener('popstate', onPopStateWrapper);
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('touchcancel', handleGlobalTouchCancel);
        };
    }, []);

    // Mount-only effect: register Android back button listener if available, with cleanup
    React.useEffect(() => {
        let _backButtonHandle: any = null;
        let isMounted = true;

        const initAndroidBackButton = async () => {
            try {
                // @ts-ignore
                const mod = await import('@capacitor/app').catch(() => null);
                if (mod && (mod as any).App && typeof (mod as any).App.addListener === 'function' && isMounted) {
                    console.log('[MainApp] Adding Android backButton listener (mount effect)');
                    const handle = await (mod as any).App.addListener('backButton', () => {
                        try {
                            window.dispatchEvent(new Event('digitalFootprintBack'));
                        } catch (e) {
                            console.log('[MainApp] Failed to dispatch digitalFootprintBack event', e);
                        }
                    });

                    if (!isMounted) {
                        // If unmounted while waiting for the promise, clean up and handle Capacitor 3/4/5 variations
                        if (handle && typeof handle.remove === 'function') {
                            handle.remove();
                        }
                    } else {
                        _backButtonHandle = handle;
                    }
                } else {
                    console.log('[MainApp] Capacitor App plugin not available/supported (mount effect)');
                }
            } catch (err) {
                console.log('[MainApp] initAndroidBackButton error (mount effect)', err);
            }
        };

        initAndroidBackButton();

        return () => {
            isMounted = false;
            try {
                if (_backButtonHandle && typeof _backButtonHandle.remove === 'function') {
                    _backButtonHandle.remove();
                    console.log('[MainApp] Removed Android backButton listener handle (cleanup)');
                } else if (_backButtonHandle && typeof _backButtonHandle.then === 'function') {
                    // If it was still a promise, handle it when it resolves
                    _backButtonHandle.then((h: any) => {
                        if (h && typeof h.remove === 'function') h.remove();
                    }).catch(() => { });
                }
            } catch (err) {
                console.log('[MainApp] Error removing backButton listener (cleanup)', err);
            }
        };
    }, []);

    // Warn before closing browser tab/page when active in a voice channel
    React.useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (activeVoiceData) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [activeVoiceData]);

    // When page changes, update URL and session history count
    React.useEffect(() => {
        const url = pageToUrl(page);
        const normalizePath = (p: string) => p.replace(/\/$/, '') || '/';

        if (normalizePath(window.location.pathname) !== normalizePath(url)) {
            if (!isPopStateRef.current) {
                if (isSwipingPostRef.current) {
                    window.history.replaceState({ type: page.type }, '', url);
                    navHistoryRef.current[Math.max(0, navHistoryRef.current.length - 1)] = page;
                } else {
                    window.history.pushState({ type: page.type }, '', url);
                    sessionHistoryCount.current++;
                    navHistoryRef.current.push(page);
                }
            }
        }
        // Reset the flags after the effect runs
        let timeoutId: any = null;
        if (normalizePath(window.location.pathname) === normalizePath(url)) {
            isPopStateRef.current = false;
        } else {
            timeoutId = setTimeout(() => {
                isPopStateRef.current = false;
            }, 500);
        }
        isSwipingPostRef.current = false;

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [page, sessionHistoryCount]);

    // Global Link Interceptor (to prevent full page reloads on internal links)
    React.useEffect(() => {
        const handleGlobalClick = (e: MouseEvent) => {
            const target = (e.target as HTMLElement).closest('a');
            if (!target || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

            const href = target.getAttribute('href');
            if (!href) return;

            // Check if it's an internal link (relative or starts with goodlyfe.net)
            const isInternal = href.startsWith('/') ||
                href.includes('goodlyfe.net') ||
                href.includes('localhost');

            if (isInternal) {
                try {
                    const url = new URL(href, window.location.origin);
                    if (url.origin === window.location.origin || url.hostname.includes('goodlyfe.net')) {
                        e.preventDefault();
                        const newPage = urlToPage(url.pathname, true);
                        console.log('[Routing] Intercepted link click, navigating to:', newPage);
                        setPage(newPage);
                    }
                } catch (err) {
                    // Not a valid URL or other error, let browser handle it
                }
            }
        };

        document.addEventListener('click', handleGlobalClick);
        return () => document.removeEventListener('click', handleGlobalClick);
    }, [setPage]);

    // Global Customization Application (Fonts, etc.)
    React.useEffect(() => {
        const userData = currentUser ? usersData[currentUser] : null;
        const activeFontId = userData?.activeCustomizations?.font;

        if (activeFontId) {
            // Map font IDs to families (matching CustomizationStorePage.tsx)
            const fontMap: Record<string, string> = {
                'font_default': 'inherit',
                'font_serif': 'Georgia, serif',
                'font_mono': 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                'font_cursive': '"Comic Sans MS", cursive, sans-serif',
                'font_inter': '"Inter", sans-serif',
                'font_playfair': '"Playfair Display", serif',
                'font_roboto_mono': '"Roboto Mono", monospace',
                'font_pacifico': '"Pacifico", cursive',
                'font_bangers': '"Bangers", cursive',
                'font_outfit': '"Outfit", sans-serif',
                'font_roboto': '"Roboto", sans-serif',
                'font_open_sans': '"Open Sans", sans-serif',
                'font_lato': '"Lato", sans-serif',
                'font_montserrat': '"Montserrat", sans-serif',
                'font_oswald': '"Oswald", sans-serif',
                'font_raleway': '"Raleway", sans-serif',
                'font_merriweather': '"Merriweather", serif',
                'font_dancing_script': '"Dancing Script", cursive',
                'font_lobster': '"Lobster", cursive',
                'font_caveat': '"Caveat", cursive',
                'font_permanent_marker': '"Permanent Marker", cursive',
                'font_righteous': '"Righteous", cursive',
                'font_creepster': '"Creepster", cursive',
                'font_press_start_2p': '"Press Start 2P", cursive',
                'font_monoton': '"Monoton", cursive',
                'font_vt323': '"VT323", monospace',
                'font_bebas_neue': '"Bebas Neue", sans-serif',
                'font_anton': '"Anton", sans-serif',
                'font_josefin_sans': '"Josefin Sans", sans-serif',
                'font_comfortaa': '"Comfortaa", cursive',
                'font_shadows_into_light': '"Shadows Into Light", cursive',
                'font_amatic_sc': '"Amatic SC", cursive',
                'font_indie_flower': '"Indie Flower", cursive',
                'font_kalam': '"Kalam", cursive',
                'font_satisfy': '"Satisfy", cursive',
                'font_courgette': '"Courgette", cursive',
                'font_great_vibes': '"Great Vibes", cursive',
                'font_bungee': '"Bungee", cursive',
                'font_alfa_slab_one': '"Alfa Slab One", cursive',
                'font_abril_fatface': '"Abril Fatface", cursive'
            };

            const fontFamily = fontMap[activeFontId] || 'inherit';
            document.documentElement.style.setProperty('--app-font-family', fontFamily);
        } else {
            document.documentElement.style.setProperty('--app-font-family', 'inherit');
        }
    }, [currentUser, usersData]);

    const handleJoinVoice = (communityId: string, channelId: string) => {
        if (activeVoiceData) {
            console.log(`[Voice] Leaving ${activeVoiceData.communityId}/${activeVoiceData.channelId} to join ${communityId}/${channelId}`);
        }
        setVoiceIsMuted(false);
        setVoiceIsDeafened(false);
        setActiveVoiceData({ communityId, channelId });
    };

    const handleLeaveVoice = () => {
        setVoiceIsMuted(false);
        setVoiceIsDeafened(false);
        setActiveVoiceData(null);
    };

    useEffect(() => {
        if (page.type === 'community_detail' && (page.channelId === 'dm' || page.channelId === 'yipyap')) {
            if (!activeVoiceData || activeVoiceData.communityId !== page.query || activeVoiceData.channelId !== page.channelId) {
                console.log('[App] Auto-joining voice room from page navigation:', page.query);
                handleJoinVoice(page.query, page.channelId);
            }
        }
    }, [page, activeVoiceData]);

    const handleSharePost = (post: HydratedPost, slideIndex?: number) => {
        setPostToShare(post);
        setShareSlideIndex(slideIndex);
        setShareModalOpen(true);
        pushModalState('share');
    };

    const handleSendSharedPost = async (targets: ShareTarget[], selectedBgUrl?: string) => {
        if (!postToShare) return;

        // Mark post as shared by author if the author is sharing it
        const isAuthorShare = currentUser === postToShare.author;
        if (isAuthorShare && !postToShare.sharedByAuthor) {
            setPosts(prev => prev.map(p => p.id === postToShare.id ? { ...p, sharedByAuthor: true } : p));
            if (!isDemo) {
                updatePostOnServer(postToShare.id, { sharedByAuthor: true }).catch(console.error);
            }
        }

        // Increment shares on the post
        const recipientCount = targets.length;
        if (recipientCount > 0 && postToShare.author !== currentUser) {
            const newShareCount = (postToShare.shares || 0) + recipientCount;
            setPosts(prev => prev.map(p => p.id === postToShare.id ? { ...p, shares: newShareCount } : p));

            if (!isDemo) {
                updatePostOnServer(postToShare.id, { shares: newShareCount }).catch(console.error);
            }
        }

        const isProfileShare = (postToShare.type as string) === 'profile';
        const isFootprintShare = (postToShare.type as string) === 'footprint';
        const shareLink = isProfileShare
            ? `https://goodlyfe.net/profile/${encodeURIComponent(postToShare.author)}`
            : isFootprintShare
                ? (postToShare as any).content?.url || `https://goodlyfe.net/app?page=footprint&taskId=${postToShare.id}`
                : `https://goodlyfe.net/post/${postToShare.id}`;

        const isStandardPost = !isProfileShare && !isFootprintShare;
        const sharedPostIdArg = isStandardPost ? postToShare.id : undefined;
        const text = isStandardPost ? '' : (postToShare.content?.text || `Follow me on GoodLyfe! ${shareLink}`);

        let targetPage: any = null;
        let singleResolvedConvoId: number | null = null;

        // Send messages to all selected targets
        for (const target of targets) {
            if (target.type === 'channel') {
                const parts = target.id.split(':');
                const communityId = parts[1];
                const channelId = parts[2];
                if (communityId && channelId) {
                    try {
                        const media = selectedBgUrl ? [{ url: selectedBgUrl, type: 'image' as const }] : undefined;
                        await sendChannelMessage(communityId, channelId, currentUser || 'goodlyfe', text, media, undefined, sharedPostIdArg, selectedBgUrl);
                    } catch (e) {
                        console.error('Failed to send share to community channel:', e);
                    }
                }
            } else {
                let resolvedConvoId: number | null = null;
                if (target.type === 'yipyap') {
                    const convoId = Number(target.id.split(':')[1]);
                    resolvedConvoId = convoId;
                } else if (target.type === 'dm') {
                    const userId = target.id.split(':')[1];
                    const existing = conversations.find(c => !c.isYipYap && c.participants.includes(userId));
                    if (existing) {
                        resolvedConvoId = existing.id;
                    } else {
                        try {
                            if (isDemo) {
                                resolvedConvoId = Date.now();
                                const newConvo: Conversation = {
                                    id: resolvedConvoId,
                                    participants: [currentUser || '', userId],
                                    messages: []
                                };
                                setConversations(prev => [newConvo, ...prev]);
                            } else {
                                const conversation = await startConversationOnServer([currentUser || '', userId]);
                                setConversations(prev => {
                                    const exists = prev.find(c => c.id === conversation.id);
                                    if (exists) return prev.map(c => c.id === conversation.id ? conversation : c);
                                    return [conversation, ...prev];
                                });
                                resolvedConvoId = conversation.id;
                            }
                        } catch (err) {
                            console.error('Failed to start conversation for share:', err);
                        }
                    }
                }

                if (resolvedConvoId !== null) {
                    singleResolvedConvoId = resolvedConvoId;
                    handleSendMessage(resolvedConvoId, text, undefined, undefined, false, undefined, undefined, sharedPostIdArg, selectedBgUrl);
                }
            }
        }

        // Determine redirect page & toast notifications if exactly 1 target selected
        if (targets.length === 1) {
            const target = targets[0];
            if (target.type === 'channel') {
                const parts = target.id.split(':');
                const communityId = parts[1];
                const channelId = parts[2];
                targetPage = {
                    type: 'community_detail' as const,
                    query: communityId,
                    channelId: channelId || 'general'
                };
            } else if (target.type === 'yipyap') {
                const convoId = Number(target.id.split(':')[1]);
                targetPage = { type: 'messages' as const, tab: 'yipyap' as const };
                setActiveConversationId(convoId);
            } else if (target.type === 'dm') {
                const userId = target.id.split(':')[1];
                targetPage = { type: 'messages' as const, username: userId, tab: 'dm' as const };
                if (singleResolvedConvoId !== null) {
                    setActiveConversationId(singleResolvedConvoId);
                }
            }
        }

        setShareModalOpen(false);
        if (targetPage) {
            if (window.history.state?.modal === 'share') {
                const targetUrl = pageToUrl(targetPage);
                window.history.replaceState({ type: targetPage.type }, '', targetUrl);
            }
            setPage(targetPage);
        } else {
            if (window.history.state?.modal === 'share') {
                skipNextPopStateRef.current = true;
                window.history.back();
            }
        }

        // Create Toast feedback
        if (targets.length > 0) {
            const names = targets.map(t => t.name);
            let toastText = '';
            if (names.length <= 3) {
                toastText = `Shared with ${names.join(', ')}`;
            } else {
                toastText = `Shared with ${names.slice(0, 3).join(', ')} + others.`;
            }

            setActiveLiveNotification({
                id: Date.now(),
                type: 'system',
                fromUser: 'goodlyfe',
                message: toastText,
                timestamp: new Date().toISOString(),
                read: false
            });
        }
    };

    // --- Conversation selection for /messages/:username ---
    React.useEffect(() => {
        if (page.type === 'messages' && page.username) {
            // Find or start conversation
            if (page.username && page.username !== currentUser) {
                const targetLower = page.username.toLowerCase();
                const convo = conversations.find(c =>
                    !c.isYipYap &&
                    c.participants.some(p => p?.toLowerCase() === targetLower) &&
                    c.participants.includes(currentUser || '')
                );
                if (convo) {
                    if (activeConversationId !== convo.id) {
                        setActiveConversationId(convo.id);
                    }
                    const hasUnread = (convo.messages || []).some(m => m.senderId !== (currentUser || '') && !m.isRead);
                    if (hasUnread) {
                        // Mark as read on refresh/URL arrival
                        markConversationAsReadOnServer(convo.id, currentUser || '').catch(err => {
                            console.error('Failed to auto-mark conversation as read:', err);
                        });
                        // Optimistic local update
                        setConversations(prev => prev.map(c =>
                            c.id === convo.id
                                ? { ...c, messages: (c.messages || []).map(m => m.senderId !== (currentUser || '') ? { ...m, isRead: true } : m) }
                                : c
                        ));
                    }
                }
                else handleStartConversation(page.username);
            }
        }
    }, [page, conversations, currentUser, activeConversationId]);

    // Refresh conversations/chat on entry into the conversation
    useEffect(() => {
        if (!currentUser || activeConversationId === null) return;

        const refreshCurrentConversation = async () => {
            try {
                const serverConvos = await fetchConversations(currentUser);
                if (serverConvos && Array.isArray(serverConvos)) {
                    const uniqueConvos = new Map<string, Conversation>();
                    serverConvos.forEach((c: Conversation) => {
                        const key = c.isYipYap ? `yip-${c.id}` : [...c.participants].sort().join(':');
                        if (uniqueConvos.has(key)) {
                            const existing = uniqueConvos.get(key)!;
                            if ((c.messages || []).length > (existing.messages || []).length) {
                                uniqueConvos.set(key, c);
                            } else if ((c.messages || []).length === (existing.messages || []).length) {
                                if (c.id > existing.id) {
                                    uniqueConvos.set(key, c);
                                }
                            }
                        } else {
                            uniqueConvos.set(key, c);
                        }
                    });
                    const dedupedConvos = Array.from(uniqueConvos.values());
                    setConversations(dedupedConvos);
                    console.log(`[Messages] Successfully refreshed conversation list on entering conversation: ${activeConversationId}`);
                }
            } catch (err) {
                console.error("Failed to refresh conversation on entry:", err);
            }
        };

        refreshCurrentConversation();
    }, [activeConversationId, currentUser]);

    // Define handleStartForward for message forwarding
    const handleStartForward = (message: Message) => {
        setPendingForward(message);
        setPage({ type: 'messages' });
    };
    // Add missing handlers for reactions, message deletion, and forwarding
    // Add missing handlers for reactions, message deletion, and forwarding
    const handleMessageReaction = (conversationId: number, messageId: number, emoji: string) => {
        setConversations(prev => {
            const updatedConversations = prev.map(c => {
                if (c.id !== conversationId) return c;
                return {
                    ...c,
                    messages: (c.messages || []).map(m => {
                        if (m.id !== messageId) return m;
                        let reactions = { ...(m.reactions || {}) } as Record<string, string[]>;
                        // Remove user from all emoji arrays (clear previous reaction)
                        Object.keys(reactions).forEach(e => {
                            reactions[e] = reactions[e].filter(u => u !== currentUser);
                            if (reactions[e].length === 0) delete reactions[e];
                        });

                        // Check if user already had this emoji; if so, toggle it off (already removed above)
                        // Otherwise, add the user to the selected emoji
                        const hadEmoji = m.reactions?.[emoji]?.includes(currentUser) ?? false;
                        if (!hadEmoji) {
                            if (!reactions[emoji]) reactions[emoji] = [];
                            reactions[emoji] = [...reactions[emoji], currentUser];
                        }

                        return { ...m, reactions };
                    })
                };
            });
            // Atomic update on server - Disabled in Demo
            if (!isDemo) {
                recentlyReactedMessageIds.set(messageId, Date.now());
                reactToMessageOnServer(conversationId, messageId, emoji, currentUser).catch(err => {
                    console.error('Failed to update reaction on server:', err);
                });
            }
            return updatedConversations;
        });
    };

    const handleDeleteMessage = (conversationId: number, messageId: number) => {
        // Optimistic update locally
        setConversations(prev => {
            return prev.map(c => {
                if (c.id !== conversationId) return c;

                const messageToDelete = (c.messages || []).find(m => m.id === messageId);
                if (!messageToDelete) return c;

                const isSender = currentUser && messageToDelete.senderId === currentUser;

                if (isSender) {
                    // Delete associated media from S3 (side effect)
                    if (messageToDelete.media) {
                        messageToDelete.media.forEach(m => {
                            if (m.url) deleteMediaFile(m.url);
                        });
                    }
                    return {
                        ...c,
                        messages: (c.messages || []).filter(m => m.id !== messageId)
                    };
                } else {
                    // Just hide it locally
                    return {
                        ...c,
                        messages: (c.messages || []).map(m => {
                            if (m.id !== messageId) return m;
                            const hiddenBy = m.hiddenBy || [];
                            return {
                                ...m,
                                hiddenBy: hiddenBy.includes(currentUser || '') ? hiddenBy : [...hiddenBy, currentUser || '']
                            };
                        })
                    };
                }
            });
        });

        // Atomic update on server - Disabled in Demo
        if (!isDemo && currentUser) {
            locallyDeletedMessageIds.add(messageId);
            deleteMessageFromConversationOnServer(conversationId, messageId, currentUser).catch(err => {
                console.error('Failed to delete/hide message on server:', err);
            });
        }
    };

    const handleForwardMessage = (conversationId: number, messageId: number) => {
        const message = conversations.find(c => c.id === conversationId)?.messages.find(m => m.id === messageId);
        if (message) handleStartForward(message);
    };

    const userConversations = useMemo(() =>
        conversations.filter(c => {
            if (c.deletedBy && c.deletedBy.includes(currentUser || '')) return false;

            if (c.isYipYap) {
                // For YipYaps, we only block if the owner is blocked by current user
                // or if current user is blocked by the owner.
                const owner = c.yipYapOwner || '';
                const isBlocked = (usersData[currentUser]?.blockedUsers || []).includes(owner) ||
                    (usersData[owner]?.blockedUsers || []).includes(currentUser);
                if (isBlocked) return false;

                // Otherwise, if the server sent it, we should show it (it handles public/group visibility)
                return true;
            }

            if (!c.participants.includes(currentUser)) return false;
            // Filter conversations with blocked users
            const otherParticipant = c.participants.find(p => p !== currentUser);
            if (otherParticipant) {
                const isBlocked = (usersData[currentUser]?.blockedUsers || []).includes(otherParticipant) ||
                    (usersData[otherParticipant]?.blockedUsers || []).includes(currentUser);
                if (isBlocked) return false;
            }
            return true;
        }),
        [conversations, currentUser, usersData]
    );

    const hasUnreadMessages = useMemo(() => {
        // Sort conversations by last message timestamp (most recent first)
        const sorted = [...userConversations].sort((a, b) => {
            const lastA = a.messages[a.messages.length - 1];
            const lastB = b.messages[b.messages.length - 1];
            if (!lastA) return 1;
            if (!lastB) return -1;
            return new Date(lastB.timestamp).getTime() - new Date(lastA.timestamp).getTime();
        }).slice(0, 5);

        // Check if any of these 5 have unread messages from someone else
        return sorted.some(c =>
            c.messages.some(m => !m.isRead && m.senderId !== currentUser)
        );
    }, [userConversations, currentUser]);

    // Calculate unread notifications
    const hasUnreadNotifications = useMemo(() => {
        if (!currentUser) return false;
        return (usersData[currentUser]?.notifications || []).some((n: any) => !n.read);
    }, [usersData, currentUser]);

    const handleMarkNotificationAsRead = useCallback((notificationId: number) => {
        if (!updateUserData || !currentUser) return;

        updateUserData((prev: Record<string, UserData>) => {
            const userData = prev[currentUser];
            if (!userData) return prev;

            const updatedNotifications = (userData.notifications || []).map(n =>
                n.id === notificationId ? { ...n, read: true } : n
            );

            // Sync with server - Disabled in Demo
            if (!isDemo) {
                updateUserOnServer(currentUser, { notifications: updatedNotifications }).catch(console.error);
            }

            return {
                ...prev,
                [currentUser]: {
                    ...userData,
                    notifications: updatedNotifications
                }
            };
        });
    }, [currentUser, updateUserData]);


    // Background Logic

    // Cleanup body styles on mount to remove any residual styles
    useEffect(() => {
        document.body.style.backgroundImage = '';
        document.body.style.backgroundColor = '';

        // Prevent context menu (copy/save) on media
        const handleContextMenu = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target && (
                target.tagName === 'IMG' ||
                target.tagName === 'VIDEO' ||
                target.closest('img') ||
                target.closest('video')
            )) {
                e.preventDefault();
            }
        };

        document.addEventListener('contextmenu', handleContextMenu);

        // Prevent dragging of images (native behavior)
        const handleDragStart = (e: DragEvent) => {
            const target = e.target as HTMLElement;
            if (target && (target.tagName === 'IMG' || target.closest('img'))) {
                e.preventDefault();
            }
        };

        document.addEventListener('dragstart', handleDragStart);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('dragstart', handleDragStart);
        };
    }, []);

    // Set daily achievements from the top of the sorted achievements list
    useEffect(() => {
        if (achievements.length > 0) {
            const cycleDate = getFootprintCycleDate();
            const currentDaily = achievements[0];

            if (dailyAchievements.date !== cycleDate || dailyAchievements.daily?.id !== currentDaily.id) {
                const prevDailyId = dailyAchievements.daily?.id;

                // If the daily task changed, automatically archive the previous one
                if (prevDailyId && prevDailyId !== currentDaily.id) {
                    const prevAch = achievements.find(a => a.id === prevDailyId);
                    if (prevAch && !prevAch.isArchived) {
                        const updatedAch = { ...prevAch, isArchived: true };
                        // Trigger update after render cycle
                        setTimeout(() => {
                            setAchievements(prev => prev.map(a => a.id === updatedAch.id ? updatedAch : a));
                            if (!isDemo) {
                                import('./api').then(({ updateAchievementOnServer }) => {
                                    updateAchievementOnServer(updatedAch.id, updatedAch).catch(console.error);
                                });
                            }
                        }, 0);
                    }
                }

                setDailyAchievements({ date: cycleDate, daily: currentDaily });
            }
        }
    }, [achievements, dailyAchievements.date, dailyAchievements.daily?.id, setDailyAchievements, isDemo]);

    const parseHashtags = (text: string): string[] => {
        if (!text) return [];
        const hashtagRegex = /#([a-zA-Z0-9_]+)/g;
        const matches = text.match(hashtagRegex);
        if (!matches) return [];
        return matches.map(tag => tag.substring(1));
    };



    // Make posts globally available for the search modal local search (small helper for this demo)
    useEffect(() => {
        (window as any).__APP_POSTS__ = posts;
        return () => { (window as any).__APP_POSTS__ = undefined; };
    }, [posts]);

    // Rehydrate media URLs for posts that reference IndexedDB blobs or uploaded records
    useEffect(() => {
        (async () => {
            // load all stored image records once
            const records = await getAllImages();
            if (!records || records.length === 0) return;

            const idToRec = new Map<number, any>();
            records.forEach((r: any) => idToRec.set(Number(r.id), r));

            let updated = false;
            const newPosts = posts.map(p => {
                const media = p.content.media?.map((m: any) => {
                    if (m.dbId && idToRec.has(Number(m.dbId))) {
                        const rec = idToRec.get(Number(m.dbId));
                        // prefer uploadedUrl if server already returned a hosted URL
                        if (rec.uploaded && rec.uploadedUrl) {
                            if (m.url !== rec.uploadedUrl) { updated = true; return { ...m, url: rec.uploadedUrl }; }
                            return m;
                        }
                        // if we have blob data, create objectURL
                        if (rec.data instanceof Blob) {
                            try {
                                const blobUrl = URL.createObjectURL(rec.data);
                                createdObjectUrls.add(blobUrl);
                                if (m.url !== blobUrl) { updated = true; return { ...m, url: blobUrl }; }
                                return m;
                            } catch (e) { return m; }
                        }
                        // fallback to stored string (data URL)
                        if (typeof rec.data === 'string' && m.url !== rec.data) { updated = true; return { ...m, url: rec.data }; }
                    }
                    return m;
                });
                return updated ? { ...p, content: { ...p.content, media } } : p;
            });

            if (updated) {
                setPosts(newPosts);
            }
        })();
    }, []);

    // One-time best-effort migration to repair posts with broken media URLs
    useEffect(() => {
        const migrationKey = 'posts_media_migration_v1';
        const runMigrationOnce = async () => {
            try {
                if (localStorage.getItem(migrationKey)) return;
                console.info('Starting posts media migration attempt...');

                const records = await getAllImages();
                if (!records || records.length === 0) {
                    console.info('No indexedDB media records found, skipping migration.');
                    localStorage.setItem(migrationKey, '0');
                    return;
                }

                const idToRec = new Map<number, any>();
                records.forEach((r: any) => idToRec.set(Number(r.id), r));

                // helper: check if a URL is reachable (best-effort)
                const isReachable = async (url: string) => {
                    if (!url) return false;
                    // data: and blob: URLs are considered non-reachable across sessions
                    if (url.startsWith('blob:') || url.startsWith('data:')) return false;
                    try {
                        const resp = await fetch(url, { method: 'HEAD' });
                        return resp.ok;
                    } catch (e) {
                        return false;
                    }
                };

                // Find posts with at least one likely-broken media URL
                const brokenCandidates: { postIndex: number; mediaIndex: number; post: Post; media: any }[] = [];
                for (let i = 0; i < posts.length; i++) {
                    const p = posts[i];
                    if (!p.content.media) continue;
                    for (let j = 0; j < p.content.media.length; j++) {
                        const m = (p.content.media as any)[j];
                        const url = m.url || '';
                        // treat blob/data as broken after reload, and also check external URLs for reachability
                        let broken = false;
                        if (!url) broken = true;
                        else if (url.startsWith('blob:') || url.startsWith('data:')) broken = true;
                        else {
                            // try quick HEAD fetch
                            // don't block for too long
                            try {
                                // attempt reachable check with timeout
                                const controller = new AbortController();
                                const timer = setTimeout(() => controller.abort(), 2000);
                                const resp = await fetch(url, { method: 'HEAD', signal: controller.signal });
                                clearTimeout(timer);
                                if (!resp.ok) broken = true;
                            } catch (e) {
                                broken = true;
                            }
                        }

                        if (broken) brokenCandidates.push({ postIndex: i, mediaIndex: j, post: p, media: m });
                    }
                }

                if (brokenCandidates.length === 0) {
                    console.info('No broken media detected.');
                    localStorage.setItem(migrationKey, '0');
                    return;
                }

                let repaired = 0;

                const newPosts = posts.map(p => ({ ...p, content: { ...p.content, media: p.content.media ? [...p.content.media] : [] } }));

                // For each candidate, attempt best-effort match
                for (const cand of brokenCandidates) {
                    const { postIndex, mediaIndex, media } = cand;
                    // prefer records that already have uploadedUrl (hosted)
                    let matchedRec: any = null;
                    for (const rec of records) {
                        if (rec.uploadedUrl) {
                            // use naive extension/type match
                            const ext = (rec.uploadedUrl || '').split('.').pop()?.toLowerCase() || '';
                            if ((media.type === 'image' && ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) ||
                                (media.type === 'video' && ['mp4', 'webm', 'mov'].includes(ext)) ||
                                ((media.type === 'audio' || media.type === 'voice') && ['mp3', 'wav', 'webm', 'm4a'].includes(ext))) {
                                matchedRec = rec; break;
                            }
                        }
                    }

                    // if no uploadedUrl match, try to match blobs by dimensions when possible
                    if (!matchedRec) {
                        for (const rec of records) {
                            if (!(rec.data instanceof Blob)) continue;
                            try {
                                const tempUrl = URL.createObjectURL(rec.data);
                                // for images/videos try to compare width/height if present on media
                                if (media.type === 'image' && (media.width || media.height)) {
                                    const img = new Image();
                                    const dimsMatch = await new Promise<boolean>((resolve) => {
                                        img.onload = () => {
                                            const w = img.naturalWidth, h = img.naturalHeight;
                                            URL.revokeObjectURL(tempUrl);
                                            const match = (!!media.width && !!media.height) ? (media.width === w && media.height === h) : false;
                                            resolve(match);
                                        };
                                        img.onerror = () => { URL.revokeObjectURL(tempUrl); resolve(false); };
                                        img.src = tempUrl;
                                    });
                                    if (dimsMatch) { matchedRec = rec; break; }
                                } else {
                                    // for audio/video or if media lacks dims, skip expensive checks
                                    URL.revokeObjectURL(tempUrl);
                                }
                            } catch (e) {
                                // ignore
                            }
                        }
                    }

                    // last-resort: pick first record whose blob type loosely matches
                    if (!matchedRec) {
                        for (const rec of records) {
                            if (rec.data instanceof Blob) {
                                const t = rec.data.type || '';
                                if (media.type === 'image' && t.startsWith('image/')) { matchedRec = rec; break; }
                                if (media.type === 'video' && t.startsWith('video/')) { matchedRec = rec; break; }
                                if ((media.type === 'audio' || media.type === 'voice') && t.startsWith('audio/')) { matchedRec = rec; break; }
                            }
                        }
                    }

                    if (matchedRec) {
                        // apply the match
                        const target = newPosts[postIndex];
                        const recId = Number(matchedRec.id);
                        if (matchedRec.uploaded && matchedRec.uploadedUrl) {
                            (target.content.media as any)[mediaIndex] = { ...media, url: matchedRec.uploadedUrl, dbId: recId };
                        } else if (matchedRec.data instanceof Blob) {
                            try {
                                const newUrl = URL.createObjectURL(matchedRec.data);
                                createdObjectUrls.add(newUrl);
                                (target.content.media as any)[mediaIndex] = { ...media, url: newUrl, dbId: recId };
                            } catch (e) {
                                // fallback to uploadedUrl if available
                                if (matchedRec.uploadedUrl) {
                                    (target.content.media as any)[mediaIndex] = { ...media, url: matchedRec.uploadedUrl, dbId: recId };
                                }
                            }
                        }
                        repaired++;
                    }
                }

                if (repaired > 0) {
                    setPosts(newPosts);
                    alert(`Migration attempt complete — repaired ${repaired} media items. Check the feed and IndexedDB entries.`);
                    console.info(`Posts media migration repaired ${repaired} items.`);
                } else {
                    alert('Migration attempt complete — no matches found to repair broken media.');
                    console.info('Posts media migration found no matches.');
                }

                localStorage.setItem(migrationKey, '1');
            } catch (err) {
                console.error('Posts media migration failed', err);
                localStorage.setItem(migrationKey, '0');
            }
        };

        // Run migration in background without blocking initial render
        runMigrationOnce();
    }, [posts]);

    // Sweeper effect to revoke object URLs that are no longer actively referenced in posts, preventing severe memory leaks and WKWebView OOM crashes
    useEffect(() => {
        const activeBlobUrls = new Set<string>();
        posts.forEach(p => {
            p.content.media?.forEach((m: any) => {
                if (m.url && m.url.startsWith('blob:')) {
                    activeBlobUrls.add(m.url);
                }
            });
            if (p.content.coverImageUrl && p.content.coverImageUrl.startsWith('blob:')) {
                activeBlobUrls.add(p.content.coverImageUrl);
            }
            const hp = p as any;
            if (hp.originalPost?.content?.media) {
                hp.originalPost.content.media.forEach((m: any) => {
                    if (m.url && m.url.startsWith('blob:')) {
                        activeBlobUrls.add(m.url);
                    }
                });
            }
        });

        createdObjectUrls.forEach(url => {
            if (!activeBlobUrls.has(url)) {
                try {
                    URL.revokeObjectURL(url);
                    createdObjectUrls.delete(url);
                } catch (e) {
                    console.warn('Failed to revoke object URL:', url, e);
                }
            }
        });
    }, [posts]);

    // Revoke all remaining object URLs on final unmount
    useEffect(() => {
        return () => {
            createdObjectUrls.forEach(url => {
                try {
                    URL.revokeObjectURL(url);
                } catch (e) { }
            });
            createdObjectUrls.clear();
        };
    }, []);

    const handleMuteUser = (rawUserId: string) => {
        const userId = getUserKeyCaseInsensitive(rawUserId) || rawUserId;
        if (!currentUser || !updateUserData) return;

        const currentUserData = usersData[currentUser];
        const isMuted = currentUserData?.mutedUsers?.includes(userId);

        const message = isMuted
            ? `Unmute ${userId}?`
            : `Mute ${userId}? You won't see their posts.`;

        if (!window.confirm(message)) return;

        updateUserData((prev: Record<string, UserData>) => {
            const userData = prev[currentUser];
            if (!userData) return prev;
            let mutedUsers = [...(userData.mutedUsers || [])];
            if (mutedUsers.includes(userId)) {
                mutedUsers = mutedUsers.filter(id => id !== userId);
            } else {
                mutedUsers.push(userId);
            }
            const updated = { ...userData, mutedUsers };
            // Sync with Server - Disabled in Demo
            if (!isDemo) {
                updateUserOnServer(currentUser, { mutedUsers }).catch(console.error);
            }
            return { ...prev, [currentUser]: updated };
        });
    };

    const handleBlockUser = (rawUserId: string) => {
        const userId = getUserKeyCaseInsensitive(rawUserId) || rawUserId;
        if (!currentUser || !updateUserData) return;

        const isCurrentlyBlocked = usersData[currentUser]?.blockedUsers?.includes(userId);

        // Confirm block
        if (!isCurrentlyBlocked && !window.confirm(`Are you sure you want to block ${userId}? They won't be able to see your posts and you won't see theirs.`)) return;

        updateUserData((prevData: Record<string, UserData>) => {
            const newUsersData = JSON.parse(JSON.stringify(prevData));
            const userData = newUsersData[currentUser];
            const targetUserData = newUsersData[userId];
            if (!userData) return prevData;

            // 1. Toggle blocked list
            let blockedUsers = [...(userData.blockedUsers || [])];
            if (blockedUsers.includes(userId)) {
                blockedUsers = blockedUsers.filter(id => id !== userId);
            } else {
                blockedUsers.push(userId);
                // 2. Bidirectional unfollow (only on block, not unblock)
                // Remove target from current user's lists
                userData.following = (userData.following || []).filter((id: string) => id !== userId);
                userData.followers = (userData.followers || []).filter((id: string) => id !== userId);

                // Remove current user from target's lists
                if (targetUserData) {
                    targetUserData.following = (targetUserData.following || []).filter((id: string) => id !== currentUser);
                    targetUserData.followers = (targetUserData.followers || []).filter((id: string) => id !== currentUser);
                }
            }

            userData.blockedUsers = blockedUsers;
            const updated = { ...userData };

            // Sync with Server - Disabled in Demo
            if (!isDemo) {
                updateUserOnServer(currentUser, { blockedUsers }).catch(console.error);
                if (targetUserData) updateUserOnServer(userId, { following: targetUserData.following, followers: targetUserData.followers }).catch(console.error);
            }
            return { ...newUsersData, [currentUser]: updated };
        });
    };

    const handleRemoveFollower = (rawUserId: string) => {
        const userId = getUserKeyCaseInsensitive(rawUserId) || rawUserId;
        if (!currentUser || !updateUserData) return;

        if (!window.confirm(`Remove ${userId} as a follower?`)) return;

        updateUserData((prevData: Record<string, UserData>) => {
            const newData = JSON.parse(JSON.stringify(prevData));
            const userData = newData[currentUser];
            const targetData = newData[userId];

            if (userData?.followers) {
                userData.followers = userData.followers.filter((id: string) => id !== userId);
            }
            if (targetData?.following) {
                targetData.following = targetData.following.filter((id: string) => id !== currentUser);
            }

            if (!isDemo) {
                updateUserOnServer(currentUser, { followers: userData.followers }).catch(console.error);
                if (targetData) updateUserOnServer(userId, { following: targetData.following }).catch(console.error);
            }
            return newData;
        });
    };

    useEffect(() => {
        const onNavigateToPost = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            const postId = detail?.postId;
            if (postId) {
                const p = posts.find(x => x.id === postId);
                if (p) {
                    setPreviousPage(page); // Important for back navigation background
                    setPage({ type: 'post', postId: postId.toString() });
                    setSelectedPost(getHydratedPosts([p])[0]);
                    setIsModalVisible(true);
                }
            }
        };
        const onNavigateToHashtag = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            const tag = detail?.tag;
            if (tag) {
                setPage({ type: 'hashtag', tag });
            }
        };
        const onNavigateToFootprint = (e: CustomEvent<{ taskId?: number }>) => {
            const { taskId } = e.detail;
            setPage({ type: 'mod', modId: 'vibeman1_footprint', params: taskId ? { taskId } : undefined });
        };

        const onAcceptFriendRequest = (e: CustomEvent<{ targetUser: string, requester: string }>) => {
            const { targetUser, requester } = e.detail;
            updateUserData((prevData: Record<string, UserData>) => {
                const newData = JSON.parse(JSON.stringify(prevData));
                const targetData = newData[targetUser];
                const requesterData = newData[requester];

                if (!targetData || !requesterData) return prevData;

                // Remove from friendRequests
                if (targetData.friendRequests) {
                    targetData.friendRequests = targetData.friendRequests.filter((id: string) => id !== requester);
                }

                // Convert the friend_request notification into a friend notification
                if (targetData.notifications) {
                    targetData.notifications = targetData.notifications.map((n: any) => {
                        if (n.type === 'friend_request' && n.fromUser === requester) {
                            return { ...n, type: 'friend' as NotificationType };
                        }
                        return n;
                    });
                }

                // Add to both users' friends lists
                if (!targetData.friends) targetData.friends = [];
                if (!requesterData.friends) requesterData.friends = [];

                if (!targetData.friends.includes(requester)) {
                    targetData.friends.push(requester);
                }
                if (!requesterData.friends.includes(targetUser)) {
                    requesterData.friends.push(targetUser);
                }

                // Remove from followers/following (they are now full friends)
                targetData.followers = (targetData.followers || []).filter((id: string) => id !== requester);
                requesterData.following = (requesterData.following || []).filter((id: string) => id !== targetUser);

                // Send a friend_accepted notification to requester
                requesterData.notifications = requesterData.notifications || [];
                requesterData.notifications.push({
                    id: Date.now(),
                    type: 'friend_accepted' as NotificationType,
                    fromUser: targetUser,
                    timestamp: new Date().toISOString(),
                    read: false,
                });

                if (!isDemo) {
                    updateUserOnServer(targetUser, {
                        friends: targetData.friends,
                        friendRequests: targetData.friendRequests,
                        followers: targetData.followers,
                        notifications: targetData.notifications
                    }).catch(console.error);
                    updateUserOnServer(requester, {
                        friends: requesterData.friends,
                        following: requesterData.following,
                        notifications: requesterData.notifications
                    }).catch(console.error);
                }

                return newData;
            });
        };

        // Legacy alias
        const onAcceptFollowRequest = onAcceptFriendRequest;

        const onRejectFriendRequest = (e: CustomEvent<{ targetUser: string, requester: string }>) => {
            const { targetUser, requester } = e.detail;
            updateUserData((prevData: Record<string, UserData>) => {
                const newData = JSON.parse(JSON.stringify(prevData));
                const targetData = newData[targetUser];
                const requesterData = newData[requester];

                if (!targetData) return prevData;

                if (targetData.friendRequests) {
                    targetData.friendRequests = targetData.friendRequests.filter((id: string) => id !== requester);
                }

                // Remove any pending follow if allowFollowers was on
                if (targetData.followers) {
                    targetData.followers = targetData.followers.filter((id: string) => id !== requester);
                }
                if (requesterData?.following) {
                    requesterData.following = requesterData.following.filter((id: string) => id !== targetUser);
                }

                // Clear the friend_request notification
                if (targetData.notifications) {
                    targetData.notifications = targetData.notifications.filter((n: any) =>
                        !(n.type === 'friend_request' && n.fromUser === requester)
                    );
                }

                if (!isDemo) {
                    updateUserOnServer(targetUser, {
                        friendRequests: targetData.friendRequests,
                        followers: targetData.followers,
                        notifications: targetData.notifications
                    }).catch(console.error);
                    if (requesterData) {
                        updateUserOnServer(requester, { following: requesterData.following }).catch(console.error);
                    }
                }
                return newData;
            });
        };

        // Legacy alias
        const onRejectFollowRequest = onRejectFriendRequest;


        const onOpenFollowerGroups = (e: CustomEvent<{ targetUser?: string }>) => {
            setFollowerGroupsTargetUser(e.detail?.targetUser);
            setIsFollowerGroupsModalOpen(true);
        };

        const onOpenLinkModal = async () => {
            // Close create post modal so both aren't open at the same time
            setIsGlobalPostModalOpen(false);
            setGlobalPostMedia(undefined);
            try {
                let permission = await Geolocation.checkPermissions();
                if (permission.location === 'prompt') {
                    await Geolocation.requestPermissions().catch(() => { });
                }
            } catch (e) {
                console.error('Permission check failed, proceeding anyway', e);
            }
            // Always open the modal so users can interact with it;
            // if location is strictly denied, it will throw an error only when they actually press "Send".
            setIsLinkModalOpen(true);
        };

        const onUpdatePostContent = (e: CustomEvent<{ postId: number; content: any }>) => {
            const { postId, content } = e.detail;
            const idToUpdate = Number(postId);

            // Persist to server using current posts closure
            if (!isDemo) {
                const targetPost = posts.find(p => p.id === idToUpdate);
                if (targetPost) {
                    const updatedContent = { ...targetPost.content, ...content };
                    updatePostOnServer(idToUpdate, { content: updatedContent }).catch(console.error);
                }
            }

            // Update local state
            setPosts(prevPosts => prevPosts.map(p => {
                if (p.id === idToUpdate) {
                    return { ...p, content: { ...p.content, ...content } };
                }
                return p;
            }));

            if (selectedPost && Number(selectedPost.id) === idToUpdate) {
                setSelectedPost(prev => prev ? { ...prev, content: { ...prev.content, ...content } } : null);
            }
        };

        const onUpdatePostShape = (e: CustomEvent<{ postId: number; postcardShape: 'square' | 'wide' | 'tall' | 'wide_tall' }>) => {
            const { postId, postcardShape } = e.detail;
            const idToUpdate = Number(postId);

            // Persist to server using current posts closure
            if (!isDemo) {
                updatePostOnServer(idToUpdate, { postcardShape }).catch(console.error);
            }

            // Update local state
            setPosts(prevPosts => prevPosts.map(p => {
                if (p.id === idToUpdate) {
                    return { ...p, postcardShape };
                }
                return p;
            }));

            if (selectedPost && Number(selectedPost.id) === idToUpdate) {
                setSelectedPost(prev => prev ? { ...prev, postcardShape } : null);
            }
        };

        window.addEventListener('app.openFollowerGroups', onOpenFollowerGroups as EventListener);
        window.addEventListener('app.openLinkModal', onOpenLinkModal as EventListener);
        window.addEventListener('app.navigateToPost', onNavigateToPost as EventListener);
        window.addEventListener('app.navigateToHashtag', onNavigateToHashtag as EventListener);
        window.addEventListener('app.navigateToFootprint', onNavigateToFootprint as EventListener);
        window.addEventListener('app.acceptFollowRequest', onAcceptFollowRequest as EventListener);
        window.addEventListener('app.rejectFollowRequest', onRejectFollowRequest as EventListener);
        window.addEventListener('app.updatePostContent', onUpdatePostContent as EventListener);
        window.addEventListener('app.updatePostShape', onUpdatePostShape as EventListener);
        return () => {
            window.removeEventListener('app.openFollowerGroups', onOpenFollowerGroups as EventListener);
            window.removeEventListener('app.openLinkModal', onOpenLinkModal as EventListener);
            window.removeEventListener('app.navigateToPost', onNavigateToPost as EventListener);
            window.removeEventListener('app.navigateToHashtag', onNavigateToHashtag as EventListener);
            window.removeEventListener('app.navigateToFootprint', onNavigateToFootprint as EventListener);
            window.removeEventListener('app.acceptFollowRequest', onAcceptFollowRequest as EventListener);
            window.removeEventListener('app.rejectFollowRequest', onRejectFollowRequest as EventListener);
            window.removeEventListener('app.updatePostContent', onUpdatePostContent as EventListener);
            window.removeEventListener('app.updatePostShape', onUpdatePostShape as EventListener);
        };
    }, [posts, selectedPost, isDemo]);

    // Ensure post modal opens when navigating directly to /post/:id via URL
    useEffect(() => {
        if (page.type === 'post' && !selectedPost) {
            const id = Number((page as any).postId);
            if (!Number.isNaN(id)) {
                const p = posts.find(x => x.id === id);
                if (p) {
                    setSelectedPost(getHydratedPosts([p])[0]);
                    setIsModalVisible(true);
                }
            }
        } else if (page.type !== 'post' && isModalVisible && !selectedPost?.linkRequestDetails?.isLinkRequest) {
            // If the page changed away from a post, close modal to keep state consistent
            setIsModalVisible(false);
            setTimeout(() => setSelectedPost(null), 10);
        }
    }, [page, posts, selectedPost, isModalVisible]);

    const [pendingShareContent, setPendingShareContent] = useState<{ text: string, media: MediaAttachment[], sourcePostId?: number } | null>(null);

    const getRecentDestinations = useCallback(() => {
        if (!currentUser) return [];

        const destinations: Array<{
            type: 'dm' | 'community';
            id: string; // userId for dm, communityId for community
            name: string;
            avatar?: string;
            lastSentTimestamp: number;
            channelId?: string; // only for community
        }> = [];

        // 1. Process DMs
        conversations.forEach(convo => {
            const lastMyMessage = [...(convo.messages || [])]
                .reverse()
                .find(m => m.senderId === currentUser);

            if (lastMyMessage) {
                const otherUserId = convo.participants.find(p => p !== currentUser);
                if (otherUserId) {
                    destinations.push({
                        type: 'dm',
                        id: otherUserId,
                        name: usersData[otherUserId]?.displayName || otherUserId,
                        avatar: usersData[otherUserId]?.pfp,
                        lastSentTimestamp: typeof lastMyMessage.timestamp === 'string' ? new Date(lastMyMessage.timestamp).getTime() : lastMyMessage.timestamp
                    });
                }
            }
        });

        // 2. Process Communities (from recently joined/interacted)
        // Note: For simplicity, we check communities the user is a member of 
        // and find the last message they sent in any channel.
        communities.filter((c: Community) => c.members?.includes(currentUser)).forEach((c: Community) => {
            // This is a bit expensive if done frequently, but okay for a small set
            // In a real app, we'd have a 'lastUserInteraction' field per community
            let lastInteraction = 0;
            let bestChannelId: string | undefined;

            // Check if we have channel messages cached/available (some might be missing if not visited)
            // For now, let's use a simpler heuristic: last community message sent by user
            // We'll iterate through all messages in all channels if they are loaded
            // (In this codebase, channel messages are often stored within community objects or fetched)

            // Fallback: If no messages found, use joined timestamp or just skip
            // Actually, the requirement is "most recently sent to chats"

            if (lastInteraction > 0) {
                destinations.push({
                    type: 'community',
                    id: c._id,
                    name: c.name,
                    avatar: c.avatar,
                    lastSentTimestamp: lastInteraction,
                    channelId: bestChannelId
                });
            }
        });

        return destinations
            .sort((a, b) => b.lastSentTimestamp - a.lastSentTimestamp)
            .slice(0, 3);
    }, [currentUser, conversations, communities, usersData]);

    const swipeContextRef = useRef<HydratedPost[] | null>(null);

    const handlePostClick = useCallback((post: HydratedPost, contextList?: HydratedPost[]) => {
        if (post.linkRequestDetails?.isLinkRequest) {
            setSelectedPost(post);
            setIsModalVisible(true);
            return;
        }
        if (contextList) {
            swipeContextRef.current = contextList;
        } else {
            swipeContextRef.current = null;
        }
        if (['home', 'profile', 'hashtag', 'footprint'].includes(page.type) || (page.type === 'mod' && page.modId === 'vibeman1_footprint')) {
            setFeedScrollPosition(window.scrollY);
        }
        setPreviousPage(page); // Save current page before opening post modal
        setPage({ type: 'post', postId: post.id.toString() });
        setSelectedPost(post);
        setIsModalVisible(true);
    }, [page, setFeedScrollPosition, setPreviousPage, setPage, setSelectedPost, setIsModalVisible]);

    const handleRespondLinkRequest = useCallback((postId: number | string, responseChoice: 'down' | 'cant_go') => {
        if (!currentUser) return;
        setSelectedPost(prev => {
            if (!prev || String(prev.id || '') !== String(postId || '')) return prev;
            const currentDetails = prev.linkRequestDetails || { isLinkRequest: true };
            const currentResponses = { ...(currentDetails.responses || {}) };
            if (currentResponses[currentUser] === responseChoice) {
                delete currentResponses[currentUser];
            } else {
                currentResponses[currentUser] = responseChoice;
            }
            return {
                ...prev,
                linkRequestDetails: {
                    ...currentDetails,
                    responses: currentResponses
                }
            };
        });
        setConversations(prevConvos => prevConvos.map(c => {
            const hasMsg = c.messages?.some(m => m && String(m.id || '') === String(postId || ''));
            if (!hasMsg) return c;
            const updatedMsgs = (c.messages || []).map(m => {
                if (!m || String(m.id || '') !== String(postId || '')) return m;
                const existingResponses = { ...(m.metadata?.responses || {}) };
                if (existingResponses[currentUser] === responseChoice) {
                    delete existingResponses[currentUser];
                } else {
                    existingResponses[currentUser] = responseChoice;
                }
                const updatedMeta = { ...m.metadata, responses: existingResponses };
                const updatedM = { ...m, metadata: updatedMeta };
                updateConversationOnServer(c.id, { messages: c.messages.map(x => (x && String(x.id || '') === String(m.id || '')) ? updatedM : x) }).catch(console.error);
                return updatedM;
            });
            return { ...c, messages: updatedMsgs };
        }));
    }, [currentUser]);

    const handleCreatePost = (postData: {
        type: PostType;
        content: Post['content'];
        visibility?: 'public' | 'followers' | 'groups' | 'private';
        visibilityGroups?: string[];
        showCaption?: boolean;
        showOnProfile?: boolean;
        textSize?: 'xs' | 'sm' | 'med' | 'lg';
        communityId?: string;
        themeOpacity?: number;
        themeColor?: string;
        textColor?: string;
        isOptimistic?: boolean;
        localId?: number;
        fontId?: string;
        postcardStyle?: string;
        postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall';
        isForSale?: boolean;
        price?: number;
        vendorId?: string;
        linkRequestDetails?: any;
        bzztDetails?: any;
    }, footprintDetails?: any) => {
        if (!currentUser) return null;

        const newPost: Post = {
            id: postData.localId || Date.now(),
            author: currentUser,
            type: postData.type,
            content: postData.content,
            visibility: postData.visibility || 'public',
            visibilityGroups: postData.visibilityGroups || [],
            showCaption: postData.showCaption ?? true,
            showOnProfile: postData.showOnProfile ?? true,
            textSize: postData.textSize || 'med',
            likes: 0,
            comments: [],
            vouches: [],
            bumps: 0,
            bookmarks: 0,
            shares: 0,
            createdAt: new Date().toISOString(),
            themeColor: postData.themeColor || usersData[currentUser]?.appThemeColor,
            themeOpacity: postData.themeOpacity,
            textColor: postData.textColor || usersData[currentUser]?.appTextColor,
            footprintDetails: footprintDetails || (postData as any).footprintDetails,
            bzztDetails: (postData as any).bzztDetails,
            communityId: postData.communityId,
            fontId: postData.fontId,
            postcardStyle: postData.postcardStyle || usersData[currentUser]?.postcardDefault || 'classic_postcards',
            postcardShape: postData.postcardShape,
            isForSale: postData.isForSale,
            price: postData.price,
            vendorId: postData.vendorId,
            linkRequestDetails: postData.linkRequestDetails
        } as any;

        if (postData.localId) {
            (newPost as any).localId = postData.localId;
        }

        setPosts(prevPosts => {
            const exists = postData.localId && prevPosts.some(p => (p as any).localId === postData.localId);
            if (exists) {
                return prevPosts.map(p => (p as any).localId === postData.localId ? newPost : p);
            }
            return [newPost, ...prevPosts];
        });

        // Record completed achievement locally and sync to server if created from GlobalPostModal / FAB
        const resolvedFootprintDetails = footprintDetails || (postData as any).footprintDetails;
        if (resolvedFootprintDetails?.achievementId) {
            const todayStr = getFootprintCycleDate();
            updateUserData((prevData: Record<string, UserData>) => {
                const user = { ...prevData[currentUser] };
                const exists = (user.completedAchievements || []).some((c: any) => String(c.achievementId) === String(resolvedFootprintDetails.achievementId));
                if (!exists) {
                    const newCompletion: CompletedAchievement = {
                        achievementId: resolvedFootprintDetails.achievementId,
                        date: todayStr,
                        postId: newPost.id,
                        status: 'pending'
                    };
                    user.completedAchievements = [...(user.completedAchievements || []), newCompletion];
                    updateUserOnServer(currentUser, { completedAchievements: user.completedAchievements }).catch(console.error);
                }
                return { ...prevData, [currentUser]: user };
            });
        }

        // Only sync to server IF it's not a pending optimistic media post (isOptimistic=true)
        if (!isDemo && !postData.isOptimistic) {
            createPostOnServer(newPost).then(serverPost => {
                if (serverPost && serverPost.id) {
                    setPosts(prev => prev.map(p => (p.id === newPost.id || (p as any).localId === newPost.id) ? serverPost : p));

                    if (footprintDetails) {
                        // After creating a footprint post, we need to update the locally added completedAchievement 
                        // to use the real serverPost.id instead of the temporary newPost.id.
                        // We use setTimeout to ensure this runs after handleCompleteAndPostAchievement has added it locally.
                        setTimeout(() => {
                            updateUserData((prevData: Record<string, UserData>) => {
                                const curData = prevData[currentUser];
                                if (!curData || !curData.completedAchievements) return prevData;

                                const updatedCompletions = curData.completedAchievements.map(c =>
                                    c.postId === newPost.id ? { ...c, postId: serverPost.id } : c
                                );

                                // Sync the updated completions to the server so it's not lost on reload
                                updateUserOnServer(currentUser, { completedAchievements: updatedCompletions }).catch(console.error);

                                return {
                                    ...prevData,
                                    [currentUser]: { ...curData, completedAchievements: updatedCompletions }
                                };
                            });
                        }, 100); // 100ms delay to allow handleCompleteAndPostAchievement to finish its synchronous local state update
                    }
                }
            }).catch(console.error);
        }

        return newPost;
    };

    // Called by UploadContext when a background media upload fails.
    // Marks the optimistic post as failed so the user can see it and delete it to retry.
    const handleMarkPostUploadFailed = (localId: number) => {
        setPosts(prev => {
            const updated = prev.map(p =>
                ((p as any).localId === localId || p.id === localId)
                    ? { ...p, uploadFailed: true }
                    : p
            );
            // Save the failed post to failedPosts state for localStorage persistence
            const failedP = updated.find(p => ((p as any).localId === localId || p.id === localId));
            if (failedP) {
                setFailedPosts(prevFailed => {
                    const exists = prevFailed.some(p => p.id === failedP.id);
                    if (exists) {
                        return prevFailed.map(p => p.id === failedP.id ? failedP : p);
                    }
                    return [failedP, ...prevFailed];
                });
            }
            return updated;
        });
        // If this was a footprint post, also revert the completedAchievements entry
        // so the task is unlocked and the user can re-attempt it.
        setPosts(prev => {
            const failedPost = prev.find(p => ((p as any).localId === localId || p.id === localId));
            if (failedPost?.footprintDetails && currentUser) {
                const achievementId = failedPost.footprintDetails.achievementId;
                updateUserData((prevData: Record<string, UserData>) => {
                    const user = { ...prevData[currentUser] };
                    user.completedAchievements = (user.completedAchievements || []).filter(
                        c => !(c.achievementId === achievementId && c.postId === failedPost.id)
                    );
                    updateUserOnServer(currentUser, { completedAchievements: user.completedAchievements }).catch(console.error);
                    return { ...prevData, [currentUser]: user };
                });
            }
            return prev; // no change to posts array here, just the side effect above
        });
    };

    // Retries uploading a failed post by grabbing stored file Blobs from IndexedDB
    const handleRetryPostUpload = async (localId: number, startUploadFn: any) => {
        if (!currentUser) return;
        const failedPost = failedPosts.find(p => ((p as any).localId === localId || p.id === localId)) || posts.find(p => ((p as any).localId === localId || p.id === localId));
        if (!failedPost) return;

        try {
            const { getPendingPostAttachments } = require('./indexedDb');
            const stored = await getPendingPostAttachments(Number(localId));
            if (!stored) {
                alert("Could not locate original attachments to retry. Please recreate the post.");
                return;
            }

            // Restore Files and build temp object URLs
            const attachments = stored.attachments.map((a: any) => {
                const url = URL.createObjectURL(a.file);
                return {
                    url,
                    type: a.type,
                    file: a.file,
                    isLive: a.isLive
                };
            });

            let coverImage = null;
            if (stored.coverImage && stored.coverImage.file) {
                coverImage = {
                    url: URL.createObjectURL(stored.coverImage.file),
                    file: stored.coverImage.file
                };
            }

            // Reset post status in feed and ensure it is in the posts array
            setPosts(prev => {
                const exists = prev.some(p => ((p as any).localId === localId || p.id === localId));
                if (exists) {
                    return prev.map(p =>
                        ((p as any).localId === localId || p.id === localId)
                            ? { ...p, uploadFailed: false, isOptimistic: true }
                            : p
                    );
                }
                const restoredPost = { ...failedPost, uploadFailed: false, isOptimistic: true };
                return [restoredPost, ...prev];
            });

            setFailedPosts(prev => prev.filter(p => !((p as any).localId === localId || p.id === localId)));

            // Re-award pending footprint completion if it is a footprint post
            if (failedPost.footprintDetails) {
                const achievementId = failedPost.footprintDetails.achievementId;
                const todayStr = getFootprintCycleDate();
                updateUserData((prevData: Record<string, UserData>) => {
                    const user = { ...prevData[currentUser] };
                    const exists = (user.completedAchievements || []).some(
                        c => c.achievementId === achievementId && c.postId === failedPost.id
                    );
                    if (!exists) {
                        const newCompletion: CompletedAchievement = {
                            achievementId,
                            date: todayStr,
                            postId: failedPost.id,
                            status: 'pending',
                        };
                        user.completedAchievements = [...(user.completedAchievements || []), newCompletion];
                        updateUserOnServer(currentUser, { completedAchievements: user.completedAchievements }).catch(console.error);
                    }
                    return { ...prevData, [currentUser]: user };
                });
            }

            // Fire upload retry
            const postData = {
                type: failedPost.type,
                content: {
                    text: failedPost.content.text,
                    media: attachments.map((a: any) => ({ url: a.url, type: a.type, width: a.width, height: a.height, isLive: a.isLive })),
                    coverImageUrl: coverImage?.url,
                    linkPreview: failedPost.content.linkPreview
                },
                visibility: failedPost.visibility,
                visibilityGroups: failedPost.visibilityGroups,
                showCaption: failedPost.showCaption,
                showOnProfile: failedPost.showOnProfile,
                themeOpacity: failedPost.themeOpacity,
                themeColor: failedPost.themeColor,
                textColor: failedPost.textColor,
                fontId: failedPost.fontId,
                postcardStyle: failedPost.postcardStyle,
                textSize: failedPost.textSize,
                postcardShape: failedPost.postcardShape,
                communityId: failedPost.communityId
            };

            await startUploadFn(
                currentUser,
                postData,
                attachments,
                coverImage,
                (finalData: any) => {
                    // Update final state upon complete
                    setPosts(prev => prev.map(p =>
                        ((p as any).localId === localId || p.id === localId)
                            ? { ...p, ...finalData, isOptimistic: false, uploadFailed: false }
                            : p
                    ));
                    // Sync finished post structure to backend database
                    if (!isDemo && !finalData.isOptimistic) {
                        createPostOnServer({ ...failedPost, ...finalData, isOptimistic: false, uploadFailed: false }).then(serverPost => {
                            if (serverPost && serverPost.id) {
                                setPosts(prev => prev.map(p => (p.id === failedPost.id || (p as any).localId === failedPost.id) ? serverPost : p));
                                // Update achievement postId reference if footprint
                                if (failedPost.footprintDetails) {
                                    updateUserData((prevData: Record<string, UserData>) => {
                                        const curData = prevData[currentUser];
                                        if (!curData || !curData.completedAchievements) return prevData;
                                        const updatedCompletions = curData.completedAchievements.map(c =>
                                            c.postId === failedPost.id ? { ...c, postId: serverPost.id } : c
                                        );
                                        updateUserOnServer(currentUser, { completedAchievements: updatedCompletions }).catch(console.error);
                                        return {
                                            ...prevData,
                                            [currentUser]: { ...curData, completedAchievements: updatedCompletions }
                                        };
                                    });
                                }
                            }
                        }).catch(console.error);
                    }
                },
                handleMarkPostUploadFailed,
                localId // Keep the original localId so it overwrites correctly
            );

        } catch (err) {
            console.error("Failed to retry upload:", err);
            alert("An error occurred during retry.");
        }
    };

    const handlePullToRefresh = async () => {
        if (!currentUser) return;
        
        // Recalculate total points on refresh to fix any potential bugs where points dropped to zero
        updateUserData((prev: Record<string, UserData>) => {
            const user = { ...prev[currentUser] };
            if (user && user.completedAchievements) {
                let recalc = 0;
                user.completedAchievements.forEach(c => {
                    if (c.status === 'approved') {
                        const ach = achievements.find(a => String(a.id) === String(c.achievementId));
                        if (ach) {
                            recalc += ach.points;
                        }
                    }
                });
                if (user.totalPoints !== recalc && recalc > 0) {
                    user.totalPoints = recalc;
                    if (!isDemo) {
                        updateUserOnServer(currentUser, { totalPoints: recalc }).catch(console.error);
                    }
                }
            }
            return { ...prev, [currentUser]: user };
        });

        try {
            const serverPosts = await fetchAllPosts(currentUser);
            if (serverPosts && Array.isArray(serverPosts)) {
                setPosts(prevPosts => {
                    const failed = prevPosts.filter(p => p.uploadFailed);
                    return [...failed, ...serverPosts.filter(p => !failed.some(lp => lp.id === p.id))];
                });
            }
            const serverCommunities = await fetchAllCommunities(currentUser);
            if (serverCommunities && Array.isArray(serverCommunities)) {
                setCommunities(serverCommunities);
            }

            // Fetch Users
            try {
                const serverUsers = await fetchAllUsers();
                if (serverUsers) {
                    updateUserData((prev: Record<string, UserData>) => mergeServerUsers(serverUsers, prev, isDemo));
                }
            } catch (err) {
                console.error("Fetch all users in pull-to-refresh failed:", err);
            }

            // Fetch Conversations
            try {
                const serverConvos = await fetchConversations(currentUser);
                if (serverConvos && Array.isArray(serverConvos)) {
                    const uniqueConvos = new Map<string, Conversation>();
                    serverConvos.forEach((c: Conversation) => {
                        const key = c.isYipYap ? `yip-${c.id}` : [...c.participants].sort().join(':');
                        if (uniqueConvos.has(key)) {
                            const existing = uniqueConvos.get(key)!;
                            if ((c.messages || []).length > (existing.messages || []).length) {
                                uniqueConvos.set(key, c);
                            } else if ((c.messages || []).length === (existing.messages || []).length) {
                                if (c.id > existing.id) {
                                    uniqueConvos.set(key, c);
                                }
                            }
                        } else {
                            uniqueConvos.set(key, c);
                        }
                    });
                    const dedupedConvos = Array.from(uniqueConvos.values());
                    setConversations(dedupedConvos);
                }
            } catch (err) {
                console.error("Fetch conversations in pull-to-refresh failed:", err);
            }
        } catch (err) {
            console.error("Pull to refresh failed:", err);
        }
    };

    const handleLike = useCallback((postId: number) => {
        const post = posts.find(p => p.id === postId);
        if (!post) return;

        if (currentUser === 'guest') {
            const isCurrentlyLiked = guestActions.likedPosts.includes(postId);
            const newLikesCount = isCurrentlyLiked ? Math.max(0, post.likes - 1) : post.likes + 1;

            if (!isCurrentlyLiked) {
                setAnimatingLikes(prev => new Set(prev).add(postId));
                setTimeout(() => {
                    setAnimatingLikes(prev => {
                        const next = new Set(prev);
                        next.delete(postId);
                        return next;
                    });
                }, 400);
            }

            setGuestActions(prev => ({
                ...prev,
                likedPosts: isCurrentlyLiked ? prev.likedPosts.filter(id => id !== postId) : [...prev.likedPosts, postId]
            }));

            setPosts(prevPosts =>
                prevPosts.map(p => p.id === postId ? { ...p, likes: newLikesCount } : p)
            );
            return;
        }

        const currentUserData = usersData[currentUser];
        if (!currentUserData) return;

        const isCurrentlyLiked = currentUserData.likedPosts?.includes(postId);
        const newLikesCount = isCurrentlyLiked ? Math.max(0, post.likes - 1) : post.likes + 1;

        if (!isCurrentlyLiked) {
            setAnimatingLikes(prev => new Set(prev).add(postId));
            setTimeout(() => {
                setAnimatingLikes(prev => {
                    const next = new Set(prev);
                    next.delete(postId);
                    return next;
                });
            }, 400); // Animation duration
        }

        setPosts(prevPosts =>
            prevPosts.map(p => p.id === postId ? { ...p, likes: newLikesCount } : p)
        );

        // Sync Post Like count - Server will handle notification trigger
        if (!isDemo) {
            updatePostOnServer(postId, {
                likes: newLikesCount,
                likedBy: !isCurrentlyLiked ? currentUser : undefined,
                unlikedBy: isCurrentlyLiked ? currentUser : undefined
            }).catch(console.error);
        }

        updateUserData((prevData: Record<string, UserData>) => {
            const cUser = prevData[currentUser];
            if (!cUser) return prevData;

            const likedPosts = cUser.likedPosts || [];
            const newLikedPosts = isCurrentlyLiked ? likedPosts.filter(id => id !== postId) : [...likedPosts, postId];

            // Sync User liked posts - Disabled in Demo
            if (!isDemo) {
                updateUserOnServer(currentUser, { likedPosts: newLikedPosts }).catch(console.error);
            }

            return { ...prevData, [currentUser]: { ...cUser, likedPosts: newLikedPosts } };
        });

        if (!isCurrentlyLiked) {
            // Notify the post author (Optimistic Local State only)
            const post = posts.find(p => p.id === postId);
            if (post && post.author !== currentUser) {
                updateUserData((prevData: Record<string, UserData>) => {
                    const authorData = prevData[post.author];
                    if (authorData && (authorData.notificationSettings?.likes !== false)) {
                        const newNotifs = [
                            {
                                id: Date.now() + Math.random(),
                                type: 'like' as NotificationType,
                                fromUser: currentUser,
                                postId: postId,
                                timestamp: new Date().toISOString(),
                                read: false
                            },
                            ...(authorData.notifications || [])
                        ];
                        // We NO LONGER call updateUserOnServer here.
                        // The server endpoint /api/posts/:id will handle it.

                        return { ...prevData, [post.author]: { ...authorData, notifications: newNotifs } };
                    }
                    return prevData;
                });
            }
        }
    }, [posts, currentUser, guestActions, usersData, isDemo, updateUserData]);

    const handleComment = (postId: number, commentText: string, parentId?: number, media?: Attachment[]) => {
        if (selectedPost && String(selectedPost.id || '') === String(postId || '') && selectedPost.linkRequestDetails?.isLinkRequest) {
            const newComment: Comment = {
                id: Date.now(),
                author: currentUser || 'guest',
                text: commentText,
                parentId: parentId,
                timestamp: new Date().toISOString(),
                media: media
            };

            setAnimatingComments(prev => new Set(prev).add(postId));
            setTimeout(() => {
                setAnimatingComments(prev => {
                    const next = new Set(prev);
                    next.delete(postId);
                    return next;
                });
            }, 500);

            setSelectedPost(prev => prev ? {
                ...prev,
                comments: [...(prev.comments || []), newComment]
            } : null);

            setConversations(prevConvos => prevConvos.map(c => {
                const hasMsg = c.messages?.some(m => m && String(m.id || '') === String(postId || ''));
                if (!hasMsg) return c;
                const updatedMsgs = (c.messages || []).map(m => {
                    if (!m || String(m.id || '') !== String(postId || '')) return m;
                    const existingComments: Comment[] = m.metadata?.comments || m.linkRequestDetails?.comments || [];
                    const updatedComments = [...existingComments, newComment];
                    const updatedMeta = { ...m.metadata, comments: updatedComments };
                    const updatedDetails = { ...m.linkRequestDetails, comments: updatedComments };
                    const updatedM = { ...m, metadata: updatedMeta, linkRequestDetails: updatedDetails as any };
                    updateConversationOnServer(c.id, { messages: c.messages.map(x => (x && String(x.id || '') === String(m.id || '')) ? updatedM : x) }).catch(console.error);
                    return updatedM;
                });
                return { ...c, messages: updatedMsgs };
            }));

            return;
        }

        const post = posts.find(p => p.id === postId);
        if (!post) return;

        const newComment: Comment = {
            id: Date.now(),
            author: currentUser || 'guest',
            text: commentText,
            parentId: parentId,
            timestamp: new Date().toISOString(),
            media: media
        };

        setAnimatingComments(prev => new Set(prev).add(postId));
        setTimeout(() => {
            setAnimatingComments(prev => {
                const next = new Set(prev);
                next.delete(postId);
                return next;
            });
        }, 500);

        if (currentUser === 'guest') {
            setGuestActions(prev => ({
                ...prev,
                localComments: {
                    ...prev.localComments,
                    [postId]: [...(prev.localComments[postId] || []), newComment]
                }
            }));
            return;
        }

        const updatedComments = [...post.comments, newComment];
        setPosts(prevPosts =>
            prevPosts.map(p => p.id === postId ? { ...p, comments: updatedComments } : p)
        );

        // Sync Comment to server - Disabled in Demo
        if (!isDemo) {
            updatePostOnServer(postId, { comments: updatedComments }).catch(console.error);
        }

        // Notify the post author/parent comment author (Optimistic Local State)
        const notifyTarget = parentId ? post.comments.find(c => c.id === parentId)?.author : post.author;
        if (notifyTarget && notifyTarget !== currentUser) {
            updateUserData((prevData: Record<string, UserData>) => {
                const authorData = prevData[notifyTarget];
                if (authorData && (authorData.notificationSettings?.comments !== false)) {
                    const isReply = !!parentId;
                    const newNotifs = [
                        {
                            id: newComment.id,
                            type: (isReply ? 'reply' : 'comment') as NotificationType,
                            fromUser: currentUser,
                            postId: postId,
                            commentText: isReply ? `replied: ${commentText}` : commentText,
                            timestamp: new Date().toISOString(),
                            read: false
                        },
                        ...(authorData.notifications || [])
                    ];
                    return { ...prevData, [notifyTarget]: { ...authorData, notifications: newNotifs } };
                }
                return prevData;
            });
        }
    };

    const handleCommentReaction = (postId: number, commentId: number, emoji: string) => {
        if (!currentUser) return;
        if (selectedPost && String(selectedPost.id || '') === String(postId || '') && selectedPost.linkRequestDetails?.isLinkRequest) {
            setSelectedPost(prev => {
                if (!prev) return null;
                const updatedComments = (prev.comments || []).map(comment => {
                    if (comment.id === commentId) {
                        const reactions = { ...(comment.reactions || {}) };
                        const users = [...(reactions[emoji] || [])];
                        const userIndex = users.indexOf(currentUser);
                        if (userIndex > -1) {
                            users.splice(userIndex, 1);
                            if (users.length === 0) delete reactions[emoji];
                            else reactions[emoji] = users;
                        } else {
                            reactions[emoji] = [...users, currentUser];
                        }
                        return { ...comment, reactions };
                    }
                    return comment;
                });
                return { ...prev, comments: updatedComments };
            });

            setConversations(prevConvos => prevConvos.map(c => {
                const hasMsg = c.messages?.some(m => m && String(m.id || '') === String(postId || ''));
                if (!hasMsg) return c;
                const updatedMsgs = (c.messages || []).map(m => {
                    if (!m || String(m.id || '') !== String(postId || '')) return m;
                    const existingComments: Comment[] = m.metadata?.comments || m.linkRequestDetails?.comments || [];
                    const updatedComments = existingComments.map((comment: Comment) => {
                        if (comment.id === commentId) {
                            const reactions = { ...(comment.reactions || {}) };
                            const users = [...(reactions[emoji] || [])];
                            const userIndex = users.indexOf(currentUser);
                            if (userIndex > -1) {
                                users.splice(userIndex, 1);
                                if (users.length === 0) delete reactions[emoji];
                                else reactions[emoji] = users;
                            } else {
                                reactions[emoji] = [...users, currentUser];
                            }
                            return { ...comment, reactions };
                        }
                        return comment;
                    });
                    const updatedMeta = { ...m.metadata, comments: updatedComments };
                    const updatedDetails = { ...m.linkRequestDetails, comments: updatedComments };
                    const updatedM = { ...m, metadata: updatedMeta, linkRequestDetails: updatedDetails as any };
                    updateConversationOnServer(c.id, { messages: c.messages.map(x => (x && String(x.id || '') === String(m.id || '')) ? updatedM : x) }).catch(console.error);
                    return updatedM;
                });
                return { ...c, messages: updatedMsgs };
            }));

            return;
        }

        setPosts(prevPosts => {
            return prevPosts.map(post => {
                if (post.id === postId) {
                    const updatedComments = post.comments.map(comment => {
                        if (comment.id === commentId) {
                            const reactions = { ...(comment.reactions || {}) };
                            const users = [...(reactions[emoji] || [])];
                            const userIndex = users.indexOf(currentUser);

                            if (userIndex > -1) {
                                users.splice(userIndex, 1);
                                if (users.length === 0) delete reactions[emoji];
                                else reactions[emoji] = users;
                            } else {
                                reactions[emoji] = [...users, currentUser];
                            }

                            return { ...comment, reactions };
                        }
                        return comment;
                    });

                    if (!isDemo) {
                        updatePostOnServer(postId, { comments: updatedComments }).catch(console.error);
                    }

                    return { ...post, comments: updatedComments };
                }
                return post;
            });
        });
    };

    const handleDeleteComment = (postId: number, commentId: number) => {
        if (selectedPost && String(selectedPost.id || '') === String(postId || '') && selectedPost.linkRequestDetails?.isLinkRequest) {
            setSelectedPost(prev => prev ? {
                ...prev,
                comments: (prev.comments || []).filter(c => c.id !== commentId)
            } : null);

            setConversations(prevConvos => prevConvos.map(c => {
                const hasMsg = c.messages?.some(m => m && String(m.id || '') === String(postId || ''));
                if (!hasMsg) return c;
                const updatedMsgs = (c.messages || []).map(m => {
                    if (!m || String(m.id || '') !== String(postId || '')) return m;
                    const existingComments: Comment[] = m.metadata?.comments || m.linkRequestDetails?.comments || [];
                    const updatedComments = existingComments.filter((c: Comment) => c.id !== commentId);
                    const updatedMeta = { ...m.metadata, comments: updatedComments };
                    const updatedDetails = { ...m.linkRequestDetails, comments: updatedComments };
                    const updatedM = { ...m, metadata: updatedMeta, linkRequestDetails: updatedDetails as any };
                    updateConversationOnServer(c.id, { messages: c.messages.map(x => (x && String(x.id || '') === String(m.id || '')) ? updatedM : x) }).catch(console.error);
                    return updatedM;
                });
                return { ...c, messages: updatedMsgs };
            }));

            return;
        }

        setPosts(prevPosts => {
            const updated = prevPosts.map(p => {
                if (p.id === postId) {
                    const newComments = p.comments.filter(c => c.id !== commentId);
                    // Sync to Server - Disabled in Demo
                    if (!isDemo) {
                        updatePostOnServer(postId, { comments: newComments }).catch(console.error);
                    }
                    return { ...p, comments: newComments };
                }
                return p;
            });

            // If we're currently viewing this post in the post modal, refresh the selectedPost
            if (page.type === 'post' && selectedPost?.id === postId) {
                const targetPost = updated.find(p => p.id === postId);
                if (targetPost) {
                    setSelectedPost(getHydratedPosts([targetPost])[0]);
                }
            }

            return updated;
        });
    };

    const handleEditComment = (postId: number, commentId: number, newText: string) => {
        if (selectedPost && String(selectedPost.id || '') === String(postId || '') && selectedPost.linkRequestDetails?.isLinkRequest) {
            setSelectedPost(prev => prev ? {
                ...prev,
                comments: (prev.comments || []).map(c => c.id === commentId ? { ...c, text: newText } : c)
            } : null);

            setConversations(prevConvos => prevConvos.map(c => {
                const hasMsg = c.messages?.some(m => m && String(m.id || '') === String(postId || ''));
                if (!hasMsg) return c;
                const updatedMsgs = (c.messages || []).map(m => {
                    if (!m || String(m.id || '') !== String(postId || '')) return m;
                    const existingComments: Comment[] = m.metadata?.comments || m.linkRequestDetails?.comments || [];
                    const updatedComments = existingComments.map((c: Comment) => c.id === commentId ? { ...c, text: newText } : c);
                    const updatedMeta = { ...m.metadata, comments: updatedComments };
                    const updatedDetails = { ...m.linkRequestDetails, comments: updatedComments };
                    const updatedM = { ...m, metadata: updatedMeta, linkRequestDetails: updatedDetails as any };
                    updateConversationOnServer(c.id, { messages: c.messages.map(x => (x && String(x.id || '') === String(m.id || '')) ? updatedM : x) }).catch(console.error);
                    return updatedM;
                });
                return { ...c, messages: updatedMsgs };
            }));

            return;
        }

        setPosts(prevPosts => {
            const updated = prevPosts.map(p => {
                if (p.id === postId) {
                    const updatedComments = p.comments.map(c => c.id === commentId ? { ...c, text: newText } : c);
                    if (!isDemo) {
                        updatePostOnServer(postId, { comments: updatedComments }).catch(console.error);
                    }
                    return { ...p, comments: updatedComments };
                }
                return p;
            });

            if (page.type === 'post' && selectedPost?.id === postId) {
                const targetPost = updated.find(p => p.id === postId);
                if (targetPost) {
                    setSelectedPost(getHydratedPosts([targetPost])[0]);
                }
            }

            return updated;
        });
    };

    const handleBookmark = useCallback((postId: number) => {
        const post = posts.find(p => p.id === postId);
        if (!post) return;

        if (currentUser === 'guest') {
            const isCurrentlyBookmarked = guestActions.bookmarkedPosts.includes(postId);
            const newBookmarkCount = isCurrentlyBookmarked ? Math.max(0, post.bookmarks - 1) : post.bookmarks + 1;

            if (!isCurrentlyBookmarked) {
                setAnimatingBookmarks(prev => new Set(prev).add(postId));
                setTimeout(() => {
                    setAnimatingBookmarks(prev => {
                        const next = new Set(prev);
                        next.delete(postId);
                        return next;
                    });
                }, 500);
            }

            setGuestActions(prev => ({
                ...prev,
                bookmarkedPosts: isCurrentlyBookmarked ? prev.bookmarkedPosts.filter(id => id !== postId) : [...prev.bookmarkedPosts, postId]
            }));

            setPosts(prevPosts =>
                prevPosts.map(p => p.id === postId ? { ...p, bookmarks: newBookmarkCount } : p)
            );
            return;
        }

        const currentUserData = usersData[currentUser];
        if (!currentUserData) return;

        const isCurrentlyBookmarked = currentUserData.bookmarkedPosts?.includes(postId);
        const newBookmarkCount = isCurrentlyBookmarked ? Math.max(0, post.bookmarks - 1) : post.bookmarks + 1;

        if (!isCurrentlyBookmarked) {
            setAnimatingBookmarks(prev => new Set(prev).add(postId));
            setTimeout(() => {
                setAnimatingBookmarks(prev => {
                    const next = new Set(prev);
                    next.delete(postId);
                    return next;
                });
            }, 500);
        }

        setPosts(prevPosts =>
            prevPosts.map(p => p.id === postId ? { ...p, bookmarks: newBookmarkCount } : p)
        );
        // Persist Post Bookmarks - Disabled in Demo
        if (!isDemo) {
            updatePostOnServer(postId, { bookmarks: newBookmarkCount }).catch(console.error);
        }

        const newBookmarkedPosts = isCurrentlyBookmarked
            ? currentUserData.bookmarkedPosts.filter((id: number) => id !== postId)
            : [...currentUserData.bookmarkedPosts, postId];

        updateUserData((prevData: Record<string, UserData>) => {
            return {
                ...prevData,
                [currentUser]: { ...prevData[currentUser], bookmarkedPosts: newBookmarkedPosts }
            };
        });
        // Persist User Bookmarks - Disabled in Demo
        if (!isDemo) {
            updateUserOnServer(currentUser, { bookmarkedPosts: newBookmarkedPosts }).catch(console.error);
        }
    }, [posts, currentUser, guestActions, usersData, isDemo, updateUserData]);



    const handleBumpClick = useCallback((post: HydratedPost) => {
        const postToTarget = post.type === 'bump' && post.originalPost ? post.originalPost : post;

        const isBumped = usersData[currentUser]?.bumpedPosts.includes(postToTarget.id);

        setPostToBump(postToTarget);
        if (isBumped) {
            setIsBumpActionModalOpen(true);
        } else {
            setIsBumpModalOpen(true);
        }
    }, [usersData, currentUser, setPostToBump, setIsBumpActionModalOpen, setIsBumpModalOpen]);

    const handleConfirmVouch = () => {
        if (!postToVouch || !vouchMessage) return;
        const newVouch = {
            userId: currentUser,
            message: vouchMessage,
            timestamp: new Date().toISOString()
        };

        setPosts(prevPosts =>
            prevPosts.map(p => p.id === postToVouch.id ? { ...p, vouches: [...(p.vouches || []), newVouch] } : p)
        );

        if (postToVouch.author !== currentUser) {
            updateUserData((prevData: Record<string, UserData>) => {
                const authorData = prevData[postToVouch.author];
                if (authorData && (authorData.notificationSettings?.vouches !== false)) {
                    const newNotifs = [
                        {
                            id: Date.now() + Math.random(),
                            type: 'vouch' as NotificationType,
                            fromUser: currentUser,
                            postId: postToVouch.id,
                            timestamp: new Date().toISOString(),
                            read: false
                        },
                        ...(authorData.notifications || [])
                    ];
                    // Server handles notification trigger in PUT /api/posts/:id
                    return { ...prevData, [postToVouch.author]: { ...authorData, notifications: newNotifs } };
                }
                return prevData;
            });
        }

        setIsVouchModalOpen(false);
        setPostToVouch(null);
    };

    const handleConfirmBump = (data: any) => {
        if (!postToBump || !currentUser) return;

        const caption = typeof data === 'string' ? data : (data.content?.text || '');
        const communityId = typeof data === 'object' ? data.communityId : undefined;

        if (currentUser === 'guest') {
            const isAlreadyBumped = guestActions.bumpedPosts.includes(postToBump.id);
            const newBump: Post = {
                id: Date.now(),
                author: 'guest',
                type: 'bump',
                content: { text: caption },
                originalPostId: postToBump.id,
                hashtags: parseHashtags(caption),
                visibility: 'public',
                likes: 0,
                comments: [],
                vouches: [],
                bumps: 0,
                bookmarks: 0,
                shares: 0,
                themeColor: '#161b22',
                ...(communityId ? { communityId } : {})
            } as any;

            if (!isAlreadyBumped) {
                setGuestActions(prev => ({
                    ...prev,
                    bumpedPosts: [...prev.bumpedPosts, postToBump.id]
                }));
            }

            setPosts(prevPosts => {
                let filtered = prevPosts;
                if (isAlreadyBumped) {
                    filtered = prevPosts.filter(p => !(p.type === 'bump' && p.originalPostId === postToBump.id && p.author === 'guest'));
                }
                const newBumpsCount = isAlreadyBumped ? postToBump.bumps : (postToBump.bumps || 0) + 1;
                return [
                    newBump,
                    ...filtered.map(p => p.id === postToBump.id ? { ...p, bumps: newBumpsCount } : p)
                ];
            });

            closeBumpModal();
            return;
        }

        const isAlreadyBumped = posts.some(p => p.type === 'bump' && p.originalPostId === postToBump.id && p.author === currentUser);

        const newBump: Post = {
            id: Date.now(),
            author: currentUser,
            type: 'bump',
            content: { text: caption },
            originalPostId: postToBump.id,
            hashtags: parseHashtags(caption),
            visibility: 'public',
            likes: 0,
            comments: [],
            vouches: [],
            bumps: 0,
            bookmarks: 0,
            shares: 0,
            themeColor: usersData[currentUser]?.appThemeColor,
            ...(communityId ? { communityId } : {})
        } as any;

        setAnimatingBumps(prev => new Set(prev).add(postToBump.id));
        setTimeout(() => setAnimatingBumps(prev => {
            const next = new Set(prev);
            next.delete(postToBump.id);
            return next;
        }), 500);

        setPosts(prevPosts => {
            let filtered = prevPosts;
            if (isAlreadyBumped) {
                filtered = prevPosts.filter(p => !(p.type === 'bump' && p.originalPostId === postToBump.id && p.author === currentUser));
            }
            const newBumpsCount = isAlreadyBumped ? postToBump.bumps : (postToBump.bumps || 0) + 1;
            return [
                newBump,
                ...filtered.map(p => p.id === postToBump.id ? { ...p, bumps: newBumpsCount } : p)
            ];
        });

        if (!isDemo) {
            if (isAlreadyBumped) {
                unbumpPostOnServer(postToBump.id, currentUser).catch(console.error);
            } else {
                const originalPost = posts.find(p => p.id === postToBump.id);
                if (originalPost) {
                    updatePostOnServer(postToBump.id, { bumps: (originalPost.bumps || 0) + 1 }).catch(console.error);
                }
            }
            createPostOnServer(newBump).catch(console.error);
        }

        updateUserData((prevData: Record<string, UserData>) => {
            const userData = prevData[currentUser];
            if (!userData) return prevData;
            const alreadyInList = (userData.bumpedPosts || []).includes(postToBump.id);
            const newbumpedPosts = alreadyInList ? userData.bumpedPosts : [...(userData.bumpedPosts || []), postToBump.id];

            if (!isDemo && !alreadyInList) {
                updateUserOnServer(currentUser, { bumpedPosts: newbumpedPosts }).catch(console.error);
            }

            return {
                ...prevData,
                [currentUser]: { ...userData, bumpedPosts: newbumpedPosts }
            };
        });

        if (postToBump.author !== currentUser && !isAlreadyBumped) {
            updateUserData((prevData: Record<string, UserData>) => {
                const authorData = prevData[postToBump.author];
                if (authorData && (authorData.notificationSettings?.bumps !== false)) {
                    const newNotifs = [
                        {
                            id: Date.now() + Math.random(),
                            type: 'bump' as NotificationType,
                            fromUser: currentUser,
                            postId: postToBump.id,
                            timestamp: new Date().toISOString(),
                            read: false
                        },
                        ...(authorData.notifications || [])
                    ];
                    return { ...prevData, [postToBump.author]: { ...authorData, notifications: newNotifs } };
                }
                return prevData;
            });
        }

        closeBumpModal();
    };



    const closeBumpModal = () => {
        setIsBumpModalOpen(false);
        setPostToBump(null);
    };

    const closeBumpActionModal = () => {
        setIsBumpActionModalOpen(false);
        setPostToBump(null);
    };

    const confirmBumpAgain = () => {
        if (!postToBump || !currentUser) return;
        setIsBumpActionModalOpen(false);
        setIsBumpModalOpen(true);
    };

    const removeBump = () => {
        if (!postToBump || !currentUser) return;

        // 1. Optimistically update local posts state
        setPosts(prevPosts =>
            prevPosts
                .filter(p => !(p.type === 'bump' && p.originalPostId === postToBump.id && p.author === currentUser))
                .map(p => p.id === postToBump.id ? { ...p, bumps: Math.max(0, (p.bumps || 0) - 1) } : p)
        );

        // 2. Optimistically update local user data
        updateUserData((prevData: Record<string, UserData>) => {
            const userData = prevData[currentUser];
            if (!userData) return prevData;
            const newbumpedPosts = (userData.bumpedPosts || []).filter(id => id !== postToBump.id);

            const copy = { ...prevData };
            copy[currentUser] = { ...userData, bumpedPosts: newbumpedPosts };

            // Remove bump notification from original author locally
            if (postToBump.author !== currentUser) {
                const authorData = prevData[postToBump.author];
                if (authorData && authorData.notifications) {
                    const newNotifications = authorData.notifications.filter(n => !(n.type === 'bump' && n.fromUser === currentUser && n.postId === postToBump.id));
                    copy[postToBump.author] = { ...authorData, notifications: newNotifications };
                }
            }

            return copy;
        });

        // 3. Persist change to server - Unified Unbump Call
        if (!isDemo) {
            unbumpPostOnServer(postToBump.id, currentUser).catch((err: any) => {
                console.error('[App] Failed to unbump on server:', err);
            });
        }

        closeBumpActionModal();
    };

    const handleEditPost = (post: HydratedPost) => {
        setPostToEdit(post);
        setIsEditModalVisible(true);
        setIsModalVisible(false);
        pushModalState('edit');
    };

    const handleUpdatePost = (postId: number, updatedData: Partial<Post>) => {
        const newText = updatedData.content?.text;
        const hashtags = newText ? parseHashtags(newText) : undefined;

        const updateLogic = (p: Post) => p.id === postId ? {
            ...p,
            ...updatedData,
            content: {
                ...p.content,
                ...(updatedData.content || {})
            },
            hashtags: hashtags || p.hashtags
        } : p;

        setPosts(prevPosts => {
            const updated = prevPosts.map(updateLogic);
            const targetPost = updated.find(p => p.id === postId);
            if (targetPost) {
                if (!isDemo) {
                    updatePostOnServer(postId, {
                        ...updatedData,
                        hashtags: hashtags || targetPost.hashtags
                    }).catch(console.error);
                }
            }
            return updated;
        });

        if (page.type === 'post' && selectedPost?.id === postId) {
            setSelectedPost(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    ...updatedData,
                    content: {
                        ...prev.content,
                        ...(updatedData.content || {})
                    },
                    hashtags: hashtags || prev.hashtags
                } as HydratedPost;
            });
        }

        setIsEditModalVisible(false);
        setPostToEdit(null);
    };

    const handleCloseEditModal = () => {
        setIsEditModalVisible(false);
        setPostToEdit(null);
        // Restore the post modal visibility when closing edit modal
        if (selectedPost) {
            setIsModalVisible(true);
        }
        if (window.history.state?.modal === 'edit') {
            window.history.back();
        }
    };

    const handleToggleVisibility = (postId: number) => {
        const idToUpdate = Number(postId);
        setPosts(prevPosts => prevPosts.map(p => {
            if (p.id === idToUpdate) {
                const newVisibility = p.visibility === 'public' ? (p.communityId ? 'private' : 'followers') : 'public';
                // Sync to Server - Disabled in Demo
                if (!isDemo) {
                    updatePostOnServer(idToUpdate, { visibility: newVisibility }).catch(console.error);
                }
                return { ...p, visibility: newVisibility };
            }
            return p;
        }));

        if (selectedPost && Number(selectedPost.id) === idToUpdate) {
            setSelectedPost(prev => prev ? { ...prev, visibility: prev.visibility === 'public' ? (prev.communityId ? 'private' : 'followers') : 'public' } : null);
        }
    };

    const handleToggleCaption = (postId: number) => {
        const idToUpdate = Number(postId);
        setPosts(prevPosts => prevPosts.map(p => {
            if (p.id === idToUpdate) {
                const newShowCaption = p.showCaption === false ? true : false;
                // Sync to Server - Disabled in Demo
                if (!isDemo) {
                    updatePostOnServer(idToUpdate, { showCaption: newShowCaption }).catch(console.error);
                }
                return { ...p, showCaption: newShowCaption };
            }
            return p;
        }));

        if (selectedPost && Number(selectedPost.id) === idToUpdate) {
            setSelectedPost(prev => prev ? { ...prev, showCaption: prev.showCaption === false ? true : false } : null);
        }
    };

    const handleUpdateTextSize = (postId: number, textSize: 'xs' | 'sm' | 'med' | 'lg') => {
        const idToUpdate = Number(postId);
        setPosts(prevPosts => prevPosts.map(p => {
            if (p.id === idToUpdate) {
                // Sync to Server - Disabled in Demo
                if (!isDemo) {
                    updatePostOnServer(idToUpdate, { textSize }).catch(console.error);
                }
                return { ...p, textSize };
            }
            return p;
        }));

        if (selectedPost && Number(selectedPost.id) === idToUpdate) {
            setSelectedPost(prev => prev ? { ...prev, textSize } : null);
        }
    };

    const handleUpdatePostcardLayout = (postId: number, postcardLayout: 'square' | 'dynamic') => {
        const idToUpdate = Number(postId);
        setPosts(prevPosts => prevPosts.map(p => {
            if (p.id === idToUpdate) {
                // Sync to Server - Disabled in Demo
                if (!isDemo) {
                    updatePostOnServer(idToUpdate, { postcardLayout }).catch(console.error);
                }
                return { ...p, postcardLayout };
            }
            return p;
        }));

        if (selectedPost && Number(selectedPost.id) === idToUpdate) {
            setSelectedPost(prev => prev ? { ...prev, postcardLayout } : null);
        }
    };

    const handleUpdatePostcardStyle = (postId: number, postcardStyle: string) => {
        const idToUpdate = Number(postId);
        setPosts(prevPosts => prevPosts.map(p => {
            if (p.id === idToUpdate) {
                // Sync to Server - Disabled in Demo
                if (!isDemo) {
                    updatePostOnServer(idToUpdate, { postcardStyle }).catch(console.error);
                }
                return { ...p, postcardStyle };
            }
            return p;
        }));

        if (selectedPost && Number(selectedPost.id) === idToUpdate) {
            setSelectedPost(prev => prev ? { ...prev, postcardStyle } : null);
        }
    };

    const handleArchivePost = (postId: number) => {
        const idToUpdate = Number(postId);
        setPosts(prevPosts => prevPosts.map(p => {
            if (p.id === idToUpdate) {
                // Sync to Server - Disabled in Demo
                if (!isDemo) {
                    toggleArchivePostOnServer(idToUpdate, true).catch(console.error);
                }
                return { ...p, isArchived: true };
            }
            return p;
        }));

        if (selectedPost && Number(selectedPost.id) === idToUpdate) {
            handleCloseModal(); // Close the modal since the post is now archived
        }
    };

    const handleUnarchivePost = (postId: number) => {
        const idToUpdate = Number(postId);
        setPosts(prevPosts => prevPosts.map(p => {
            if (p.id === idToUpdate) {
                // Sync to Server - Disabled in Demo
                if (!isDemo) {
                    toggleArchivePostOnServer(idToUpdate, false).catch(console.error);
                }
                return { ...p, isArchived: false };
            }
            return p;
        }));
    };

    const handlePinPost = (postId: number) => {
        if (!currentUser) return;
        const pinnedList = usersData[currentUser]?.pinnedPosts || [];
        if (pinnedList.includes(postId)) return;
        if (pinnedList.length >= 10) {
            alert("You can only pin up to 10 posts!");
            return;
        }

        const newPinned = [...pinnedList, postId];
        updateUserData((prev: Record<string, UserData>) => ({
            ...prev,
            [currentUser]: {
                ...prev[currentUser],
                pinnedPosts: newPinned
            }
        }));

        if (!isDemo) {
            updateUserOnServer(currentUser, { pinnedPosts: newPinned }).catch(console.error);
        }
    };

    const handleReorderPinnedPosts = (newPinnedOrder: number[]) => {
        if (!currentUser) return;
        updateUserData((prev: Record<string, UserData>) => ({
            ...prev,
            [currentUser]: {
                ...prev[currentUser],
                pinnedPosts: newPinnedOrder
            }
        }));

        if (!isDemo) {
            updateUserOnServer(currentUser, { pinnedPosts: newPinnedOrder }).catch(console.error);
        }
    };

    const handleUnpinPost = (postId: number) => {
        if (!currentUser) return;
        const pinnedList = usersData[currentUser]?.pinnedPosts || [];
        if (!pinnedList.includes(postId)) return;

        const newPinned = pinnedList.filter((id: number) => id !== postId);
        updateUserData((prev: Record<string, UserData>) => ({
            ...prev,
            [currentUser]: {
                ...prev[currentUser],
                pinnedPosts: newPinned
            }
        }));

        if (!isDemo) {
            updateUserOnServer(currentUser, { pinnedPosts: newPinned }).catch(console.error);
        }
    };

    const handleDeleteClick = useCallback((postId: number, post?: Post | HydratedPost) => {
        setPostToDeleteId(postId);
        if (post) setPostToDelete(post);
        else {
            const p = posts.find(x => x.id === postId);
            if (p) setPostToDelete(p);
        }
        setIsDeleteModalOpen(true);
        pushModalState('delete');
    }, [posts, setPostToDeleteId, setPostToDelete, setIsDeleteModalOpen, pushModalState]);

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setPostToDeleteId(null);
        setPostToDelete(null);
        if (window.history.state?.modal === 'delete') {
            window.history.back();
        }
    };

    const lastBackActionRef = useRef<number>(0);

    const handleBackNavigation = useCallback(() => {
        // Debounce back navigation to prevent double-back events on mobile hardware
        const now = Date.now();
        if (now - lastBackActionRef.current < 400) return;
        // Close active YipYap conversation if open
        const activeConvo = activeConversationId ? conversations.find(c => c.id === activeConversationId) : null;
        if (activeConvo && activeConvo.isYipYap) {
            setActiveConversationId(null);
            return;
        }

        // 1. Modals that should just close locally (no URL push)
        if (isAccountSwitcherOpen) { setIsAccountSwitcherOpen(false); return; }
        if (isDeleteModalOpen) { handleCloseDeleteModal(); return; }
        if (isEditModalVisible) { handleCloseEditModal(); return; }
        if (isDebugOpen) {
            setIsDebugOpen(false);
            if (window.history.state?.modal === 'debug') {
                window.history.back();
            }
            return;
        }
        if (isBumpModalOpen) { closeBumpModal(); return; }
        if (isBumpActionModalOpen) { closeBumpActionModal(); return; }
        if (isVouchModalOpen) { setIsVouchModalOpen(false); setPostToVouch(null); return; }
        if (shareModalOpen) {
            setShareModalOpen(false);
            if (window.history.state?.modal === 'share') {
                window.history.back();
            }
            return;
        }
        if (isShareToGoodLyfeModalOpen) {
            setIsShareToGoodLyfeModalOpen(false);
            setIncomingShareData(null);
            if (window.history.state?.modal === 'share_intent') {
                window.history.back();
            }
            return;
        }
        if (followListModalState.isOpen) {
            setFollowListModalState({ isOpen: false, listType: null, userId: null });
            if (window.history.state?.modal === 'follow_list') {
                window.history.back();
            }
            return;
        }
        if (isAdminModalOpen) { setIsAdminModalOpen(false); return; }
        if (isInternalModalOpen) {
            setCloseInternalModalsTrigger(prev => prev + 1);
            if (window.history.state?.modal === 'internal') {
                window.history.back();
            }
            return;
        }

        // 2. Modals with URLs (handled via handleCloseModal which decides history.back() vs setPage)
        if (isModalVisible) {
            handleCloseModal();
            return;
        }

        // 3. Exit Confirmation on Home Page
        if (page.type === 'home') {
            if (activeVoiceData) {
                setFlashMessage("Leave the voice channel to exit");
                setTimeout(() => setFlashMessage(null), 2000);
                return;
            }
            if (now - lastBackPressTimeRef.current < 2000) {
                // Second press: exit or go back
                try {
                    const { App: CapApp } = require('@capacitor/app');
                    CapApp.minimizeApp();
                } catch (e) {
                    window.history.back();
                }
            } else {
                setFlashMessage("Swipe again to exit");
                lastBackPressTimeRef.current = now;
                setTimeout(() => setFlashMessage(null), 2000);
            }
            return;
        }

        // 4. Standard Navigation
        if (isDeleteModalOpen || isEditModalVisible || isBumpModalOpen || isGlobalPostModalOpen ||
            isBumpActionModalOpen || isModLibraryModalOpen || isAccountSwitcherOpen) {
            // Placeholder logic to handle back navigation for additional modals
        }
        if (sessionHistoryCount.current > 0) {
            window.history.back();
        } else {
            setPage({ type: 'home' });
        }
    }, [
        isDeleteModalOpen, isEditModalVisible, isBumpModalOpen, isGlobalPostModalOpen,
        isBumpActionModalOpen, isVouchModalOpen, shareModalOpen,
        isShareToGoodLyfeModalOpen, isModalVisible, followListModalState.isOpen,
        isAdminModalOpen, isInternalModalOpen, isDebugOpen, page.type, handleCloseModal,
        isAccountSwitcherOpen, activeVoiceData
    ]);

    const handleConfirmDelete = () => {
        if (postToDeleteId) {
            const deletedPost = posts.find(p => p.id === postToDeleteId);

            if (deletedPost) {
                // Delete associated media from S3
                if (deletedPost.content.media) {
                    deletedPost.content.media.forEach(m => {
                        if (m.url) deleteMediaFile(m.url);
                    });
                }

                setPosts(posts => posts.filter(p => p.id !== postToDeleteId));
                setFailedPosts(failed => failed.filter(p => p.id !== postToDeleteId));

                if (selectedPost && selectedPost.id === postToDeleteId) {
                    handleCloseModal();
                }

                // Call Server Delete
                if (!isDemo) {
                    deletePostOnServer(postToDeleteId).catch(console.error);
                }

                // Delete from IndexedDB pending attachments
                try {
                    const { deletePendingPostAttachments } = require('./indexedDb');
                    deletePendingPostAttachments(Number(postToDeleteId)).catch(console.error);
                } catch (e) { }

                // If Footprint post, remove points and reset daily achievement
                // Only do this if the post was successfully uploaded (not a failed upload)
                // — failed uploads already had their completion reverted in handleMarkPostUploadFailed.
                if (deletedPost.footprintDetails && !deletedPost.uploadFailed) {
                    const achievementId = deletedPost.footprintDetails.achievementId;
                    updateUserData((prev: Record<string, UserData>) => {
                        const user = { ...prev[currentUser] };
                        const completion = user.completedAchievements?.find(a => a.achievementId === achievementId && a.postId === postToDeleteId);
                        // Remove achievement from completedAchievements
                        user.completedAchievements = (user.completedAchievements || []).filter(a => a.achievementId !== achievementId || a.postId !== postToDeleteId);
                        // Remove points for this achievement only if it was actually approved
                        const achievement = achievements.find(a => a.id === achievementId);
                        if (achievement && completion && completion.status === 'approved') {
                            const isTaskOfTheDay = dailyAchievements?.daily?.id === achievementId;
                            // Read the timely flag stored at post-creation time.
                            const wasTimely = deletedPost.footprintDetails?.isTimely ?? false;
                            const pointsToRemove = isTaskOfTheDay
                                ? achievement.points + 50
                                : wasTimely
                                    ? achievement.points               // was awarded full points
                                    : Math.round(achievement.points * 0.2); // was awarded 20%
                            user.totalPoints = Math.max(0, (user.totalPoints || 0) - pointsToRemove);
                        }
                        // Reset lastCompletionDate if this was today's achievement
                        const today = getFootprintCycleDate();
                        if (user.lastCompletionDate === today) {
                            user.lastCompletionDate = undefined;
                            user.streak = Math.max(0, user.streak - 1);
                        }

                        // Sync reversed user data to server
                        updateUserOnServer(currentUser, {
                            completedAchievements: user.completedAchievements,
                            totalPoints: user.totalPoints,
                            lastCompletionDate: user.lastCompletionDate,
                            // streak: user.streak // Disabled
                        }).catch(console.error);

                        return { ...prev, [currentUser]: user };
                    });
                }
            }
            handleCloseDeleteModal();
        }
    };



    const handleNavigateToProfile = useCallback((userId: string) => {
        const canonicalId = getUserKeyCaseInsensitive(userId);
        if (canonicalId) {
            setSelectedPost(null);
            setIsSettingsModalOpen(false);
            setPage({ type: 'profile', userId: canonicalId });
            console.log('Navigating to profile:', canonicalId);
            window.scrollTo(0, 0);
        } else {
            console.warn(`User "${userId}" not found, cannot navigate to profile.`);
        }
    }, [getUserKeyCaseInsensitive, setSelectedPost, setIsSettingsModalOpen, setPage]);

    /*const handleNavigateToPost = (postId: string) => {
                                                        setPage({ type: 'post', post[postId] });
                                                    window.history.pushState({ }, '', `/profile/${encodeURIComponent(postId)}`);
                                                    window.scrollTo(0, 0);
        } else {
                                                        console.warn(`User "${userId}" not found, cannot navigate to profile.`);
        }
    };*/

    const handleProfilePictureChange = (url: string) => {
        console.log('[DEBUG-PFP-APP] handleProfilePictureChange called with:', url.substring(0, 50));
        // Update UI immediately (optimistic update)
        updateUserData((prev: Record<string, UserData>) => ({
            ...prev,
            [currentUser]: { ...prev[currentUser], pfp: url }
        }));

        // If url is a blob/object URL, upload to server in the background
        if (url.startsWith('blob:')) {
            console.log('[DEBUG-PFP-APP] Detected blob URL, starting background upload...');
            return fetch(url)
                .then(res => res.blob())
                .then(blob => {
                    const file = new File([blob], `profile-${currentUser}-${Date.now()}.png`, { type: 'image/png' });
                    if (isDemo) {
                        console.log('[DEBUG-PFP-APP] Demo mode: Skipping upload');
                        return Promise.resolve(url);
                    }
                    return uploadMediaFile(file, currentUser);
                })
                .then((uploadedUrl: string) => {
                    if (uploadedUrl && uploadedUrl !== url) {
                        console.log('[DEBUG-PFP-APP] Upload successful, updating state with final URL:', uploadedUrl);
                        // Update with final server URL
                        updateUserData((prev: Record<string, UserData>) => ({
                            ...prev,
                            [currentUser]: { ...prev[currentUser], pfp: uploadedUrl }
                        }));
                        // Sync with Server
                        if (!isDemo) {
                            return updateUserOnServer(currentUser, { pfp: uploadedUrl });
                        }
                    }
                })
                .catch(err => {
                    console.error('[DEBUG-PFP-APP] Background upload/sync failed:', err);
                });
        } else {
            // Regular URL (already uploaded or external), just sync
            if (!isDemo) {
                updateUserOnServer(currentUser, { pfp: url }).catch(console.error);
            }
        }
    };

    const handleProfileBgChange = async (bg: { url: string, style: string }) => {
        const targetUserId = page.type === 'profile' ? page.userId : currentUser;
        if (!targetUserId) return;

        if (bg.style === 'color') {
            const updates = { profileBgColor: bg.url };
            updateUserData((prev: Record<string, UserData>) => ({
                ...prev,
                [targetUserId]: { ...prev[targetUserId], ...updates }
            }));
            if (!isDemo) updateUserOnServer(targetUserId, updates).catch(console.error);
        } else if (bg.url.startsWith('blob:')) {
            try {
                // Fetch the blob to get the File/Blob object
                const cleanUrl = bg.url.split('#')[0];
                const res = await fetch(cleanUrl);
                const blob = await res.blob();
                let extension = 'png';
                if (blob.type === 'image/gif') extension = 'gif';
                else if (blob.type === 'image/webp') extension = 'webp';
                else if (blob.type.startsWith('video/')) {
                    const mimeExt = blob.type.split('/')[1];
                    extension = mimeExt === 'quicktime' ? 'mov' : (mimeExt || 'mp4');
                }
                const file = new File([blob], `profile-bg-${targetUserId}-${Date.now()}.${extension}`, { type: blob.type });

                const uploadedUrl = await uploadMediaFile(file, targetUserId);
                if (uploadedUrl) {
                    const updates = { profileBg: { url: uploadedUrl, style: bg.style } };
                    updateUserData((prev: Record<string, UserData>) => ({
                        ...prev,
                        [targetUserId]: { ...prev[targetUserId], ...updates }
                    }));
                    if (!isDemo) updateUserOnServer(targetUserId, updates).catch(console.error);
                }
            } catch (err) {
                console.error('Failed to upload profile background:', err);
            }
        } else {
            const updates = { profileBg: bg };
            updateUserData((prev: Record<string, UserData>) => ({
                ...prev,
                [targetUserId]: { ...prev[targetUserId], ...updates }
            }));
            if (!isDemo) updateUserOnServer(targetUserId, updates).catch(console.error);
        }
    };

    const handleHomeBgChange = async (bg: { url: string, style: string }) => {
        if (!currentUser) return;

        if (bg.style === 'color') {
            const updates = { homeBgColor: bg.url };
            updateUserData((prev: Record<string, UserData>) => ({
                ...prev,
                [currentUser]: { ...prev[currentUser], ...updates }
            }));
            if (!isDemo) updateUserOnServer(currentUser, updates).catch(console.error);
        } else if (bg.url.startsWith('blob:')) {
            try {
                const cleanUrl = bg.url.split('#')[0];
                const res = await fetch(cleanUrl);
                const blob = await res.blob();
                let extension = 'png';
                if (blob.type === 'image/gif') extension = 'gif';
                else if (blob.type === 'image/webp') extension = 'webp';
                else if (blob.type.startsWith('video/')) {
                    const mimeExt = blob.type.split('/')[1];
                    extension = mimeExt === 'quicktime' ? 'mov' : (mimeExt || 'mp4');
                }
                const file = new File([blob], `home-bg-${currentUser}-${Date.now()}.${extension}`, { type: blob.type });

                const uploadedUrl = await uploadMediaFile(file, currentUser);
                if (uploadedUrl) {
                    const updates = { homeBg: { url: uploadedUrl, style: bg.style } };
                    updateUserData((prev: Record<string, UserData>) => ({
                        ...prev,
                        [currentUser]: { ...prev[currentUser], ...updates }
                    }));
                    if (!isDemo) updateUserOnServer(currentUser, updates).catch(console.error);
                }
            } catch (err) {
                console.error('Failed to upload home background:', err);
            }
        } else {
            const updates = { homeBg: bg };
            updateUserData((prev: Record<string, UserData>) => ({
                ...prev,
                [currentUser]: { ...prev[currentUser], ...updates }
            }));
            if (!isDemo) updateUserOnServer(currentUser, updates).catch(console.error);
        }
    };

    const handleLoginBgChange = async (bg: { url: string, style: string }) => {
        const targetUserId = 'goodlyfe';
        if (!targetUserId) return;

        if (bg.style === 'color') {
            const updates = { loginBgColor: bg.url };
            updateUserData((prev: Record<string, UserData>) => ({
                ...prev,
                [targetUserId]: { ...prev[targetUserId], ...updates }
            }));
            if (!isDemo) updateUserOnServer(targetUserId, updates).catch(console.error);
        } else if (bg.url.startsWith('blob:')) {
            try {
                const cleanUrl = bg.url.split('#')[0];
                const res = await fetch(cleanUrl);
                const blob = await res.blob();
                let extension = 'png';
                if (blob.type === 'image/gif') extension = 'gif';
                else if (blob.type === 'image/webp') extension = 'webp';
                else if (blob.type.startsWith('video/')) {
                    const mimeExt = blob.type.split('/')[1];
                    extension = mimeExt === 'quicktime' ? 'mov' : (mimeExt || 'mp4');
                }
                const file = new File([blob], `login-bg-${Date.now()}.${extension}`, { type: blob.type });

                const uploadedUrl = await uploadMediaFile(file, currentUser || 'goodlyfe');
                if (uploadedUrl) {
                    const updates = { loginBg: { url: uploadedUrl, style: bg.style } };
                    updateUserData((prev: Record<string, UserData>) => ({
                        ...prev,
                        [targetUserId]: { ...prev[targetUserId], ...updates }
                    }));
                    if (!isDemo) updateUserOnServer(targetUserId, updates).catch(console.error);
                }
            } catch (err) {
                console.error('Failed to upload login background:', err);
            }
        } else {
            const updates = { loginBg: bg };
            updateUserData((prev: Record<string, UserData>) => ({
                ...prev,
                [targetUserId]: { ...prev[targetUserId], ...updates }
            }));
            if (!isDemo) updateUserOnServer(targetUserId, updates).catch(console.error);
        }
    };

    const handleLoginBgModeChange = (mode: 'image' | 'color') => {
        const targetUserId = 'goodlyfe';
        const updates = { loginBgMode: mode };
        updateUserData((prev: Record<string, UserData>) => ({
            ...prev,
            [targetUserId]: { ...prev[targetUserId], ...updates }
        }));
        if (!isDemo) updateUserOnServer(targetUserId, updates).catch(console.error);
    };

    const handleLoginBgColorChange = (color: string) => {
        const targetUserId = 'goodlyfe';
        const updates = { loginBgColor: color };
        updateUserData((prev: Record<string, UserData>) => ({
            ...prev,
            [targetUserId]: { ...prev[targetUserId], ...updates }
        }));
        if (!isDemo) updateUserOnServer(targetUserId, updates).catch(console.error);
    };

    const handleLoginBgResolutionChange = (res: number) => {
        const targetUserId = 'goodlyfe';
        const updates = { loginBgResolution: res };
        updateUserData((prev: Record<string, UserData>) => ({
            ...prev,
            [targetUserId]: { ...prev[targetUserId], ...updates }
        }));
        if (!isDemo) updateUserOnServer(targetUserId, updates).catch(console.error);
    };

    const handleProfileUpdate = (updates: Partial<UserData> & { newUsername?: string }) => {
        const { newUsername, ...profileData } = updates;
        const oldUsername = currentUser;

        if (newUsername && newUsername !== oldUsername) {
            // Username Change Logic
            const lastChange = new Date(usersData[oldUsername].lastUsernameChange || 0);
            const now = new Date();
            const diffDays = (now.getTime() - lastChange.getTime()) / (1000 * 3600 * 24);
            if (diffDays < 7) {
                alert("You can only change your username once a week.");
                return;
            }
            if (usersData[newUsername]) {
                alert("Username is already taken.");
                return;
            }

            // This is a complex state migration.
            // 1. Update usersData keys and follower/following lists
            const newUsersData = JSON.parse(JSON.stringify(usersData));
            const currentUserData = { ...newUsersData[oldUsername], ...profileData, lastUsernameChange: now.toISOString() };
            delete newUsersData[oldUsername];
            newUsersData[newUsername] = currentUserData;

            Object.values(newUsersData).forEach((user: any) => {
                user.following = user.following.map((u: string) => u === oldUsername ? newUsername : u);
                user.followers = user.followers.map((u: string) => u === oldUsername ? newUsername : u);
            });

            // 2. Update posts
            const newPosts = posts.map(p => {
                const updatedPost = { ...p };
                if (p.author === oldUsername) updatedPost.author = newUsername;
                updatedPost.comments = p.comments.map(c => c.author === oldUsername ? { ...c, author: newUsername } : c);
                return updatedPost;
            });

            // 3. Update conversations
            const newConversations = conversations.map(c => ({
                ...c,
                participants: c.participants.map(p => p === oldUsername ? newUsername : p),
                messages: c.messages.map(m => m.senderId === oldUsername ? { ...m, senderId: newUsername } : m)
            }));

            // 4. Atomically update all states
            updateUserData(newUsersData);
            setPosts(newPosts);
            setConversations(newConversations);

            // 5. Update current user session
            setCurrentUser(newUsername);
            localStorage.setItem('currentUser', newUsername);
            setPage({ type: 'profile', userId: newUsername });

        } else {
            // Simple profile data update
            updateUserData((prev: Record<string, UserData>) => ({
                ...prev,
                [currentUser]: { ...prev[currentUser], ...profileData }
            }));
            // Sync with Server - Disabled in Demo
            if (!isDemo) {
                updateUserOnServer(currentUser, profileData).catch(console.error);
            }
        }
        setIsSettingsModalOpen(false);
    };

    const handleSelectConversation = (conversationId: number) => {
        setActiveConversationId(conversationId);
        if (!currentUser) return;

        // Optimistically mark messages as read locally
        setConversations(prev => {
            return prev.map(c =>
                c.id === conversationId
                    ? { ...c, messages: (c.messages || []).map(m => m.senderId !== currentUser ? { ...m, isRead: true } : m) }
                    : c
            );
        });

        // Atomic update on server - Disabled in Demo
        if (!isDemo) {
            markConversationAsReadOnServer(conversationId, currentUser).catch(err => {
                console.error('Failed to mark conversation as read on server:', err);
            });
        }

        // Update page state and URL with username
        const convo = conversations.find(c => c.id === conversationId);
        if (convo) {
            if (convo.isYipYap) {
                setPage({ type: 'messages' });
                return;
            }
            const otherUser = convo.participants.find(u => u !== currentUser);
            if (otherUser) {
                setPage({ type: 'messages', username: otherUser });
            }
        }
    };

    const handleNavigateToYipYap = useCallback(async (config: { name: string, mode: 'broadcast' | 'anonymous' | 'forum', visibility: 'public' | 'open', groupIds?: string[] }) => {
        if (!currentUser) return;
        const { name, mode, visibility, groupIds } = config;

        try {
            // Always create a new YipYap channel with a unique ID
            const newConvo = await createYipYapOnServer({
                owner: currentUser,
                groupIds,
                name,
                mode,
                visibility,
                visibilityGroups: groupIds || []
            });

            setConversations((prev: any[]) => [...prev, newConvo]);

            // Navigate first to ensure visibility
            setPage({ type: 'messages' });
            window.history.pushState({}, '', '/messages');

            // Then select it
            setActiveConversationId(newConvo.id);
            if (!isDemo) {
                markConversationAsReadOnServer(newConvo.id, currentUser).catch(err => {
                    console.error('Failed to mark conversation as read on server:', err);
                });
            }
        } catch (err) {
            console.error("Failed to manage YipYap:", err);
            setPage({ type: 'messages' });
            window.history.pushState({}, '', '/messages');
        }
    }, [userConversations, currentUser, handleSelectConversation, setConversations]);

    const handleSelectCommunityChannel = useCallback((communityId: string, channelId: string) => {
        setPage({ type: 'community_detail', query: communityId, channelId });
    }, []);

    // Typing indicator helpers
    const setUserTyping = (conversationId: number, username: string, isTyping: boolean) => {
        setTypingUsers((prev: Record<number, string[]>) => {
            const list = new Set(prev[conversationId] || []);
            if (isTyping) list.add(username); else list.delete(username);
            return { ...prev, [conversationId]: Array.from(list) };
        });
    };

    const handleSendMessage = async (
        conversationId: number,
        text: string,
        replyTo?: { id: number, text: string, senderId: string } | null,
        attachments?: Attachment[],
        isNote?: boolean,
        isRecovering?: boolean,
        existingTempId?: number,
        sharedPostId?: number,
        sharedPostBgUrl?: string
    ) => {
        // 0. Pre-checks and ID preparation
        const tempId = existingTempId || nextMsgId();

        // Check for blocks
        const convo = conversations.find(c => c.id === conversationId);
        if (convo) {
            const recipientId = convo.participants.find(p => p !== currentUser);
            if (recipientId) {
                if (usersData[currentUser]?.blockedUsers?.includes(recipientId)) {
                    alert('You have blocked this user. Unblock them to send a message.');
                    return;
                }
                if (usersData[recipientId]?.blockedUsers?.includes(currentUser)) {
                    alert('You cannot message this user.');
                    return;
                }
            }
        }

        // 1. SAVE TO PERSISTENCE QUEUE IMMEDIATELY (if not already recovering)
        if (!isRecovering) {
            try {
                await savePendingMessage({
                    tempId,
                    conversationId,
                    text,
                    replyTo,
                    attachments, // IndexedDB can store File objects
                    isNote,
                    timestamp: Date.now(),
                    sharedPostId,
                    sharedPostBgUrl
                });
            } catch (err) {
                console.error('Failed to save message to persistence queue:', err);
            }
        }

        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const urls = text?.match(urlRegex);

        const performOptimisticUpdate = (msgs: Message[]) => {
            setConversations(prev => {
                return prev.map(c =>
                    c.id === conversationId
                        ? { ...c, messages: [...(c.messages || []), ...msgs] }
                        : c
                );
            });
        };

        const performServerSync = async (msgs: Message[]) => {
            if (!isDemo) {
                try {
                    await addMessageToConversationOnServer(conversationId, msgs);
                    // SUCCESS: Clear from persistence queue
                    await deletePendingMessage(tempId);
                } catch (err) {
                    console.error('Failed to add message on server:', err);
                }
            } else {
                // In demo mode, just clear it immediately as there's no server
                await deletePendingMessage(tempId);
            }
        };

        const processAttachments = async (atts?: Attachment[]): Promise<Attachment[]> => {
            if (!atts || atts.length === 0) return [];
            const processed: Attachment[] = [];
            for (const att of atts) {
                if ((att as any).file && currentUser) {
                    try {
                        const url = await uploadMediaFile((att as any).file, currentUser);
                        processed.push({ ...att, url });
                    } catch (err) {
                        console.error('Failed to upload attachment:', err);
                        processed.push(att);
                    }
                } else {
                    processed.push(att);
                }
            }
            return processed;
        };

        // 1. Prepare optimistic messages
        const optimisticMessages: Message[] = [];
        const baseId = tempId;

        if (text && text.trim() && attachments && attachments.length > 0) {
            // Mixed text and media
            const textMsg: Message = {
                id: baseId,
                isNote: isNote || undefined,
                senderId: isNote ? "goodlyfe" : currentUser,
                text,
                timestamp: new Date().toISOString(),
                isRead: false,
                replyTo: replyTo || undefined,
                sharedPostId,
                sharedPostBgUrl,
            };
            optimisticMessages.push(textMsg);

            attachments.forEach((att, idx) => {
                optimisticMessages.push({
                    id: baseId + idx + 1,
                    isNote: isNote || undefined,
                    senderId: currentUser,
                    text: '',
                    timestamp: new Date().toISOString(),
                    isRead: false,
                    media: [att],
                });
            });
        } else if (attachments && attachments.length > 0) {
            // Media only
            attachments.forEach((att, idx) => {
                optimisticMessages.push({
                    id: baseId + idx + 1,
                    isNote: isNote || undefined,
                    senderId: currentUser,
                    text: '',
                    timestamp: new Date().toISOString(),
                    isRead: false,
                    media: [att],
                });
            });
        } else {
            // Text only
            optimisticMessages.push({
                id: baseId,
                isNote: isNote || undefined,
                senderId: isNote ? 'goodlyfe' : currentUser,
                text: text || '',
                timestamp: new Date().toISOString(),
                isRead: isNote ? true : false,
                replyTo: replyTo || undefined,
                sharedPostId,
                sharedPostBgUrl,
            });
        }

        // 2. Resolve link previews for text messages if needed
        if (urls && urls.length > 0) {
            try {
                const preview = await getLinkPreview(urls[0]);
                optimisticMessages.forEach(m => {
                    if (m.text && m.text.includes(urls[0])) {
                        m.linkPreview = preview || undefined;
                    }
                });
            } catch (e) { }
        }

        // 3. APPLY OPTIMISTIC UPDATE IMMEDIATELY
        performOptimisticUpdate(optimisticMessages);

        // 4. PROCESS UPLOADS ASYNCHRONOUSLY
        const finalAttachments = await processAttachments(attachments);

        // 5. PREPARE FINAL MESSAGES FOR SERVER
        const serverMessages = optimisticMessages.map(m => {
            if (m.media && m.media.length > 0) {
                const updatedMedia = m.media.map(att => {
                    const match = finalAttachments.find(fa => fa.type === att.type && (fa as any).file === (att as any).file);
                    return match ? { ...att, url: match.url } : att;
                });
                return { ...m, media: updatedMedia };
            }
            return m;
        });

        // 6. SYNC TO SERVER
        await performServerSync(serverMessages);
    };
    const handleShareToGoodLyfe = async (params: {
        destination: 'profile' | 'community' | 'dm';
        communityId?: string;
        channelId?: string;
        recipientId?: string;
        text: string;
        media: MediaAttachment[];
    }) => {
        if (!currentUser) return;

        setIsShareToGoodLyfeModalOpen(false);
        setIncomingShareData(null);

        let finalMedia = [...params.media];
        try {
            for (let i = 0; i < finalMedia.length; i++) {
                const m = finalMedia[i];
                if (m.url.startsWith('http://localhost') || m.url.startsWith('capacitor://') || m.url.startsWith('content://') || m.url.startsWith('file://') || m.url.startsWith('blob:')) {
                    const response = await fetch(m.url);
                    const blob = await response.blob();
                    const extension = m.type === 'video' ? 'mp4' : m.type === 'audio' ? 'mp3' : 'jpg';
                    const file = new File([blob], `shared_${Date.now()}.${extension}`, { type: blob.type || (m.type === 'video' ? 'video/mp4' : 'image/jpeg') });

                    let processedBlob = file as Blob;
                    if (m.type === 'image') processedBlob = await compressImage(file).catch(() => file);
                    else if (m.type === 'video') processedBlob = await compressVideo(file).catch(() => file);
                    const processedFile = new File([processedBlob], file.name, { type: processedBlob.type });

                    const uploadedUrl = await uploadMediaFile(processedFile, currentUser);
                    finalMedia[i] = { ...m, url: uploadedUrl };
                }
            }
        } catch (e) {
            console.error('Failed to process and upload shared media:', e);
            alert('Failed to upload some shared media files.');
        }

        if (params.destination === 'dm' && params.recipientId) {
            // Find or start conversation first
            let convo = userConversations.find(c =>
                c.participants.includes(params.recipientId!) && c.participants.includes(currentUser)
            );

            if (!convo) {
                // We'll need to handle potential async creation, but for now let's hope it exists
                console.warn('[Share] Conversation not found for DM, attempting to start one');
                await handleStartConversation(params.recipientId!);
                convo = conversations.find(c =>
                    !c.isYipYap &&
                    c.participants.includes(params.recipientId!) && c.participants.includes(currentUser)
                );
            }

            if (convo) {
                handleSendMessage(convo.id, params.text, null, finalMedia as any);
            }

            // Navigate user to that DM conversation
            setPage({ type: 'messages', username: params.recipientId, tab: 'dm' });
        } else {
            // Post to profile or community
            handleCreatePost({
                type: finalMedia.length > 0 ? (finalMedia[0].type as PostType) : 'text',
                content: {
                    text: params.text,
                    media: finalMedia,
                },
                communityId: params.communityId,
                visibility: 'public',
            } as any);

            // Navigate user to community chat conversation if destination is community
            if (params.destination === 'community' && params.communityId) {
                setPage({
                    type: 'community_detail',
                    query: params.communityId,
                    channelId: params.channelId || 'general'
                });
            }
        }
    };
    const handleStartConversation = async (rawUserId: string) => {

        const userId = getUserKeyCaseInsensitive(rawUserId) || rawUserId;
        if (!currentUser) return;

        if (usersData[currentUser]?.blockedUsers?.includes(userId)) {
            alert('You have blocked this user. Unblock them to start a conversation.');
            return;
        }
        if (usersData[userId]?.blockedUsers?.includes(currentUser)) {
            alert('You cannot start a conversation with this user.');
            return;
        }

        try {
            if (isDemo) {
                const existingConversation = conversations.find(c =>
                    !c.isYipYap &&
                    c.participants.length === 2 &&
                    c.participants.includes(userId) &&
                    c.participants.includes(currentUser)
                );
                if (existingConversation) {
                    setActiveConversationId(existingConversation.id);
                    setPage({ type: 'messages', username: userId });
                } else {
                    const newConvo: Conversation = {
                        id: Date.now(),
                        participants: [currentUser, userId],
                        messages: []
                    };
                    setConversations(prev => [newConvo, ...prev]);
                    setActiveConversationId(newConvo.id);
                    setPage({ type: 'messages', username: userId });
                }
                return;
            }

            // Use the server to find or create the conversation safely
            const conversation = await startConversationOnServer([currentUser, userId]);
            // ... (rest of the try block)
            setConversations(prev => {
                const exists = prev.find(c => c.id === conversation.id);
                if (exists) {
                    return prev.map(c => c.id === conversation.id ? conversation : c);
                }
                return [conversation, ...prev];
            });

            setActiveConversationId(conversation.id);
            setPage({ type: 'messages', username: userId });
        } catch (err) {
            console.error('Failed to start conversation:', err);
            // Fallback for offline or error
            const existingConversation = conversations.find(c =>
                !c.isYipYap &&
                c.participants.length === 2 &&
                c.participants.includes(userId) &&
                c.participants.includes(currentUser)
            );
            if (existingConversation) {
                setActiveConversationId(existingConversation.id);
                setPage({ type: 'messages', username: userId });
            }
        }
    };

    // Award points and approve a pending completion once vouch threshold is met
    const awardCompletionForPost = (postId: number) => {
        const post = posts.find(p => p.id === postId);
        if (!post || !post.footprintDetails) return;
        const author = post.author;

        updateUserData((prev: Record<string, UserData>) => {
            const user = { ...prev[author] } as UserData;
            const todayStr = getFootprintCycleDate();

            // Find the completion entry created when the user posted proof
            const idx = (user.completedAchievements || []).findIndex(c => c.postId === postId);
            if (idx === -1) {
                // If no record exists, insert an approved completion
                const achievementId = post.footprintDetails!.achievementId;
                user.completedAchievements = [...(user.completedAchievements || []), { achievementId, date: todayStr, postId, status: 'approved' }];
            } else {
                user.completedAchievements = user.completedAchievements.map((c) => c.postId === postId ? { ...c, status: 'approved' } : c);
            }

            /* Streak update disabled
            let newStreak = user.streak || 0;
            if (user.lastCompletionDate) {
                const lastDate = new Date(user.lastCompletionDate + 'T00:00:00');
                const currentDate = new Date(todayStr + 'T00:00:00');
                const diffTime = currentDate.getTime() - lastDate.getTime();
                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                if (diffDays === 1) {
                    newStreak += 1;
                } else if (diffDays > 1) {
                    newStreak = 1;
                }
            } else {
                newStreak = 1;
            }

            user.streak = newStreak;
            */
            user.lastCompletionDate = todayStr;

            const achievement = achievements.find(a => a.id === post.footprintDetails!.achievementId);
            let pointsAwarded = 0;
            if (achievement) {
                // const streakMultiplier = 1 + (Math.min(user.streak || 0, 5) - 1) * 0.1;
                const streakMultiplier = 1; // Disabled

                // Check if this achievement is the Task of the Day
                const isTaskOfTheDay = dailyAchievements?.daily?.id === achievement.id;

                // isTimely was computed at post-creation time and stored on the post.
                // We just read it here — no need to recalculate timing at vouch time.
                const isTimely = post.footprintDetails!.isTimely ?? false;

                let pointsToAdd = isTaskOfTheDay
                    ? Math.round(achievement.points * streakMultiplier) + 50
                    : isTimely
                        ? Math.round(achievement.points * streakMultiplier)       // On time → full points
                        : Math.round(achievement.points * 0.2 * streakMultiplier); // Late → 20%

                user.totalPoints = (user.totalPoints || 0) + pointsToAdd;
                pointsAwarded = pointsToAdd;
            }

            // Sync with server - Disabled in Demo
            if (!isDemo) {
                updateUserOnServer(author, {
                    completedAchievements: user.completedAchievements,
                    // streak: user.streak, // Disabled
                    lastCompletionDate: user.lastCompletionDate,
                    totalPoints: user.totalPoints
                }).catch(console.error);
            }

            // If the user who completed the footprint is the current user, show them the verified points notification popup
            if (author === currentUser && achievement) {
                const newNotification: Notification = {
                    id: Date.now(),
                    type: 'system',
                    fromUser: 'goodlyfe',
                    text: `Footprint Verified: ${achievement.title}`,
                    message: `+${pointsAwarded} Lyfe pts!`,
                    timestamp: new Date().toISOString(),
                    read: false
                };
                setTimeout(() => {
                    setActiveLiveNotification(newNotification);
                }, 100);

                user.notifications = [newNotification, ...(user.notifications || [])];

                // Trigger native local push notification
                LocalNotifications.schedule({
                    notifications: [
                        {
                            id: achievement.id,
                            title: `Footprint Verified: ${achievement.title}`,
                            body: `+${pointsAwarded} Lyfe pts!`,
                            ongoing: false,
                            autoCancel: true,
                            extra: {
                                type: 'footprint'
                            }
                        }
                    ]
                }).catch(err => {
                    console.error('[LocalNotifications] Failed to schedule footprint verification notification:', err);
                });
            }

            return { ...prev, [author]: user };
        });
    };

    const handleReportPost = (postId: number) => {
        if (!currentUser) return;
        const currentReports = usersData[currentUser]?.reportedPosts || [];
        if (currentReports.includes(postId)) return;

        const updatedReports = [...currentReports, postId];
        updateUserData((prev: Record<string, UserData>) => ({
            ...prev,
            [currentUser]: { ...prev[currentUser], reportedPosts: updatedReports }
        }));

        // In a real app, call API
        updateUserOnServer(currentUser, { reportedPosts: updatedReports }).catch(console.error);

        console.log(`[MainApp] Post ${postId} reported by ${currentUser}`);
        setFlashMessage("Post reported. It will be hidden from your feed.");
        setTimeout(() => setFlashMessage(null), 3000);
    };

    // Add or remove a vouch (endorsement) for a post from the current user.
    const handleAddVouch = (postId: number) => {
        if (!currentUser) return;
        const post = posts.find(p => p.id === postId);
        if (!post) return;

        if (post.author === currentUser) {
            alert("You can't vouch for your own post.");
            return;
        }

        const existing = post.vouches || [];
        const already = existing.find(v => v.userId === currentUser);

        let updatedPost: Post;

        if (already) {
            // remove vouch
            const newVouches = existing.filter(v => v.userId !== currentUser);
            updatedPost = { ...post, vouches: newVouches } as Post;
        } else {
            const userMsg = usersData[currentUser]?.vouchMessage || 'I vouch for this!';
            const vouch = { userId: currentUser, message: userMsg, timestamp: new Date().toISOString() };
            const newVouches = [...existing, vouch];
            updatedPost = { ...post, vouches: newVouches } as Post;

            // Check for achievement completion (side effect, no persistence needed for this logic itself as it calls awardCompletion)
            setTimeout(() => {
                const distinct = new Set(newVouches.map(v => v.userId).filter(u => u !== post.author));
                if (distinct.size >= 3) {
                    // Award logic
                    awardCompletionForPost(postId);
                    alert(`Post ${postId} has received ${distinct.size} vouches — awarding points.`);
                }
            }, 0);
        }

        setPosts(prev => prev.map(p => p.id === postId ? updatedPost : p));

        // Persist to Server - Disabled in Demo
        if (!isDemo) {
            updatePostOnServer(postId, { vouches: updatedPost.vouches }).catch(console.error);
        }

        // Notify and update author points locally for immediate feedback
        if (post.author !== currentUser) {
            updateUserData((prevData: Record<string, UserData>) => {
                const authorData = prevData[post.author];
                if (!authorData) return prevData;

                const newPoints = already
                    ? Math.max(0, (authorData.totalPoints || 0) - 1)
                    : (authorData.totalPoints || 0) + 1;

                let newNotifs = authorData.notifications || [];
                if (!already && (authorData.notificationSettings?.vouches !== false)) {
                    newNotifs = [
                        ...newNotifs,
                        {
                            id: Date.now() + Math.random(),
                            type: 'vouch',
                            fromUser: currentUser,
                            postId: postId,
                            timestamp: new Date().toISOString(),
                            read: false
                        } as any
                    ];
                }

                return {
                    ...prevData,
                    [post.author]: {
                        ...authorData,
                        totalPoints: newPoints,
                        notifications: newNotifs
                    }
                };
            });
        }
    };
    // ========= RENDER =========
    // Leaderboard modal route
    const openLeaderboard = () => {
        window.history.pushState({}, '', '/leaderboard');
        // Open leaderboard modal from FootprintPage
        setTimeout(() => {
            const ev = new CustomEvent('app.openLeaderboard');
            window.dispatchEvent(ev);
        }, 0);
    };

    const handleGrantAdmin = (targetUserId: string) => {
        if (!currentUser || usersData[currentUser]?.role !== 'admin') return;

        // Disabled in Demo
        if (isDemo) {
            alert('Admin functions are disabled in Play Around mode.');
            return;
        }

        const targetUser = usersData[targetUserId];
        const isTargetAdmin = targetUser?.role === 'admin';
        const action = isTargetAdmin ? 'revoke admin privileges from' : 'grant admin privileges to';

        if (window.confirm(`Are you sure you want to ${action} ${targetUserId}?`)) {
            updateUserData((prevData: Record<string, UserData>) => {
                const newUsersData = JSON.parse(JSON.stringify(prevData));
                if (newUsersData[targetUserId]) {
                    newUsersData[targetUserId].role = isTargetAdmin ? 'user' : 'admin';
                    updateUserOnServer(targetUserId, { role: newUsersData[targetUserId].role }).catch(console.error);
                }
                return newUsersData;
            });
            alert(`${targetUserId} is ${isTargetAdmin ? 'no longer' : 'now'} an admin.`);
        }
    };

    const handleDeleteUser = async (targetUserId: string) => {
        if (!currentUser || usersData[currentUser]?.role !== 'admin') return;

        // Disabled in Demo
        if (isDemo) {
            alert('Admin functions are disabled in Play Around mode.');
            return;
        }

        try {
            await deleteUserOnServer(targetUserId, currentUser);

            // Update local state
            updateUserData((prevData: Record<string, UserData>) => {
                const newData = { ...prevData };
                delete newData[targetUserId];
                // Remove from everyone's follow lists
                Object.keys(newData).forEach(uid => {
                    if (newData[uid]) {
                        newData[uid].following = (newData[uid].following || []).filter((id: string) => id !== targetUserId);
                        newData[uid].followers = (newData[uid].followers || []).filter((id: string) => id !== targetUserId);
                        newData[uid].followerRequests = (newData[uid].followerRequests || []).filter((id: string) => id !== targetUserId);
                    }
                });
                return newData;
            });

            // Filter out posts
            setPosts(prev => prev.filter(p => p.author !== targetUserId));

            // Filter out conversations
            setConversations(prev => prev.filter(c => !c.participants.includes(targetUserId)));

            alert(`User ${targetUserId} has been permanently deleted.`);
            if (page.type === 'profile' && page.userId === targetUserId) {
                setPage({ type: 'home' });
            }
        } catch (error: any) {
            console.error("Failed to delete user details:", error);
            if (error.response?.status === 404) {
                alert(`User ${targetUserId} was not found on the server, but cleaning up local state anyway.`);
                // Still update local state even if server says 404
                updateUserData((prevData: Record<string, UserData>) => {
                    const newData = { ...prevData };
                    delete newData[targetUserId];
                    return newData;
                });
            } else {
                alert(`Failed to delete user: ${error.response?.data?.error || error.message}`);
            }
        }
    };

    const handleDeactivateAccount = async (password: string) => {
        if (!currentUser) return;
        try {
            await deactivateAccount(currentUser, password);
            alert('Your account has been deactivated. You can reactivate it by logging in again.');
            logout();
        } catch (error: any) {
            alert(`Failed to deactivate account: ${error.response?.data?.error || error.message}`);
        }
    };

    const handleDeleteAccount = async (password: string) => {
        if (!currentUser) return;
        if (window.confirm('Are you sure you want to PERMANENTLY delete your account? This cannot be undone.')) {
            try {
                await deleteAccountPermanently(currentUser, password);
                alert('Your account has been permanently deleted.');
                logout();
            } catch (error: any) {
                alert(`Failed to delete account: ${error.response?.data?.error || error.message}`);
            }
        }
    };

    const handleUnblockUser = useCallback(async (targetId: string) => {
        if (!currentUser || !updateUserData) return;
        updateUserData((prev: Record<string, UserData>) => {
            const u = { ...prev[currentUser] };
            u.blockedUsers = (u.blockedUsers || []).filter((id: string) => id !== targetId);
            return { ...prev, [currentUser]: u };
        });
        try { await updateUserOnServer(currentUser, { blockedUsers: (usersData[currentUser].blockedUsers || []).filter((id: string) => id !== targetId) }); } catch (e) { console.error(e); }
    }, [currentUser, updateUserData, usersData]);

    const handleUnmuteUser = useCallback(async (targetId: string) => {
        if (!currentUser || !updateUserData) return;
        updateUserData((prev: Record<string, UserData>) => {
            const u = { ...prev[currentUser] };
            u.mutedUsers = (u.mutedUsers || []).filter((id: string) => id !== targetId);
            return { ...prev, [currentUser]: u };
        });
        try { await updateUserOnServer(currentUser, { mutedUsers: (usersData[currentUser].mutedUsers || []).filter((id: string) => id !== targetId) }); } catch (e) { console.error(e); }
    }, [currentUser, updateUserData, usersData]);

    /**
     * handleFriendToggle - The main social graph action handler.
     *
     * State machine:
     * 1. Not connected → Send friend request
     *    - If target has `allowFollowers` ON: sender is added to target's `followers` list
     *      (pending auto-follow), and a `friend_request` notification is sent.
     *    - If target has `allowFollowers` OFF: only a `friend_request` notification is sent.
     * 2. Request pending → Cancel request (remove from friendRequests + followers if applicable)
     * 3. Already friends → Unfriend (remove from both users' `friends` lists)
     */
    const handleFriendToggle = (rawTargetUserId: string, forceRemove: boolean = false) => {
        const targetUserId = getUserKeyCaseInsensitive(rawTargetUserId) || rawTargetUserId;
        if (!currentUser) return;

        if (usersData[currentUser]?.blockedUsers?.includes(targetUserId)) {
            alert('You have blocked this user.');
            return;
        }
        if (usersData[targetUserId]?.blockedUsers?.includes(currentUser)) {
            alert('You cannot connect with this user.');
            return;
        }

        const isFriend = (usersData[currentUser]?.friends || []).includes(targetUserId);
        const isRequested = (usersData[targetUserId]?.friendRequests || []).includes(currentUser);

        if (!forceRemove) {
            if (isFriend) {
                setRemoveConnectionModalState({ isOpen: true, targetId: targetUserId, type: 'friend' });
                return;
            } else if (isRequested) {
                setRemoveConnectionModalState({ isOpen: true, targetId: targetUserId, type: 'request' });
                return;
            } else {
                if (targetUserId.toLowerCase() === 'goodlyfe') {
                    alert('You are now friends with @goodlyfe!');
                } else {
                    alert('Friend request sent!');
                }
            }
        }

        updateUserData((prevData: Record<string, UserData>) => {
            const newUsersData = JSON.parse(JSON.stringify(prevData));
            const currentUserData = newUsersData[currentUser];
            const targetUserData = newUsersData[targetUserId];

            if (!currentUserData || !targetUserData) return prevData;

            const isFriend = (currentUserData.friends || []).includes(targetUserId);
            const isRequested = (targetUserData.friendRequests || []).includes(currentUser);

            if (isFriend) {
                // --- UNFRIEND ---
                currentUserData.friends = (currentUserData.friends || []).filter((id: string) => id !== targetUserId);
                targetUserData.friends = (targetUserData.friends || []).filter((id: string) => id !== currentUser);

                // Also clean up any follower residue
                currentUserData.following = (currentUserData.following || []).filter((id: string) => id !== targetUserId);
                targetUserData.followers = (targetUserData.followers || []).filter((id: string) => id !== currentUser);
                targetUserData.following = (targetUserData.following || []).filter((id: string) => id !== currentUser);
                currentUserData.followers = (currentUserData.followers || []).filter((id: string) => id !== targetUserId);

                // Remove friend notification from target
                if (targetUserData.notifications) {
                    targetUserData.notifications = targetUserData.notifications.filter((n: any) =>
                        !((n.type === 'friend' || n.type === 'friend_request' || n.type === 'friend_accepted') && n.fromUser === currentUser)
                    );
                }

                if (!isDemo) {
                    updateUserOnServer(currentUser, {
                        friends: currentUserData.friends,
                        following: currentUserData.following,
                        followers: currentUserData.followers
                    }).catch(console.error);
                    updateUserOnServer(targetUserId, {
                        friends: targetUserData.friends,
                        following: targetUserData.following,
                        followers: targetUserData.followers,
                        notifications: targetUserData.notifications
                    }).catch(console.error);
                }

            } else if (isRequested) {
                // --- CANCEL REQUEST ---
                targetUserData.friendRequests = (targetUserData.friendRequests || []).filter((id: string) => id !== currentUser);

                // If allowFollowers caused a pending follow, remove it too
                targetUserData.followers = (targetUserData.followers || []).filter((id: string) => id !== currentUser);
                currentUserData.following = (currentUserData.following || []).filter((id: string) => id !== targetUserId);

                // Remove friend_request notification from target
                if (targetUserData.notifications) {
                    targetUserData.notifications = targetUserData.notifications.filter((n: any) =>
                        !(n.type === 'friend_request' && n.fromUser === currentUser)
                    );
                }

                if (!isDemo) {
                    updateUserOnServer(currentUser, { following: currentUserData.following }).catch(console.error);
                    updateUserOnServer(targetUserId, {
                        friendRequests: targetUserData.friendRequests,
                        followers: targetUserData.followers,
                        notifications: targetUserData.notifications
                    }).catch(console.error);
                }

            } else {
                // --- SEND FRIEND REQUEST ---
                if (targetUserId.toLowerCase() === 'goodlyfe') {
                    // Auto-accept for @goodlyfe
                    if (!targetUserData.friends) targetUserData.friends = [];
                    if (!currentUserData.friends) currentUserData.friends = [];

                    if (!targetUserData.friends.includes(currentUser)) {
                        targetUserData.friends.push(currentUser);
                    }
                    if (!currentUserData.friends.includes(targetUserId)) {
                        currentUserData.friends.push(targetUserId);
                    }

                    // Remove from followers/following if any
                    targetUserData.followers = (targetUserData.followers || []).filter((id: string) => id !== currentUser);
                    currentUserData.following = (currentUserData.following || []).filter((id: string) => id !== targetUserId);

                    // Send friend_accepted to current user
                    currentUserData.notifications = currentUserData.notifications || [];
                    currentUserData.notifications.push({
                        id: Date.now() + Math.floor(Math.random() * 1000),
                        type: 'friend_accepted' as NotificationType,
                        fromUser: targetUserId,
                        timestamp: new Date().toISOString(),
                        read: false
                    });

                    // Send friend to goodlyfe
                    targetUserData.notifications = targetUserData.notifications || [];
                    targetUserData.notifications.push({
                        id: Date.now() + Math.floor(Math.random() * 1000) + 1,
                        type: 'friend' as NotificationType,
                        fromUser: currentUser,
                        timestamp: new Date().toISOString(),
                        read: false
                    });

                    if (!isDemo) {
                        updateUserOnServer(currentUser, {
                            friends: currentUserData.friends,
                            following: currentUserData.following,
                            notifications: currentUserData.notifications
                        }).catch(console.error);

                        updateUserOnServer(targetUserId, {
                            friends: targetUserData.friends,
                            followers: targetUserData.followers,
                            notifications: targetUserData.notifications
                        }).catch(console.error);
                    }
                } else {
                    targetUserData.friendRequests = targetUserData.friendRequests || [];
                    if (!targetUserData.friendRequests.includes(currentUser)) {
                        targetUserData.friendRequests.push(currentUser);
                    }

                    // If allowFollowers is ON: sender auto-follows target (pending approval)
                    const allowFollowers = targetUserData.allowFollowers !== false; // default true
                    if (allowFollowers) {
                        if (!currentUserData.following) currentUserData.following = [];
                        if (!targetUserData.followers) targetUserData.followers = [];
                        if (!currentUserData.following.includes(targetUserId)) {
                            currentUserData.following.push(targetUserId);
                        }
                        if (!targetUserData.followers.includes(currentUser)) {
                            targetUserData.followers.push(currentUser);
                        }
                    }

                    // Send friend_request notification to target
                    targetUserData.notifications = targetUserData.notifications || [];
                    targetUserData.notifications.push({
                        id: Date.now() + Math.floor(Math.random() * 1000),
                        type: 'friend_request' as NotificationType,
                        fromUser: currentUser,
                        timestamp: new Date().toISOString(),
                        read: false
                    });

                    if (!isDemo) {
                        updateUserOnServer(currentUser, { following: currentUserData.following }).catch(console.error);
                        updateUserOnServer(targetUserId, {
                            friendRequests: targetUserData.friendRequests,
                            followers: targetUserData.followers,
                            notifications: targetUserData.notifications
                        }).catch(console.error);
                    }
                }
            }

            return newUsersData;
        });
    };

    // Legacy alias so any remaining callers still work
    const handleFollowToggle = handleFriendToggle;


    const handleShowFollowList = (listType: 'followers' | 'following' | 'friends' | 'connections', userId: string) => {
        setFollowListModalState({ isOpen: true, listType, userId });
        pushModalState('follow_list');
    };

    const handleCompleteAndPostAchievement = (achievement: Achievement, postData: {
        type: PostType;
        content: Post['content'];
        visibility?: 'public' | 'followers' | 'groups' | 'private';
        visibilityGroups?: string[];
        showCaption?: boolean;
        showOnProfile?: boolean;
        textSize?: 'xs' | 'sm' | 'med' | 'lg';
        communityId?: string;
        themeOpacity?: number;
        themeColor?: string;
        textColor?: string;
        isOptimistic?: boolean;
        localId?: number;
        fontId?: string;
        postcardStyle?: string;
        postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall'
    }) => {
        const todayStr = getFootprintCycleDate();

        // Determine timely status now, at post creation — not later at vouch time.
        // This is the only moment we know for certain whether the user posted within
        // the current footprint cycle window (noon ET → noon ET next day).
        const isTimely = wasPostMadeInFootprintWindow(new Date().toISOString());

        const footprintDetails = {
            achievementId: achievement.id,
            achievementTitle: achievement.title,
            achievementDifficulty: achievement.difficulty,
            isTimely,
        };

        const newPost = handleCreatePost(postData, footprintDetails);

        if (!newPost) return;

        // When posting an achievement proof, require vouches from peers before awarding points.
        // Mark the completion as 'pending' so it can be approved once enough vouches are received.
        updateUserData((prevData: Record<string, UserData>) => {
            const data = { ...prevData[currentUser] };

            const newCompletion: CompletedAchievement = {
                achievementId: achievement.id,
                date: todayStr,
                postId: newPost.id,
                status: 'pending', // Require endorsements before approval
            };
            data.completedAchievements = [...(data.completedAchievements ?? []), newCompletion];

            return { ...prevData, [currentUser]: data };
        });
    };

    const handleSetDailyAchievement = (daily: Achievement) => {
        setDailyAchievements(prev => ({ ...prev, daily }));
    };

    const handleAddAchievement = (newAchievement: Omit<Achievement, 'id'>) => {
        const tempId = Date.now();
        const optimisticAch = { ...newAchievement, id: tempId } as Achievement;
        setAchievements(prev => [...prev, optimisticAch]);

        if (!isDemo) {
            createAchievementOnServer(newAchievement)
                .then(created => {
                    if (created && created.id) {
                        setAchievements(prev => prev.map(a => a.id === tempId ? created : a));
                    }
                })
                .catch(err => {
                    console.error(err);
                });
        }
    };

    const handleReorderAchievements = (reordered: Achievement[]) => {
        setAchievements(reordered);
        if (!isDemo) {
            const orders = reordered.map((ach, index) => ({ id: ach.id, order: index }));
            reorderAchievementsOnServer(orders).catch(console.error);
        }
    };

    const handleDeleteAchievement = (achievementId: number) => {
        setAchievements(prev => prev.filter(a => a.id !== achievementId));
        // Also remove from daily achievements if it was selected
        setDailyAchievements(prev => {
            const updated = { ...prev };
            if (updated.daily?.id === achievementId) updated.daily = null;
            // if (updated.medium?.id === achievementId) updated.medium = null;
            // if (updated.hard?.id === achievementId) updated.hard = null;
            return updated;
        });
        // Sync with Server - Disabled in Demo
        if (!isDemo) {
            deleteAchievementOnServer(achievementId).catch(console.error);
        }
    };

    const handleEditAchievement = (updatedAchievement: Achievement) => {
        setAchievements(prev => prev.map(a => a.id === updatedAchievement.id ? updatedAchievement : a));
        // Update in daily achievements if it was selected
        setDailyAchievements(prev => {
            const updated = { ...prev };
            if (updated.daily?.id === updatedAchievement.id) updated.daily = updatedAchievement;
            //if (updated.medium?.id === updatedAchievement.id) updated.medium = updatedAchievement;
            //if (updated.hard?.id === updatedAchievement.id) updated.hard = updatedAchievement;
            return updated;
        });
        // Sync with Server - Disabled in Demo
        if (!isDemo) {
            updateAchievementOnServer(updatedAchievement.id, updatedAchievement).catch(console.error);
        }
    };

    useEffect(() => {
        if (!selectedPost || selectedPost.linkRequestDetails?.isLinkRequest) return;

        const freshPost = postsById.get(selectedPost.id);
        if (!freshPost) {
            // Post was deleted, close modal
            handleCloseModal();
            return;
        }

        const hydratedVersion = getHydratedPosts([freshPost])[0];

        // Only update selectedPost if something meaningful changed to avoid infinite loops
        try {
            const prevStr = JSON.stringify(selectedPost);
            const nextStr = JSON.stringify(hydratedVersion);
            if (prevStr !== nextStr) {
                setSelectedPost(hydratedVersion);
            }
        } catch (err) {
            // Fallback: if stringify fails, do a shallow id check
            if (selectedPost.id !== hydratedVersion.id) {
                setSelectedPost(hydratedVersion);
            }
        }
    }, [posts, postsById, selectedPost, getHydratedPosts]);

    const homePagePosts = useMemo(() => {
        if (!currentUser) return [];

        if (currentUser === 'guest') {
            const publicPosts = posts.filter(p => {
                if (p.isArchived) return false;
                if (p.communityId) return false; // Hide community posts from home feed for guest users
                if (homeFeedMode === 'public') {
                    return p.visibility === 'public';
                }
                // Default guest view: only vibeman1
                return p.author === 'vibeman1' && p.visibility === 'public';
            });
            return getHydratedPosts(publicPosts);
        }

        if (!usersData[currentUser]) return [];

        const following = usersData[currentUser].following || [];
        const friends = usersData[currentUser].friends || [];
        const usersForFeed = new Set([...following, ...friends, currentUser]);

        const filteredPosts = posts.filter(post => {
            // FILTER BY MODE/TAB
            if (homeFeedMode === 'following') {
                if (!usersForFeed.has(post.author)) return false;
            } else if (homeFeedMode === 'public') {
                if (post.visibility !== 'public') return false;
            } else {
                // FOLLOWER GROUP TAB
                const groups = usersData[currentUser]?.followerGroups || [];
                const activeGroup = groups.find((g: FollowerGroup) => g.id === homeFeedMode);
                if (activeGroup) {
                    const groupUserIds = new Set(activeGroup.followers || []);
                    if (!groupUserIds.has(post.author)) return false;
                } else {
                    // Fallback to following if group not found
                    if (!usersForFeed.has(post.author)) return false;
                }
            }

            // GLOBAL FILTERS (Apply to all modes)
            if (!isPostVisible(post)) return false;
            if (post.isArchived) return false;

            const currentUserData = usersData[currentUser];
            if (currentUserData) {
                if (currentUserData.blockedUsers?.includes(post.author)) return false;
                if (currentUserData.mutedUsers?.includes(post.author)) return false;
                if (currentUserData.reportedPosts?.includes(post.id)) return false;
            }

            if (post.communityId) {
                if (!usersData[currentUser]?.showCommunityPostsOnHomeFeed) return false;
                const matchedCommunity = communities.find((c: Community) => c._id === post.communityId);
                if (!matchedCommunity || !matchedCommunity.members?.includes(currentUser)) return false;
            }

            return true;
        });

        // Ensure failed posts stay at the top of the feed grid/list
        const sortedPosts = [...filteredPosts].sort((a, b) => {
            if (a.uploadFailed && !b.uploadFailed) return -1;
            if (!a.uploadFailed && b.uploadFailed) return 1;
            // Otherwise maintain default chronological order (newer first)
            return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        });

        return getHydratedPosts(sortedPosts);
    }, [posts, currentUser, usersData, getHydratedPosts, communities, isPostVisible, homeFeedMode]);



    const currentUserData = usersData[currentUser];
    // user-specific UI preferences (stored on the user record)
    const postBlurOpacity = currentUserData?.postBlurOpacity ?? 50;
    const appThemeColor = currentUserData?.appThemeColor ?? '#161b22';
    const appTextColor = currentUserData?.appTextColor ?? '#ffffff';
    
    const isDarkAppTextColor = useMemo(() => {
        let r = 255, g = 255, b = 255;
        try {
            const cleanColor = (appTextColor || '').trim().toLowerCase();
            if (cleanColor.startsWith('#')) {
                let hex = cleanColor.replace('#', '');
                if (hex.length === 3) {
                    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
                } else if (hex.length < 6) {
                    hex = hex.padEnd(6, '0');
                } else if (hex.length > 6) {
                    hex = hex.substring(0, 6);
                }
                const parsedR = parseInt(hex.substring(0, 2), 16);
                const parsedG = parseInt(hex.substring(2, 4), 16);
                const parsedB = parseInt(hex.substring(4, 6), 16);
                if (!isNaN(parsedR)) r = parsedR;
                if (!isNaN(parsedG)) g = parsedG;
                if (!isNaN(parsedB)) b = parsedB;
            } else if (cleanColor.startsWith('rgb')) {
                const matches = cleanColor.match(/\d+/g);
                if (matches && matches.length >= 3) {
                    const parsedR = parseInt(matches[0]);
                    const parsedG = parseInt(matches[1]);
                    const parsedB = parseInt(matches[2]);
                    if (!isNaN(parsedR)) r = parsedR;
                    if (!isNaN(parsedG)) g = parsedG;
                    if (!isNaN(parsedB)) b = parsedB;
                }
            } else if (cleanColor === 'black' || cleanColor === 'transparent') {
                r = 0; g = 0; b = 0;
            }
        } catch (e) {}
        const brightness = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
        return isNaN(brightness) ? false : brightness < 127.5;
    }, [appTextColor]);

    // Convert hex to RGB for use in rgba() CSS
    const themeRgb = useMemo(() => {
        try {
            const color = appThemeColor?.startsWith('#') ? appThemeColor : '#161b22';
            let hex = color.replace('#', '');
            if (hex.length === 3) {
                hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
            }
            if (hex.length !== 6) return "22, 27, 34"; // default #161b22 RGB
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            if (isNaN(r) || isNaN(g) || isNaN(b)) return "22, 27, 34";
            return `${r}, ${g}, ${b}`;
        } catch (e) {
            return "22, 27, 34";
        }
    }, [appThemeColor]);

    const setPostBlurOpacity = (val: number) => {
        if (!currentUser || !updateUserData) return;
        updateUserData((prevData: Record<string, UserData>) => ({ ...prevData, [currentUser]: { ...prevData[currentUser], postBlurOpacity: val } }));
        // Sync with Server - Disabled in Demo
        if (!isDemo) {
            updateUserOnServer(currentUser, { postBlurOpacity: val }).catch(console.error);
        }
    };
    const galleryAnimation = currentUserData?.galleryAnimation ?? 'fade';
    const setGalleryAnimation = (val: string) => {
        if (!currentUser || !updateUserData) return;
        updateUserData((prevData: Record<string, UserData>) => ({ ...prevData, [currentUser]: { ...prevData[currentUser], galleryAnimation: val } }));
        // Sync with Server - Disabled in Demo
        if (!isDemo) {
            updateUserOnServer(currentUser, { galleryAnimation: val }).catch(console.error);
        }
    };
    const uiScale = currentUserData?.uiScale ?? 1.0;
    const setUiScale = (val: number) => {
        if (!currentUser || !updateUserData) return;
        updateUserData((prevData: Record<string, UserData>) => ({ ...prevData, [currentUser]: { ...prevData[currentUser], uiScale: val } }));
        if (!isDemo) {
            updateUserOnServer(currentUser, { uiScale: val }).catch(console.error);
        }
    };

    // Apply UI Scale globally to the root element
    useEffect(() => {
        document.documentElement.style.setProperty('--ui-scale', (uiScale * 0.9).toString());
    }, [uiScale]);

    const handleNavigateToHashtag = (tag: string) => {
        setSelectedPost(null);
        setIsSettingsModalOpen(false);
        setPage({ type: 'hashtag', tag });
        window.scrollTo(0, 0);
    };
    const handleSwipePost = useCallback((direction: 'left' | 'right', currentPostId: number) => {
        let contextPosts: HydratedPost[] = swipeContextRef.current || [];

        if (contextPosts.length === 0) {
            if (backgroundPage) {
                if (backgroundPage.type === 'home') {
                    contextPosts = homePagePosts;
                } else if (backgroundPage.type === 'communities') {
                    contextPosts = getHydratedPosts(posts.filter(p => isPostVisible(p) && !usersData[currentUser || '']?.reportedPosts?.includes(p.id)));
                } else if (backgroundPage.type === 'profile') {
                    contextPosts = getHydratedPosts(posts.filter(p => p.author === backgroundPage.userId && isPostVisible(p)));
                } else if (backgroundPage.type === 'hashtag') {
                    contextPosts = getHydratedPosts(posts.filter(p => {
                        if (p.isArchived) return false;
                        if ((p as any).communityId) return false;
                        if (usersData[currentUser || '']?.reportedPosts?.includes(p.id)) return false;
                        if (!isPostVisible(p)) return false;
                        let hashtags = p.hashtags;
                        if ((!hashtags || hashtags.length === 0) && p.content.text) {
                            hashtags = Array.from((p.content.text.match(/#([a-zA-Z0-9_\-]+)/gi) || []).map(t => t.slice(1)));
                        }
                        return hashtags && hashtags.some(ht => ht.toLowerCase() === backgroundPage.tag.toLowerCase());
                    }));
                } else if (backgroundPage.type === 'community_detail') {
                    contextPosts = getHydratedPosts(posts.filter(p => p.communityId === backgroundPage.query && isPostVisible(p)));
                } else if (backgroundPage.type === 'footprint' || (backgroundPage.type === 'mod' && backgroundPage.modId === 'vibeman1_footprint')) {
                    // Filter same as FootprintPage:
                    let userPosts = posts.filter(p => !p.isArchived && p.footprintDetails && !p.uploadFailed && !usersData[currentUser || '']?.reportedPosts?.includes(p.id));
                    if (lastFootprintTaskId) {
                        userPosts = userPosts.filter(post => String(post.footprintDetails?.achievementId) === String(lastFootprintTaskId));
                    }

                    // Sub-filter by tab (today, week, connections, all)
                    if (footprintFeedMode === 'today') {
                        const todayStr = getFootprintCycleDate();
                        const getPostCycleDate = (createdAtStr: string | undefined): string => {
                            if (!createdAtStr) return '';
                            try {
                                const date = new Date(createdAtStr);
                                const etStr = date.toLocaleString("en-US", { timeZone: "America/New_York" });
                                const etDate = new Date(etStr);
                                const hour = etDate.getHours();
                                const cycleDate = new Date(etDate);
                                if (hour < 12) cycleDate.setDate(cycleDate.getDate() - 1);
                                return cycleDate.getFullYear() + '-' +
                                    String(cycleDate.getMonth() + 1).padStart(2, '0') + '-' +
                                    String(cycleDate.getDate()).padStart(2, '0');
                            } catch (e) { return ''; }
                        };
                        contextPosts = getHydratedPosts(userPosts.filter(p => getPostCycleDate(p.createdAt) === todayStr));
                    } else if (footprintFeedMode === 'week') {
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        const weekAgoStr = weekAgo.toISOString().slice(0, 10);
                        const getPostCycleDate = (createdAtStr: string | undefined): string => {
                            if (!createdAtStr) return '';
                            try {
                                const date = new Date(createdAtStr);
                                const etStr = date.toLocaleString("en-US", { timeZone: "America/New_York" });
                                const etDate = new Date(etStr);
                                const hour = etDate.getHours();
                                const cycleDate = new Date(etDate);
                                if (hour < 12) cycleDate.setDate(cycleDate.getDate() - 1);
                                return cycleDate.getFullYear() + '-' +
                                    String(cycleDate.getMonth() + 1).padStart(2, '0') + '-' +
                                    String(cycleDate.getDate()).padStart(2, '0');
                            } catch (e) { return ''; }
                        };
                        contextPosts = getHydratedPosts(userPosts.filter(p => getPostCycleDate(p.createdAt) >= weekAgoStr));
                    } else if (footprintFeedMode === 'connections') {
                        const cur = usersData[currentUser || ''];
                        if (!cur) {
                            contextPosts = [];
                        } else {
                            const set = new Set<string>();
                            (cur.following || []).forEach((u: string) => set.add(u.toLowerCase()));
                            (cur.followers || []).forEach((u: string) => set.add(u.toLowerCase()));
                            contextPosts = getHydratedPosts(userPosts.filter(p => set.has(p.author.toLowerCase())));
                        }
                    } else {
                        contextPosts = getHydratedPosts(userPosts);
                    }
                }
            } else {
                const currentPost = posts.find(p => p.id === currentPostId);
                if (currentPost) {
                    contextPosts = getHydratedPosts(posts.filter(p => p.author === currentPost.author && isPostVisible(p)));
                }
            }
        }

        const currentIndex = contextPosts.findIndex(p => p.id.toString() === currentPostId.toString());
        if (currentIndex === -1) return;

        let nextIndex = currentIndex;
        if (direction === 'left') {
            if (currentIndex < contextPosts.length - 1) {
                nextIndex = currentIndex + 1;
            }
        } else if (direction === 'right') {
            if (currentIndex > 0) {
                nextIndex = currentIndex - 1;
            }
        }

        if (nextIndex !== currentIndex) {
            const nextPost = contextPosts[nextIndex];
            isSwipingPostRef.current = true;
            setPage({ type: 'post', postId: nextPost.id.toString(), fromLink: page.type === 'post' ? (page as any).fromLink : false });
            setSelectedPost(nextPost);
        }
    }, [backgroundPage, homePagePosts, posts, currentUser, usersData, page.type, footprintFeedMode, lastFootprintTaskId, getHydratedPosts]);

    const handleReorderTabs = useCallback((pageType: 'home' | 'profile' | 'footprint', newOrder: string[]) => {
        if (!currentUser) return;
        const fieldName = `${pageType}TabsOrder`;
        updateUserData((prev: any) => ({
            ...prev,
            [currentUser]: {
                ...prev[currentUser],
                [fieldName]: newOrder
            }
        }));
        if (!isDemo) {
            updateUserOnServer(currentUser, { [fieldName]: newOrder }).catch(console.error);
        }
    }, [currentUser, isDemo, updateUserData]);

    const [tempCommunityBgResolution, setTempCommunityBgResolution] = useState<number | null>(null);

    const activeBg = useMemo(() => {
        const defaultData = getDefaultUserData(currentUser || 'goodlyfe');
        const bgSourcePage = backgroundPage;

        // Determine which user's profile background settings to use
        let targetUserId: string | null = null;
        let usePostAuthorBg = false;

        let activePostAuthor: string | null = null;
        if (selectedPost) {
            activePostAuthor = selectedPost.author;
        } else if (page.type === 'post' && page.postId) {
            const rawPost = posts.find(p => p.id.toString() === page.postId);
            if (rawPost) {
                activePostAuthor = rawPost.author;
            }
        } else if (selectedFeedPostId !== null) {
            const rawPost = posts.find(p => p.id === selectedFeedPostId);
            if (rawPost) {
                activePostAuthor = rawPost.author;
            }
        }

        console.log('[DEBUG-BG] activeBg start:', {
            pageType: page.type,
            bgSourcePageType: bgSourcePage.type,
            selectedPostId: selectedPost?.id,
            activePostAuthor,
            hasUsersData: Object.keys(usersData).length > 0
        });

        if (activePostAuthor) {
            const postAuthorId = getUserKeyCaseInsensitive(activePostAuthor) || activePostAuthor;
            const postAuthorData = postAuthorId ? usersData[postAuthorId] : null;
            console.log('[DEBUG-BG] postAuthorData check:', {
                postAuthorId,
                hasData: !!postAuthorData,
                profileBgSlideshowCount: postAuthorData?.profileBgSlideshow?.attachments?.length
            });
            if (postAuthorData) {
                targetUserId = postAuthorId;
                usePostAuthorBg = true;
            }
        }

        if (!usePostAuthorBg) {
            if (bgSourcePage.type === 'profile') {
                targetUserId = getUserKeyCaseInsensitive(bgSourcePage.userId) || bgSourcePage.userId;
            } else {
                targetUserId = lastVisitedProfileUserId;
            }
        }

        const userData = targetUserId ? usersData[targetUserId] : null;
        const baseUserData = userData || defaultData;

        if (!usePostAuthorBg && bgSourcePage.type === 'community_detail') {
            const community = communities.find((c: Community) => c._id === bgSourcePage.query);
            if (community) {
                const isColor = community.bg?.style === 'color';
                if (!isColor && community.bgSlideshow?.attachments?.length > 0) {
                    const sIdx = communityBgSlideshowIdx % community.bgSlideshow.attachments.length;
                    const slide = community.bgSlideshow.attachments[sIdx];
                    if (slide && slide.url) {
                        return {
                            bg: { url: slide.url, style: slide.style || community.bg?.style || 'cover' },
                            resolution: tempCommunityBgResolution !== null ? tempCommunityBgResolution : (slide.resolution || community.bgResolution || 100),
                            mode: 'image'
                        };
                    }
                }
                if (community.bg && community.bg.url) {
                    return {
                        bg: community.bg,
                        resolution: tempCommunityBgResolution !== null ? tempCommunityBgResolution : (community.bgResolution || 100),
                        mode: isColor ? 'color' : 'image'
                    };
                }
            }
        }

        // Retrieve background settings from the determined user's profile configuration
        const mode = baseUserData.profileBgMode || 'image';
        let bg = mode === 'color'
            ? { url: baseUserData.profileBgColor || '#000000', style: 'color' }
            : (baseUserData.profileBg?.url ? baseUserData.profileBg : defaultData.profileBg);

        // Check slideshow FIRST — if user has slides, they take full priority
        if (mode === 'image' && baseUserData.profileBgSlideshow?.attachments?.length > 0) {
            const sIdx = profileBgSlideshowIdx % baseUserData.profileBgSlideshow.attachments.length;
            const slide = baseUserData.profileBgSlideshow.attachments[sIdx];
            if (slide && slide.url) {
                return {
                    bg: { url: slide.url, style: slide.style || baseUserData.profileBg?.style || 'cover' },
                    resolution: Math.min(200, Math.max(25, slide.resolution || baseUserData.profileBgResolution || 100)),
                    mode: 'image'
                };
            }
        }

        // If background matches default, has broken URL (including legacy beach URL), or if style is empty, force clean default cover mountain background
        const isDefaultOrBroken = !bg.url ||
            bg.url.trim() === '' ||
            bg.url.includes('photo-1507525428034-b723a996f6ea') ||
            bg.url === defaultData.profileBg.url ||
            bg.url === defaultData.homeBg.url ||
            !bg.style;

        if (isDefaultOrBroken && mode !== 'color') {
            bg = { url: defaultData.homeBg.url, style: 'cover' };
        }

        return {
            bg: bg,
            resolution: isDefaultOrBroken ? 100 : Math.min(200, Math.max(25, baseUserData.profileBgResolution || 100)),
            mode: mode
        };
    }, [backgroundPage, usersData, currentUser, communities, lastVisitedProfileUserId, profileBgSlideshowIdx, communityBgSlideshowIdx, tempCommunityBgResolution, selectedPost, posts, selectedFeedPostId]);

    const [renderedBg, setRenderedBg] = useState<typeof activeBg | null>(activeBg);
    const [bgLoadFailed, setBgLoadFailed] = useState(false);

    useEffect(() => {
        let active = true;
        setBgLoadFailed(false);

        if (!activeBg) {
            setRenderedBg(null);
            return;
        }

        if (activeBg.bg?.url && activeBg.bg.style !== 'color' && !isVideoUrl(activeBg.bg.url)) {
            const img = new Image();
            img.src = activeBg.bg.url;
            img.onload = () => {
                if (active) {
                    setBgLoadFailed(false);
                    setRenderedBg(activeBg);
                }
            };
            img.onerror = () => {
                if (active) {
                    console.warn('[App] Background image failed to load, falling back:', activeBg.bg.url);
                    setBgLoadFailed(true);
                    setRenderedBg(activeBg);
                }
            };
        } else {
            // Update color backgrounds, video backgrounds, or empty URLs immediately
            setRenderedBg(activeBg);
        }

        return () => {
            active = false;
        };
    }, [activeBg]);
    const isPostChannel = useMemo(() => {
        if (backgroundPage.type !== 'community_detail') return false;
        const community = communities.find((c: Community) => c._id === backgroundPage.query);
        const channels: Channel[] = community?.channels && community.channels.length > 0 ? community.channels : [
            { id: 'posts', name: 'posts', type: 'posts', order: 0 },
            { id: 'chat', name: 'chat', type: 'chat', order: 1 },
            { id: 'voice', name: 'voice', type: 'voice', order: 2 }
        ];
        const currentChannelId = lastCommunityChannelId || backgroundPage.channelId || (channels.length > 0 ? channels[0].id : 'posts');
        const channel = channels.find((c: Channel) => c.id === currentChannelId);
        return channel?.type === 'posts';
    }, [backgroundPage, communities, lastCommunityChannelId]);

    const isPullToRefreshEnabled = useMemo(() => {
        if (backgroundPage.type === 'messages' && activeConversationId !== null) return false;
        const refreshablePages = ['home', 'profile', 'footprint', 'hashtag', 'communities', 'messages'];
        if (refreshablePages.includes(backgroundPage.type)) return true;
        if (backgroundPage.type === 'community_detail' && isPostChannel) return true;
        if (backgroundPage.type === 'mod' && (backgroundPage as any).modId === 'vibeman1_footprint') return true;
        return false;
    }, [backgroundPage, isPostChannel, activeConversationId]);

    // Desktop Zoom Shortcuts and Trackpad Zoom Overrides
    useEffect(() => {
        const performZoomIn = () => {
            const isChatPage = backgroundPage.type === 'messages' || (backgroundPage.type === 'community_detail' && !isPostChannel);
            if (isChatPage) {
                setMessageFontSize(prev => Math.min(48, prev + 2));
            } else {
                const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
                const minCols = 1;
                setGridScale(prev => {
                    const next = prev + 0.1;
                    if (next >= 1.25) {
                        if (gridColumns > minCols) {
                            setGridColumns((gridColumns - 1) as 1 | 2 | 3 | 4 | 5 | 6);
                            return 1.0;
                        }
                        return 1.2;
                    }
                    return parseFloat(next.toFixed(1));
                });
            }
        };

        const performZoomOut = () => {
            const isChatPage = backgroundPage.type === 'messages' || (backgroundPage.type === 'community_detail' && !isPostChannel);
            if (isChatPage) {
                setMessageFontSize(prev => Math.max(14, prev - 2));
            } else {
                const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
                const maxCols = isDesktop ? 6 : 3;
                setGridScale(prev => {
                    const next = prev - 0.1;
                    if (next <= 0.75) {
                        if (gridColumns < maxCols) {
                            setGridColumns((gridColumns + 1) as 1 | 2 | 3 | 4 | 5 | 6);
                            return 1.0;
                        }
                        return 0.8;
                    }
                    return parseFloat(next.toFixed(1));
                });
            }
        };

        const performZoomReset = () => {
            const isChatPage = backgroundPage.type === 'messages' || (backgroundPage.type === 'community_detail' && !isPostChannel);
            if (isChatPage) {
                setMessageFontSize(16);
            } else {
                setGridColumns(2);
                const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
                setGridScale(isDesktop ? 0.9 : 1.0);
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            const isCtrlOrCmd = e.ctrlKey || e.metaKey;
            if (!isCtrlOrCmd) return;

            if (e.key === '=' || e.key === '+') {
                e.preventDefault();
                performZoomIn();
            } else if (e.key === '-') {
                e.preventDefault();
                performZoomOut();
            } else if (e.key === '0') {
                e.preventDefault();
                performZoomReset();
            }
        };

        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey) {
                e.preventDefault();
                if (e.deltaY < 0) {
                    performZoomIn();
                } else if (e.deltaY > 0) {
                    performZoomOut();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown, { capture: true });
        window.addEventListener('wheel', handleWheel, { passive: false, capture: true });

        return () => {
            window.removeEventListener('keydown', handleKeyDown, { capture: true });
            window.removeEventListener('wheel', handleWheel, { capture: true });
        };
    }, [backgroundPage, isPostChannel, gridColumns, setMessageFontSize, setGridScale, setGridColumns]);

    // --- MODULAR ENGINE: DYNAMIC MOD RENDERER ---
    const renderDynamicMod = (page: PageState) => {
        if (page.type !== 'mod') return null;

        // Custom website-based mods render persistently at root level
        const customMod = currentUserData?.customMods?.find((m: any) => m.id === page.modId);
        if (customMod?.url) {
            return null;
        }

        const mod = getDynamicModDefinition(MOD_REGISTRY[page.modId], usersData);
        if (!mod) return (
            <div className="flex flex-col items-center justify-center p-8 mt-20 text-gray-400">
                <h2 className="text-xl font-bold text-white mb-2">Mod Not Found</h2>
                <p className="text-center max-w-xs text-sm">The in-app app "{page.modId}" is not registered.</p>
            </div>
        );

        const ModComponent = mod.component;

        return (
            <React.Suspense fallback={
                <div className="flex items-center justify-center p-20">
                    <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
                </div>
            }>
                <ModComponent
                    params={page.params}
                    currentUser={currentUser}
                    usersData={usersData}
                    appThemeColor={appThemeColor}
                    onNavigate={setPage}
                    setPage={setPage}
                    updateUserData={updateUserData}
                    currentOpacity={postBlurOpacity}
                    onNavigateToProfile={handleNavigateToProfile}
                    onPostClick={handlePostClick}
                    // Compatibility aliases
                    username={currentUser}
                    themeColor={appThemeColor}
                    currentUserId={currentUser}
                    currentUserData={usersData[currentUser]}
                    // Core Page Props (for Footprint/Leaderboards)
                    posts={getHydratedPosts(posts.filter(p => isPostVisible(p) && !usersData[currentUser]?.reportedPosts?.includes(p.id)))}
                    allUsers={usersData}
                    allPosts={getHydratedPosts(posts.filter(p => isPostVisible(p) && !usersData[currentUser]?.reportedPosts?.includes(p.id)))}
                    animatingLikes={animatingLikes}
                    animatingBumps={animatingBumps}
                    animatingBookmarks={animatingBookmarks}
                    galleryAnimation={galleryAnimation}
                    postBlurOpacity={postBlurOpacity}
                    preferredColumns={gridColumns}
                    onColumnsChange={setGridColumns}
                    preferredScale={gridScale}
                    onScaleChange={setGridScale}
                    postcardGap={postcardGap}
                    onPostcardGapChange={setPostcardGap}
                    onCreatePost={handleCreatePost}
                    onInternalModalStateChange={setIsInternalModalOpen}
                    closeInternalModalsTrigger={closeInternalModalsTrigger}
                    onLike={handleLike}
                    onBump={handleBumpClick}
                    onBookmark={handleBookmark}
                    onEdit={handleEditPost}
                    onDelete={handleDeleteClick}
                    onVouch={handleAddVouch}
                    onNavigateToHashtag={handleNavigateToHashtag}
                    onRefreshPosts={() => syncAppDataRef.current?.()}
                    onReportPost={handleReportPost}
                    onShare={handleSharePost}
                    onMuteUser={handleMuteUser}
                    onBlockUser={handleBlockUser}
                    onGrantAdmin={handleGrantAdmin}
                    onDeleteUser={handleDeleteUser}
                    dailyAchievements={dailyAchievements}
                    onCompleteAchievement={handleCompleteAndPostAchievement}
                    onOpenAdminSettings={() => setIsAdminModalOpen(true)}
                    isAdmin={usersData[currentUser]?.role === 'admin'}
                    // Pass all available variables for complex pages (Footprint/etc)
                    communities={communities}
                    socket={socketRef.current}
                    voiceRooms={voiceRooms}
                    isDemo={isDemo}
                    onJoinVoice={handleJoinVoice}
                    onLeaveVoice={handleLeaveVoice}
                    activeVoiceData={activeVoiceData}
                    achievements={achievements}
                    taskId={lastFootprintTaskId}
                    tabsOrder={usersData[currentUser]?.footprintTabsOrder}
                    onReorderTabs={(newOrder: string[]) => {
                        if (!currentUser) return;
                        updateUserData((prev: any) => ({
                            ...prev,
                            [currentUser]: { ...prev[currentUser], footprintTabsOrder: newOrder }
                        }));
                        if (!isDemo) {
                            updateUserOnServer(currentUser, { footprintTabsOrder: newOrder }).catch(console.error);
                        }
                    }}
                />
            </React.Suspense>
        );
    };
    useEffect(() => {
        if (!isIos) return;
        const root = document.documentElement;
        const canGoBack = backgroundPage.type !== 'home' || isModalVisible || isInternalModalOpen;
        if (canGoBack) {
            root.style.setProperty('--safe-area-top', 'calc(env(safe-area-inset-top, 0px))');
        } else {
            root.style.setProperty('--safe-area-top', 'env(safe-area-inset-top, 0px)');
        }
    }, [backgroundPage.type, isModalVisible, isInternalModalOpen]);

    const renderBackButtonContent = () => {
        const isModalPage = ['post', 'privacy', 'safety', 'settings', 'additional_settings', 'notifications', 'search', 'store'].includes(page.type);
        const targetPage = (isModalVisible || isInternalModalOpen || isModalPage)
            ? backgroundPage
            : (navHistoryRef.current.length >= 2 ? navHistoryRef.current[navHistoryRef.current.length - 2] : { type: 'home' });
        switch (targetPage.type) {
            case 'home':
                return <HomeIcon className="w-4 h-4" />;
            case 'profile': {
                const user = usersData[targetPage.userId || ''];
                if (user?.photoURL) {
                    return <img src={user.photoURL} alt="" className="w-4 h-4 rounded-full object-cover" />;
                }
                return <UserIcon className="w-4 h-4" />;
            }
            case 'communities':
                return <UsersIcon className="w-4 h-4" />;
            case 'settings':
            case 'additional_settings':
                return <SettingsIcon className="w-4 h-4" />;
            case 'community_detail': {
                const community = communities.find((c: any) => c.id === targetPage.query);
                const channel = community?.channels?.find((ch: any) => ch.id === targetPage.channelId);
                return (
                    <div className="flex items-center gap-1">
                        {community?.avatarUrl ? (
                            <img src={community.avatarUrl} alt="" className="w-4 h-4 rounded-full object-cover" />
                        ) : (
                            <UsersIcon className="w-4 h-4" />
                        )}
                        {channel?.type === 'voice' && <VolumeHighIcon className="w-3 h-3 text-gray-400" />}
                        {channel?.type === 'post' && <PostcardIcon className="w-3 h-3 text-gray-400" />}
                        {(!channel || channel.type === 'chat') && <span className="text-xs font-bold text-gray-400">#</span>}
                    </div>
                );
            }
            case 'messages': {
                if (targetPage.username) {
                    const partner = Object.values(usersData).find((u: any) => u.username === targetPage.username) as any;
                    return (
                        <div className="flex items-center gap-1">
                            {partner?.photoURL ? (
                                <img src={partner.photoURL} alt="" className="w-4 h-4 rounded-full object-cover" />
                            ) : (
                                <UserIcon className="w-4 h-4" />
                            )}
                            <span className="text-[10px] font-bold uppercase">DM</span>
                        </div>
                    );
                }
                return <MailIcon className="w-4 h-4" />;
            }
            case 'mod': {
                const mod = getActiveMods(activeMods, usersData, currentUserData?.username).find((m: any) => m?.id === targetPage.modId);
                return (
                    <div className="flex items-center gap-1">
                        {typeof mod?.icon === 'string' ? (
                            <img src={mod.icon} alt="" className="w-4 h-4 rounded-full object-cover" />
                        ) : mod?.icon ? (
                            <mod.icon className="w-4 h-4" />
                        ) : null}
                    </div>
                );
            }
            case 'post': {
                const post = posts.find((p: any) => p.id.toString() === targetPage.postId);
                const author = post ? usersData[post.author] : null;
                return (
                    <div className="flex items-center gap-1">
                        <PostcardIcon className="w-4 h-4" />
                        {author?.photoURL ? (
                            <img src={author.photoURL} alt="" className="w-4 h-4 rounded-full object-cover" />
                        ) : null}
                    </div>
                );
            }
            default:
                return <span className="text-[10px] font-bold uppercase tracking-wider">Back</span>;
        }
    };

    return (
        <div
            ref={touchContainerRef}
            className="min-h-[100vh]"
        >
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: -3, backgroundColor: '#000' }} />
            <AnimatePresence>
                {renderedBg && (
                    <motion.div
                        key={renderedBg.bg?.url || 'color'}
                        initial={
                            galleryAnimation === 'fade' ? { opacity: 0 } :
                                galleryAnimation === 'slide' ? { x: '100%' } :
                                    galleryAnimation === 'zoom' ? { scale: 1.1, opacity: 0 } :
                                        {}
                        }
                        animate={
                            galleryAnimation === 'fade' ? { opacity: 1 } :
                                galleryAnimation === 'slide' ? { x: 0 } :
                                    galleryAnimation === 'zoom' ? { scale: 1, opacity: 1 } :
                                        {}
                        }
                        exit={
                            galleryAnimation === 'fade' ? { opacity: 0 } :
                                galleryAnimation === 'slide' ? { x: '-100%' } :
                                    galleryAnimation === 'zoom' ? { scale: 0.9, opacity: 0 } :
                                        {}
                        }
                        transition={{ duration: 0.5, ease: 'easeInOut' }}
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: -2, pointerEvents: 'none' }}
                    >
                        {isVideoUrl(renderedBg.bg?.url) ? (
                            renderedBg.bg.style === 'tile' ? (
                                <TiledVideoBackground
                                    src={renderedBg.bg?.url || ''}
                                    resolutionVw={renderedBg.resolution
                                        ? (windowWidth < 768
                                            ? renderedBg.resolution
                                            : renderedBg.resolution / 3)
                                        : 33.33
                                    }
                                    style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
                                />
                            ) : (
                                <video
                                    src={renderedBg.bg?.url}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        pointerEvents: 'none'
                                    }}
                                />
                            )
                        ) : (
                            <div style={{
                                width: '100%',
                                height: '100%',
                                ...(renderedBg.bg.style === 'color' || bgLoadFailed ? {
                                    backgroundColor: bgLoadFailed ? undefined : renderedBg.bg.url,
                                    backgroundImage: bgLoadFailed ? `url('${renderedBg.bg.url || 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80'}')` : undefined,
                                    backgroundPosition: 'center',
                                    backgroundSize: 'cover',
                                    backgroundRepeat: 'no-repeat'
                                } : {
                                    backgroundImage: `url('${renderedBg.bg.url}')`,
                                    backgroundPosition: renderedBg.bg.style === 'tile' ? 'center top' : 'center',
                                    backgroundSize: renderedBg.bg.style === 'tile'
                                        ? (renderedBg.resolution
                                            ? (windowWidth < 768
                                                ? `${renderedBg.resolution}% auto`
                                                : `${renderedBg.resolution / 3}% auto`)
                                            : 'auto')
                                        : 'cover',
                                    backgroundRepeat: renderedBg.bg.style === 'tile' ? 'repeat' : 'no-repeat'
                                })
                            }} />
                        )}
                    </motion.div>
                )}
            </AnimatePresence>


            <style>{`
                @keyframes slide-in-left {
                    from { transform: translateX(-100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                @keyframes slide-in-right {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .page-slide-left {
                    animation: slide-in-right 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.1) forwards;
                }
                .page-slide-right {
                    animation: slide-in-left 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.1) forwards;
                }
                .hidden-page {
                    position: absolute !important;
                    width: 100% !important;
                    height: 0 !important;
                    overflow: hidden !important;
                    visibility: hidden !important;
                    pointer-events: none !important;
                }

                :root {
                    --app-theme-color: ${appThemeColor};
                    --app-text-color: ${appTextColor};
                    --app-theme-rgb: ${themeRgb};
                    --app-theme-opacity: ${postBlurOpacity / 100};
                    --app-theme-bg: rgba(${themeRgb}, ${postBlurOpacity / 100});
                    --app-theme-bg-sidebar: rgba(${themeRgb}, ${postBlurOpacity / 100});
                }
                #root::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: inherit;
                    backdrop-filter: ${'none'};
                    z-index: -1;
                }
                 input[type="range"].range-sm {
                    -webkit-appearance: none;
                    appearance: none;
                    background: transparent;
                    cursor: pointer;
                }
                input[type="range"].range-sm::-webkit-slider-runnable-track {
                    background-color: #4a5568;
                    height: 0.25rem;
                    border-radius: 9999px;
                }
                input[type="range"].range-sm::-moz-range-track {
                     background-color: #4a5568;
                     height: 0.25rem;
                     border-radius: 9999px;
                }
                input[type="range"].range-sm::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    margin-top: -4px;
                    background-color: #fff;
                    height: 12px;
                    width: 12px;
                    border-radius: 9999px;
                }
                 input[type="range"].range-sm:focus::-webkit-slider-thumb {
                    outline: 2px solid #3b82f6;
                    outline-offset: 2px;
                 }
                input[type="range"].range-sm::-moz-range-thumb {
                    border: none;
                    border-radius: 9999px;
                    background-color: #fff;
                    height: 12px;
                    width: 12px;
                }
                @keyframes fade-in {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fade-in {
                    animation: fade-in 0.3s ease-out forwards;
                }
                @keyframes fade-in-fast { 
                    from { opacity: 0; transform: translateY(-10px); } 
                    to { opacity: 1; transform: translateY(0); } 
                }
                .animate-fade-in-fast { animation: fade-in-fast 0.2s ease-out forwards; }
                 @keyframes fade-in-image {
                    from { opacity: 0.8; }
                    to { opacity: 1; }
                }
                .animate-fade-in-image {
                    animation: fade-in-image 0.7s ease-in-out;
                }
                @keyframes slideIn {
                    from { opacity: 0; transform: translateX(-20px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes zoomIn {
                    from { opacity: 0; transform: scale(0.8); }
                    to { opacity: 1; transform: scale(1); }
                }
                @keyframes gallery-slide {
                    from { opacity: 0; transform: translateX(20px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .animate-gallery-slide { animation: gallery-slide 0.5s ease-out forwards; }
                
                @keyframes gallery-zoom {
                    from { opacity: 0; transform: scale(0.9); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-gallery-zoom { animation: gallery-zoom 0.5s ease-out forwards; }

                @keyframes heart-burst {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.4); }
                }
                .animate-heart-burst {
                    animation: heart-burst 0.4s ease-out;
                }
                @keyframes comment-pop {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.3); }
                }
                .animate-comment-pop {
                    animation: comment-pop 0.5s ease-out;
                }
                @keyframes bump-spin-grow {
                    0% { transform: scale(1) rotate(0deg); }
                    50% { transform: scale(1.4) rotate(180deg); }
                    100% { transform: scale(1) rotate(360deg); }
                }
                .animate-bump-spin {
                    animation: bump-spin-grow 0.5s ease-in-out;
                }
                @keyframes bookmark-pop {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.3); }
                }
                .animate-bookmark-pop {
                    animation: bookmark-pop 0.5s ease-out;
                }
                .line-clamp-2 {
                   overflow: hidden;
                   display: -webkit-box;
                   -webkit-box-orient: vertical;
                   -webkit-line-clamp: 2;
                }
                .line-clamp-3 {
                   overflow: hidden;
                   display: -webkit-box;
                   -webkit-box-orient: vertical;
                   -webkit-line-clamp: 3;
                }
                .line-clamp-4 {
                   overflow: hidden;
                   display: -webkit-box;
                   -webkit-box-orient: vertical;
                   -webkit-line-clamp: 4;
                }
                @keyframes fade-in-up {
                    from { opacity: 0; transform: translate(-50%, 20px); }
                    to { opacity: 1; transform: translate(-50%, 0); }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 0.4s ease-out forwards;
                }
                @keyframes bounce-subtle {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-3px); }
                }
                .animate-bounce-subtle {
                    animation: bounce-subtle 2s infinite ease-in-out;
                }
                @keyframes ios-wiggle {
                    0% { transform: rotate(-0.5deg) translate(-0.5px, -0.5px); }
                    50% { transform: rotate(0.5deg) translate(0.5px, 0.5px); }
                    100% { transform: rotate(-0.5deg) translate(-0.5px, -0.5px); }
                }
                .ios-shaking:nth-child(odd) {
                    animation: ios-wiggle 0.23s ease-in-out infinite;
                }
                .ios-shaking:nth-child(even) {
                    animation: ios-wiggle 0.21s ease-in-out infinite -0.1s;
                }
                @keyframes pulse {
                    0% { transform: scale(1.1); filter: brightness(1); }
                    100% { transform: scale(1.15); filter: brightness(1.2); }
                }
            `}</style>

            {!isSettingsModalOpen && !isInternalModalOpen && !(
                (backgroundPage.type === 'messages' && activeConversationId !== null) ||
                (backgroundPage.type === 'community_detail' && !isPostChannel)
            ) && (
                    <NavBar
                        page={backgroundPage}
                        setPage={handleNavigateWithAnimation}
                        currentUserId={currentUser}
                        usersData={usersData}
                        role={currentUser ? usersData[currentUser]?.role : undefined}
                        hasUnreadMessages={hasUnreadMessages}
                        uiScale={uiScale}
                        hasUnreadNotifications={hasUnreadNotifications}
                        onSettingsClick={() => handleNavigateWithAnimation({ type: 'settings' })}
                        onLogout={logout}
                        isDemo={isDemo}
                        activeVoiceData={activeVoiceData}
                        columns={gridColumns}
                        isIos={isIos}
                        canGoBack={backgroundPage.type !== 'home' || isModalVisible || isInternalModalOpen}
                        onBack={handleBackNavigation}
                        activeMods={activeMods}
                        pinnedModId={pinnedModId}
                        onOpenModLibrary={() => setIsModLibraryModalOpen(true)}
                        onOpenAccountSwitcher={() => setIsAccountSwitcherOpen(true)}
                        backButtonContent={renderBackButtonContent()}
                        isPeeking={isPeeking}
                        onTogglePeek={!isModalVisible ? () => setIsPeeking(prev => !prev) : undefined}
                        isShakingMode={isShakingMode}
                        onToggleShakingMode={() => setIsShakingMode(prev => !prev)}
                        onOpenCommunitiesModal={() => setIsYourCommunitiesModalOpen(true)}
                        postcardGap={postcardGap}
                        setPostcardGap={setPostcardGap}
                    />
                )}

            <main
                ref={mainRef}
                className={`relative
                    ${(backgroundPage.type === 'messages' || (backgroundPage.type === 'community_detail' && !isPostChannel))
                        ? 'h-[100vh] mt-0 overflow-hidden'
                        : 'pt-[var(--safe-area-top,0px)] min-h-[100vh] pb-[calc(6rem+var(--safe-area-bottom,0px))]'}
                    ${swipeDirection === 'left' ? 'page-slide-left' : ''}
                    ${swipeDirection === 'right' ? 'page-slide-right' : ''}
                `}
                onAnimationEnd={() => {
                    setSwipeDirection(null);
                }}
            >
                {!isSettingsModalOpen && (
                    <>
                        <PullToRefresh onRefresh={handlePullToRefresh} enabled={isPullToRefreshEnabled}>
                            {renderDynamicMod(backgroundPage)}
                            <div key="page-container-home" data-page-container="home" className={backgroundPage.type === 'home' ? '' : 'hidden-page'}>
                                <HomePage
                                    posts={homePagePosts}
                                    onPostClick={handlePostClick}
                                    onLike={handleLike}
                                    onBump={handleBumpClick}
                                    onBookmark={handleBookmark}
                                    onEdit={handleEditPost}
                                    onDelete={handleDeleteClick}
                                    onNavigateToProfile={handleNavigateToProfile}
                                    onNavigateToCommunity={(id: string, channelId?: string) => setPage({ type: 'community_detail', query: id, channelId })}
                                    onCreatePost={handleCreatePost}
                                    homeFeedMode={homeFeedMode}
                                    onHomeFeedModeChange={setHomeFeedMode}
                                    followerGroups={(currentUserData?.followerGroups || []).map((g: any) => g.id === 'default_all' ? { ...g, name: currentUserData.followerNickname || currentUserData.friendNickname || g.name } : g)}
                                    isDemo={isDemo}
                                    onUploadFailed={handleMarkPostUploadFailed}
                                    failedPosts={getHydratedPosts(posts.filter(p => !p.isArchived && p.author === currentUser && p.uploadFailed && !p.footprintDetails))}
                                    onRetryPostUpload={handleRetryPostUpload}

                                    animatingLikes={animatingLikes}
                                    animatingBumps={animatingBumps}
                                    animatingBookmarks={animatingBookmarks}
                                    galleryAnimation={galleryAnimation}
                                    postBlurOpacity={postBlurOpacity}
                                    onNavigateToHashtag={handleNavigateToHashtag}
                                    onShare={handleSharePost}
                                    tabsOrder={currentUser ? usersData[currentUser]?.homeTabsOrder : undefined}
                                    onReorderTabs={(newOrder) => handleReorderTabs('home', newOrder)}
                                    onVouch={handleAddVouch}
                                    gridColumns={gridColumns}
                                    onGridColumnsChange={setGridColumns}
                                    preferredScale={gridScale}
                                    onScaleChange={setGridScale}
                                    isModalOpen={isModalVisible}
                                    onAddToCart={handleAddToCart}
                                    onSwipeTransition={handleSwipeTransition}
                                    onMuteUser={handleMuteUser}
                                    onBlockUser={handleBlockUser}
                                    onGrantAdmin={handleGrantAdmin}
                                    onDeleteUser={handleDeleteUser}
                                    onReportPost={handleReportPost}
                                    initialVisibleCount={scrollPositions.current['home']?.visibleCount}
                                    onVisibleCountChange={(count) => { lastVisibleCountRef.current = count; }}
                                    onInternalModalStateChange={setIsInternalModalOpen}
                                    closeInternalModalsTrigger={closeInternalModalsTrigger}
                                    isShakingMode={isShakingMode}
                                    postcardGap={postcardGap}
                                    onPostcardGapChange={setPostcardGap}
                                    isLoading={isInitialLoad}
                                    appTextColor={appTextColor}
                                    initialSharedContent={pendingShareContent}
                                    clearSharedContent={() => setPendingShareContent(null)}
                                />
                            </div>


                            {hasVisitedProfile && lastProfileUserId && (() => {
                                const canonicalId = getUserKeyCaseInsensitive(lastProfileUserId);
                                if (!canonicalId) {
                                    return (
                                        <div key="page-container-profile-not-found" className={backgroundPage.type === 'profile' || backgroundPage.type === 'storefront' ? '' : 'hidden-page'}>
                                            <div className="flex flex-col items-center justify-center p-8 mt-20 text-gray-400">
                                                <SearchIcon className="w-16 h-16 mb-4 opacity-50" />
                                                <h2 className="text-xl font-bold text-white mb-2">User Not Found</h2>
                                                <p className="text-center max-w-xs text-sm">The profile "@{lastProfileUserId}" does not exist.</p>
                                            </div>
                                        </div>
                                    );
                                }

                                const isBlocked = currentUser && (
                                    usersData[currentUser]?.blockedUsers?.includes(canonicalId) ||
                                    usersData[canonicalId]?.blockedUsers?.includes(currentUser)
                                );

                                if (isBlocked) {
                                    return (
                                        <div key="page-container-profile-blocked" className={backgroundPage.type === 'profile' || backgroundPage.type === 'storefront' ? '' : 'hidden-page'}>
                                            <div className="flex flex-col items-center justify-center p-8 mt-20 text-gray-400">
                                                <BrokenHeartIcon className="w-16 h-16 mb-4 opacity-50" />
                                                <h2 className="text-xl font-bold text-white mb-2">Profile Unavailable</h2>
                                                <p className="text-center max-w-xs">You cannot view this profile.</p>
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div key={`page-container-profile-${canonicalId}`} data-page-container="profile" className={backgroundPage.type === 'profile' || backgroundPage.type === 'storefront' ? '' : 'hidden-page'}>
                                        <ProfilePage
                                            key={canonicalId}
                                            hideContent={isFollowerGroupsModalOpen || isPeeking}
                                            posts={getHydratedPosts(posts.filter(p => p.author === canonicalId && isPostVisible(p) && !usersData[currentUser]?.reportedPosts?.includes(p.id)))}
                                            allPosts={getHydratedPosts(posts.filter(p => isPostVisible(p) && !usersData[currentUser]?.reportedPosts?.includes(p.id)))}
                                            onPostClick={handlePostClick}
                                            onLike={handleLike}
                                            onBump={handleBumpClick}
                                            onBookmark={handleBookmark}
                                            onEdit={handleEditPost}
                                            onDelete={handleDeleteClick}
                                            onNavigateToProfile={handleNavigateToProfile}
                                            onNavigateToCommunity={(id: string, channelId?: string) => setPage({ type: 'community_detail', query: id, channelId })}
                                            onCreatePost={handleCreatePost}
                                            onVouch={handleAddVouch}
                                            isOwner={canonicalId === currentUser}
                                            viewedUser={{ id: canonicalId, data: usersData[canonicalId] }}
                                            onProfilePictureChange={handleProfilePictureChange}
                                            animatingLikes={animatingLikes}
                                            animatingBumps={animatingBumps}
                                            animatingBookmarks={animatingBookmarks}
                                            onProfileBgChange={handleProfileBgChange}
                                            galleryAnimation={galleryAnimation}
                                            postBlurOpacity={postBlurOpacity}
                                            onStartConversation={handleStartConversation}
                                            onFollowToggle={handleFollowToggle}
                                            onShowFollowList={handleShowFollowList}
                                            onNavigateToHashtag={handleNavigateToHashtag}
                                            onShare={handleSharePost}
                                            tabsOrder={currentUser ? usersData[currentUser]?.profileTabsOrder : undefined}
                                            onReorderTabs={(newOrder) => handleReorderTabs('profile', newOrder)}
                                            gridColumns={gridColumns}
                                            onGridColumnsChange={setGridColumns}
                                            preferredScale={gridScale}
                                            onScaleChange={setGridScale}
                                            isModalOpen={isModalVisible}
                                            onAddToCart={handleAddToCart}
                                            onSwipeTransition={handleSwipeTransition}
                                            onMuteUser={handleMuteUser}
                                            onBlockUser={handleBlockUser}
                                            onRemoveFollower={handleRemoveFollower}
                                            onGrantAdmin={handleGrantAdmin}
                                            onDeleteUser={handleDeleteUser}
                                            onReportPost={handleReportPost}
                                            currentUser={currentUser}
                                            initialVisibleCount={scrollPositions.current[`profile:${lastProfileUserId}`]?.visibleCount}
                                            onVisibleCountChange={(count) => { lastVisibleCountRef.current = count; }}
                                            onInternalModalStateChange={setIsInternalModalOpen}
                                            closeInternalModalsTrigger={closeInternalModalsTrigger}
                                            postcardGap={postcardGap}
                                            isLoading={isInitialLoad}
                                            initialSharedContent={pendingShareContent}
                                            clearSharedContent={() => setPendingShareContent(null)}
                                            onNavigateToYipYap={handleNavigateToYipYap}
                                            existingNonAnonymousYipYapNames={userConversations.filter((c: any) => c.isYipYap && c.yipYapOwner === currentUser && c.yipYapMode !== 'anonymous').map((c: any) => c.yipYapName)}
                                            onPin={handlePinPost}
                                            onUnpin={handleUnpinPost}
                                            onReorderPinnedPosts={handleReorderPinnedPosts}
                                            onRetryPostUpload={handleRetryPostUpload}
                                        />
                                    </div>
                                );
                            })()}
                            {page.type === 'privacy' && (
                                <PrivacyPage />
                            )}
                            {page.type === 'safety' && (
                                <SafetyPage />
                            )}
                            {hasVisitedMessages && (
                                <div key="page-container-messages" data-page-container="messages" className={backgroundPage.type === 'messages' ? 'h-full w-full' : 'hidden-page'}>
                                    <MessagesPage
                                        socket={socketRef.current}
                                        requestedTab={page.type === 'messages' && page.tab ? page.tab : undefined}
                                        conversations={userConversations}
                                        currentUserId={currentUser}
                                        usersData={usersData}
                                        onSendMessage={(conversationId, text, replyToId, attachments, isNote) => {
                                            // Adapt to expected signature: replyToId is number
                                            const replyTo = replyToId ? (() => {
                                                const convo = userConversations.find(c => c.id === conversationId);
                                                return convo?.messages.find(m => m.id === replyToId) || undefined;
                                            })() : undefined;
                                            handleSendMessage(conversationId, text, replyTo ? { id: replyTo.id, text: replyTo.text, senderId: replyTo.senderId } : undefined, attachments, isNote);
                                        }}
                                        onSelectConversation={handleSelectConversation}
                                        activeConversationId={activeConversationId}
                                        onNavigateToProfile={handleNavigateToProfile}
                                        communities={communities.filter((c: Community) => c.members.includes(currentUser || ''))}
                                        onSelectCommunityChannel={handleSelectCommunityChannel}
                                        onAddReaction={handleMessageReaction}
                                        onDeleteMessage={handleDeleteMessage}
                                        onOpenCreateYipYapWithUser={(userId) => {
                                            setCreateYipYapTarget(userId);
                                        }}
                                        onOpenNewMessage={() => setShowNewMessageModal(true)}
                                        onOpenCreateYipYap={() => setCreateYipYapTarget('__self__')}
                                        onOpenCreateCommunity={() => setCreateCommunityTarget('__self__')}
                                        onNavigateToCommunity={(id, channelId) => {
                                            setPage({ type: 'community_detail', query: id, channelId });
                                        }}
                                        onOpenCreateCommunityWithUser={(userId) => {
                                            setCreateCommunityTarget(userId);
                                        }}
                                        onForwardMessage={(message, targetConversationIds) => {
                                            if (targetConversationIds.length === 1) {
                                                handleSelectConversation(targetConversationIds[0]);
                                            } else {
                                                targetConversationIds.forEach(targetId => {
                                                    handleSendMessage(targetId, `${message.text}`, undefined, message.media);
                                                });
                                                const names = targetConversationIds.map(id => {
                                                    const convo = userConversations.find(c => c.id === id);
                                                    if (!convo) return 'Unknown';
                                                    const otherId = convo.participants.find(p => p !== currentUser);
                                                    return usersData[otherId || '']?.displayName || otherId || 'Unknown';
                                                });
                                                setFlashMessage(`Sent to ${names.join(', ')}`);
                                                setTimeout(() => setFlashMessage(null), 3000);
                                            }
                                        }}
                                        onUpdateConversation={(conversationId, updates) => {
                                            const currentConvo = userConversations.find(c => c.id === conversationId);
                                            if (!currentConvo) return;

                                            const mergedMetadata = {
                                                ...updates,
                                                theme: { ...currentConvo.theme, ...(updates.theme || {}) },
                                                nicknames: { ...(currentConvo.nicknames || {}), ...(updates.nicknames || {}) },
                                                timestamp: Date.now()
                                            };
                                            recentlyUpdatedConversationMetadata.set(conversationId, mergedMetadata);

                                            setConversations(prev => prev.map(c => {
                                                if (c.id === conversationId) {
                                                    // Robust deep merge for theme and nicknames
                                                    const newTheme = c.theme ? { ...c.theme, ...(updates.theme || {}) } : updates.theme;
                                                    const newNicknames = { ...(c.nicknames || {}), ...(updates.nicknames || {}) };

                                                    const updated: Conversation = {
                                                        ...c,
                                                        ...updates,
                                                        theme: newTheme as Conversation['theme'],
                                                        nicknames: newNicknames
                                                    };

                                                    const { messages, ...serverUpdates } = updates as any;
                                                    updateConversationOnServer(conversationId, serverUpdates).catch(console.error);
                                                    return updated;
                                                }
                                                return c;
                                            }));
                                        }}
                                        onDeleteConversation={async (convoId) => {
                                            if (!currentUser) return;
                                            try {
                                                await deleteConversationOnServer(convoId, currentUser);
                                                setConversations(prev => prev.filter(c => c.id !== convoId));
                                                if (activeConversationId === convoId) {
                                                    setActiveConversationId(null);
                                                }
                                            } catch (err) {
                                                console.error("Failed to delete conversation:", err);
                                                setFlashMessage("Failed to delete conversation");
                                                setTimeout(() => setFlashMessage(null), 3000);
                                            }
                                        }}
                                        onClearConversation={() => {
                                            setActiveConversationId(null);
                                            setPage({ type: 'messages' });
                                        }}

                                        onPostClick={handlePostClick}
                                        isDemo={isDemo}
                                        messageFontSize={messageFontSize}
                                        updateMessageFontSize={updateMessageFontSize}
                                        onSwipeTransition={handleSwipeTransition}
                                        onInternalModalStateChange={setIsInternalModalOpen}
                                        closeInternalModalsTrigger={closeInternalModalsTrigger}
                                        activeUsers={activeUsers}
                                        initialSharedContent={pendingShareContent}
                                        clearSharedContent={() => setPendingShareContent(null)}
                                        customMods={currentUserData?.customMods || []}
                                        activeSystemModId={currentUserData?.activeSystemModId}
                                        onOpenSystemModModal={() => setIsSystemModModalOpen(true)}
                                        onPublishMod={async (modId: string, previewData: any) => {
                                            if (!currentUser || !updateUserData) return;
                                            updateUserData((prev: Record<string, UserData>) => {
                                                const userData = { ...prev[currentUser] };
                                                const mods = [...(userData.customMods || [])];
                                                const modIndex = mods.findIndex((m: any) => m.id === modId);
                                                if (modIndex > -1) {
                                                    mods[modIndex] = { ...mods[modIndex], generatedUI: previewData };
                                                }
                                                userData.customMods = mods;
                                                return { ...prev, [currentUser]: userData };
                                            });
                                            if (!isDemo) {
                                                try {
                                                    const mods = [...(usersData[currentUser].customMods || [])];
                                                    const modIndex = mods.findIndex((m: any) => m.id === modId);
                                                    if (modIndex > -1) {
                                                        mods[modIndex] = { ...mods[modIndex], generatedUI: previewData };
                                                        await updateUserOnServer(currentUser, { customMods: mods });
                                                        setFlashMessage("Mod published to Pimp My Ride!");
                                                        setTimeout(() => setFlashMessage(null), 3000);
                                                    }
                                                } catch (e) {
                                                    console.error("Failed to publish mod", e);
                                                    setFlashMessage("Failed to publish mod");
                                                    setTimeout(() => setFlashMessage(null), 3000);
                                                }
                                            } else {
                                                setFlashMessage("Mod published to Pimp My Ride!");
                                                setTimeout(() => setFlashMessage(null), 3000);
                                            }
                                        }}
                                        appThemeColor={appThemeColor}
                                        onNavigate={setPage}
                                        updateUserData={updateUserData}
                                        postBlurOpacity={postBlurOpacity}
                                        posts={getHydratedPosts(posts.filter(p => isPostVisible(p) && !usersData[currentUser]?.reportedPosts?.includes(p.id)))}
                                        animatingLikes={animatingLikes}
                                        animatingBumps={animatingBumps}
                                        animatingBookmarks={animatingBookmarks}
                                        galleryAnimation={galleryAnimation}
                                        gridColumns={gridColumns}
                                        onGridColumnsChange={(cols: number) => setGridColumns(cols as 1 | 2 | 3 | 4 | 5 | 6)}
                                        onLike={handleLike}
                                        onBump={handleBumpClick}
                                        onBookmark={handleBookmark}
                                        onEdit={handleEditPost}
                                        onDelete={handleDeleteClick}
                                        onVouch={handleAddVouch}
                                        onReportPost={handleReportPost}
                                        onNavigateToHashtag={handleNavigateToHashtag}
                                        onShare={handleSharePost}
                                        onMuteUser={handleMuteUser}
                                        onBlockUser={handleBlockUser}
                                        onGrantAdmin={handleGrantAdmin}
                                        onDeleteUser={handleDeleteUser}
                                        dailyAchievements={dailyAchievements}
                                        onCompleteAchievement={handleCompleteAndPostAchievement}
                                        onOpenAdminSettings={() => setIsAdminModalOpen(true)}
                                        isAdmin={usersData[currentUser]?.role === 'admin'}
                                        voiceRooms={voiceRooms}
                                        onJoinVoice={handleJoinVoice}
                                        onLeaveVoice={handleLeaveVoice}
                                        activeVoiceData={activeVoiceData}
                                    />
                                </div>
                            )}
                            {hasVisitedHashtag && lastHashtagTag && (
                                <div key="page-container-hashtag" data-page-container="hashtag" className={backgroundPage.type === 'hashtag' ? '' : 'hidden-page'}>
                                    <HashtagPage
                                        tag={lastHashtagTag}
                                        posts={getHydratedPosts(posts.filter(p => {
                                            // First check visibility and exclude community posts
                                            if (p.isArchived) return false;
                                            if ((p as any).communityId) return false;
                                            if (usersData[currentUser]?.reportedPosts?.includes(p.id)) return false;
                                            if (!isPostVisible(p)) return false;
                                            // Extract hashtags from post text if not present or empty
                                            let hashtags = p.hashtags;
                                            if ((!hashtags || hashtags.length === 0) && p.content.text) {
                                                hashtags = Array.from((p.content.text.match(/#([a-zA-Z0-9_\-]+)/gi) || []).map(t => t.slice(1)));
                                            }
                                            return hashtags && hashtags.some(ht => ht.toLowerCase() === lastHashtagTag.toLowerCase());
                                        }))}
                                        onPostClick={handlePostClick}
                                        onLike={handleLike}
                                        onBump={handleBumpClick}
                                        onBookmark={handleBookmark}
                                        onNavigateToProfile={handleNavigateToProfile}
                                        onNavigateToCommunity={(id: string, channelId?: string) => setPage({ type: 'community_detail', query: id, channelId })}
                                        onNavigateToHashtag={handleNavigateToHashtag}
                                        animatingLikes={animatingLikes}
                                        animatingBumps={animatingBumps}
                                        animatingBookmarks={animatingBookmarks}
                                        galleryAnimation={galleryAnimation}
                                        postBlurOpacity={postBlurOpacity}
                                        gridColumns={gridColumns}
                                        onGridColumnsChange={setGridColumns}
                                        preferredScale={gridScale}
                                        onScaleChange={setGridScale}
                                        onSwipeTransition={handleSwipeTransition}
                                        onMuteUser={handleMuteUser}
                                        onBlockUser={handleBlockUser}
                                        onGrantAdmin={handleGrantAdmin}
                                        onDeleteUser={handleDeleteUser}
                                        onReportPost={handleReportPost}
                                        onShare={handleSharePost}
                                        isModalOpen={isModalVisible}
                                        initialVisibleCount={scrollPositions.current[getPageKey(backgroundPage) || '']?.visibleCount}
                                        onVisibleCountChange={(count) => { lastVisibleCountRef.current = count; }}
                                        isLoading={isInitialLoad}
                                        isShakingMode={isShakingMode}
                                        postcardGap={postcardGap}
                                        onPostcardGapChange={setPostcardGap}
                                    />
                                </div>
                            )}
                            {backgroundPage.type === 'master_hand' && currentUser && usersData[currentUser]?.role === 'admin' && (
                                <MasterHand onBack={() => setPage({ type: 'settings' })} />
                            )}
                            {activeVoiceData && (
                                <VoicePortalWrapper>
                                    <VoiceRoom
                                        communityId={activeVoiceData.communityId}
                                        channelId={activeVoiceData.channelId}
                                        currentUser={currentUser!}
                                        usersData={usersData}
                                        socket={socketRef.current}
                                        themeColor={communities.find((c: Community) => c._id === activeVoiceData.communityId)?.themeColor}
                                        onLeave={() => setActiveVoiceData(null)}
                                        onRemoteStreamsUpdate={setVoiceRemoteStreams}
                                        onLocalStreamUpdate={setVoiceLocalStream}
                                        isMuted={voiceIsMuted}
                                        setIsMuted={setVoiceIsMuted}
                                        isDeafened={voiceIsDeafened}
                                        setIsDeafened={setVoiceIsDeafened}
                                    />
                                </VoicePortalWrapper>
                            )}
                            {hasVisitedCommunities && (
                                <div key="page-container-communities" data-page-container="communities" className={backgroundPage.type === 'communities' ? '' : 'hidden-page'}>
                                    <CommunitiesPage
                                        onNavigateToCommunity={(id: string, channelId?: string) => setPage({ type: 'community_detail', query: id, channelId })}
                                        currentUserId={currentUser || ''}
                                        posts={posts}
                                        communities={communities}
                                        setCommunities={setCommunities}
                                        onPostClick={handlePostClick}
                                        onLike={handleLike}
                                        onBump={handleBumpClick}
                                        onBookmark={handleBookmark}
                                        onEdit={handleEditPost}
                                        onDelete={handleDeleteClick}
                                        onNavigateToProfile={handleNavigateToProfile}
                                        onNavigateToHashtag={handleNavigateToHashtag}
                                        onVouch={handleAddVouch}
                                        animatingLikes={animatingLikes}
                                        animatingBumps={animatingBumps}
                                        animatingBookmarks={animatingBookmarks}
                                        galleryAnimation={galleryAnimation}
                                        postBlurOpacity={postBlurOpacity}
                                        usersData={usersData}
                                        getHydratedPosts={getHydratedPosts}
                                        gridColumns={gridColumns}
                                        onGridColumnsChange={setGridColumns}
                                        preferredScale={gridScale}
                                        onScaleChange={setGridScale}
                                        onSwipeTransition={handleSwipeTransition}
                                        isDemo={isDemo}
                                        isModalOpen={isModalVisible}
                                        onReportPost={handleReportPost}
                                        onShare={handleSharePost}
                                        initialVisibleCount={scrollPositions.current[getPageKey(page) || '']?.visibleCount}
                                        onVisibleCountChange={(count) => { lastVisibleCountRef.current = count; }}
                                        isLoading={isInitialLoad}
                                        isShakingMode={isShakingMode}
                                        postcardGap={postcardGap}
                                        onPostcardGapChange={setPostcardGap}
                                    />
                                </div>
                            )}
                            {hasVisitedCommunityDetail && lastCommunityQuery && (
                                <div key={`page-container-community_detail-${lastCommunityQuery}`} data-page-container="community_detail" className={backgroundPage.type === 'community_detail' ? (isPostChannel ? '' : 'h-full w-full') : 'hidden-page'}>
                                    <CommunityDetailPage
                                        query={lastCommunityQuery}
                                        currentUserId={currentUser || ''}
                                        onPostClick={handlePostClick}
                                        onLike={handleLike}
                                        onBump={handleBumpClick}
                                        onBookmark={handleBookmark}
                                        onEdit={handleEditPost}
                                        onDelete={handleDeleteClick}
                                        onNavigateToProfile={handleNavigateToProfile}
                                        onNavigateToHashtag={handleNavigateToHashtag}
                                        onNavigateToCommunity={(id: string, channelId?: string) => setPage({ type: 'community_detail', query: id, channelId })}
                                        onChannelChange={(channelId) => {
                                            setLastCommunityChannelId(channelId);
                                            setLastCommunityPage(prev => prev.type === 'community_detail' ? { ...prev, channelId } : prev);
                                            setSelectedPost(null);
                                        }}
                                        onCreatePost={handleCreatePost}
                                        getHydratedPosts={getHydratedPosts}
                                        onVouch={handleAddVouch}
                                        animatingLikes={animatingLikes}
                                        animatingBumps={animatingBumps}
                                        animatingBookmarks={animatingBookmarks}
                                        galleryAnimation={galleryAnimation}
                                        postBlurOpacity={postBlurOpacity}
                                        usersData={usersData}
                                        gridColumns={gridColumns}
                                        onGridColumnsChange={setGridColumns}
                                        preferredScale={gridScale}
                                        onScaleChange={setGridScale}
                                        onSwipeTransition={handleSwipeTransition}
                                        socket={socketRef.current}
                                        voiceRooms={voiceRooms}
                                        isDemo={isDemo}
                                        onJoinVoice={handleJoinVoice}
                                        onLeaveVoice={handleLeaveVoice}
                                        activeVoiceData={activeVoiceData}
                                        onUploadFailed={handleMarkPostUploadFailed}
                                        failedPosts={getHydratedPosts(posts.filter(p => !p.isArchived && p.author === currentUser && p.uploadFailed && p.communityId === (backgroundPage.type === 'community_detail' ? backgroundPage.query : lastCommunityQuery)))}
                                        onRetryPostUpload={handleRetryPostUpload}
                                        voiceRemoteStreams={voiceRemoteStreams}
                                        voiceLocalStream={voiceLocalStream}
                                        channelId={(backgroundPage.type === 'community_detail' && backgroundPage.channelId) ? backgroundPage.channelId : lastCommunityChannelId}
                                        messageId={backgroundPage.type === 'community_detail' ? backgroundPage.messageId : lastCommunityMessageId}
                                        isModalOpen={isModalVisible}
                                        messageFontSize={messageFontSize}
                                        updateMessageFontSize={updateMessageFontSize}
                                        onReportPost={handleReportPost}
                                        onUpdatePostcardLayout={handleUpdatePostcardLayout}
                                        onBack={() => setPage({ type: 'communities' })}
                                        onShare={handleSharePost}
                                        updateUserData={updateUserData}
                                        initialVisibleCount={scrollPositions.current[getPageKey(backgroundPage) || '']?.visibleCount}
                                        onUpdateCommunity={(updated) => {
                                            setCommunities((prev: any[]) => prev.map((c: any) => c._id === updated._id ? updated : c));
                                            setTempCommunityBgResolution(null);
                                        }}
                                        onPreviewBgResolution={setTempCommunityBgResolution}
                                        onVisibleCountChange={(count: number) => { lastVisibleCountRef.current = count; }}
                                        isLoading={isInitialLoad}
                                        initialSharedContent={pendingShareContent}
                                        clearSharedContent={() => setPendingShareContent(null)}
                                        posts={posts}
                                        conversations={conversations}
                                        communities={communities}
                                        isShakingMode={isShakingMode}
                                        postcardGap={postcardGap}
                                        onPostcardGapChange={setPostcardGap}
                                    />
                                </div>
                            )}
                            {page.type === 'revert_email' && (
                                <RevertEmailPage
                                    username={page.username}
                                    token={page.token}
                                    onClose={() => setPage({ type: 'home' })}
                                />
                            )}
                        </PullToRefresh>

                        {page.type === 'post' && (() => {
                            const rawPost = postsById.get(Number(page.postId));

                            // Check if user can view this post
                            if (rawPost && !isPostVisible(rawPost)) {
                                return (
                                    <div className="flex items-center justify-center min-h-[100vh]">
                                        <div className="bg-gray-800/50 rounded-2xl p-8 max-w-md mx-4 text-center">
                                            <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                            <h2 className="text-2xl font-bold mb-2">Private Post</h2>
                                            <p className="text-gray-400 mb-4">This post is private. Only followers of @{rawPost.author} can view it.</p>
                                            <button
                                                onClick={() => setPage({ type: 'home' })}
                                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-full transition-colors"
                                            >
                                                Go to Home
                                            </button>
                                        </div>
                                    </div>
                                );
                            }

                            const hydratedPost = rawPost ? getHydratedPosts([rawPost])[0] : null;
                            return (
                                <PostModal
                                    post={hydratedPost}
                                    onClose={handleCloseModal}
                                    onLike={handleLike}
                                    onPostClick={handlePostClick}
                                    onComment={handleComment}
                                    onCommentReaction={handleCommentReaction}
                                    onBump={handleBumpClick}
                                    onBookmark={handleBookmark}
                                    onEdit={handleEditPost}
                                    onDelete={handleDeleteClick}
                                    onArchive={handleArchivePost}
                                    onUnarchive={handleUnarchivePost}
                                    onDeleteComment={handleDeleteComment}
                                    onEditComment={handleEditComment}
                                    onToggleVisibility={handleToggleVisibility}
                                    onToggleCaption={handleToggleCaption}
                                    onUpdateTextSize={handleUpdateTextSize}
                                    onUpdatePostcardStyle={handleUpdatePostcardStyle}
                                    onUpdatePostcardLayout={handleUpdatePostcardLayout}
                                    onNavigateToProfile={handleNavigateToProfile}
                                    onVouch={handleAddVouch}
                                    onRespondLinkRequest={handleRespondLinkRequest}
                                    isVisible={isModalVisible}
                                    isFromLink={page.type === 'post' ? page.fromLink : false}
                                    animatingLikes={animatingLikes}
                                    animatingBumps={animatingBumps}
                                    animatingComments={animatingComments}
                                    animatingBookmarks={animatingBookmarks}
                                    onNavigateToHashtag={handleNavigateToHashtag}
                                    onNavigateToCommunity={(id: string, channelId?: string) => {
                                        setSelectedPost(null);
                                        setIsSettingsModalOpen(false);
                                        setPage({ type: 'community_detail', query: id, channelId });
                                    }}
                                    setPage={setPage}
                                    onShare={(post: any) => {
                                        setPostToShare(post);
                                        setShareModalOpen(true);
                                    }}
                                    onMuteUser={handleMuteUser}
                                    onBlockUser={handleBlockUser}
                                    onGrantAdmin={handleGrantAdmin}
                                    onDeleteUser={handleDeleteUser}
                                    onReportPost={handleReportPost}
                                    onSwipePost={handleSwipePost}
                                    communityName={undefined}

                                />
                            );
                        })()}

                    </>
                )}
            </main>

            {showNewMessageModal && (
                <NewConversationModal
                    isOpen={true}
                    onClose={() => setShowNewMessageModal(false)}
                    onStartConversation={(userId) => {
                        setShowNewMessageModal(false);
                        setPage({ type: 'messages', username: userId } as any);
                    }}
                    suggestedUserIds={getWeightedUserList(currentUser || '', Object.keys(usersData), usersData, userConversations, communities, posts, 3)}
                    usersData={usersData}
                />
            )}

            {createYipYapTarget && (
                <YipYapCreateModal
                    isOpen={true}
                    onClose={() => setCreateYipYapTarget(null)}
                    onSubmit={async (data) => {
                        const target = createYipYapTarget;
                        setCreateYipYapTarget(null);
                        const newYipYap: any = await handleNavigateToYipYap(data);
                        if (newYipYap && newYipYap.id && target !== '__self__') {
                            import('./api').then(({ inviteToYipYap }) => {
                                inviteToYipYap(newYipYap.id, currentUser || '', target).catch(err => console.error(err));
                            });
                        }
                    }}
                    followerGroups={currentUserData?.followerGroups || []}
                    usersData={usersData}
                    communities={communities ? communities.filter((c: any) => c.members?.includes(currentUser)) : []}
                    existingNonAnonymousYipYapNames={userConversations.filter(c => c.isYipYap && c.yipYapOwner === currentUser && c.yipYapMode !== 'anonymous').map(c => c.yipYapName || '')}
                />
            )}

            {createCommunityTarget && (
                <CommunitiesPage
                    communities={communities}
                    setCommunities={setCommunities}
                    posts={posts}
                    currentUserId={currentUser || ''}
                    onPostClick={() => { }}
                    onNavigateToCommunity={(id: string, channelId?: string) => {
                        setPage({ type: 'community_detail', query: id, channelId });
                        setCreateCommunityTarget(null);
                    }}
                    onLike={() => { }}
                    onBump={() => { }}
                    onBookmark={() => { }}
                    onDelete={() => { }}
                    onNavigateToProfile={() => { }}
                    onNavigateToHashtag={() => { }}
                    onEdit={() => { }}
                    animatingLikes={new Set()}
                    animatingBumps={new Set()}
                    animatingBookmarks={new Set()}
                    galleryAnimation=""
                    postBlurOpacity={100}
                    getHydratedPosts={() => []}
                    usersData={usersData}
                    initialCreateModalOpen={true}
                    initialCreateWithUser={createCommunityTarget !== '__self__' ? createCommunityTarget : null}
                    onCloseCreateModal={() => setCreateCommunityTarget(null)}
                    onlyRenderCreateModal={true}
                />
            )}

            {/* Persistent Website Mods Container */}
            {currentUser && usersData[currentUser] && (usersData[currentUser].customMods || []).filter((m: any) => m.url).map((m: any) => {
                const isCurrentPage = backgroundPage.type === 'mod' && backgroundPage.modId === m.id;
                const rawUrl = m.url.startsWith('http://') || m.url.startsWith('https://') ? m.url : `https://${m.url}`;
                const proxyUrl = `/api/proxy?url=${encodeURIComponent(rawUrl)}`;
                return (
                    <div
                        key={m.id}
                        style={{ display: isCurrentPage ? 'block' : 'none' }}
                        className="fixed inset-0 z-40 bg-black flex flex-col"
                    >
                        <ProxiedIframe
                            url={m.url}
                            className="w-full flex-grow border-none pt-[var(--safe-area-top,0px)] pb-[calc(6rem+var(--safe-area-bottom,0px))]"
                        />
                    </div>
                );
            })}





            {shareModalOpen && postToShare && (
                <SharePostModal
                    isOpen={shareModalOpen}
                    onClose={() => {
                        setShareModalOpen(false);
                        if (window.history.state?.modal === 'share') {
                            window.history.back();
                        }
                    }}
                    post={postToShare}
                    conversations={conversations}
                    usersData={usersData}
                    posts={posts}
                    onShare={handleSendSharedPost}
                    onExternalShare={() => {
                        if (postToShare) {
                            const isAuthor = currentUser === postToShare.author;
                            if (isAuthor && !postToShare.sharedByAuthor) {
                                setPosts(prev => prev.map(p => p.id === postToShare.id ? { ...p, sharedByAuthor: true } : p));
                                if (!isDemo) {
                                    updatePostOnServer(postToShare.id, { sharedByAuthor: true }).catch(console.error);
                                }
                            }
                            if (!isDemo && postToShare.author !== currentUser) {
                                const newShareCount = (postToShare.shares || 0) + 1;
                                setPosts(prev => prev.map(p => p.id === postToShare.id ? { ...p, shares: newShareCount } : p));
                                updatePostOnServer(postToShare.id, { shares: newShareCount }).catch(console.error);
                            }
                        }
                    }}
                    currentSlideIndex={shareSlideIndex}
                    onCrossPost={() => {
                        setPostToBump(postToShare);
                        setIsBumpModalOpen(true);
                        setShareModalOpen(false);
                    }}
                />
            )}


            <PostOptionsModal
                isOpen={isEditModalVisible}
                post={postToEdit}
                onClose={handleCloseEditModal}
                onConfirm={(data) => handleUpdatePost(postToEdit!.id, data)}
                showTextEditing={true}
                isFootprintPost={!!postToEdit?.footprintDetails}
            />

            {currentUser && usersData[currentUser] && (
                <FollowerGroupsModal
                    isOpen={isFollowerGroupsModalOpen}
                    onClose={() => {
                        setIsFollowerGroupsModalOpen(false);
                        setFollowerGroupsTargetUser(undefined);
                    }}
                    userData={usersData[currentUser]}
                    usersData={usersData}
                    currentUser={currentUser}
                    onUpdateGroups={(newGroups) => {
                        const followerGroups = newGroups.map(g => ({
                            id: g.id,
                            name: g.name,
                            followers: g.followers || [],
                            isDefault: g.isDefault,
                            isReadOnlyName: g.isReadOnlyName
                        }));
                        updateUserData((prev: any) => ({
                            ...prev,
                            [currentUser]: {
                                ...prev[currentUser],
                                followerGroups
                            }
                        }));
                        updateUserOnServer(currentUser, { followerGroups }).catch(console.error);
                    }}
                    initialHighlightUser={followerGroupsTargetUser}
                />
            )}

            {currentUser && usersData[currentUser] && (
                <LinkModal
                    isOpen={isLinkModalOpen}
                    onClose={() => setIsLinkModalOpen(false)}
                    friends={usersData[currentUser].friends || []}
                    followers={usersData[currentUser].followers || []}
                    followerGroups={usersData[currentUser].followerGroups || []}
                    communities={communities ? communities.filter((c: any) => c.members?.includes(currentUser)) : []}
                    themeColor={appThemeColor}
                    onSubmit={async (targetUsers, text, imageFile, sendLocation, mediaUrl, eventDate, eventTime) => {
                        setIsLinkModalOpen(false);
                        try {
                            let lat: number | undefined;
                            let lng: number | undefined;
                            if (sendLocation) {
                                const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
                                lat = position.coords.latitude;
                                lng = position.coords.longitude;
                            }
                            let uploadedUrl;
                            if (imageFile) {
                                uploadedUrl = await uploadMediaFile(imageFile, currentUser);
                            } else if (mediaUrl) {
                                uploadedUrl = mediaUrl;
                            }
                            await sendLinkRequest(currentUser, targetUsers, lat, lng, text, uploadedUrl, sendLocation, eventDate, eventTime);
                            const updatedConvos = await fetchConversations(currentUser);

                            if (updatedConvos && Array.isArray(updatedConvos)) {
                                setConversations(updatedConvos);
                            }

                            alert('Link request sent successfully!');
                        } catch (e: any) {
                            console.error('Failed to send link request', e);
                            const backendError = e.response?.data?.stack || e.response?.data?.error;
                            alert(`Failed to send link request. ${backendError ? `\n\nServer Error: ${backendError}` : `Make sure location services are enabled. (${e.message})`}`);
                        }
                    }}
                />
            )}

            {selectedPost?.linkRequestDetails?.isLinkRequest && (
                <PostModal
                    post={selectedPost}
                    onClose={handleCloseModal}
                    onLike={handleLike}
                    onPostClick={handlePostClick}
                    onComment={handleComment}
                    onCommentReaction={handleCommentReaction}
                    onBump={handleBumpClick}
                    onBookmark={handleBookmark}
                    onEdit={handleEditPost}
                    onDelete={handleDeleteClick}
                    onArchive={handleArchivePost}
                    onUnarchive={handleUnarchivePost}
                    onDeleteComment={handleDeleteComment}
                    onEditComment={handleEditComment}
                    onToggleVisibility={handleToggleVisibility}
                    onToggleCaption={handleToggleCaption}
                    onUpdateTextSize={handleUpdateTextSize}
                    onUpdatePostcardStyle={handleUpdatePostcardStyle}
                    onNavigateToProfile={handleNavigateToProfile}
                    onNavigateToHashtag={handleNavigateToHashtag}
                    onRespondLinkRequest={handleRespondLinkRequest}
                    isVisible={isModalVisible}
                    animatingLikes={animatingLikes}
                    animatingBumps={animatingBumps}
                    animatingComments={animatingComments}
                    animatingBookmarks={animatingBookmarks}
                    setPage={setPage}
                />
            )}

            <ShareToGoodLyfeModal
                isOpen={isShareToGoodLyfeModalOpen}
                onClose={() => {
                    setIsShareToGoodLyfeModalOpen(false);
                    setIncomingShareData(null);
                    if (window.history.state?.modal === 'share_intent') {
                        window.history.back();
                    }
                }}
                sharedData={incomingShareData}
                currentUser={currentUser}
                usersData={usersData}
                communities={communities}
                onShare={handleShareToGoodLyfe}
            />


            {removeConnectionModalState && (
                <RemoveConnectionModal
                    isOpen={removeConnectionModalState.isOpen}
                    onClose={() => setRemoveConnectionModalState(null)}
                    onConfirm={() => {
                        const targetId = removeConnectionModalState.targetId;
                        setRemoveConnectionModalState(null);
                        handleFriendToggle(targetId, true);
                    }}
                    type={removeConnectionModalState.type}
                    targetUsername={usersData[removeConnectionModalState.targetId]?.displayName || removeConnectionModalState.targetId}
                />
            )}

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                onConfirm={handleConfirmDelete}
                post={postToDelete}
            />
            <BumpActionModal
                isOpen={isBumpActionModalOpen}
                onClose={closeBumpActionModal}
                onConfirm={confirmBumpAgain}
                onRemove={removeBump}
                post={postToBump}
            />

            {/* Global Post FAB */}
            {currentUser && !isGlobalPostModalOpen && !['messages', 'settings', 'mod'].includes(page.type) && (() => {
                let isVoiceOrChat = false;
                if (page.type === 'community_detail' && page.channelId) {
                    if (page.channelId === 'dm' || page.channelId === 'yipyap') {
                        isVoiceOrChat = true;
                    } else {
                        const community = communities.find((c: any) => c._id === page.query);
                        const channel = community?.channels?.find((ch: any) => ch.id === page.channelId);
                        if (channel && (channel.type === 'voice' || channel.type === 'chat')) {
                            isVoiceOrChat = true;
                        }
                    }
                }
                if (isVoiceOrChat) return null;

                const hexToRgb = (hex: string) => {
                    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                    return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
                };
                const rgb = hexToRgb(appThemeColor);
                const opacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;
                const bgColor = rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})` : appThemeColor;
                const textColor = currentUserData?.appTextColor || '#ffffff';

                return (
                    <button
                        onClick={() => {
                            setIsGlobalPostModalOpen(true);
                        }}
                        className="fixed bottom-24 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105 z-40 border border-white/20 hover:brightness-110"
                        style={{ backgroundColor: bgColor, color: textColor, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
                        aria-label="Create Post"
                    >
                        <PlusIcon className="w-8 h-8" />
                    </button>
                );
            })()}

            <GlobalPostModal
                isOpen={isGlobalPostModalOpen}
                onClose={() => {
                    setIsGlobalPostModalOpen(false);
                    setGlobalPostMedia(undefined);
                }}
                onCreatePost={async (data) => {
                    await handleCreatePost(data);
                    setIsGlobalPostModalOpen(false);
                }}
                communities={communities}
                conversations={conversations}
                postBlurOpacity={currentUserData?.postBlurOpacity ?? 50}
                achievements={achievements}
                dailyAchievements={dailyAchievements}
                initialMedia={globalPostMedia}
                initialDestination={(page.type === 'mod' && page.modId === 'vibeman1_footprint') ? 'footprint' : undefined}
            />

            <BumpModal
                isOpen={isBumpModalOpen}
                onClose={closeBumpModal}
                onConfirm={handleConfirmBump}
                onNavigateToProfile={handleNavigateToProfile}
                onNavigateToHashtag={handleNavigateToHashtag}
                post={postToBump}
                currentUser={currentUser}
                usersData={usersData}
            />
            <AudioPlayerPill onPostClick={handlePostClick} />
            {currentUserData && <GlobalSettingsModal
                isOpen={isSettingsModalOpen}
                onNavigateToFootprint={() => setPage({ type: 'mod', modId: 'vibeman1_footprint' })}
                onNavigateToPage={(pageType: string, tab?: string) => {
                    if (pageType === 'vibeman1_footprint') {
                        setPage({ type: 'mod', modId: 'vibeman1_footprint' });
                    } else if (pageType === 'messages' && tab) {
                        setPage({ type: 'messages', tab: tab as any });
                    } else {
                        setPage({ type: pageType as any });
                    }
                }}
                initialTab={
                    page.type === 'search' ? 'search' :
                        page.type === 'notifications' ? 'notifs' :
                            (page.type === 'mod' && page.modId === 'pimp_my_ride') ? 'store' :
                                'settings'
                }
                onTabChange={(tab) => {
                    const typeMap: Record<string, PageState> = {
                        'search': { type: 'search' },
                        'notifs': { type: 'notifications' },
                        'store': { type: 'mod', modId: 'pimp_my_ride' },
                        'settings': { type: 'settings' }
                    };
                    setPage(typeMap[tab]);
                }}
                allPosts={posts}
                onClose={handleCloseModal}
                bg={currentUserData.homeBg}
                onBgChange={handleHomeBgChange}
                homeBgResolution={currentUserData.homeBgResolution}
                onHomeBgResolutionChange={(res: number) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], homeBgResolution: res } }));
                    if (!isDemo) updateUserOnServer(currentUser, { homeBgResolution: res }).catch(console.error);
                }}
                profileBg={currentUserData.profileBg}
                onProfileBgChange={handleProfileBgChange}
                profileBgResolution={currentUserData.profileBgResolution}
                onProfileBgResolutionChange={(res: number) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], profileBgResolution: res } }));
                    if (!isDemo) updateUserOnServer(currentUser, { profileBgResolution: res }).catch(console.error);
                }}
                homeBgSlideshow={currentUserData.homeBgSlideshow}
                onHomeBgSlideshowChange={(slideshow) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], homeBgSlideshow: slideshow } }));
                    if (!isDemo) updateUserOnServer(currentUser, { homeBgSlideshow: slideshow }).catch(console.error);
                }}
                profileBgSlideshow={currentUserData.profileBgSlideshow}
                onProfileBgSlideshowChange={(slideshow) => {
                    if (!currentUser || !updateUserData) return;
                    const defaults = getDefaultUserData(currentUser);
                    updateUserData((prev: Record<string, UserData>) => ({
                        ...prev,
                        [currentUser]: {
                            ...prev[currentUser],
                            profileBgSlideshow: slideshow,
                            profileBg: {
                                ...(prev[currentUser]?.profileBg || {}),
                                url: defaults.profileBg.url
                            }
                        }
                    }));
                    if (!isDemo) {
                        updateUserOnServer(currentUser, {
                            profileBgSlideshow: slideshow,
                            profileBg: {
                                url: defaults.profileBg.url,
                                style: currentUserData.profileBg?.style || defaults.profileBg.style || 'cover'
                            }
                        }).catch(console.error);
                    }
                }}
                currentAnimation={galleryAnimation}
                onAnimationChange={(val: string) => {
                    setGalleryAnimation(val);
                    if (currentUser && !isDemo) {
                        updateUserOnServer(currentUser, { galleryAnimation: val }).catch(console.error);
                    }
                }}
                currentOpacity={postBlurOpacity}
                onOpacityChange={(val: number) => {
                    setPostBlurOpacity(val);
                    if (currentUser && !isDemo) {
                        updateUserOnServer(currentUser, { postBlurOpacity: val }).catch(console.error);
                    }
                }}
                uiScale={uiScale}
                onUiScaleChange={(val: number) => {
                    setUiScale(val);
                    if (currentUser && !isDemo) {
                        updateUserOnServer(currentUser, { uiScale: val }).catch(console.error);
                    }
                }}
                onLogout={logout}
                allowFollowers={currentUserData.allowFollowers !== false}
                onAllowFollowersChange={(val: boolean) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], allowFollowers: val } }));
                    if (!isDemo) updateUserOnServer(currentUser, { allowFollowers: val }).catch(console.error);
                }}
                autoApproveFollows={currentUserData.autoApproveFollows}
                onAutoApproveFollowsChange={(val: boolean) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], autoApproveFollows: val } }));
                    if (!isDemo) updateUserOnServer(currentUser, { autoApproveFollows: val }).catch(console.error);
                }}
                showCommunityPostsOnHomeFeed={!!currentUserData.showCommunityPostsOnHomeFeed}
                onShowCommunityPostsOnHomeFeedChange={(val: boolean) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], showCommunityPostsOnHomeFeed: val } }));
                    if (!isDemo) updateUserOnServer(currentUser, { showCommunityPostsOnHomeFeed: val }).catch(console.error);
                }}
                enableNavbarAutohide={!!currentUserData.enableNavbarAutohide}
                onEnableNavbarAutohideChange={(val: boolean) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], enableNavbarAutohide: val } }));
                    if (!isDemo) updateUserOnServer(currentUser, { enableNavbarAutohide: val }).catch(console.error);
                }}
                notificationSettings={currentUserData.notificationSettings || { likes: true, comments: true, follows: true, vouches: true, bumps: true, communities: true, linkRequests: true }}
                onNotificationSettingsChange={(settings: any) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], notificationSettings: settings } }));
                    if (!isDemo) updateUserOnServer(currentUser, { notificationSettings: settings }).catch(console.error);
                }}
                onNavigateToPrivacy={() => {
                    setPage({ type: 'privacy' });
                }}
                onNavigateToSafety={() => {
                    setPage({ type: 'safety' });
                }}
                onNavigateToAdditionalSettings={() => {
                    setPage({ type: 'additional_settings' });
                }}
                enforceThemeColor={currentUserData.enforceThemeColor}
                onEnforceThemeColorChange={(val: boolean) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], enforceThemeColor: val } }));
                    if (!isDemo) updateUserOnServer(currentUser, { enforceThemeColor: val }).catch(console.error);
                }}
                enforceThemeOpacity={currentUserData.enforceThemeOpacity}
                onEnforceThemeOpacityChange={(val: boolean) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], enforceThemeOpacity: val } }));
                    if (!isDemo) updateUserOnServer(currentUser, { enforceThemeOpacity: val }).catch(console.error);
                }}
                enforceTextColor={currentUserData.enforceTextColor}
                onEnforceTextColorChange={(val: boolean) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], enforceTextColor: val } }));
                    if (!isDemo) updateUserOnServer(currentUser, { enforceTextColor: val }).catch(console.error);
                }}
                onShare={handleSharePost}
                commentsTabVisibility={currentUserData.commentsTabVisibility || 'public'}
                onCommentsTabVisibilityChange={(val: 'public' | 'followers' | 'private') => {
                    if (updateUserData && currentUser) {
                        updateUserData((prev: Record<string, UserData>) => ({
                            ...prev,
                            [currentUser]: { ...prev[currentUser], commentsTabVisibility: val }
                        }));
                        if (!isDemo) updateUserOnServer(currentUser, { commentsTabVisibility: val }).catch(console.error);
                    }
                }}
                favoriteEmoji={currentUserData.favoriteEmoji || '❤️'}
                onFavoriteEmojiChange={(val: string) => {
                    if (updateUserData && currentUser) {
                        updateUserData((prev: Record<string, UserData>) => ({
                            ...prev,
                            [currentUser]: { ...prev[currentUser], favoriteEmoji: val }
                        }));
                        if (!isDemo) updateUserOnServer(currentUser, { favoriteEmoji: val }).catch(console.error);
                    }
                }}
                postcardDefault={currentUserData.postcardDefault}
                onPostcardDefaultChange={(val: string) => {
                    if (updateUserData && currentUser) {
                        updateUserData((prev: Record<string, UserData>) => ({
                            ...prev,
                            [currentUser]: { ...prev[currentUser], postcardDefault: val }
                        }));
                        if (!isDemo) updateUserOnServer(currentUser, { postcardDefault: val }).catch(console.error);
                    }
                }}
                postcardAppearance={currentUserData.postcardAppearance}
                onPostcardAppearanceChange={(val: 'original' | 'equipped') => {
                    if (updateUserData && currentUser) {
                        updateUserData((prev: Record<string, UserData>) => ({
                            ...prev,
                            [currentUser]: { ...prev[currentUser], postcardAppearance: val }
                        }));
                        if (!isDemo) updateUserOnServer(currentUser, { postcardAppearance: val }).catch(console.error);
                    }
                }}
                notifications={currentUserData.notifications || []}
                onMarkAsRead={handleMarkNotificationAsRead}
                onNavigateToPost={(postId) => {
                    const p = posts.find(x => x.id.toString() === postId.toString());
                    if (p?.footprintDetails) {
                        setPage({ type: 'mod', modId: 'vibeman1_footprint' });
                    } else if (p) {
                        handlePostClick(getHydratedPosts([p])[0]);
                    } else {
                        setPage({ type: 'post', postId });
                    }
                }}
                onNavigateToProfile={handleNavigateToProfile}
                onNavigateToChat={(username) => setPage({ type: 'messages', username })}
                onNavigateToHashtag={handleNavigateToHashtag}
                allUsers={usersData}
                communities={communities}
                hasUnreadNotifications={hasUnreadNotifications}
                homeBgMode={currentUserData.homeBgMode || 'image'}
                onHomeBgModeChange={(mode: 'image' | 'color') => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], homeBgMode: mode } }));
                    if (!isDemo) updateUserOnServer(currentUser, { homeBgMode: mode }).catch(console.error);
                }}
                homeBgColor={currentUserData.homeBgColor || '#000000'}
                onHomeBgColorChange={(color: string) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], homeBgColor: color } }));
                    if (!isDemo) updateUserOnServer(currentUser, { homeBgColor: color }).catch(console.error);
                }}
                profileBgMode={currentUserData.profileBgMode || 'image'}
                onProfileBgModeChange={(mode: 'image' | 'color') => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], profileBgMode: mode } }));
                    if (!isDemo) updateUserOnServer(currentUser, { profileBgMode: mode }).catch(console.error);
                }}
                profileBgColor={currentUserData.profileBgColor || '#000000'}
                onProfileBgColorChange={(color: string) => {
                    if (!currentUser || !updateUserData) return;
                    updateUserData((prev: Record<string, UserData>) => ({ ...prev, [currentUser]: { ...prev[currentUser], profileBgColor: color } }));
                    if (!isDemo) updateUserOnServer(currentUser, { profileBgColor: color }).catch(console.error);
                }}
                homeBgImage={currentUserData.homeBg?.url}
                profileBgImage={currentUserData.profileBg?.url}
                loginBg={usersData['goodlyfe']?.loginBg}
                onLoginBgChange={handleLoginBgChange}
                loginBgMode={usersData['goodlyfe']?.loginBgMode}
                onLoginBgModeChange={handleLoginBgModeChange}
                loginBgColor={usersData['goodlyfe']?.loginBgColor}
                onLoginBgColorChange={handleLoginBgColorChange}
                loginBgResolution={usersData['goodlyfe']?.loginBgResolution || 100}
                onLoginBgResolutionChange={handleLoginBgResolutionChange}
                loginBgSlideshow={usersData['goodlyfe']?.loginBgSlideshow}
                onLoginBgSlideshowChange={(slideshow) => {
                    const targetUserId = 'goodlyfe';
                    if (!targetUserId || !updateUserData) return;
                    const defaults = getDefaultUserData(targetUserId);
                    const defaultLoginUrl = defaults.loginBg?.url || 'https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2029&auto=format&fit=crop';
                    updateUserData((prev: Record<string, UserData>) => ({
                        ...prev,
                        [targetUserId]: {
                            ...prev[targetUserId],
                            loginBgSlideshow: slideshow,
                            loginBg: {
                                ...(prev[targetUserId]?.loginBg || {}),
                                url: defaultLoginUrl
                            }
                        }
                    }));
                    if (!isDemo) {
                        updateUserOnServer(targetUserId, {
                            loginBgSlideshow: slideshow,
                            loginBg: {
                                url: defaultLoginUrl,
                                style: usersData['goodlyfe']?.loginBg?.style || defaults.loginBg?.style || 'cover'
                            }
                        }).catch(console.error);
                    }
                }}
                isAdmin={currentUserData.role === 'admin' || currentUser === 'goodlyfe'}
                onOpenDebug={() => {
                    setIsDebugOpen(true);
                    pushModalState('debug');
                }}
                onNavigateToMasterHand={() => setPage({ type: 'master_hand' })}
                onOpenIdeaBank={() => {
                    setIsIdeaBankOpen(true);
                    pushModalState('idea_bank');
                }}
            />}

            <DebugLogViewer
                isOpen={isDebugOpen}
                onClose={() => {
                    setIsDebugOpen(false);
                    if (window.history.state?.modal === 'debug') {
                        window.history.back();
                    }
                }}
                onRefreshPush={() => {
                    if (currentUser) {
                        requestAppPermissions(setPage, currentUser);
                    }
                }}
                currentUser={currentUser}
            />
            {currentUser && (
                <IdeaBankModal
                    isOpen={isIdeaBankOpen}
                    onClose={() => {
                        setIsIdeaBankOpen(false);
                        if (window.history.state?.modal === 'idea_bank') {
                            window.history.back();
                        }
                    }}
                    username={currentUser}
                />
            )}
            {currentUser && (
                <AdditionalSettingsModal
                    isOpen={isAdditionalSettingsModalOpen}
                    onClose={() => {
                        setPage({ type: 'home' });
                    }}
                    username={currentUser}
                    onLogout={logout}
                    usersData={usersData}
                    onUnblockUser={handleUnblockUser}
                />
            )}
            <FollowListModal
                isOpen={followListModalState.isOpen}
                onClose={() => {
                    setFollowListModalState({ isOpen: false, listType: null, userId: null });
                    if (window.history.state?.modal === 'follow_list') {
                        window.history.back();
                    }
                }}
                listType={followListModalState.listType}
                user={followListModalState.userId ? { id: followListModalState.userId, data: usersData[followListModalState.userId] } : null}
                usersData={usersData}
                onNavigateToProfile={handleNavigateToProfile}
                currentUser={currentUser || undefined}
            />
            {currentUserData && (
                <SystemModModal
                    isOpen={isSystemModModalOpen}
                    onClose={() => setIsSystemModModalOpen(false)}
                    customMods={(() => {
                        const officialMods = getAllMods(usersData).filter((m: any) => m.author === currentUser);
                        const userCustomMods = currentUserData.customMods || [];
                        // Create a map to avoid duplicates, prioritizing userCustomMods
                        const merged = [...userCustomMods];
                        officialMods.forEach((om: any) => {
                            if (!merged.find((m: any) => m.id === om.id)) {
                                merged.push({
                                    id: om.id,
                                    name: om.name,
                                    icon: typeof om.icon === 'string' ? om.icon : 'https://api.dicebear.com/7.x/bottts/svg?seed=mod'
                                });
                            }
                        });
                        return merged;
                    })()}
                    activeSystemModId={currentUserData.activeSystemModId}
                    currentUser={currentUser}
                    onSaveMod={(mod: SystemMod) => {
                        const existing = currentUserData.customMods || [];
                        const index = existing.findIndex((m: SystemMod) => m.id === mod.id);
                        let next: SystemMod[];
                        if (index > -1) {
                            next = [...existing];
                            next[index] = mod;
                        } else {
                            next = [...existing, mod];
                        }
                        updateUserData((prev: any) => ({ ...prev, [currentUser!]: { ...prev[currentUser!], customMods: next, activeSystemModId: mod.id } }));
                        if (!isDemo) updateUserOnServer(currentUser!, { customMods: next, activeSystemModId: mod.id }).catch(console.error);
                    }}
                    onDeleteMod={(modId: string) => {
                        const next = (currentUserData.customMods || []).filter((m: SystemMod) => m.id !== modId);
                        const nextActive = currentUserData.activeSystemModId === modId ? undefined : currentUserData.activeSystemModId;
                        updateUserData((prev: any) => ({ ...prev, [currentUser!]: { ...prev[currentUser!], customMods: next, activeSystemModId: nextActive } }));
                        if (!isDemo) updateUserOnServer(currentUser!, { customMods: next, activeSystemModId: nextActive }).catch(console.error);
                    }}
                    onSelectMod={(modId: string) => {
                        const nextId = currentUserData.activeSystemModId === modId ? undefined : modId;
                        updateUserData((prev: any) => ({ ...prev, [currentUser!]: { ...prev[currentUser!], activeSystemModId: nextId } }));
                        if (!isDemo) updateUserOnServer(currentUser!, { activeSystemModId: nextId }).catch(console.error);
                    }}
                />
            )}
            {currentUserData?.role === 'admin' && (
                <FootprintAdminModal
                    isOpen={isAdminModalOpen}
                    onClose={() => setIsAdminModalOpen(false)}
                    allAchievements={achievements}
                    dailyAchievements={dailyAchievements}
                    onSetDaily={handleSetDailyAchievement}
                    onAddAchievement={handleAddAchievement}
                    onDeleteAchievement={handleDeleteAchievement}
                    onEditAchievement={handleEditAchievement}
                    onReorderAchievements={handleReorderAchievements}
                />
            )}
            <UploadProgressBar />
            <BackgroundVideoRenderer />
            {flashMessage && (
                <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-6 py-3 rounded-full shadow-2xl z-[100] animate-fade-in-up font-bold">
                    {flashMessage}
                </div>
            )}

            {cart.length > 0 && (
                <button
                    onClick={() => setIsCartOpen(true)}
                    className="fixed bottom-24 right-6 bg-green-500 hover:bg-green-600 text-white font-black p-4 rounded-full shadow-2xl z-[90] animate-bounce transition-transform active:scale-95 cursor-pointer flex items-center gap-2 border border-white/20"
                    style={{ ...stickerTextStyle(0.18) }}
                    title="Open Cart"
                >
                    <span className="text-xl">🛒</span>
                    <span className="bg-white text-green-600 rounded-full px-2 py-0.5 text-xs font-black">
                        {cart.reduce((sum, item) => sum + item.quantity, 0)}
                    </span>
                </button>
            )}

            <ShoppingCartDrawer
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
                cart={cart}
                onRemoveFromCart={handleRemoveFromCart}
                onUpdateQuantity={handleUpdateCartQuantity}
                onClearCart={handleClearCart}
                setFlashMessage={setFlashMessage}
            />
            {/* Logs FAB */}
            {(currentUserData?.role === 'admin' || currentUser === 'goodlyfe') && currentUserData?.showLogsFab && (
                <button
                    onClick={() => {
                        setIsDebugOpen(true);
                        pushModalState('debug');
                    }}
                    className="fixed bottom-36 left-6 bg-black/60 hover:bg-black/80 backdrop-blur-md text-white font-black p-4 rounded-full shadow-2xl z-[90] active:scale-95 cursor-pointer flex items-center justify-center gap-2 border border-white/20 transition-all duration-300"
                    style={{ ...stickerTextStyle(0.18) }}
                    title="Open Logs"
                >
                    <span className="text-xl">📋</span>
                    <span className="text-[10px] uppercase tracking-widest hidden sm:inline-block">Logs</span>
                </button>
            )}
            {/* Single shared iframe — follows postcard placeholder rect when inline, snaps to corner when PiP */}
            {activeVideo && (() => {
                const pip = true;
                const isShorts = activeVideo.isShorts;
                const isPiP = activeVideoRect ? activeVideoRect.isPiP : true;
                const sharedStyle: React.CSSProperties = {
                    position: 'fixed',
                    top: isPiP ? 'auto' : (activeVideoRect?.top ?? 0),
                    left: isPiP ? 'auto' : (activeVideoRect?.left ?? 0),
                    bottom: isPiP ? '96px' : 'auto',
                    right: isPiP ? '16px' : 'auto',
                    width: isPiP ? (isShorts ? '162px' : '256px') : (activeVideoRect?.width ?? '100%'),
                    height: isPiP ? (isShorts ? '288px' : '144px') : (activeVideoRect?.height ?? '100%'),
                    borderRadius: isPiP ? '12px' : '0px',
                    overflow: 'hidden',
                    boxShadow: isPiP ? '0 25px 50px -12px rgba(0,0,0,0.9)' : 'none',
                    border: isPiP ? '1px solid rgba(255,255,255,0.12)' : 'none',
                    backgroundColor: 'black',
                    zIndex: 99999,
                };
                return (
                    <motion.div
                        className="no-swipe"
                        initial={false}
                        animate={{
                            top: isPiP ? window.innerHeight - (isShorts ? 384 : 240) : (activeVideoRect?.top ?? 0),
                            left: isPiP ? window.innerWidth - (isShorts ? 178 : 272) : (activeVideoRect?.left ?? 0),
                            width: isPiP ? (isShorts ? '162px' : '256px') : (activeVideoRect?.width ?? '100%'),
                            height: isPiP ? (isShorts ? '288px' : '144px') : (activeVideoRect?.height ?? '100%'),
                            borderRadius: isPiP ? '16px' : '0px',
                            boxShadow: isPiP ? '0 25px 50px -12px rgba(0,0,0,0.9)' : 'none',
                        }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        style={{
                            position: 'fixed',
                            overflow: 'hidden',
                            border: isPiP ? '1px solid rgba(255,255,255,0.12)' : 'none',
                            backgroundColor: 'black',
                            zIndex: 99999,
                            touchAction: (isPiP && pipInteractMode) ? 'auto' : 'none',
                            pointerEvents: (isPiP || pipInteractMode) ? 'auto' : 'none'
                        }}
                        drag={isPiP && !pipInteractMode}
                        dragMomentum={false}
                        dragConstraints={{ top: -window.innerHeight + (isShorts ? 300 : 200), bottom: 20, left: -window.innerWidth + (isShorts ? 200 : 300), right: 20 }}
                        dragElastic={0.1}
                    >
                        {/* Drag handle covering the entire area when in PiP mode and not in interact mode */}
                        {isPiP && !pipInteractMode && (
                            <div
                                style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, cursor: 'grab', zIndex: 2, background: 'rgba(0,0,0,0.1)' }}
                                aria-label="Drag handle"
                            />
                        )}

                        {/* Top bar with controls (shown in both PiP and inline modes to float overlays correctly) */}
                        <div
                            style={{
                                position: 'absolute', top: 0, left: 0, right: 0, height: '40px',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '0 8px', zIndex: 3,
                                pointerEvents: 'none',
                                background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent)'
                            }}
                        >
                            {/* Left-side controls: Interact Toggle (clickable even if container pointerEvents is none) */}
                            <div style={{ pointerEvents: 'auto' }}>
                                <button
                                    onClick={() => setPipInteractMode(!pipInteractMode)}
                                    style={{
                                        background: 'rgba(0,0,0,0.7)', border: 'none',
                                        borderRadius: '12px', padding: '4px 8px',
                                        cursor: 'pointer', color: 'white',
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                        fontSize: '10px', fontWeight: 'bold',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                        transition: 'all 0.2s'
                                    }}
                                    aria-label={pipInteractMode ? "Lock position and drag" : "Interact with video controls"}
                                >
                                    {pipInteractMode ? '🔒 Lock' : '🔓 Interact'}
                                </button>
                            </div>

                            {/* Right-side controls: Watch on YouTube link and PiP close button */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', pointerEvents: 'auto' }}>


                                {isPiP && pip && (
                                    <button
                                        onClick={() => {
                                            setIgnoredPipIds((prev: Set<number>) => new Set(prev).add(activeVideo.id));
                                            setActiveVideo(null);
                                            setActiveVideoRect(null);
                                            setPipInteractMode(false);
                                        }}
                                        style={{
                                            background: 'rgba(0,0,0,0.7)', border: 'none',
                                            borderRadius: '50%', width: '20px', height: '20px',
                                            cursor: 'pointer', color: 'white',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '12px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                        }}
                                        aria-label="Close mini player"
                                    >×</button>
                                )}
                            </div>
                        </div>
                        <iframe
                            key={activeVideo.videoId}
                            src={`https://www.youtube.com/embed/${activeVideo.videoId}?autoplay=1&controls=1&playsinline=1&rel=0`}
                            allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                            allowFullScreen
                            title="YouTube Player"
                            style={{ width: '100%', height: '100%', border: 'none' }}
                        />
                    </motion.div>
                );
            })()}
            <ModLibraryModal
                isOpen={isModLibraryModalOpen}
                onClose={() => setIsModLibraryModalOpen(false)}
                activeMods={activeMods}
                onSelectMod={(modId) => {
                    setPinnedModId(modId);
                    setPage({ type: 'mod', modId });
                }}
                onOpenStore={() => handleNavigateWithAnimation({ type: 'mod', modId: 'pimp_my_ride' })}
                usersData={usersData}
            />
            <AccountSwitcherModal
                isOpen={isAccountSwitcherOpen}
                onClose={() => setIsAccountSwitcherOpen(false)}
                loggedInAccounts={loggedInAccounts}
                currentUserId={currentUser}
                usersData={usersData}
                onSwitchAccount={handleSwitchAccount}
                onAddAccount={handleAddAccount}
            />
            <YourCommunitiesModal
                isOpen={isYourCommunitiesModalOpen}
                onClose={() => setIsYourCommunitiesModalOpen(false)}
                communities={communities}
                onSelectCommunity={(communityId) => handleNavigateWithAnimation({ type: 'community_detail', query: communityId })}
            />
            {activeLiveNotification && (
                <LiveNotificationPopup
                    notification={activeLiveNotification}
                    usersData={usersData}
                    communities={communities}
                    currentUser={currentUser}
                    onClose={() => setActiveLiveNotification(null)}
                    onNavigateToPost={(postId) => {
                        const p = posts.find(x => x.id.toString() === postId.toString());
                        if (p?.footprintDetails) {
                            setPage({ type: 'mod', modId: 'vibeman1_footprint' });
                        } else {
                            if (p) handlePostClick(getHydratedPosts([p])[0]);
                            else setPage({ type: 'post', postId });
                        }
                    }}
                    onNavigateToProfile={(userId) => setPage({ type: 'profile', userId })}
                    onNavigateToChat={(username) => setPage({ type: 'messages', username })}
                    onNavigateToFootprint={() => setPage({ type: 'mod', modId: 'vibeman1_footprint' })}
                    onMarkAsRead={(id) => handleMarkNotificationAsRead(id)}
                />
            )}
            {incomingCall && (
                <div className="fixed inset-0 flex items-center justify-center evils-z-[9999] z-[9999] p-4 animate-fade-in bg-black/60 backdrop-blur-sm">
                    <div
                        className="rounded-[2.5rem] w-full max-w-sm overflow-hidden flex flex-col p-6 text-center border border-white/10 shadow-2xl"
                        style={{
                            backgroundColor: (() => {
                                const currentUserData = usersData[currentUser];
                                const themeColor = currentUserData?.appThemeColor || '#3b82f6';
                                return themeColor.startsWith('#') ? `${themeColor}cc` : 'rgba(22, 27, 34, 0.9)';
                            })(),
                            color: usersData[currentUser]?.appTextColor || '#ffffff'
                        }}
                    >
                        <div className="flex flex-col items-center space-y-4 my-6">
                            <div className="relative">
                                <img
                                    src={usersData[incomingCall.caller]?.pfp || 'assets/goodieboy.png'}
                                    alt={incomingCall.nickname}
                                    className="w-24 h-24 rounded-full border-4 border-white/20 object-cover shadow-lg"
                                />
                                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white animate-pulse" />
                            </div>

                            <div>
                                <h3
                                    className="text-2xl font-bold tracking-tight"
                                    style={stickerTextStyle(0.15)}
                                >
                                    {incomingCall.nickname}
                                </h3>
                                <p
                                    className="text-sm opacity-80 mt-1"
                                    style={stickerTextStyle(0.18)}
                                >
                                    Incoming Call...
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-center space-x-6 mt-6">
                            <button
                                onClick={handleDeclineCall}
                                className="w-16 h-16 flex items-center justify-center bg-red-500 hover:bg-red-600 active:scale-95 text-white rounded-full transition-all shadow-md"
                                title="Decline"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            <button
                                onClick={handleAcceptCall}
                                className="w-16 h-16 flex items-center justify-center bg-green-500 hover:bg-green-600 active:scale-95 text-white rounded-full transition-all shadow-md animate-bounce"
                                title="Accept"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}



const SavePasswordModal = ({
    pendingSaveCreds,
    onSave,
    onClose,
    usersData
}: {
    pendingSaveCreds: { username: string; pass: string } | null;
    onSave: () => void;
    onClose: () => void;
    usersData: Record<string, UserData>;
}) => {
    if (!pendingSaveCreds) return null;

    const username = pendingSaveCreds.username;
    const currentUserData = usersData[username];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center z-[9999] p-4 animate-fade-in">
            <div
                className="rounded-[2.5rem] w-full max-w-sm overflow-hidden flex flex-col border border-white/10 shadow-2xl backdrop-blur-md animate-scale-up"
                style={{ backgroundColor: modalBg, color: userTextColor }}
            >
                <div className="p-8 flex flex-col items-center text-center">
                    <div
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-white/10 shadow-lg"
                        style={{ color: userThemeColor }}
                    >
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>

                    <h3
                        className="text-2xl font-bold mb-2"
                        style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                    >
                        Save Password?
                    </h3>

                    <p
                        className="text-sm opacity-80 mb-6 px-2"
                        style={stickerTextStyle(0.18)}
                    >
                        Would you like to save credentials for <span className="font-bold">{username}</span> to sign in faster next time?
                    </p>

                    <div className="w-full bg-black/20 border border-white/10 rounded-2xl p-4 mb-6 flex items-center gap-3">
                        <img
                            src={currentUserData?.pfp || `https://api.dicebear.com/7.x/pixel-art/svg?seed=${username}`}
                            alt={username}
                            className="w-10 h-10 rounded-full border border-white/10 object-cover"
                        />
                        <div className="text-left">
                            <p className="text-sm font-bold text-white leading-none mb-1">{username}</p>
                            <p className="text-xs text-white/50 leading-none">Password Saved Locally</p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 w-full">
                        <button
                            onClick={onSave}
                            className="w-full py-3 rounded-2xl font-bold transition-all text-sm shadow-md active:scale-[0.98] hover:brightness-110 text-white"
                            style={{ backgroundColor: userThemeColor }}
                        >
                            Save Credentials
                        </button>
                        <button
                            onClick={onClose}
                            className="w-full py-3 rounded-2xl font-bold transition-all text-sm bg-white/5 hover:bg-white/10 text-white border border-white/10 active:scale-[0.98]"
                        >
                            Not Now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


function App() {
    const [initError, setInitError] = useState<string | null>(null);

    // Global Error Handler
    useEffect(() => {
        const handleError = (event: ErrorEvent) => {
            const msg = event.error?.message || event.message || 'Unknown error';
            if (msg === 'Script error.') return;
            setInitError(msg);
            console.error('[Global Error]', event);
        };
        const handleRejection = (event: PromiseRejectionEvent) => {
            const msg = `Promise rejected: ${event.reason?.message || event.reason || 'Unknown reason'}`;
            setInitError(msg);
            console.error('[Global Rejection]', event);
        };
        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleRejection);
        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleRejection);
        };
    }, []);

    // Safe Area fallback for Android edge-to-edge rendering
    useEffect(() => {
        const root = document.documentElement;
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
            const div = document.createElement('div');
            div.style.paddingTop = 'var(--safe-area-top)';
            document.body.appendChild(div);
            const computedTop = getComputedStyle(div).paddingTop;
            document.body.removeChild(div);

            if (computedTop === '0px') {
                root.style.setProperty('--safe-area-top', '24px');
                root.style.setProperty('--safe-area-bottom', '16px');
            } else {
                root.style.setProperty('--safe-area-top', 'var(--safe-area-top)');
                root.style.setProperty('--safe-area-bottom', 'var(--safe-area-bottom)');
            }
        } else {
            root.style.setProperty('--safe-area-top', 'var(--safe-area-top)');
            root.style.setProperty('--safe-area-bottom', 'var(--safe-area-bottom)');
        }
    }, []);

    const [usersData, setUsersData] = usePersistentState<Record<string, UserData>>('usersData', {});
    const [pendingSaveCreds, setPendingSaveCreds] = useState<{ username: string; pass: string } | null>(null);

    const [activeVideo, setActiveVideo] = useState<{ id: number, html: string, videoId: string, isShorts?: boolean } | null>(null);
    const [activeVideoRect, setActiveVideoRect] = useState<{ top: number, left: number, width: number, height: number, isPiP: boolean } | null>(null);
    const [ignoredPipIds, setIgnoredPipIds] = useState<Set<number>>(new Set());

    // Global Audio Session State
    const [activeAudioPost, setActiveAudioPost] = useState<any | null>(null);
    const [audioIsPlaying, setAudioIsPlaying] = useState(false);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);
    const [audioIsMuted, setAudioIsMuted] = useState(false);
    const [audioPlayerOpen, setAudioPlayerOpen] = useState(false);
    const globalAudioRef = useRef<HTMLAudioElement | null>(null);
    // Ref exposed to visualizer — no createMediaElementSource (breaks CORS audio)
    // Visualizer uses time-based pseudo-FFT instead
    const globalAnalyserRef = useRef<AnalyserNode | null>(null);
    const ensureGlobalAnalyser = () => { }; // no-op: kept for context shape compatibility

    // Audio Queue State
    const [audioQueue, setAudioQueue] = useState<any[]>([]);
    const [currentQueueIndex, setCurrentQueueIndex] = useState<number>(0);

    const audioQueueRef = useRef<any[]>([]);
    const currentQueueIndexRef = useRef<number>(0);

    useEffect(() => {
        audioQueueRef.current = audioQueue;
    }, [audioQueue]);

    useEffect(() => {
        currentQueueIndexRef.current = currentQueueIndex;
    }, [currentQueueIndex]);

    const hasInteractedRef = useRef(false);
    const [pendingAutoplay, setPendingAutoplay] = useState<any | null>(null);
    const resumeAudioPostRef = useRef<any>(null);
    const activeAudioPostRef = useRef<any>(null);
    const playAudioPostRef = useRef<any>(null);
    const audioIsPlayingRef = useRef<boolean>(false);

    useEffect(() => {
        activeAudioPostRef.current = activeAudioPost;
    }, [activeAudioPost]);

    useEffect(() => {
        audioIsPlayingRef.current = audioIsPlaying;
    }, [audioIsPlaying]);

    // Audio functions
    const playAudioPost = useCallback((post: any, playlistContext?: any[]) => {
        if (!hasInteractedRef.current) {
            console.log('[Autoplay] Queueing audio post for first user interaction:', post);
            setPendingAutoplay({ post, playlistContext });
        }

        const audio = globalAudioRef.current;
        if (!audio) return;

        const media = (post.type === 'bump' || post.type === 'repost') ? (post.originalPost?.content.media) : post.content.media;
        const firstAudio = media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
        if (!firstAudio) return;

        let newQueue = [...audioQueueRef.current];
        let newIndex = currentQueueIndexRef.current;

        if (playlistContext && playlistContext.length > 0) {
            const postIdx = newQueue.findIndex(item => item.id === post.id);
            if (postIdx !== -1) {
                newIndex = postIdx;
            } else {
                newQueue.push(post);
                newIndex = newQueue.length - 1;

                const otherTracks = playlistContext.filter(item => item.id !== post.id);
                newQueue = [...newQueue, ...otherTracks];
            }
        } else {
            const postIdx = newQueue.findIndex(item => item.id === post.id);
            if (postIdx !== -1) {
                newIndex = postIdx;
            } else {
                if (newQueue.length === 0) {
                    newQueue = [post];
                    newIndex = 0;
                } else {
                    newQueue[newIndex] = post;
                }
            }
        }

        setAudioQueue(newQueue);
        audioQueueRef.current = newQueue;
        setCurrentQueueIndex(newIndex);
        currentQueueIndexRef.current = newIndex;

        if (activeAudioPostRef.current && String(activeAudioPostRef.current.id) === String(post.id)) {
            const originalUrl = firstAudio.originalUrl || firstAudio.url || '';
            const previewUrl = firstAudio.url || '';
            const hasDirectPreview = previewUrl.includes('p.scdn.co') || previewUrl.includes('audio-ssl.itunes.apple.com') || previewUrl.includes('.mp3') || previewUrl.includes('.m4a');
            const isStreaming = !hasDirectPreview && (originalUrl.includes('spotify.com') || originalUrl.includes('apple.com') || originalUrl.includes('music.apple.com') || originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be'));
            if (isStreaming) {
                setAudioIsPlaying(!audioIsPlayingRef.current);
            } else {
                if (audioIsPlayingRef.current) {
                    audio.pause();
                    setAudioIsPlaying(false);
                } else {
                    if (audio.readyState === 0 || !audio.src) {
                        audio.src = firstAudio.url;
                        audio.load();
                    }
                    audio.play().then(() => setAudioIsPlaying(true)).catch((err) => {
                        console.warn('[Audio Play Blocked]', err);
                        setAudioIsPlaying(false);
                    });
                }
            }
        } else {
            const originalUrl = firstAudio.originalUrl || firstAudio.url || '';
            const previewUrl = firstAudio.url || '';
            const hasDirectPreview = previewUrl.includes('p.scdn.co') || previewUrl.includes('audio-ssl.itunes.apple.com') || previewUrl.includes('.mp3') || previewUrl.includes('.m4a');
            const isYoutube = originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be');
            const isStreaming = !hasDirectPreview && (originalUrl.includes('spotify.com') || originalUrl.includes('apple.com') || originalUrl.includes('music.apple.com') || isYoutube);
            if (isStreaming) {
                audio.src = '';
                setAudioDuration(0);
                setAudioCurrentTime(0);
                setActiveAudioPost(post);
                setAudioIsPlaying(true);
                if (isYoutube) {
                    let videoId = '';
                    const match = originalUrl.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
                    if (match) videoId = match[1];
                    if (videoId) {
                        setActiveVideo({ id: post.id, videoId: videoId, html: '' });
                    }
                }
            } else {
                audio.src = firstAudio.url;
                setAudioDuration(0);
                setAudioCurrentTime(0);
                audio.load();
                audio.play().then(() => {
                    setActiveAudioPost(post);
                    setAudioIsPlaying(true);
                }).catch((err) => {
                    console.warn('[Audio Autoplay Blocked]', err);
                    setActiveAudioPost(post);
                    setAudioIsPlaying(false);
                });
            }
        }
    }, [setActiveVideo]);

    useEffect(() => {
        playAudioPostRef.current = playAudioPost;
    }, [playAudioPost]);

    const playNextTrack = useCallback(() => {
        const queue = audioQueueRef.current;
        const curIndex = currentQueueIndexRef.current;
        if (queue.length === 0) return;
        const nextIndex = (curIndex + 1) % queue.length;
        const nextPost = queue[nextIndex];

        setCurrentQueueIndex(nextIndex);
        currentQueueIndexRef.current = nextIndex;

        const audio = globalAudioRef.current;
        if (!audio || !nextPost) return;
        const media = (nextPost.type === 'bump' || nextPost.type === 'repost') ? (nextPost.originalPost?.content.media) : nextPost.content.media;
        const firstAudio = media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
        if (!firstAudio) return;

        const originalUrl = firstAudio.originalUrl || firstAudio.url || '';
        const previewUrl = firstAudio.url || '';
        const hasDirectPreview = previewUrl.includes('p.scdn.co') || previewUrl.includes('audio-ssl.itunes.apple.com') || previewUrl.includes('.mp3') || previewUrl.includes('.m4a');
        const isYoutube = originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be');
        const isStreaming = !hasDirectPreview && (originalUrl.includes('spotify.com') || originalUrl.includes('apple.com') || originalUrl.includes('music.apple.com') || isYoutube);
        if (isStreaming) {
            audio.src = '';
            setAudioDuration(0);
            setAudioCurrentTime(0);
            setActiveAudioPost(nextPost);
            setAudioIsPlaying(true);
            if (isYoutube) {
                let videoId = '';
                const match = originalUrl.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
                if (match) videoId = match[1];
                if (videoId) {
                    setActiveVideo({ id: nextPost.id, videoId: videoId, html: '' });
                }
            }
        } else {
            audio.src = firstAudio.url;
            setAudioDuration(0);
            setAudioCurrentTime(0);
            audio.load();
            audio.play().then(() => {
                setActiveAudioPost(nextPost);
                setAudioIsPlaying(true);
            }).catch(() => { });
        }
    }, [setActiveVideo]);

    const playPrevTrack = useCallback(() => {
        const queue = audioQueueRef.current;
        const curIndex = currentQueueIndexRef.current;
        if (queue.length === 0) return;
        const prevIndex = (curIndex - 1 + queue.length) % queue.length;
        const prevPost = queue[prevIndex];

        setCurrentQueueIndex(prevIndex);
        currentQueueIndexRef.current = prevIndex;

        const audio = globalAudioRef.current;
        if (!audio || !prevPost) return;
        const media = (prevPost.type === 'bump' || prevPost.type === 'repost') ? (prevPost.originalPost?.content.media) : prevPost.content.media;
        const firstAudio = media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
        if (!firstAudio) return;

        const originalUrl = firstAudio.originalUrl || firstAudio.url || '';
        const prevPreviewUrl = firstAudio.url || '';
        const prevHasDirectPreview = prevPreviewUrl.includes('p.scdn.co') || prevPreviewUrl.includes('audio-ssl.itunes.apple.com') || prevPreviewUrl.includes('.mp3') || prevPreviewUrl.includes('.m4a');
        const isYoutube = originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be');
        const isStreaming = !prevHasDirectPreview && (originalUrl.includes('spotify.com') || originalUrl.includes('apple.com') || originalUrl.includes('music.apple.com') || isYoutube);
        if (isStreaming) {
            audio.src = '';
            setAudioDuration(0);
            setAudioCurrentTime(0);
            setActiveAudioPost(prevPost);
            setAudioIsPlaying(true);
            if (isYoutube) {
                let videoId = '';
                const match = originalUrl.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
                if (match) videoId = match[1];
                if (videoId) {
                    setActiveVideo({ id: prevPost.id, videoId: videoId, html: '' });
                }
            }
        } else {
            audio.src = firstAudio.url;
            setAudioDuration(0);
            setAudioCurrentTime(0);
            audio.load();
            audio.play().then(() => {
                setActiveAudioPost(prevPost);
                setAudioIsPlaying(true);
            }).catch(() => { });
        }
    }, [setActiveVideo]);

    useEffect(() => {
        const handleYoutubeMessage = (event: MessageEvent) => {
            if (event.data) {
                if (event.data.type === 'youtube-state') {
                    const { state } = event.data;
                    if (state === 0) { // ENDED
                        playNextTrack();
                    } else if (state === 1) { // PLAYING
                        setAudioIsPlaying(true);
                    } else if (state === 2) { // PAUSED
                        setAudioIsPlaying(false);
                    }
                } else if (event.data.type === 'youtube-progress') {
                    const { currentTime, duration } = event.data;
                    setAudioCurrentTime(currentTime);
                    setAudioDuration(duration);
                }
            }
        };
        window.addEventListener('message', handleYoutubeMessage);
        return () => {
            window.removeEventListener('message', handleYoutubeMessage);
        };
    }, [playNextTrack]);

    useEffect(() => {
        const handleFirstInteraction = () => {
            hasInteractedRef.current = true;
            if (activeAudioPostRef.current && !audioIsPlayingRef.current) {
                console.log('[Autoplay] First interaction detected. Synchronously playing/resuming active audio post:', activeAudioPostRef.current);
                if (playAudioPostRef.current) {
                    playAudioPostRef.current(activeAudioPostRef.current, audioQueueRef.current);
                }
            }
        };

        window.addEventListener('click', handleFirstInteraction, { once: true, capture: true });
        window.addEventListener('touchstart', handleFirstInteraction, { once: true, capture: true });
        window.addEventListener('touchmove', handleFirstInteraction, { once: true, capture: true });
        window.addEventListener('touchend', handleFirstInteraction, { once: true, capture: true });
        window.addEventListener('keydown', handleFirstInteraction, { once: true, capture: true });
        window.addEventListener('scroll', handleFirstInteraction, { once: true, capture: true });
        window.addEventListener('wheel', handleFirstInteraction, { once: true, capture: true });
        window.addEventListener('mousedown', handleFirstInteraction, { once: true, capture: true });
        window.addEventListener('pointerdown', handleFirstInteraction, { once: true, capture: true });

        return () => {
            window.removeEventListener('click', handleFirstInteraction, { capture: true });
            window.removeEventListener('touchstart', handleFirstInteraction, { capture: true });
            window.removeEventListener('touchmove', handleFirstInteraction, { capture: true });
            window.removeEventListener('touchend', handleFirstInteraction, { capture: true });
            window.removeEventListener('keydown', handleFirstInteraction, { capture: true });
            window.removeEventListener('scroll', handleFirstInteraction, { capture: true });
            window.removeEventListener('wheel', handleFirstInteraction, { capture: true });
            window.removeEventListener('mousedown', handleFirstInteraction, { capture: true });
            window.removeEventListener('pointerdown', handleFirstInteraction, { capture: true });
        };
    }, []);

    const pauseAudioPost = useCallback(() => {
        const audio = globalAudioRef.current;
        if (!audio) return;

        if (activeAudioPost) {
            const media = (activeAudioPost.type === 'bump' || activeAudioPost.type === 'repost') ? (activeAudioPost.originalPost?.content.media) : activeAudioPost.content.media;
            const firstAudio = media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
            const originalUrl = firstAudio?.originalUrl || firstAudio?.url || '';
            const isYoutube = originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be');
            if (isYoutube) {
                let videoId = '';
                const match = originalUrl.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
                if (match) videoId = match[1];
                if (videoId) {
                    const iframe = document.getElementById('youtube-iframe-player') as HTMLIFrameElement;
                    if (iframe && iframe.contentWindow) {
                        iframe.contentWindow.postMessage({
                            type: 'youtube-command',
                            videoId: videoId,
                            command: 'pause'
                        }, '*');
                    }
                }
            }
        }

        audio.pause();
        setAudioIsPlaying(false);
    }, [activeAudioPost]);

    const resumeAudioPost = useCallback(() => {
        const audio = globalAudioRef.current;
        if (!audio) return;

        if (activeAudioPost) {
            const media = (activeAudioPost.type === 'bump' || activeAudioPost.type === 'repost') ? (activeAudioPost.originalPost?.content.media) : activeAudioPost.content.media;
            const firstAudio = media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
            const originalUrl = firstAudio?.originalUrl || firstAudio?.url || '';
            const isYoutube = originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be');
            if (isYoutube) {
                let videoId = '';
                const match = originalUrl.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
                if (match) videoId = match[1];
                if (videoId) {
                    const iframe = document.getElementById('youtube-iframe-player') as HTMLIFrameElement;
                    if (iframe && iframe.contentWindow) {
                        iframe.contentWindow.postMessage({
                            type: 'youtube-command',
                            videoId: videoId,
                            command: 'play'
                        }, '*');
                    }
                }
                setAudioIsPlaying(true);
                return;
            }
        }

        audio.play().then(() => setAudioIsPlaying(true)).catch(() => { });
    }, [activeAudioPost]);

    useEffect(() => {
        resumeAudioPostRef.current = resumeAudioPost;
    }, [resumeAudioPost]);

    const seekAudioPost = useCallback((time: number) => {
        const audio = globalAudioRef.current;
        if (!audio) return;

        if (activeAudioPost) {
            const media = (activeAudioPost.type === 'bump' || activeAudioPost.type === 'repost') ? (activeAudioPost.originalPost?.content.media) : activeAudioPost.content.media;
            const firstAudio = media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
            const originalUrl = firstAudio?.originalUrl || firstAudio?.url || '';
            const isYoutube = originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be');
            if (isYoutube) {
                let videoId = '';
                const match = originalUrl.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
                if (match) videoId = match[1];
                if (videoId) {
                    const iframe = document.getElementById('youtube-iframe-player') as HTMLIFrameElement;
                    if (iframe && iframe.contentWindow) {
                        iframe.contentWindow.postMessage({
                            type: 'youtube-command',
                            videoId: videoId,
                            command: 'seek',
                            time: time
                        }, '*');
                    }
                }
                setAudioCurrentTime(time);
                return;
            }
        }

        audio.currentTime = time;
        setAudioCurrentTime(time);
    }, [activeAudioPost]);

    const toggleMuteAudio = useCallback(() => {
        const audio = globalAudioRef.current;
        if (!audio) return;
        audio.muted = !audio.muted;
        setAudioIsMuted(audio.muted);
    }, []);

    const closeAudioSession = useCallback(() => {
        const audio = globalAudioRef.current;
        if (audio) {
            audio.pause();
            audio.src = '';
        }
        setActiveAudioPost(null);
        setAudioIsPlaying(false);
        setAudioCurrentTime(0);
        setAudioDuration(0);
        setAudioPlayerOpen(false);
        setAudioQueue([]);
        setCurrentQueueIndex(0);
    }, []);

    // Sync React audio state to native MediaSession
    useEffect(() => {
        if (!activeAudioPost) return;

        const media = (activeAudioPost.type === 'bump' || activeAudioPost.type === 'repost') ? (activeAudioPost.originalPost?.content.media) : activeAudioPost.content.media;
        const firstAudio = media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
        if (!firstAudio) return;

        try {
            const audioName = firstAudio?.name;
            const artworkUrl = activeAudioPost.content.coverImageUrl || usersData[activeAudioPost.author]?.pfp || 'https://goodlyfe.net/logo.png';
            MediaSession.setMetadata({
                title: audioName || activeAudioPost.content.text || 'GoodLyfe Audio',
                artist: usersData[activeAudioPost.author]?.displayName || activeAudioPost.author,
                album: 'GoodLyfe',
                artwork: [
                    { src: artworkUrl, sizes: '512x512', type: 'image/png' }
                ]
            });
            MediaSession.setPlaybackState({
                playbackState: audioIsPlaying ? 'playing' : 'paused'
            });
            if (audioDuration > 0) {
                MediaSession.setPositionState({
                    duration: audioDuration,
                    playbackRate: 1,
                    position: audioCurrentTime || 0
                });
            }
        } catch (e) {
            console.warn('[MediaSession] Error setting metadata', e);
        }
    }, [activeAudioPost, audioIsPlaying, audioDuration, audioCurrentTime, usersData]);

    // Setup action handlers once
    useEffect(() => {
        try {
            MediaSession.setActionHandler({ action: 'play' }, () => {
                resumeAudioPost();
            });
            MediaSession.setActionHandler({ action: 'pause' }, () => {
                pauseAudioPost();
            });
            MediaSession.setActionHandler({ action: 'stop' }, () => {
                closeAudioSession();
            });
            MediaSession.setActionHandler({ action: 'seekto' }, (details: any) => {
                if (details.seekTime !== undefined && details.seekTime !== null) {
                    seekAudioPost(details.seekTime);
                }
            });
            MediaSession.setActionHandler({ action: 'nexttrack' }, () => {
                playNextTrack();
            });
            MediaSession.setActionHandler({ action: 'previoustrack' }, () => {
                const audio = globalAudioRef.current;
                if (audio && audio.currentTime > 2) {
                    seekAudioPost(0);
                } else {
                    playPrevTrack();
                }
            });
        } catch (e) {
            console.warn('[MediaSession] Error setting action handlers', e);
        }
    }, [resumeAudioPost, pauseAudioPost, closeAudioSession, seekAudioPost, playNextTrack, playPrevTrack]);



    const [currentUser, setCurrentUser] = useState<string | null>(null);
    const [loggedInAccounts, setLoggedInAccounts] = useState<string[]>(() => {
        const stored = localStorage.getItem('goodlyfe_logged_in_accounts');
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                console.error('Failed to parse logged in accounts:', e);
            }
        }
        const current = localStorage.getItem('currentUser');
        return current ? [current] : [];
    });
    const [communities, setCommunities] = useState<Community[]>([]);
    const [isDemo, setIsDemo] = useState<boolean>(() => localStorage.getItem('isDemo') === 'true');
    const [hasAcceptedEula, setHasAcceptedEula] = usePersistentState<boolean>(EULA_ACCEPTED_KEY, false);
    const [page, setPage] = useState<PageState>(() => urlToPage(window.location.pathname, true));
    const pageRef = useRef(page);
    useEffect(() => {
        pageRef.current = page;
    }, [page]);
    const sessionHistoryCountRef = useRef(0);

    const setPageState = useCallback((newPage: PageState | ((prev: PageState) => PageState)) => {
        setPage(newPage);
    }, []);
    const [isAppReady, setIsAppReady] = useState(false);
    const [homeFeedModeState, setHomeFeedMode] = useState<string>('');
    const homeFeedMode = useMemo(() => {
        if (homeFeedModeState) return homeFeedModeState;
        const userHomeTabsOrder = currentUser ? usersData[currentUser]?.homeTabsOrder : null;
        return (userHomeTabsOrder && userHomeTabsOrder.length > 0) ? userHomeTabsOrder[0] : 'public';
    }, [homeFeedModeState, currentUser, usersData]);

    const [footprintFeedModeState, setFootprintFeedMode] = useState<string>('');
    const footprintFeedMode = useMemo(() => {
        if (footprintFeedModeState) return footprintFeedModeState as any;
        const userFootprintTabsOrder = currentUser ? usersData[currentUser]?.footprintTabsOrder : null;
        return (userFootprintTabsOrder && userFootprintTabsOrder.length > 0) ? userFootprintTabsOrder[0] : 'all';
    }, [footprintFeedModeState, currentUser, usersData]);
    const [isAppFadeOut, setIsAppFadeOut] = useState(false);



    useEffect(() => {
        if (!(window as any).YT) {
            const tag = document.createElement('script');
            tag.src = "https://www.youtube.com/iframe_api";
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
        }
    }, []);

    // Fail-safe to keep isDemo state and localStorage in sync with currentUser
    useEffect(() => {
        if (currentUser === 'guest') {
            localStorage.setItem('isDemo', 'true');
            setIsDemo(true);
        } else {
            localStorage.removeItem('isDemo');
            setIsDemo(false);
        }
    }, [currentUser]);



    const login = async (username: string, pass: string) => {
        try {
            const user = await loginUser({ username, password: pass });
            const canonicalUsername = user.username || username;
            if (canonicalUsername.toLowerCase() === 'agk') {
                setUsersData(prev => ({
                    ...prev,
                    [canonicalUsername]: { ...prev[canonicalUsername], role: 'admin' }
                }));
            }
            localStorage.setItem('currentUser', canonicalUsername);
            localStorage.removeItem('isDemo');
            setLoggedInAccounts((prev: string[]) => {
                const next = prev.includes(canonicalUsername) ? prev : [...prev, canonicalUsername];
                localStorage.setItem('goodlyfe_logged_in_accounts', JSON.stringify(next));
                return next;
            });
            setCurrentUser(canonicalUsername);
            setIsDemo(false);
            console.log('[App] Login complete, user set:', canonicalUsername);
        } catch (serverError: any) {
            console.error('[App] Server login failed:', serverError);
            console.warn("Server login failed, falling back to local check (legacy mode)", serverError);
            return new Promise<void>((resolve, reject) => {
                const user = Object.entries(usersData).find(([name, data]) =>
                    name.toLowerCase() === username.toLowerCase() ||
                    data.email?.toLowerCase() === username.toLowerCase()
                );
                if (!user) {
                    return reject(new Error('No account found with that username or email.'));
                }
                if (user[1].password !== pass) {
                    const error = new Error('Invalid password. Please try again.');
                    (error as any).response = { status: 401 };
                    return reject(error);
                }
                const [userName] = user;
                localStorage.setItem('currentUser', userName);
                setLoggedInAccounts((prev: string[]) => {
                    const next = prev.includes(userName) ? prev : [...prev, userName];
                    localStorage.setItem('goodlyfe_logged_in_accounts', JSON.stringify(next));
                    return next;
                });
                setCurrentUser(userName);
                resolve();
            });
        }
    };

    const signup = async (username: string, email: string, pass: string) => {
        try {
            const newUser = await signupUser({ username, email, password: pass });
            const canonicalUsername = newUser.username || username;
            setUsersData(prev => ({ ...prev, [canonicalUsername]: newUser }));
            localStorage.setItem('currentUser', canonicalUsername);
            localStorage.removeItem('isDemo');
            setLoggedInAccounts((prev: string[]) => {
                const next = prev.includes(canonicalUsername) ? prev : [...prev, canonicalUsername];
                localStorage.setItem('goodlyfe_logged_in_accounts', JSON.stringify(next));
                return next;
            });
            setCurrentUser(canonicalUsername);
            setIsDemo(false);
        } catch (error: any) {
            console.error("Signup failed:", error);
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error);
            }
            throw error;
        }
    };

    const oauthLogin = async (userObj: any) => {
        const canonicalUsername = userObj.username;
        if (canonicalUsername.toLowerCase() === 'agk') {
            setUsersData(prev => ({
                ...prev,
                [canonicalUsername]: { ...prev[canonicalUsername], role: 'admin' }
            }));
        } else {
            setUsersData(prev => ({ ...prev, [canonicalUsername]: userObj }));
        }
        localStorage.setItem('currentUser', canonicalUsername);
        localStorage.removeItem('isDemo');
        setLoggedInAccounts((prev: string[]) => {
            const next = prev.includes(canonicalUsername) ? prev : [...prev, canonicalUsername];
            localStorage.setItem('goodlyfe_logged_in_accounts', JSON.stringify(next));
            return next;
        });
        setCurrentUser(canonicalUsername);
        setIsDemo(false);
    };

    const logout = () => {
        if (currentUser === 'guest') {
            sessionStorage.setItem('goodlyfe_guest_mode_dismissed', 'true');
        }

        // Remove current account from the logged-in list
        const nextAccounts = loggedInAccounts.filter((u: string) => u !== currentUser);
        setLoggedInAccounts(nextAccounts);
        localStorage.setItem('goodlyfe_logged_in_accounts', JSON.stringify(nextAccounts));

        if (nextAccounts.length > 0) {
            const nextUser = nextAccounts[nextAccounts.length - 1];
            localStorage.setItem('currentUser', nextUser);
            setCurrentUser(nextUser);
            if (nextUser === 'guest') {
                localStorage.setItem('isDemo', 'true');
                setIsDemo(true);
            } else {
                localStorage.removeItem('isDemo');
                setIsDemo(false);
            }
        } else {
            localStorage.removeItem('currentUser');
            localStorage.removeItem('isDemo');
            setCurrentUser(null);
            setIsDemo(false);
            // Force path to home to avoid auto-login loops on public routes.
            // MainApp will pick this up on its next mount via urlToPage(window.location.pathname).
            window.history.pushState({}, '', '/');
        }
    };

    const handleSwitchAccount = (username: string) => {
        localStorage.setItem('currentUser', username);
        setCurrentUser(username);
        if (username === 'guest') {
            localStorage.setItem('isDemo', 'true');
            setIsDemo(true);
        } else {
            localStorage.removeItem('isDemo');
            setIsDemo(false);
        }
        if (username !== 'goodlyfe') {
            setLoggedInAccounts((prev: string[]) => {
                const next = prev.includes(username) ? prev : [...prev, username];
                localStorage.setItem('goodlyfe_logged_in_accounts', JSON.stringify(next));
                return next;
            });
        }
    };

    const handleAddAccount = () => {
        setCurrentUser(null);
    };

    const handleCancelSwitchBack = () => {
        if (loggedInAccounts.length > 0) {
            const lastUser = loggedInAccounts[loggedInAccounts.length - 1];
            localStorage.setItem('currentUser', lastUser);
            setCurrentUser(lastUser);
            if (lastUser === 'guest') {
                localStorage.setItem('isDemo', 'true');
                setIsDemo(true);
            } else {
                localStorage.removeItem('isDemo');
                setIsDemo(false);
            }
        }
    };

    const loginAsDemo = useCallback(() => {
        sessionStorage.removeItem('goodlyfe_guest_mode_dismissed');
        const demoUser = 'guest';
        localStorage.setItem('isDemo', 'true');
        setIsDemo(true);
        setUsersData(prev => ({
            ...prev,
            [demoUser]: {
                ...getDefaultUserData(demoUser),
                displayName: 'Guest',
                bio: 'Just looking around 👀',
                pfp: `https://i.pravatar.cc/150?u=goodlyfe_guest`,
                following: ['vibeman1'],
                followers: [],
            }
        }));
        localStorage.setItem('currentUser', demoUser);
        setCurrentUser(demoUser);
    }, [setUsersData]);

    const guestLogin = async () => {
        try {
            const data = await guestLoginUser();
            const user = data.user;
            const canonicalUsername = user.username;
            localStorage.setItem('currentUser', canonicalUsername);
            localStorage.removeItem('isDemo');
            setLoggedInAccounts((prev: string[]) => {
                const next = prev.includes(canonicalUsername) ? prev : [...prev, canonicalUsername];
                localStorage.setItem('goodlyfe_logged_in_accounts', JSON.stringify(next));
                return next;
            });
            setCurrentUser(canonicalUsername);
            setIsDemo(false);
            console.log('[App] Guest login complete:', canonicalUsername);
            return user;
        } catch (serverError: any) {
            console.error('[App] Server guest login failed:', serverError);
            throw serverError;
        }
    };

    const authContextValue = {
        currentUser,
        usersData,
        setUsersData,
        communities,
        setCommunities,
        isAuthenticated: !!currentUser,
        isDemo,
        login,
        guestLogin,
        signup,
        logout,
        loginAsDemo,
        oauthLogin,
        updateUserData: setUsersData,
        page,
        setPageState,
        sessionHistoryCount: sessionHistoryCountRef,
        homeFeedMode,
        setHomeFeedMode,
        footprintFeedMode,
        setFootprintFeedMode,
        activeVideo,
        setActiveVideo,
        activeVideoRect,
        setActiveVideoRect,
        ignoredPipIds,
        setIgnoredPipIds,
        loggedInAccounts,
        setLoggedInAccounts,
        handleSwitchAccount,
        handleAddAccount
    };

    const audioPlayerStateValue = {
        activeAudioPost,
        setActiveAudioPost,
        audioIsPlaying,
        setAudioIsPlaying,
        audioIsMuted,
        audioPlayerOpen,
        setAudioPlayerOpen,
        playAudioPost,
        pauseAudioPost,
        resumeAudioPost,
        seekAudioPost,
        toggleMuteAudio,
        closeAudioSession,
        audioQueue,
        setAudioQueue,
        currentQueueIndex,
        setCurrentQueueIndex,
        playNextTrack,
        playPrevTrack,
        // Expose the global analyser ref so CustomAudioPlayer can tap real frequency data
        globalAnalyserRef,
        ensureGlobalAnalyser
    };

    const audioPlayerProgressValue = {
        audioCurrentTime,
        audioDuration
    };

    const handleNotificationNavigation = useCallback((pageState: PageState, recipient?: string) => {
        if (recipient && recipient !== currentUser) {
            const hasAccount = loggedInAccounts.includes(recipient);
            if (hasAccount) {
                console.log(`[Push] Switching account to recipient: ${recipient}`);
                handleSwitchAccount(recipient);
            } else {
                console.warn(`[Push] Recipient account ${recipient} is not logged in.`);
            }
        }
        setPage(pageState);
    }, [currentUser, loggedInAccounts, handleSwitchAccount, setPage]);

    useEffect(() => {
        // Only run auto-login check after initial sync and reconciliation is complete
        if (!isAppReady) return;

        // If there is any saved session for a real user, DO NOT auto-login to demo/guest mode
        const loggedInUser = localStorage.getItem('currentUser');
        if (loggedInUser && loggedInUser !== 'guest') {
            return;
        }

        // If they follow a deep link to a profile, storefront, or post, and they aren't logged in,
        // automatically log them in as a guest/demo user so they can view it instantly and their music starts playing!
        // But do NOT auto-login if they have explicitly dismissed guest mode to go to the login screen.
        const isDismissed = sessionStorage.getItem('goodlyfe_guest_mode_dismissed') === 'true';
        if (!currentUser && !isDismissed && (page.type === 'profile' || page.type === 'storefront' || page.type === 'post')) {
            console.log('[Guest Auto-Login] Auto-logging in as demo/guest user for deep link page:', page);
            loginAsDemo();
        }
    }, [currentUser, page, loginAsDemo, isAppReady]);

    // Initial Sync with Server
    useEffect(() => {
        if (currentUser) {
            console.log('[App] Triggering requestAppPermissions for:', currentUser);
            requestAppPermissions(handleNotificationNavigation, currentUser);
        }
    }, [currentUser, handleNotificationNavigation]);

    useEffect(() => {
        if (!isNative) return;

        // Handle Deep Linking (Universal Links and Custom Schemes)
        // Registered at top-level so it works even when logged out (e.g. password reset)
        CapApp.addListener('appUrlOpen', (data: any) => {
            console.log('[DeepLink] App opened with URL:', data.url);
            try {
                const url = new URL(data.url);
                // Normalize custom scheme goodlyfe://host/path to /host/path
                let path = url.pathname;
                if (url.protocol === 'goodlyfe:' && url.host) {
                    path = '/' + url.host + url.pathname;
                }
                const page = urlToPage(path, true);
                console.log('[DeepLink] Navigating from universal link to:', page);
                setPage(page);
            } catch (err) {
                console.error('[DeepLink] Error parsing URL:', err);
            }
        });

        // Handle cold start with a URL
        CapApp.getLaunchUrl().then(launchUrl => {
            if (launchUrl && launchUrl.url) {
                console.log('[DeepLink] App launched with URL:', launchUrl.url);
                try {
                    const url = new URL(launchUrl.url);
                    let path = url.pathname;
                    if (url.protocol === 'goodlyfe:' && url.host) {
                        path = '/' + url.host + url.pathname;
                    }
                    const page = urlToPage(path, true);
                    setPage(page);
                } catch (err) {
                    console.error('[DeepLink] Error parsing launch URL:', err);
                }
            }
        });
    }, []);

    const syncData = useCallback(async () => {
        try {
            const serverUsers = await fetchAllUsers();
            if (serverUsers) {
                const current = localStorage.getItem('currentUser');
                // console.log('[DEBUG-SYNC] Current User (localStorage):', current);
                // console.log('[DEBUG-SYNC] Server User Keys:', Object.keys(serverUsers));

                if (current && serverUsers[current]) {
                    // console.log(`[DEBUG-SYNC] FOUND exact match for ${current}. Mods:`, serverUsers[current].customMods?.length);
                } else if (current) {
                    const match = Object.keys(serverUsers).find(k => k.toLowerCase() === current.toLowerCase());
                    //console.log(`[DEBUG-SYNC] NO exact match for ${current}. Potential case-insensitive match:`, match);
                }

                setUsersData(prev => mergeServerUsers(serverUsers, prev, isDemo));
            }
        } catch (error) {
            console.error("Failed to sync with server:", error);
        } finally {
            console.log('[App] syncData operation finished');
        }
    }, [setUsersData, isDemo]);

    useEffect(() => {
        syncData();
    }, [syncData]);

    // Check for routes manually since we don't use React Router
    // Public routes that don't require authentication
    const isPublicPage = page.type === 'privacy' || page.type === 'safety';

    useEffect(() => {
        let watchdogTimer: NodeJS.Timeout;
        let isAborted = false;

        const initializeApp = async () => {
            try {
                console.log('--- App Version: 1.0.2 (Mod Sync Debug) ---');
                const loggedInUser = localStorage.getItem('currentUser');
                console.log('[App] Startup Trace: Stage 1 - Initialization Started. Found user:', loggedInUser);

                // Start watchdog timer (8 seconds)
                watchdogTimer = setTimeout(() => {
                    if (!isAppReady && !isAborted) {
                        console.warn('[App] Startup Watchdog: Initialization is taking too long. Forcing app ready state.');
                        setIsAppFadeOut(true);
                        setTimeout(() => setIsAppReady(true), 700);
                    }
                }, 8000);

                // 1. Sync User Data first to get backgrounds/settings
                console.log('[App] Startup Trace: Stage 2 - Syncing data...');
                try {
                    // Check if we already have the logged in user's data cached
                    const hasCachedData = loggedInUser && usersData[loggedInUser];
                    if (hasCachedData) {
                        console.log('[App] Found cached user data, bypassing await on syncData for instant startup.');
                        syncData(); // Fire and forget
                    } else {
                        await syncData();
                    }
                } catch (syncErr) {
                    console.error('[App] syncData failed in initializeApp:', syncErr);
                }
                console.log('[App] Startup Trace: Stage 3 - Sync step complete.');

                if (isAborted) return;

                // 2. Reconcile Current User
                const latestPage = pageRef.current;
                const isLatestPublicPage = latestPage.type === 'privacy' || latestPage.type === 'safety';
                let resolvedUser = currentUser;
                if (!currentUser) {
                    if (loggedInUser === 'guest' && isDemo && latestPage.type !== 'home') {
                        setCurrentUser('guest');
                        setIsDemo(true);
                        localStorage.setItem('isDemo', 'true');
                        resolvedUser = 'guest';
                    } else if (loggedInUser && loggedInUser !== 'guest') {
                        setCurrentUser(loggedInUser);
                        setIsDemo(false);
                        localStorage.removeItem('isDemo');
                        resolvedUser = loggedInUser;
                    } else if (!loggedInUser || (loggedInUser === 'guest' && latestPage.type === 'home')) {
                        const isContentRoute = [
                            'profile', 'post', 'community_detail', 'hashtag', 'leaderboards',
                            'communities', 'settings', 'additional_settings', 'footprint'
                        ].includes(latestPage.type);

                        if (isContentRoute && !isLatestPublicPage && latestPage.type !== 'reset_password') {
                            if (loggedInAccounts.length === 0) {
                                console.log('[App] Auto-logging in for content route:', latestPage.type);
                                loginAsDemo();
                                resolvedUser = 'guest';
                            } else {
                                console.log('[App] Skipping auto-login for content route because logged in sessions exist.');
                            }
                        }
                    }
                }

                // 3. Preload Background Image
                if (resolvedUser && usersData[resolvedUser]) {
                    const userData = usersData[resolvedUser];
                    let bgUrl = '';
                    if (latestPage.type === 'profile' && latestPage.userId && usersData[latestPage.userId]) {
                        bgUrl = usersData[latestPage.userId].profileBg?.url || '';
                    } else {
                        bgUrl = userData.homeBg?.url || '';
                    }

                    if (bgUrl && !isAborted) {
                        console.log('[App] Startup Trace: Stage 4 - Preloading background:', bgUrl);
                        await preloadImage(bgUrl);
                    }
                }

                console.log('[App] Startup Trace: Stage 5 - Finalizing setup...');

                // 4. Success - Clear watchdog and trigger fade out
                if (!isAborted) {
                    clearTimeout(watchdogTimer);
                    setTimeout(() => {
                        setIsAppFadeOut(true);
                        setTimeout(() => setIsAppReady(true), 700);
                    }, 100);
                }
            } catch (err) {
                console.error('[App] CRITICAL Error during initialization:', err);
                // Recovery: ensure we still clear the splash screen
                setIsAppFadeOut(true);
                setTimeout(() => setIsAppReady(true), 700);
            }
        };

        initializeApp();

        return () => {
            isAborted = true;
            clearTimeout(watchdogTimer);
        };
    }, []); // Only run ONCE at mount

    if (page.type === 'reset_password' && page.token) {
        return <ResetPasswordPage token={page.token} />;
    }
    return (
        <AuthContext.Provider value={authContextValue}>
            <AudioPlayerStateContext.Provider value={audioPlayerStateValue}>
                <AudioPlayerProgressContext.Provider value={audioPlayerProgressValue}>
                    {!isAppReady && (() => {
                        const goodlyfeData = usersData['goodlyfe'];
                        const loginBg = goodlyfeData?.loginBg;
                        const loginBgMode = goodlyfeData?.loginBgMode || 'image';
                        const loginBgColor = goodlyfeData?.loginBgColor || '#000000';
                        const loginBgResolution = goodlyfeData?.loginBgResolution || 100;
                        const loginBgSlideshow = goodlyfeData?.loginBgSlideshow;

                        let activeLoginBg = loginBg;
                        const slides = loginBgSlideshow?.attachments;
                        if (loginBgMode === 'image' && slides && slides.length > 0) {
                            activeLoginBg = { url: slides[0].url, style: loginBg?.style || 'cover' };
                        }

                        return (
                            <SplashScreen
                                fadeOut={isAppFadeOut}
                                loginBgMode={loginBgMode}
                                loginBgColor={loginBgColor}
                                loginBgResolution={loginBgResolution}
                                activeLoginBg={activeLoginBg}
                            />
                        );
                    })()}
                    <UploadProvider>
                        <ErrorBoundary onCrash={setInitError}>
                            <audio
                                id="global-audio-player"
                                ref={globalAudioRef}
                                preload="metadata"
                                style={{ position: 'fixed', left: '-9999px', top: '-9999px', width: '1px', height: '1px', pointerEvents: 'none', opacity: 0, zIndex: -9999 }}
                                tabIndex={-1}
                                onTimeUpdate={() => {
                                    const audio = globalAudioRef.current;
                                    if (!audio) return;
                                    setAudioCurrentTime(audio.currentTime);
                                    // For webm blobs, duration starts as Infinity — track it as it plays
                                    if (audio.duration !== Infinity && !isNaN(audio.duration) && audio.duration > 0) {
                                        setAudioDuration(audio.duration);
                                    } else if (audio.duration === Infinity && audio.currentTime > 0) {
                                        setAudioDuration(prev => Math.max(prev, audio.currentTime));
                                    }
                                }}
                                onLoadedMetadata={() => {
                                    const audio = globalAudioRef.current;
                                    if (!audio) return;
                                    if (!isNaN(audio.duration) && audio.duration !== Infinity && audio.duration > 0) {
                                        setAudioDuration(audio.duration);
                                    } else {
                                        // Chrome webm blob duration bug: seek to end to force duration calculation
                                        audio.currentTime = 1e10;
                                        setTimeout(() => {
                                            if (audio && !isNaN(audio.duration) && audio.duration !== Infinity) {
                                                setAudioDuration(audio.duration);
                                            }
                                            audio.currentTime = 0;
                                        }, 200);
                                    }
                                }}
                                onDurationChange={() => {
                                    const audio = globalAudioRef.current;
                                    if (audio && !isNaN(audio.duration) && audio.duration !== Infinity && audio.duration > 0) {
                                        setAudioDuration(audio.duration);
                                    }
                                }}
                                onPlay={() => setAudioIsPlaying(true)}
                                onPause={() => setAudioIsPlaying(false)}
                                onEnded={() => {
                                    if (audioQueueRef.current && audioQueueRef.current.length > 0) {
                                        playNextTrack();
                                    } else {
                                        setAudioIsPlaying(false);
                                    }
                                }}
                                onError={(e) => {
                                    console.error("Global audio error:", e);
                                    setAudioIsPlaying(false);
                                }}
                            />
                            <div style={{ visibility: isAppReady ? 'visible' : 'hidden', display: isAppReady ? 'block' : 'none' }}>
                                {authContextValue.isAuthenticated || isPublicPage ? (
                                    <MainApp />
                                ) : (
                                    <AuthPage
                                        hasAcceptedEula={hasAcceptedEula}
                                        setHasAcceptedEula={setHasAcceptedEula}
                                        loginBg={usersData['goodlyfe']?.loginBg}
                                        loginBgMode={usersData['goodlyfe']?.loginBgMode}
                                        loginBgColor={usersData['goodlyfe']?.loginBgColor}
                                        loginBgResolution={usersData['goodlyfe']?.loginBgResolution}
                                        loginBgSlideshow={usersData['goodlyfe']?.loginBgSlideshow}
                                        onLoginSuccess={(username, password) => {
                                            try {
                                                let saved: any[] = [];
                                                const stored = localStorage.getItem('goodlyfe_saved_credentials');
                                                if (stored) saved = JSON.parse(stored);
                                                const exists = saved.some(c => c.username.toLowerCase() === username.toLowerCase());
                                                if (!exists) {
                                                    setPendingSaveCreds({ username, pass: password });
                                                }
                                            } catch (e) {
                                                console.error(e);
                                            }
                                        }}
                                    />
                                )}
                            </div>
                            <SavePasswordModal
                                pendingSaveCreds={pendingSaveCreds}
                                onSave={() => {
                                    if (!pendingSaveCreds) return;
                                    try {
                                        let saved: any[] = [];
                                        const stored = localStorage.getItem('goodlyfe_saved_credentials');
                                        if (stored) saved = JSON.parse(stored);

                                        const obfuscated = btoa(unescape(encodeURIComponent(pendingSaveCreds.pass)));
                                        const updated = saved.filter(c => c.username.toLowerCase() !== pendingSaveCreds.username.toLowerCase());
                                        updated.unshift({
                                            username: pendingSaveCreds.username,
                                            password: obfuscated,
                                            savedAt: Date.now()
                                        });
                                        localStorage.setItem('goodlyfe_saved_credentials', JSON.stringify(updated));
                                    } catch (e) {
                                        console.error("Failed to save credentials:", e);
                                    }
                                    setPendingSaveCreds(null);
                                }}
                                onClose={() => setPendingSaveCreds(null)}
                                usersData={usersData}
                            />
                        </ErrorBoundary>
                    </UploadProvider>
                </AudioPlayerProgressContext.Provider>
            </AudioPlayerStateContext.Provider>
        </AuthContext.Provider>
    );
}

export default App;
